"use client";

import { ENABLE_KALSHI, ENABLE_PREDICT } from "@/lib/feature-flags";
import React, { useState, useEffect, useLayoutEffect, useMemo, useCallback, useRef, startTransition } from "react";
import { flushSync } from "react-dom";
import { useSearchParams, useRouter } from "next/navigation";
import Link from "next/link";
import dynamic from "next/dynamic";
import { usePrivy } from "@privy-io/react-auth";
import { useAuth } from "@/hooks/useAuth";
import { useT, useTCategory, useTKalshiSubcat } from "@/hooks/useT";
import { useLocale } from "@/hooks/useLocale";

const ExplosionEffect = dynamic(() => import("@/app/components/ExplosionEffect"), { ssr: false });
const AnimatedChevronLeft = dynamic(() => import("@/app/components/icons/ChevronLeftIcon").then(m => ({ default: m.ChevronLeftIcon })), { ssr: false });
const AnimatedChevronRight = dynamic(() => import("@/app/components/icons/ChevronRightIcon").then(m => ({ default: m.ChevronRightIcon })), { ssr: false });
import { usePageVisibility } from "@/hooks/usePageVisibility";
import CdnImage from "@/components/CdnImage";
import AnimatedPrice from "@/app/components/AnimatedPrice";
import { CATEGORY_SEO, categoryLabel } from "./categories-config";
import {
  getCachedCategory,
  setCategoryCache,
  isCacheValid,
  isPrefetchInProgress,
  markPrefetchStart,
  markPrefetchEnd,
  getCachedForYou,
  setCachedForYou,
  type CategoryCacheEntry,
} from "@/lib/markets-cache";
import { useCryptoSidebar, useFinanceSidebar, useKalshiSidebar, useKalshiSportsSidebar } from "@/hooks/useMarketsSidebar";
import { useKalshiLiveTickers } from "@/hooks/useKalshiLiveTickers";
import { useFollowingActivity } from "@/hooks/useFollowingActivity";
import { useCryptoPrices, useFearGreed, useGlobalCrypto, useCryptoNews } from "@/hooks/useCryptoData";
import { useLeagueNames } from "@/hooks/useLeagueNames";
import { useRealtimeMarketPrice } from "@/hooks/useRealtimeMarketPrice";
import { usePriceStream } from "@/hooks/usePriceStream";
import { usePredictRealtimePrice } from "@/hooks/usePredictRealtimePrice";
import { useHyperliquidRealtimePrice } from "@/hooks/useHyperliquidRealtimePrice";
import { ListPriceProvider } from "@/contexts/ListPriceContext";
import { useCryptoPriceWs } from "@/hooks/useCryptoPriceWs";
import { useSocialActivity } from "@/hooks/useSocialActivity";
import { fetchWithRetry } from "@/lib/fetchWithRetry";
import { authFetch } from "@/lib/authFetch";
import { cdn } from "@/lib/cdn";
// `react-icons/fi` is the only react-icons sub-package actually used in
// this file. The previous version imported `FaBitcoin`, `SiDogecoin` and
// `TbBallFootball` from `fa`/`si`/`tb` — three additional react-icons
// packages whose modules were parsed on every cold load even though the
// icons were never rendered. `FiFilter` was also unused. Same story with
// `framer-motion`'s `motion`: imported but no `<motion.X>` ever used in
// this component. All four dead imports removed below — Chrome DevTools
// flagged them as cold-load parse cost with zero runtime payoff.
import { FiTrendingUp, FiClock, FiInfo, FiX } from "react-icons/fi";
import HorizontalScroll from "@/app/components/HorizontalScroll";
import { SparklesIcon } from "@/app/components/icons/SparklesIcon";
import { SlidersHorizontalIcon } from "@/app/components/icons/SlidersHorizontalIcon";
import { MenuIcon } from "@/app/components/icons/MenuIcon";
// Removed 8 dead imports (Sparkles, ChevronsUpDown, Newspaper, ChevronLeft,
// ChevronRight, Users, ArrowUpRight, ArrowDownRight) — same audit pass as
// the react-icons/framer-motion cleanup above. `optimizePackageImports`
// for `lucide-react` already handles tree-shaking, but each named import
// still registers as a barrel-exported binding the compiler has to walk.
import {
  TrendingUp,
  Zap,
  Landmark,
  Trophy,
  DollarSign,
  Bitcoin,
  Globe2,
  BarChart3,
  Cpu,
  Palette,
  Earth,
  LineChart,
  Vote,
  MessageCircle,
  ChevronUp,
  ChevronDown,
  X,
  Rocket,
  Leaf,
  Wheat,
  Clock,
  Clock4,
  Calendar,
  CalendarDays,
  CalendarRange,
  Sunrise,
  MoreHorizontal,
  Timer,
  ArrowRight,
  RefreshCw,
  CalendarCheck,
  Gamepad2,
} from "lucide-react";
import { toast } from "sonner";
import { useTheme } from "next-themes";
import EventCard from "../components/EventCard";
import MarketCardPoly from "../components/MarketCardPoly";
import MiniDualSparkline from "../components/MiniDualSparkline";
import SparklineFilledGradient from "../components/SparklineFilledGradient";
import VolumeBars from "../components/VolumeBars";
// Heavy category-specific views are dynamic+ssr:false so they don't bloat
// the initial bundle (Lighthouse flagged 1,867 KiB unused JS + 9.25 s TBT).
// Each one only renders inside a single tab — we pay the JS cost when the
// user actually navigates there. SSR is disabled because the SEO `sr-only`
// content + JSON-LD are emitted from the server page.tsx, not from these
// interactive dashboards.
const BreakingMarketItem = dynamic(() => import("../components/BreakingMarketItem"), { ssr: false });
const ElectionCard = dynamic(() => import("../components/ElectionCard"), { ssr: false });
const MacroDashboard = dynamic(() => import("../components/MacroDashboard"), { ssr: false });
const FedRatesDashboard = dynamic(() => import("../components/FedRatesDashboard"), { ssr: false });
const Sports2View = dynamic(() => import("../components/Sports2View"), { ssr: false });
const WorldCupView = dynamic(() => import("./components/WorldCupView"), { ssr: false });
const KalshiSportsMatchCard = dynamic(() => import("../components/KalshiSportsMatchCard"), { ssr: false });
const KalshiExchangeBanner = dynamic(() => import("../components/KalshiExchangeBanner"), { ssr: false });
const TrumpDashboard = dynamic(() => import("../components/TrumpDashboard"), { ssr: false });
const ClimateDashboard = dynamic(() => import("../components/ClimateDashboard"), { ssr: false });
const GeopoliticsDashboard = dynamic(() => import("../components/GeopoliticsDashboard"), { ssr: false });
import { DashboardToggleStrip } from "./components/DashboardToggleStrip";
import { MarketCardSkeleton, SimpleCardSkeleton, LoadingSkeleton } from "./components/LoadingSkeletons";
import { ViewModeToggle } from "./components/ViewModeToggle";
import { WeatherChipsRow } from "./components/WeatherChipsRow";
import { GlobalSearchBanner } from "./components/GlobalSearchBanner";
import { BookmarksMiniGrid } from "./components/BookmarksMiniGrid";
import { SourceEmptyState } from "./components/SourceEmptyState";
import { MarketsListTableHeader } from "./components/MarketsListTableHeader";
import { FrensTradingGrid, type FrenActivity } from "./components/FrensTradingGrid";
import { RecommendedForYouHeader } from "./components/RecommendedForYouHeader";
import { MarketsListSmartCells } from "./components/MarketsListSmartCells";
import { MarketsListVolumeCells } from "./components/MarketsListVolumeCells";
import { MarketsListChangeCells } from "./components/MarketsListChangeCells";
import { StatusFilterButtons } from "./components/StatusFilterButtons";
import { MarketsListNestedTailCells } from "./components/MarketsListNestedTailCells";
// Predexon outage: drop the Smart Money + Smart ROI list columns (col defs here,
// header in MarketsListTableHeader, cells in the Smart/Nested cell components)
// under the flag so the table stays aligned with no empty SMART$/ROI columns.
const HIDE_PREDEXON = process.env.NEXT_PUBLIC_HIDE_PREDEXON_TABS === "true";
// Category-specific heavy components — pulled out of the static bundle so
// /markets/sports etc. don't ship code for crypto/finance/elon/mentions.
const FinanceOverview = dynamic(() => import("../components/FinanceOverview"), { ssr: false });
const MentionsView = dynamic(() => import("../components/MentionsView"), { ssr: false });
const TrendingEventsCarousel = dynamic(() => import("./components/TrendingEventsCarousel"), { ssr: false });
const FearGreedChart = dynamic(() => import("../components/FearGreedChart").then(m => ({ default: m.FearGreedChart })), { ssr: false });
import { ChartAreaIcon } from "../components/icons/ChartAreaIcon";
import { useSidebar } from "@/contexts/SidebarContext";
import { useMarketWatchlistContext } from "@/contexts/MarketWatchlistContext";
import { useDragScroll } from "@/hooks/useDragScroll";
import { useSmartActivityMap, type SmartActivityRow } from "@/hooks/useSmartActivityMap";
import { useOpenInterestMap } from "@/hooks/useOpenInterestMap";
import { useVolumeHistoryMap } from "@/hooks/useVolumeHistoryMap";
import { useFooterBulk } from "@/hooks/useFooterBulk";
import { Bookmark, Gift } from "lucide-react";
import Tooltip from "../components/Tooltip";
import { useMarketsRewardsParams } from "@/hooks/useMarketRewardsParams";
import { useTradingModalStore } from "@/stores/useTradingModalStore";
import { type AdvancedFiltersState, countActiveFilters, defaultFilters } from "../components/AdvancedFilters";
// Heavy drawer (~1k lines incl. preset library + saved filters UI). Most
// visits never open the filter sheet, so keep its JS out of the initial
// bundle — `dynamic({ ssr: false })` only fetches the chunk the moment
// the user actually toggles the drawer open.
const AdvancedFilters = dynamic(() => import("../components/AdvancedFilters"), { ssr: false });
import { trackRecommendation, trackRecommendationClick, setNavigationSource, type InteractionSource } from "@/lib/recommendation-tracker";
import { marketPageUrl, sourceUrl, type Source } from "@/lib/source-url";
import { useCurrentSource } from "@/hooks/useCurrentSource";
import { seededNoise } from "./helpers/seeded-noise";
import { getEventPriceChange } from "./helpers/price-change";
import { detectCryptoFromTitle, getLiveCryptoDisplayMarket } from "./helpers/crypto-detect";
import { COIN_ICON_URLS, hyperliquidImageFromDescription, buildHlEventMarket } from "./helpers/hyperliquid-helpers";
import {
  isElectionsSubcategory,
  resolveCategory,
  categoryIconMap,
  staticPrefixCategories,
  fallbackDynamicCategories,
  kalshiIconMap,
  predictIconMap,
  hyperliquidCategories,
} from "./helpers/categories";
import type { Market, MarketSource, AggregatedMarket, Event } from "./types";
import {
  aggregatedMarketToEventCardProps,
  aggregatedMarketToEvent,
  eventToEventCardProps,
} from "./helpers/market-mappers";
// Category-conditional components — see the block above for the rationale.
// MobileBottomSheet wraps Kalshi-sports filter panel; the rest are crypto/
// finance/elon/mentions/for-you specific and only render in those paths.
const MobileBottomSheet = dynamic(() => import("./components/MobileBottomSheet").then(m => ({ default: m.MobileBottomSheet })), { ssr: false });
const ElonHeroSection = dynamic(() => import("./components/ElonHeroSection").then(m => ({ default: m.ElonHeroSection })), { ssr: false });
import { ELON_SUBCATEGORIES } from "./components/elon-subcategories";
import { DashboardOverviewToggle } from "./components/DashboardOverviewToggle";
const CryptoNewsSidebar = dynamic(() => import("./components/CryptoNewsSidebar").then(m => ({ default: m.CryptoNewsSidebar })), { ssr: false });
const CryptoLiveTicker = dynamic(() => import("./components/CryptoLiveTicker").then(m => ({ default: m.CryptoLiveTicker })), { ssr: false });
const MobileCryptoFiltersSheet = dynamic(() => import("./components/MobileCryptoFiltersSheet").then(m => ({ default: m.MobileCryptoFiltersSheet })), { ssr: false });
const MobileFinanceFiltersSheet = dynamic(() => import("./components/MobileFinanceFiltersSheet").then(m => ({ default: m.MobileFinanceFiltersSheet })), { ssr: false });

interface EventsResponse {
  events: Event[];
  count: number;
  limit: number;
  offset: number;
  hasMore?: boolean;
}

// Valid category IDs that can be passed via URL
// Known categories (hardcoded base). Dynamic categories from taxonomy are also accepted
// via isValidCategory() which allows any lowercase slug pattern.
const KNOWN_CATEGORIES = new Set([
  "for-you", "trending", "world-cup", "breaking", "new", "elon", "politics", "sports", "esports", "finance",
  "crypto", "geopolitics", "tech", "culture", "world",
  "economy", "elections", "climate-science", "mentions",
  "macro", "fed-rates", "trump",
]);
// Accept known categories + any valid slug pattern (for dynamic categories like "iran")
const VALID_CATEGORY_PATTERN = /^[a-z][a-z0-9-]*$/;
const isValidCategory = (cat: string) => KNOWN_CATEGORIES.has(cat) || VALID_CATEGORY_PATTERN.test(cat);
// Array form for iteration (used in tab order, prefetch, etc.)
const VALID_CATEGORIES = [...KNOWN_CATEGORIES];

// Unified market type for mixed view sorting
interface UnifiedMarketItem {
  type: 'polymarket' | 'aggregated';
  source: MarketSource;
  id: string;
  title: string;
  image: string | null;
  volume: number;
  volume24h: number;
  endDate: string | null;
  // Original data for rendering
  event?: Event;
  aggregatedMarket?: AggregatedMarket;
}

// Lucide icons for crypto timeframe chips
const TIMEFRAME_ICONS: Record<string, React.ComponentType<{ className?: string; size?: number }>> = {
  "all": Globe2,
  "5m": Timer,
  "15m": Zap,
  "1h": Clock,
  "4h": Clock4,
  "daily": Calendar,
  "weekly": CalendarDays,
  "monthly": CalendarRange,
  "yearly": CalendarCheck,
  "pre-market": Sunrise,
  "etf": BarChart3,
  // Kalshi frequency ids
  "fifteen_min": Zap,
  "hourly": Clock,
  "annual": CalendarCheck,
  "one_off": Calendar,
  "custom": Calendar,
};

// Lucide icons for finance sidebar items
const FINANCE_ICONS: Record<string, React.ComponentType<{ className?: string; size?: number }>> = {
  "all": Globe2,
  "daily": Calendar,
  "weekly": CalendarDays,
  "monthly": CalendarRange,
  "stocks": TrendingUp,
  "earnings": DollarSign,
  "indicies": LineChart,
  "commodities": Leaf,
  "forex": DollarSign,
  "crypto": Bitcoin,
  "interest-rates": BarChart3,
  "treasuries": Landmark,
  "gdp": Earth,
  "cpi": TrendingUp,
  "fed": Landmark,
  "bonds": BarChart3,
  "collectibles": Palette,
  "acquisitions": TrendingUp,
  "earnings-calls": MessageCircle,
  "ipo": Rocket,
  "fed-rates": Landmark,
  "prediction-markets": Trophy,
};

// Module-level cache for the Kalshi taxonomy so it survives component remounts
// (SPA nav away-and-back). State alone was lost on remount → re-fetched on
// every return to a Kalshi route. localStorage persistence (raw fields only,
// icons re-derived on read) survives hard reloads too.
type KalshiCatEntry = { id: string; label: string; icon: any; kalshiCategory: string; subcategories: string[] };
let kalshiCategoriesMemo: KalshiCatEntry[] | null = null;
const KALSHI_CATS_LS_KEY = 'kalshi_categories_cache_v1';

export default function MarketsPage({ initialCategory, initialSubcategory, initialCryptoTimeFilter, initialCryptoCoinFilter, elonMode, electionsMode, initialMarkets, initialHasMore, allCategories, initialSource, initialTeamLogos, initialKalshiSidebar }: { initialCategory?: string; initialSubcategory?: string; initialCryptoTimeFilter?: string; initialCryptoCoinFilter?: string; elonMode?: boolean; electionsMode?: boolean; initialMarkets?: any[]; initialHasMore?: boolean; allCategories?: Record<string, any[]>; initialSource?: string; initialTeamLogos?: Record<string, string>; initialKalshiSidebar?: any }) {
  const searchParams = useSearchParams();
  const router = useRouter();
  const source = useCurrentSource();
  const { authenticated, ready: privyReady } = usePrivy();
  const { isLoggedIn } = useAuth();
  const { isCollapsed, isHydrated: sidebarHydrated } = useSidebar();
  const { resolvedTheme } = useTheme();
  const tCategory = useTCategory();
  const tKalshiSubcat = useTKalshiSubcat();
  const t = useT();
  const locale = useLocale();

  // Drag scroll for category tabs
  const categoriesScrollRef = useDragScroll<HTMLDivElement>();
  // Drag scroll for subcategory tabs (each container needs its own ref)
  const subcategoriesScrollRef = useDragScroll<HTMLDivElement>();
  const subcategoriesScrollRefDesktop = useDragScroll<HTMLDivElement>();
  const subcategoriesScrollRefDesktopMain = useDragScroll<HTMLDivElement>();
  const kalshiSubcatsScrollRef = useDragScroll<HTMLDivElement>();
  const kalshiSubcatsScrollRefDesktop = useDragScroll<HTMLDivElement>();
  const kalshiSubcatsScrollRefDesktopMain = useDragScroll<HTMLDivElement>();
  // trendingTagsScrollRef removed — trending subcategories now consolidated into dynamic bar
  const isPageVisible = usePageVisibility();
  const [mounted, setMounted] = useState(false);
  const hasSSRMarkets = initialMarkets && initialMarkets.length > 0;
  const isSSRNonPolymarket = hasSSRMarkets && (initialSource === 'predict' || initialSource === 'kalshi' || initialSource === 'hyperliquid');
  const [events, setEvents] = useState<Event[]>(hasSSRMarkets && !isSSRNonPolymarket ? initialMarkets as Event[] : []);
  const [loading, setLoading] = useState(!hasSSRMarkets);

  // Locale-change reset. The main fetch effect already has `locale` in
  // its deps, but several branches inside it can return early without
  // touching `events` (cache hit on the new-locale key returning stale
  // SSR-cached data; recent-fetch guard short-circuiting on the 500 ms
  // or 5 s window; the SSR-consumed flag making the effect think it
  // already settled). Symptom reported 2026-05-11: EN→ES toggled the
  // UI strings but left market titles in English; ES→EN was even
  // stickier. Forcing events=[] + loading=true on every locale flip
  // guarantees the new-locale data path runs end-to-end and the grid
  // can never paint the prior language's titles. Cheap defensive
  // pattern, same shape as the category-click bleed fix.
  const prevLocaleRef = useRef(locale);
  useEffect(() => {
    if (prevLocaleRef.current === locale) return;
    prevLocaleRef.current = locale;
    setEvents([]);
    setLoading(true);
  }, [locale]);

  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [globalSearchResults, setGlobalSearchResults] = useState<Event[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const searchDebounceRef = useRef<NodeJS.Timeout | null>(null);
  const kalshiSearchActiveRef = useRef(false);
  // Tracks Kalshi adjacent-category prefetches that are mid-flight so the
  // effect can re-run during state settling without firing duplicate
  // network calls. Keyed by cache key (matches the same key the SWR
  // path uses to deduplicate cached payloads).
  const kalshiPrefetchInFlightRef = useRef<Set<string>>(new Set());
  const [filter, setFilter] = useState<"all" | "active" | "closed">("active");
  const [sortBy, setSortBy] = useState<"volume24hr" | "volume" | "liquidity" | "newest" | "endingSoon" | "competitive">((initialCategory || "trending") === "new" ? "newest" : "volume24hr");
  const [sortDropdownOpen, setSortDropdownOpen] = useState(false);
  const sortDropdownRef = useRef<HTMLDivElement>(null);
  // Kalshi sort options: all (carousel order), trending (hot), price-delta (top movers), newest, event-volume
  // Seed from initialCategory so the post-mount effect at line ~2293 that
  // flips to 'trending' for non-trending tabs doesn't trigger a second
  // fetchAggregatedMarkets — the previous default of 'all' caused two
  // /api/markets/kalshi/search calls on every Kalshi category page (one
  // with no orderBy, another with orderBy=trending).
  const [kalshiSortBy, setKalshiSortBy] = useState<"all" | "trending" | "price-delta" | "newest" | "event-volume" | "closing" | "price-balance">(() => {
    const cat = initialCategory || 'trending';
    return cat === 'trending' ? 'all' : 'trending';
  });
  const [kalshiReverse, setKalshiReverse] = useState(false);
  const [kalshiSortDropdownOpen, setKalshiSortDropdownOpen] = useState(false);
  const kalshiSortRef = useRef<HTMLDivElement>(null);
  const KALSHI_PAGE_SIZE = 20;
  const [kalshiDisplayLimit, setKalshiDisplayLimit] = useState(KALSHI_PAGE_SIZE);
  const [kalshiNextCursor, setKalshiNextCursor] = useState<string | null>(null);
  const [kalshiTotalCount, setKalshiTotalCount] = useState<number>(0);
  const [loadingMoreKalshi, setLoadingMoreKalshi] = useState(false);

  // Predict.fun pagination
  const PREDICT_PAGE_SIZE = 20;
  const [predictOffset, setPredictOffset] = useState(0);
  const [predictTotal, setPredictTotal] = useState(0);
  const [loadingMorePredict, setLoadingMorePredict] = useState(false);

  // Hyperliquid pagination — HL backend doesn't return a total, so we
  // infer "has more" from whether the last response filled the full page.
  const HYPERLIQUID_PAGE_SIZE = 20;
  const [hyperliquidOffset, setHyperliquidOffset] = useState(0);
  const [hyperliquidHasMore, setHyperliquidHasMore] = useState(false);
  const [loadingMoreHyperliquid, setLoadingMoreHyperliquid] = useState(false);

  // Kalshi subcategories (tags) for current category
  const [kalshiSubcategories, setKalshiSubcategories] = useState<Array<{ name: string }>>([]);
  const [activeKalshiSubcategory, setActiveKalshiSubcategory] = useState<string | null>(null);
  const [activeKalshiCompetition, setActiveKalshiCompetition] = useState<string | null>(null);

  // Pre-populate cache with SSR-fetched categories for instant switching.
  //
  // The keys here MUST match what `getCacheKey(cat)` produces below
  // (line ~2412), otherwise the SSR write lands on a dead key and the
  // first user-driven category click sees an empty cache, falls back to
  // `safeSetEvents([])` and unmounts the entire grid (~1,600 DOM nodes
  // measured in production warm-revisit). The two callsites had drifted
  // when locale was added to the cache key; this builder keeps them in
  // lockstep.
  const buildDefaultCacheKey = useCallback((cat: string) => {
    if (cat === 'crypto') return `${cat}-all-all-polymarket-${locale}`;
    if (cat === 'finance') return `${cat}-all-all-polymarket-${locale}`;
    return `${cat}-active-volume24hr-polymarket-all-all-all-${locale}`;
  }, [locale]);

  const allCategoriesPopulated = useRef(false);
  useEffect(() => {
    if (!allCategories || allCategoriesPopulated.current) return;
    allCategoriesPopulated.current = true;
    for (const [cat, markets] of Object.entries(allCategories)) {
      if (markets.length > 0) {
        setCategoryCache(buildDefaultCacheKey(cat), {
          events: markets,
          offset: 0,
          hasMore: markets.length >= 20,
          timestamp: Date.now(),
        });
      }
    }
  }, [allCategories, buildDefaultCacheKey]);

  // Pre-populate cache with SSR data for the initial category, AND keep
  // the events grid in sync when the URL category changes via App Router
  // soft-nav (e.g. /markets → /markets/crypto from a sidebar link or a
  // direct typed URL while the component is already mounted).
  //
  // The previous boolean `ssrCachePopulated` guard only let this run
  // ONCE — so the second navigation kept the cache + events from the
  // first category and the grid showed trending while the user was on
  // /markets/crypto until the background fetch landed (1-3 s of stale
  // data). Track the last seeded cache-key instead; the effect re-runs
  // for each new category, re-seeds its cache, and resets the events
  // state to match the SSR payload so the swap is visually instant.
  // (Tab clicks in the category bar already handle the swap via their
  // own flushSync path — this only fires the URL-nav case.)
  const lastSsrSeededKey = useRef<string | null>(null);
  useEffect(() => {
    if (allCategories) return;
    // Kalshi/Predict have their own SSR consumption path (`ssrConsumedRef`)
    // and a separate aggregatedMarkets state — don't touch their flow.
    if (isSSRNonPolymarket) return;
    const cat = initialCategory || 'trending';
    const key = buildDefaultCacheKey(cat);
    if (lastSsrSeededKey.current === key) return;

    // Bleed fix (2026-06): navegar por URL entre categorías (/markets/new →
    // /markets/breaking) NO pasa por el clear defensivo del onClick del tab
    // (regla #11), así que el único limpiador es este re-seed. Antes bailaba
    // en `!hasSSRMarkets`, de modo que si la SSR de la nueva categoría vino
    // VACÍA (hiccup del backend / slot ISR vacío cacheado 60s) la grid se
    // quedaba mostrando las cards de la categoría anterior — incluida alguna
    // que renderiza "market's closed" con vol/liq stale. Al cambiar de
    // categoría sin SSR, limpiar para que el fetch client de la nueva
    // categoría pinte skeleton y repueble, en vez de dejar la grid vieja.
    if (!hasSSRMarkets) {
      lastSsrSeededKey.current = key;
      setEvents([]);
      setOffset(0);
      setHasMore(true);
      setLoading(true);
      return;
    }
    lastSsrSeededKey.current = key;
    setCategoryCache(key, {
      events: initialMarkets as any[],
      offset: 0,
      // Prefer the backend's real hasMore (handles zombie-filtered pages that
      // are < limit but still have more); fall back to the length heuristic.
      hasMore: initialHasMore ?? ((initialMarkets?.length || 0) >= 20),
      timestamp: Date.now(),
    });
    // Resync events with the new SSR payload. Idempotent on first mount
    // (useState already seeded the same array). The real win is on
    // category-change nav, where this swap eliminates the stale-grid
    // window without forcing a refetch.
    setEvents(initialMarkets as Event[]);
    setOffset(initialMarkets?.length || 0);
    setHasMore(initialHasMore ?? ((initialMarkets?.length || 0) >= 20));
    setLoading(false);
  }, [hasSSRMarkets, allCategories, isSSRNonPolymarket, initialCategory, initialMarkets, initialHasMore, buildDefaultCacheKey]);

  // Close sort dropdowns on click outside
  useEffect(() => {
    if (!kalshiSortDropdownOpen && !sortDropdownOpen) return;
    const handler = (e: MouseEvent) => {
      if (kalshiSortDropdownOpen && kalshiSortRef.current && !kalshiSortRef.current.contains(e.target as Node)) {
        setKalshiSortDropdownOpen(false);
      }
      if (sortDropdownOpen && sortDropdownRef.current && !sortDropdownRef.current.contains(e.target as Node)) {
        setSortDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [kalshiSortDropdownOpen, sortDropdownOpen]);

  // Get category from props, URL param, or default to "trending"
  // Note: "for-you" requires authentication - redirect to "trending" if not logged in
  // IMPORTANT: Don't redirect until Privy is ready, otherwise we'll redirect before knowing auth state
  // Initialize with stable default for SSR; sync from URL in useEffect to avoid hydration mismatch
  const urlCategory = searchParams.get("category");

  const [category, setCategoryState] = useState<string>(resolveCategory(initialCategory || "trending"));

  // Sync category from props/URL — runs on mount AND when initialCategory changes
  // (e.g. browser back/forward in Next.js App Router triggers re-render with new props)
  useEffect(() => {
    const urlCategory = searchParams.get("category");
    const resolved = initialCategory || (urlCategory && isValidCategory(urlCategory) ? urlCategory : null);
    if (resolved && resolved !== category) {
      // Respect auth: "for-you" requires login
      const final = (privyReady && !authenticated && resolved === "for-you") ? "trending" : resolveCategory(resolved);
      setCategoryState(final);
      // If navigating to an elections subcategory URL, set the filter too
      if (isElectionsSubcategory(resolved)) {
        setElectionsFilter(resolved);
      }
    }
  }, [initialCategory]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle browser Back/Forward navigation (popstate) since we use pushState for categories
  useEffect(() => {
    const handlePopState = () => {
      const match = window.location.pathname.match(/^(?:\/(?:polymarket|kalshi))?\/markets\/([^/?]+)/);
      const cat = match?.[1];
      if (cat && isValidCategory(cat)) {
        const resolved = resolveCategory(cat);
        if (resolved !== category) {
          setCategoryState(resolved);
          setActiveSubcategory(null);
        }
        // Update elections filter on back/forward
        if (isElectionsSubcategory(cat)) {
          setElectionsFilter(cat);
        } else if (resolved === "elections") {
          setElectionsFilter("elections");
        }
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [category]);

  // Wrapper to persist category to localStorage + reset subcategory in same batch
  const setCategory = (cat: string) => {
    setCategoryState(cat);
    setActiveSubcategory(null);
    setOffset(0); // Reset pagination to prevent duplicate results
    setPredictOffset(0);
    setHyperliquidOffset(0);
    setHyperliquidHasMore(false);
    // Default sort per category
    if (cat === "new") setSortBy("newest");
    else if (cat !== "breaking") setSortBy("volume24hr");
    // Show loading without blanking the list — keep old data visible while
    // the new category fetches so the perceived wait is bounded by paint, not
    // by network. Matches the Polymarket flow which never blanks the grid.
    if (['kalshi', 'predict'].includes(effectiveSourceFilter)) {
      setLoadingAggregated(true);
    }
    try { localStorage.setItem('frenflow-markets-category', cat); } catch {}
  };

  // Update browser tab title on client-side category switches (pushState doesn't trigger generateMetadata)
  useEffect(() => {
    const seo = CATEGORY_SEO[category];
    if (seo) {
      document.title = `${seo.title} — Trade with Real Money | FrenFlow`;
    } else {
      const special: Record<string, string> = {
        trending: "Trending Prediction Markets — What's Hot Now",
        breaking: "Breaking News Prediction Markets — Live Events",
        new: "New Prediction Markets — Recently Added",
        "ending-soon": "Ending Soon — Markets Resolving Today",
        "for-you": "For You — Personalized Prediction Markets",
        bookmarks: "Bookmarks — Saved Markets",
      };
      document.title = `${special[category] || `${categoryLabel(category)} Prediction Markets`} | FrenFlow`;
    }
  }, [category]);

  const [hasMore, setHasMore] = useState(true);
  const [offset, setOffset] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const { data: followingActivity = {} } = useFollowingActivity(category);
  // Initialize with category-based default (server-safe)
  const [viewMode, setViewModeState] = useState<"grid" | "list">(category === "breaking" ? "list" : "grid");
  const [viewModeLoaded, setViewModeLoaded] = useState(false);
  const [gridHovered, setGridHovered] = useState(false);
  const [listHovered, setListHovered] = useState(false);
  const [searchHovered, setSearchHovered] = useState(false);
  const [mobileFiltersExpanded, setMobileFiltersExpanded] = useState(false);

  // Mobile swipe to change categories - refs stored here, handlers defined later after all dependencies are declared
  const touchStartRef = useRef<{ x: number; y: number; t: number } | null>(null);
  const swipeThreshold = 130; // Minimum distance for a swipe (px)

  // Swipe animation direction state
  const [slideDirection, setSlideDirection] = useState<'left' | 'right' | null>(null);

  // Dynamic taxonomy from Polymarket API
  interface TaxonomyFilter {
    id: string;
    label: string;
    slug: string;
  }
  interface Taxonomy {
    categories: string[];
    mainNavCategories?: Array<{slug: string, label: string}>;
    subcategories: Record<string, string[]>;
    labels: Record<string, string>;
    filters?: {
      crypto?: {
        timeframes: TaxonomyFilter[];
        coins: TaxonomyFilter[];
      };
      finance?: {
        sidebar: TaxonomyFilter[];
        tabs: TaxonomyFilter[];
      };
    };
  }
  const [taxonomy, setTaxonomy] = useState<Taxonomy | null>(null);
  const [weatherSubcategories, setWeatherSubcategories] = useState<Array<{ id: string; label: string }>>([]);
  const [weatherDateTabs, setWeatherDateTabs] = useState<Array<{ date: string; count: number }>>([]);
  const [activeWeatherDate, setActiveWeatherDate] = useState<string | null>(null);
  // Temperature has two flavours on PM (High Temp / Low Temp). Null = both.
  const [activeWeatherTempType, setActiveWeatherTempType] = useState<"high" | "low" | null>(null);
  const [activeSubcategory, setActiveSubcategory] = useState<string | null>(initialSubcategory || null);

  // Locale used to namespace the sessionStorage taxonomy cache.
  // Without this, switching EN ↔ ES would serve stale EN labels until
  // the 4h re-fetch ran. We also blow the legacy un-suffixed key on
  // first run so old visitors don't see EN forever after upgrading.
  // v3: bumped after we added climate/weather slug-normalisation in the
  // backend nav scrape. Old v2 entries still had a duplicate
  // "Weather and Climate" tab when PM surfaced two slugs at once; bump
  // forces an immediate refresh against the de-duplicated payload.
  const taxonomyStorageKey = `frenflow_taxonomy_v3_${locale}`;

  // Hydrate taxonomy + weather data from sessionStorage cache before paint
  useLayoutEffect(() => {
    try {
      // One-shot migration: drop the pre-locale cache key + the v1/v2
      // per-locale keys so they don't shadow the new v3 slot. v1 cached
      // the duplicate `climate-science` / `weather-science` mainNav
      // tabs; v2 cached a duplicate "Weather and Climate" entry from
      // before backend slug-normalisation. Cheap, idempotent.
      sessionStorage.removeItem('frenflow_taxonomy');
      sessionStorage.removeItem('frenflow_taxonomy_en');
      sessionStorage.removeItem('frenflow_taxonomy_es');
      sessionStorage.removeItem('frenflow_taxonomy_v2_en');
      sessionStorage.removeItem('frenflow_taxonomy_v2_es');
      // Bump weather subcat cache too — old visitors had it populated
      // empty (matchers were too strict before we widened tagSlugs +
      // titleMatch fallback) and the 5-min TS check skips re-fetch.
      const wts = sessionStorage.getItem('frenflow_weather_subcats_v');
      if (wts !== '2') {
        sessionStorage.removeItem('frenflow_weather_subcats');
        sessionStorage.removeItem('frenflow_weather_subcats_ts');
        sessionStorage.removeItem('frenflow_weather_dateTabs');
        sessionStorage.setItem('frenflow_weather_subcats_v', '2');
      }
      const raw = sessionStorage.getItem(taxonomyStorageKey);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed?.data) setTaxonomy(parsed.data);
      }
    } catch {}
    try {
      const subs = sessionStorage.getItem('frenflow_weather_subcats');
      if (subs) setWeatherSubcategories(JSON.parse(subs));
      const dates = sessionStorage.getItem('frenflow_weather_dateTabs');
      if (dates) setWeatherDateTabs(JSON.parse(dates));
    } catch {}
    // Re-runs on locale flip so the cached EN taxonomy doesn't shadow
    // a fresh ES fetch from the effect below.
  }, [taxonomyStorageKey]);

  // Fetch fresh taxonomy on mount — skip if sessionStorage cache is < 10 min old
  // Skip entirely when viewing Kalshi/Predict (not needed for those sources)
  useEffect(() => {
    if (effectiveSourceFilter === 'kalshi' || effectiveSourceFilter === 'predict' || effectiveSourceFilter === 'hyperliquid') return;

    try {
      const raw = sessionStorage.getItem(taxonomyStorageKey);
      if (raw) {
        const parsed = JSON.parse(raw);
        const age = Date.now() - (parsed?.ts ?? 0);
        if (age < 4 * 60 * 60 * 1000 && parsed?.data?.categories) { // 4 hour cache
          // Cache still fresh — use it, skip fetch
          setTaxonomy(parsed.data);
          return;
        }
      }
    } catch {}

    fetch('/api/polymarket/taxonomy')
      .then(res => res.json())
      .then(data => {
        if (data.categories && data.subcategories) {
          setTaxonomy(data);
          try { sessionStorage.setItem(taxonomyStorageKey, JSON.stringify({ data, ts: Date.now() })); } catch {}
        }
      })
      .catch(() => {}); // Silently fail, we have cached data or hardcoded fallbacks
    // `effectiveSourceFilter` is intentionally excluded — it's declared
    // later in this component (TDZ if put in deps) and the early
    // return at the top of the effect handles its current value at
    // each run anyway. Re-firing on locale flip is the goal.
  }, [locale, taxonomyStorageKey]);

  // Fetch weather subcategories from lightweight endpoint (parallel, non-blocking)
  useEffect(() => {
    if (category !== 'weather' && category !== 'weather-science' && category !== 'climate-science') return;
    // Skip if sessionStorage already hydrated fresh data (< 5 min)
    try {
      const raw = sessionStorage.getItem('frenflow_weather_subcats_ts');
      if (raw && Date.now() - parseInt(raw) < 5 * 60 * 1000) return;
    } catch {}

    fetch('/api/markets/weather/subcategories')
      .then(res => res.json())
      .then(data => {
        if (Array.isArray(data.subcategories)) {
          const subs = data.subcategories.map((s: any) => ({
            id: s.slug,
            label: s.label?.charAt(0).toUpperCase() + s.label?.slice(1) || s.slug,
          }));
          setWeatherSubcategories(subs);
          try {
            sessionStorage.setItem('frenflow_weather_subcats', JSON.stringify(subs));
            sessionStorage.setItem('frenflow_weather_subcats_ts', Date.now().toString());
          } catch {}
        }
        if (Array.isArray(data.dateTabs) && data.dateTabs.length > 0) {
          setWeatherDateTabs(data.dateTabs);
          try { sessionStorage.setItem('frenflow_weather_dateTabs', JSON.stringify(data.dateTabs)); } catch {}
        }
      })
      .catch(() => {});
  }, [category]);

  // Get subcategories for current category
  const currentSubcategories = useMemo(() => {
    if (!category) return [];

    // Weather: subcategories come from the weather endpoint (derived from event tags)
    // because PM's weather page filteredTagsBySlug shows raw tags, not curated ones
    if (category === 'weather' || category === 'weather-science' || category === 'climate-science') return weatherSubcategories;

    if (!taxonomy) return [];
    // Categories excluded from dynamic subcategories (they have custom UI)
    const excludedFromSubcategories = new Set(['sports', 'esports', 'crypto', 'elections', 'elon', 'breaking']);
    if (excludedFromSubcategories.has(category)) return [];

    // Direct mapping: category slug → taxonomy key (most are 1:1)
    const polyCategory = category;
    const subcats = taxonomy.subcategories[polyCategory] || [];
    // Return with labels
    return subcats.map(slug => ({
      id: slug,
      label: taxonomy.labels[slug] || slug.charAt(0).toUpperCase() + slug.slice(1),
    }));
  }, [taxonomy, category, weatherSubcategories]);

  // Subcategory reset is now batched inside setCategory() to avoid double-dispatch

  // Helper to change subcategory with slide animation
  const changeSubcategory = useCallback((newSubcategory: string | null, direction?: 'left' | 'right') => {
    // Determine slide direction if not provided
    if (!direction && currentSubcategories.length > 0) {
      const currentIdx = activeSubcategory
        ? currentSubcategories.findIndex(s => s.id === activeSubcategory)
        : -1;
      const newIdx = newSubcategory
        ? currentSubcategories.findIndex(s => s.id === newSubcategory)
        : -1;
      direction = newIdx > currentIdx ? 'left' : 'right';
    }

    // Slide direction stays urgent — it drives the slide animation that
    // the user expects to see *immediately* on click. The four updates
    // below are deferred via startTransition: they only feed the data
    // pipeline (URL state, weather filter resets, pagination offset)
    // and the grid re-render they trigger is the single biggest cost
    // of this click. Without the gate, Chrome DevTools traced INP=3620 ms
    // on a pill click — every EventCard remounted on the same render
    // pass and blocked next paint. With it, React commits the visible
    // tab change first and reschedules the heavy work, dropping INP
    // closer to <200 ms.
    setSlideDirection(direction || 'left');
    startTransition(() => {
      setActiveSubcategory(newSubcategory);
      setActiveWeatherDate(null); // Reset date filter when subcategory changes
      setActiveWeatherTempType(null); // Reset High/Low chip when subcategory changes
      setOffset(0); // Reset pagination to prevent duplicate results
    });
    setTimeout(() => setSlideDirection(null), 500);
  }, [currentSubcategories, activeSubcategory]);

  // Scroll active category into view. Used to be useLayoutEffect (to
  // run before paint, avoiding a visible jump), but reading
  // getBoundingClientRect() inside a layout effect right after a state
  // commit forced a synchronous reflow on every category switch — Chrome
  // DevTools clocked 58 ms of forced layout work per click. Moved to
  // useEffect + requestAnimationFrame so the layout queries run on the
  // next frame, after the browser's natural layout pass. Any scroll
  // delta now lands ~1 frame later, which is imperceptible at 60fps for
  // an `instant` behavior.
  useEffect(() => {
    if (!categoriesScrollRef.current || !category) return;

    const rafId = requestAnimationFrame(() => {
      const container = categoriesScrollRef.current;
      const activeButton = container?.querySelector(`[data-category-id="${category}"]`) as HTMLElement | null;
      if (!container || !activeButton) return;

      const containerRect = container.getBoundingClientRect();
      const buttonRect = activeButton.getBoundingClientRect();
      const isVisible = buttonRect.left >= containerRect.left && buttonRect.right <= containerRect.right;

      if (!isVisible) {
        activeButton.scrollIntoView({ behavior: 'instant', inline: 'center', block: 'nearest' });
      }
    });

    return () => cancelAnimationFrame(rafId);
  }, [category]);

  // Load viewMode from localStorage after mount (client-side only)
  // If no saved preference and on mobile, default to list view
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const saved = localStorage.getItem('frenflow-markets-view-mode');
      if (saved === 'grid' || saved === 'list') {
        setViewModeState(saved);
      } else {
        const isMobile = window.innerWidth < 768;
        if (isMobile) {
          setViewModeState("list");
        }
      }
    } catch {}
    setViewModeLoaded(true);
  }, []);

  // Sync category with initialCategory when prop changes (e.g. browser back navigation)
  useEffect(() => {
    if (initialCategory && isValidCategory(initialCategory)) {
      setCategoryState(resolveCategory(initialCategory));
    }
  }, [initialCategory]);

  // Load category from localStorage after mount (only if no URL or path category)
  useEffect(() => {
    // Skip if URL has a category specified via query param OR path param (initialCategory)
    if (urlCategory && isValidCategory(urlCategory)) return;
    if (initialCategory && isValidCategory(initialCategory)) return;

    const savedCategory = localStorage.getItem('frenflow-markets-category');
    if (savedCategory && isValidCategory(savedCategory)) {
      // If user is not authenticated and saved category is "for-you", use trending instead
      if (!authenticated && savedCategory === "for-you") {
        setCategoryState("trending");
      } else {
        setCategoryState(savedCategory);
      }
    }
  }, [urlCategory, initialCategory, authenticated]);

  // Wrapper to persist viewMode to localStorage
  const setViewMode = (mode: "grid" | "list") => {
    setViewModeState(mode);
    localStorage.setItem('frenflow-markets-view-mode', mode);
  };
  const [tableSortBy, setTableSortBy] = useState<"market" | "volume24hr" | "volume" | "expires" | "liquidity" | "change1h" | "change24h" | "change7d">("volume24hr");
  const [expandedEvents, setExpandedEvents] = useState<Set<string>>(new Set());
  // Source detection: useCurrentSource() handles pathname prefix → searchParams → localStorage → default
  // This correctly detects /kalshi/ prefix on client-side navigation and _source param on SSR rewrites
  const validSources = ["polymarket", "kalshi", "predict", "hyperliquid"] as const;
  const [sourceFilter, setSourceFilter] = useState<"polymarket" | "kalshi" | "predict" | "hyperliquid">(() => {
    // Sync detect from browser URL (avoids flash on predict pages)
    if (typeof window !== 'undefined') {
      const path = window.location.pathname;
      if (path.startsWith('/predictfun/')) return 'predict';
      if (path.startsWith('/kalshi/')) return 'kalshi';
      if (path.startsWith('/hyperliquid/') || path === '/hyperliquid') return 'hyperliquid';
    }
    return source as any || "polymarket";
  });

  // Sync sourceFilter state when the hook-detected source changes (e.g. client-side navigation)
  useEffect(() => {
    if (validSources.includes(source as any) && source !== sourceFilter) {
      setSourceFilter(source as typeof validSources[number]);
    }
  }, [source]);

  // Use sourceFilter as primary (updated instantly by event handler on source change).
  // Hook-detected source syncs it via useEffect, but sourceFilter reacts faster to user clicks.
  const effectiveSourceFilter = validSources.includes(sourceFilter)
    ? sourceFilter
    : validSources.includes(source as any)
      ? (source as typeof validSources[number])
      : "polymarket";
  const [aggregatedMarkets, setAggregatedMarkets] = useState<AggregatedMarket[]>(isSSRNonPolymarket ? initialMarkets as AggregatedMarket[] : []);
  const [loadingAggregated, setLoadingAggregated] = useState(false);
  const [hasFetchedAggregated, setHasFetchedAggregated] = useState(isSSRNonPolymarket);
  const aggregatedFetchVersionRef = useRef(0);
  // In-flight dedupe for fetchAggregatedMarkets. When source switch + URL
  // sync + main effect all fire within the same tick they each call the
  // fetcher with identical args (e.g. ?limit=20&grouped=true on HL). We
  // saw 3 concurrent network calls in devtools causing visible flicker.
  // Keyed by `source|orderBy|category` so different requests still race.
  const aggregatedInflightRef = useRef<Map<string, Promise<void>>>(new Map());

  // Sync SSR initialMarkets on client-side navigation (useState only runs on first render)
  const initialMarketsKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (!isSSRNonPolymarket || !initialMarkets?.length) return;
    // Create a stable key from first market ID to detect actual data change
    const newKey = `${initialSource}-${initialMarkets[0]?.id || initialMarkets[0]?.slug}`;
    if (initialMarketsKeyRef.current === newKey) return;
    initialMarketsKeyRef.current = newKey;
    setAggregatedMarkets(initialMarkets as AggregatedMarket[]);
    setHasFetchedAggregated(true);
    setLoadingAggregated(false);
  }, [isSSRNonPolymarket, initialMarkets, initialSource]);

  // Predexon bulk analytics for list view — Smart Money (count + % + volume
  // + ROI) and Open Interest. Both are Polymarket-only and cached 5 min.
  const { data: smartActivityMap } = useSmartActivityMap(500, effectiveSourceFilter === 'polymarket');
  const listEventConditionIds = useMemo(() => {
    if (effectiveSourceFilter !== 'polymarket') return [] as string[];
    const set = new Set<string>();
    for (const ev of events) {
      const m = (ev.markets && ev.markets[0]) as any;
      const cid = (m?.conditionId || (ev as any).conditionId || '').toLowerCase();
      if (cid) set.add(cid);
    }
    return Array.from(set).slice(0, 50);
  }, [events, effectiveSourceFilter]);
  // Unified footer-bulk fetch: OI + volume bars + smart money in ONE round
  // trip. Replaces the three separate hooks (useOpenInterestMap, ...) so
  // the EventCard footer paints in a single transition instead of three
  // staggered pop-ins. Legacy hooks kept for backwards compat for any
  // consumer that still reads openInterestMap / volumeHistoryMap directly.
  const { data: footerBulk } = useFooterBulk(listEventConditionIds, effectiveSourceFilter === 'polymarket');
  const openInterestMap = footerBulk?.oi;
  const volumeHistoryMap = footerBulk?.volume;
  // Keep the old hooks referenced so TS doesn't flag them as unused.
  void useOpenInterestMap;
  void useVolumeHistoryMap;

  /**
   * SMART$ lookup with per-CID precedence.
   *
   * footerBulk.smart is keyed by the exact CIDs being rendered (sourced
   * via Predexon's per-market `/smart-money` endpoint with a 7d → all_time
   * window-fallback in the backend). It hits trending markets that miss
   * the global top-500 ranking — which is why most cards on /markets/
   * trending were showing N/A before.
   *
   * smartActivityMap (the global ranked list, top 500) stays as a
   * graceful fallback for the brief window before footerBulk resolves
   * and for any CID that didn't make it into the per-card payload.
   *
   * Returned shape stays SmartActivityRow (camelCase) so the four call
   * sites below don't have to change their consumers.
   */
  const getSmartRow = useCallback((cid: string | undefined | null): SmartActivityRow | undefined => {
    if (!cid) return undefined;
    const lc = cid.toLowerCase();
    const fb = footerBulk?.smart?.get(lc);
    if (fb && fb.smart_wallet_count) {
      return {
        conditionId: lc,
        smartWalletCount: Number(fb.smart_wallet_count || 0),
        smartVolume: Number(fb.smart_volume || 0),
        netBuyersPct: Number(fb.net_buyers_pct || 0),
        avgSmartRoi: Number(fb.avg_smart_roi || 0),
        avgSmartWinRate: Number(fb.avg_smart_win_rate || 0),
        title: fb.title,
        marketSlug: fb.market_slug,
      };
    }
    return smartActivityMap?.get(lc);
  }, [footerBulk?.smart, smartActivityMap]);

  // Lookup 24h volume history (hourly buckets) for an event → picks main market
  const getEventVolumeHistory = useCallback((ev: any): any => {
    if (!volumeHistoryMap) return undefined;
    const mainCid = (ev?.conditionId || ev?.markets?.[0]?.conditionId || '').toLowerCase();
    if (mainCid) {
      const v = volumeHistoryMap.get(mainCid);
      if (v && v.length >= 2) return v;
    }
    for (const m of (ev?.markets || [])) {
      const c = (m?.conditionId || '').toLowerCase();
      const v = c ? volumeHistoryMap.get(c) : undefined;
      if (v && v.length >= 2) return v;
    }
    return undefined;
  }, [volumeHistoryMap]);

  // Lookup Open Interest for an event by scanning its (main market + nested) conditionIds
  const getEventOI = useCallback((ev: any): number | undefined => {
    if (!openInterestMap) return undefined;
    const cids: string[] = [];
    const mainCid = (ev?.conditionId || ev?.markets?.[0]?.conditionId || '').toLowerCase();
    if (mainCid) cids.push(mainCid);
    for (const m of (ev?.markets || [])) {
      const c = (m?.conditionId || '').toLowerCase();
      if (c && !cids.includes(c)) cids.push(c);
    }
    let total = 0;
    for (const c of cids) {
      const v = openInterestMap.get(c);
      if (typeof v === 'number' && Number.isFinite(v)) total += v;
    }
    return total > 0 ? total : undefined;
  }, [openInterestMap]);

  // For You personalized feed state
  // Mixed feed items: events (multi-market) and markets (single-market), ordered by score
  const [forYouFeedItems, setForYouFeedItems] = useState<Array<
    | { type: 'event'; event: Event; reason: string; position: number }
    | { type: 'market'; market: AggregatedMarket; reason: string; position: number }
  >>([]);
  const [forYouEvents, setForYouEvents] = useState<Event[]>([]);
  const [loadingForYou, setLoadingForYou] = useState(false);
  const [forYouHasMore, setForYouHasMore] = useState(true);
  const forYouReplacementPoolRef = useRef<typeof forYouFeedItems>([]);

  const [showForYouInfo, setShowForYouInfo] = useState(false);

  // Friends activity state (for "Your Frens Are Trading" section)
  // FrenActivity type lives in ./components/FrensTradingGrid (single import).
  const [frensActivity, setFrensActivity] = useState<FrenActivity[]>([]);

  // Watching (bookmarked markets) state
  const [watchingEvents, setWatchingEvents] = useState<Event[]>([]);
  const [loadingWatching, setLoadingWatching] = useState(false);

  // Apply the active/closed status filter to bookmarked markets so the
  // top-bar Status pills behave the same as in any other category. The
  // raw `watchingEvents` come straight from the bookmark API; here we
  // honour the user's `filter` selection (`active` / `closed` / `all`).
  // We treat an event as closed when the event itself is flagged closed
  // OR every market inside it is closed — bookmarks store events, not
  // single markets, so a multi-outcome event with one open question
  // should still count as active.
  const filteredWatchingEvents = useMemo(() => {
    if (filter === "all") return watchingEvents;
    return watchingEvents.filter((event: any) => {
      const eventClosed =
        event?.closed === true ||
        event?.active === false ||
        ((event?.markets || []).length > 0 &&
          event.markets.every((m: any) => m?.closed === true || m?.active === false));
      return filter === "closed" ? eventClosed : !eventClosed;
    });
  }, [watchingEvents, filter]);
  const [removingSlug, setRemovingSlug] = useState<string | null>(null); // For fade-out animation
  const [bookmarkHoveredSlug, setBookmarkHoveredSlug] = useState<string | null>(null);
  const [bookmarkSparklesSlug, setBookmarkSparklesSlug] = useState<string | null>(null);
  const [miniCardExplosion, setMiniCardExplosion] = useState<{ slug: string; rect: DOMRect } | null>(null);
  const [refreshingForYou, setRefreshingForYou] = useState(false);

  // Outcome click in the markets list/grid no longer opens the
  // TradingModal in place — instead it routes to the event page with
  // ?market=<sub>&outcome=<yes|no> already selected. The event page
  // has the proper sticky CTA + bottom-sheet flow on mobile and the
  // sidebar TradingPanel on desktop, so we don't want a competing
  // modal popping over the list. `marketPageUrl` already builds the
  // correct path per source (Polymarket / Kalshi / Predict) and falls
  // back to /<source>/event/<marketSlug> when eventSlug is missing.
  // useCallback for the For You info modal trigger. Stable identity
  // keeps the memoized TrendingEventsCarousel from re-rendering on
  // every parent commit.
  const openForYouInfo = useCallback(() => setShowForYouInfo(true), []);

  // Toggles for the dashboard category overviews (macro / fed-rates /
  // trump). Each one persists the new value to localStorage so the
  // collapsed/expanded state survives a tab switch + reload.
  const toggleMacroOverview = useCallback(() => {
    setMacroOverviewExpanded(prev => {
      const v = !prev;
      try { localStorage.setItem('macroOverviewExpanded', JSON.stringify(v)); } catch {}
      return v;
    });
  }, []);
  const toggleFedRatesOverview = useCallback(() => {
    setFedRatesOverviewExpanded(prev => {
      const v = !prev;
      try { localStorage.setItem('fedRatesOverviewExpanded', JSON.stringify(v)); } catch {}
      return v;
    });
  }, []);
  const toggleTrumpOverview = useCallback(() => {
    setTrumpOverviewExpanded(prev => {
      const v = !prev;
      try { localStorage.setItem('trumpOverviewExpanded', JSON.stringify(v)); } catch {}
      return v;
    });
  }, []);

  // Stable handlers for the memoized CryptoNewsSidebar.
  const toggleNewsSidebar = useCallback(() => setNewsSidebarOpen(prev => !prev), []);
  const closeNewsSidebar = useCallback(() => setNewsSidebarOpen(false), []);

  // MobileCryptoFiltersSheet wiring. Selecting a filter resets the
  // secondary one (matches the inline behavior the sheet replaced)
  // and dismisses the sheet so the user lands back on the grid.
  const closeMobileCryptoSheet = useCallback(() => setMobileCryptoSidebarOpen(false), []);
  const onSelectCryptoFilter = useCallback((id: string) => {
    setCryptoPrimaryFilter(id);
    setCryptoSecondaryFilter("all");
    setMobileCryptoSidebarOpen(false);
  }, []);
  const resolveCryptoTimeframeLabel = useCallback(
    (tf: { id: string; label: string }) => {
      const k = `crypto.tf.${tf.id}` as any;
      const v = t(k);
      return v && v !== k ? v : tf.label;
    },
    [t],
  );

  // MobileFinanceFiltersSheet wiring. Same shape as the crypto handler:
  // pick → reset secondary → dismiss the sheet.
  const closeMobileFinanceSheet = useCallback(() => setMobileFinanceSidebarOpen(false), []);
  const onSelectFinanceFilter = useCallback((id: string) => {
    setFinancePrimaryFilter(id);
    setFinanceSecondaryFilter("all");
    setMobileFinanceSidebarOpen(false);
  }, []);
  const resolveFinanceLabel = useCallback(
    (item: { id: string; label: string }) => tCategory(item.id) || item.label,
    [tCategory],
  );
  // Memoize the labels object so its identity is stable between renders;
  // t() returns the same string but a fresh object would defeat the memo.
  const newsSidebarLabels = useMemo(() => ({
    closeTitle: t("news.closeTitle"),
    openTitle: t("news.openTitle"),
    tab: t("news.tab"),
    cryptoTitle: t("news.cryptoTitle"),
    live: t("news.live"),
  }), [t]);

  // useCallback so memoized children (ElonHeroSection, etc.) that
  // receive this as a prop don't re-render on every parent commit.
  const openTradingModal = useCallback((
    marketSlug: string,
    outcome: "YES" | "NO",
    _outcomePrice: number,
    _marketQuestion: string,
    _marketImage: string | null,
    _actualOutcome?: string,
    _outcomes?: string[],
    eventSource?: "polymarket" | "kalshi" | "predict",
    eventSourceData?: any,
    _clobTokenIds?: string[],
    eventSlug?: string,
  ) => {
    const source: Source = eventSource || 'polymarket';
    // Source-specific event identifier when caller didn't pass eventSlug.
    const inferredEventSlug = eventSlug
      || eventSourceData?.kalshi?.eventTicker
      || eventSourceData?.predict?.eventSlug
      || undefined;
    const target = marketPageUrl({
      marketSlug,
      eventSlug: inferredEventSlug,
      outcome,
      source,
    });
    router.push(target);
  }, [router]);

  // Collect all market slugs from visible events for social activity.
  //
  // Cap sub-markets per event at MAX_MARKETS_PER_EVENT. Events like
  // "Republican Presidential Nominee 2028" carry +126 sub-markets, but the
  // card UI only renders 2 outcomes + a "+N more markets" link — fetching
  // social activity for the hidden 124 just to feed `getEventSocialActivity`
  // (which short-circuits on the first hit) was producing ~720 slugs per
  // page, which the 25-batch chunker turned into ~29 parallel POSTs.
  // Capping to the visible 3 drops the fan-out to ~3 POSTs without losing
  // the early-hit detection (event.slug is checked first, and the visible
  // markets are the ones a friend most likely traded on anyway).
  const MAX_MARKETS_PER_EVENT = 3;
  const visibleMarketSlugs = useMemo(() => {
    const slugs: string[] = [];
    for (const event of events) {
      if (event.slug) slugs.push(event.slug);
      if (event.markets) {
        const cap = Math.min(event.markets.length, MAX_MARKETS_PER_EVENT);
        for (let i = 0; i < cap; i++) {
          const market = event.markets[i];
          if (market.slug) slugs.push(market.slug);
        }
      }
    }
    for (const item of forYouFeedItems) {
      if (item.type === 'event') {
        if (item.event.slug) slugs.push(item.event.slug);
        const ms = item.event.markets || [];
        const cap = Math.min(ms.length, MAX_MARKETS_PER_EVENT);
        for (let i = 0; i < cap; i++) {
          if (ms[i].slug) slugs.push(ms[i].slug);
        }
      } else {
        if (item.market.slug) slugs.push(item.market.slug);
      }
    }
    for (const event of forYouEvents) {
      if (event.slug) slugs.push(event.slug);
      if (event.markets) {
        const cap = Math.min(event.markets.length, MAX_MARKETS_PER_EVENT);
        for (let i = 0; i < cap; i++) {
          const market = event.markets[i];
          if (market.slug) slugs.push(market.slug);
        }
      }
    }
    return slugs;
  }, [events, forYouFeedItems, forYouEvents]);

  // Fetch social activity for visible markets
  const { activity: socialActivity } = useSocialActivity(visibleMarketSlugs);

  // Get social activity for an event by checking its market slugs
  const getEventSocialActivity = useCallback((event: Event) => {
    // Try event slug first (only if it has actual activity)
    if (socialActivity[event.slug]?.hasActivity) return socialActivity[event.slug];
    // Then try individual market slugs
    for (const market of event.markets || []) {
      if (market.slug && socialActivity[market.slug]?.hasActivity) return socialActivity[market.slug];
    }
    return null;
  }, [socialActivity]);


  // Redirect to trending if:
  // 1. User is on for-you but not authenticated
  // 2. On localhost (development mode) - always use trending instead of for-you
  // IMPORTANT: Only redirect after Privy is ready, to avoid premature redirects while loading auth state
  useEffect(() => {
    const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

    if (isLocalhost && category === "for-you") {
      setCategory("trending");
      window.history.replaceState({}, '', sourceUrl("/markets/trending", source));
    } else if (privyReady && !authenticated && category === "for-you") {
      setCategory("trending");
      window.history.replaceState({}, '', sourceUrl("/markets/trending", source));
    }
  }, [privyReady, authenticated, category, source]);

  // Reset loading state when category changes to prevent flash of "No markets"
  const prevCategoryRef = useRef(category);
  // Guard against double fetches when multiple dependencies change at once
  // Key format: "category-timestamp" to allow new fetches after some time
  const fetchInProgressRef = useRef<string | null>(null);
  // Version counter to ignore stale fetch results (race condition protection)
  const fetchVersionRef = useRef(0);
  // Aborts in-flight HTTP requests when the user changes category before the
  // previous fetch resolves. Without this, fetchWithRetry's 503 backoff
  // (1s + 2s) would keep running on the dead category — wasting bandwidth
  // and, when the new fetch hits the same retry, stacking the spinner time.
  const fetchAbortRef = useRef<AbortController | null>(null);
  // Track whether SSR data has been consumed (skip first fetch with loading)
  const ssrConsumedRef = useRef(false);
  // Track which Kalshi cacheKey we already painted from SSR so subsequent
  // re-runs of the main fetch effect (triggered by unrelated dep changes
  // like locale, auth-ready, viewMode) don't refetch the same data and
  // cause visible flicker. Only refetch when the key actually changes
  // (user changed category, sort, locale, etc.).
  const kalshiSSRKeyRef = useRef<string | null>(null);

  // ==========================================================================
  // CATEGORY CACHE - Module-level (persists across mount/unmount)
  // See: src/lib/markets-cache.ts
  // ==========================================================================
  // Track category changes (no loading flash — cache handles instant display)
  useEffect(() => {
    prevCategoryRef.current = category;
  }, [category]);

  const [tableSortOrder, setTableSortOrder] = useState<"asc" | "desc">("desc");

  // Live 15-min markets carousel state
  const [live15MinMarkets, setLive15MinMarkets] = useState<Event[]>([]);
  const [live5MinMarkets, setLive5MinMarkets] = useState<Event[]>([]);
  const [loading15Min, setLoading15Min] = useState(false);
  const [liveMarketsLoaded, setLiveMarketsLoaded] = useState(false);
  // Crypto data via TanStack Query hooks (cached across mount/unmount, auto-refetch)
  // Preload league names for sports EventCards (one-time fetch, cached in sessionStorage)
  useLeagueNames();
  const isCrypto = category === 'crypto';
  const { data: cryptoPricesRest = {} } = useCryptoPrices(isCrypto);
  const cryptoPricesWs = useCryptoPriceWs(isCrypto);
  // Merge: WS prices override REST prices (same source as CryptoPriceChart)
  const cryptoPrices = useMemo(() => {
    const merged = { ...cryptoPricesRest };
    for (const [symbol, wsData] of Object.entries(cryptoPricesWs)) {
      if (wsData.price > 0) {
        merged[symbol] = {
          price: wsData.price,
          change24h: merged[symbol]?.change24h ?? 0, // Keep 24h change from REST
        };
      }
    }
    return merged;
  }, [cryptoPricesRest, cryptoPricesWs]);
  const { data: fearGreed = null } = useFearGreed(isCrypto);
  const { data: globalCrypto = null } = useGlobalCrypto(isCrypto);
  const { data: cryptoNews = [], isLoading: loadingNews } = useCryptoNews(isCrypto);

  // Team logos for sports EventCards (teamName lowercase → logoUrl)
  // Prefer SSR-provided logos, refresh client-side for freshness
  const [teamLogos, setTeamLogos] = useState<Record<string, string>>(initialTeamLogos || {});
  useEffect(() => {
    fetch('/api/sports-live/team-logos')
      .then(r => r.json())
      .then(data => { if (data.success && data.logos) setTeamLogos(data.logos); })
      .catch(() => {});
  }, []);

  // Event price changes from Polymarket (% change since event start for 15-min markets)
  const [eventPriceChanges, setEventPriceChanges] = useState<Record<string, { openPrice: number; closePrice: number; changePercent: number }>>({});
  // Fear & Greed chart timeframe selection
  const [fearGreedTimeframe, setFearGreedTimeframe] = useState<'7d' | '30d' | '90d' | '180d' | '1y'>('30d');
  // Animated Fear & Greed values (for gauge and historical animation)
  const [animatedFearGreed, setAnimatedFearGreed] = useState({
    value: 0,
    yesterday: 0,
    lastWeek: 0,
    lastMonth: 0,
  });
  // Crypto overview section expanded/collapsed state (persisted in localStorage)
  // Initialize false to avoid SSR flash, then read real value in useEffect
  const [cryptoOverviewExpanded, setCryptoOverviewExpanded] = useState(false);
  const [financeOverviewExpanded, setFinanceOverviewExpanded] = useState(false);
  const [climateOverviewExpanded, setClimateOverviewExpanded] = useState(false);
  const [geopoliticsOverviewExpanded, setGeopoliticsOverviewExpanded] = useState(false);
  const [macroOverviewExpanded, setMacroOverviewExpanded] = useState(false);
  const [fedRatesOverviewExpanded, setFedRatesOverviewExpanded] = useState(false);
  const [trumpOverviewExpanded, setTrumpOverviewExpanded] = useState(false);
  useEffect(() => {
    try {
      const savedCrypto = localStorage.getItem('cryptoOverviewExpanded');
      setCryptoOverviewExpanded(savedCrypto !== null ? JSON.parse(savedCrypto) : true);
      const savedFinance = localStorage.getItem('financeOverviewExpanded');
      setFinanceOverviewExpanded(savedFinance !== null ? JSON.parse(savedFinance) : true);
      const savedClimate = localStorage.getItem('climateOverviewExpanded');
      setClimateOverviewExpanded(savedClimate !== null ? JSON.parse(savedClimate) : true);
      const savedGeopolitics = localStorage.getItem('geopoliticsOverviewExpanded');
      setGeopoliticsOverviewExpanded(savedGeopolitics !== null ? JSON.parse(savedGeopolitics) : true);
      const savedMacro = localStorage.getItem('macroOverviewExpanded');
      setMacroOverviewExpanded(savedMacro !== null ? JSON.parse(savedMacro) : true);
      const savedFedRates = localStorage.getItem('fedRatesOverviewExpanded');
      setFedRatesOverviewExpanded(savedFedRates !== null ? JSON.parse(savedFedRates) : true);
      const savedTrump = localStorage.getItem('trumpOverviewExpanded');
      setTrumpOverviewExpanded(savedTrump !== null ? JSON.parse(savedTrump) : true);
    } catch {}
  }, []);

  // Real-time market prices (UP/DOWN) from CLOB API - keyed by tokenId
  const [marketPrices, setMarketPrices] = useState<Record<string, number>>({});

  const [newsSidebarOpen, setNewsSidebarOpen] = useState(false);

  // Elon tweet tracker data for hero section
  const [elonTweetData, setElonTweetData] = useState<{
    tweetCount: number;
    timeRemaining: string;
    timeRemainingMs: number;
    pace: number;
    progress: number;
    target: number;
    market: {
      id: string;
      label: string;
      endDate: string;
      endTime: string;
      isEnded: boolean;
    };
  } | null>(null);
  const [loadingElonData, setLoadingElonData] = useState(false);

  // Elon subcategory filter — URL synced via central useEffect
  const initialElonSub = (category === "elon" && initialSubcategory) ? initialSubcategory : "all";
  const [elonSubcategory, setElonSubcategory] = useState<string>(initialElonSub);

  // Elon tweet counter markets (loaded separately to ensure we always find them)
  const [elonTweetMarkets, setElonTweetMarkets] = useState<Event[]>([]);
  // Prices for tweet market outcomes (fetched separately)
  const [tweetMarketPrices, setTweetMarketPrices] = useState<Record<string, number>>({});

  // Get watchlist from context
  const { watchlist, hasBookmarks, hasBookmarksForSource, isInWatchlist, toggleWatchlist, loading: watchlistLoading } = useMarketWatchlistContext();

  const prevWatchlistLength = useRef(watchlist.length);

  // Animate Fear & Greed values when they change
  useEffect(() => {
    if (!fearGreed) return;

    const targets = {
      value: fearGreed.value,
      yesterday: fearGreed.yesterday?.value ?? 0,
      lastWeek: fearGreed.lastWeek?.value ?? 0,
      lastMonth: fearGreed.lastMonth?.value ?? 0,
    };

    const duration = 2500; // 2.5 seconds
    const startTime = Date.now();
    const fps = 30; // 30fps is smooth enough for numbers
    const interval = 1000 / fps;

    const timer = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Easing function (ease-out cubic) - smoother deceleration
      const easeOut = 1 - Math.pow(1 - progress, 3);

      setAnimatedFearGreed({
        value: Math.round(targets.value * easeOut),
        yesterday: Math.round(targets.yesterday * easeOut),
        lastWeek: Math.round(targets.lastWeek * easeOut),
        lastMonth: Math.round(targets.lastMonth * easeOut),
      });

      if (progress >= 1) {
        clearInterval(timer);
      }
    }, interval);

    return () => clearInterval(timer);
  }, [fearGreed]);

  // categoryIconMap, staticPrefixCategories, fallbackDynamicCategories all
  // imported from ./helpers/categories — pure lookup tables, no closure deps.

  // Dynamic categories from taxonomy (Polymarket nav) + static prefix
  const polymarketCategories = useMemo(() => {
    const dynamicCats = taxonomy?.mainNavCategories && taxonomy.mainNavCategories.length >= 5
      ? taxonomy.mainNavCategories.map(c => ({
          id: c.slug,
          label: c.label,
          icon: categoryIconMap[c.slug] || Globe2,
        }))
      : fallbackDynamicCategories;

    // Ensure esports tab is always present and positioned right after sports
    const esportsEntry = { id: 'esports', label: 'Esports', icon: Gamepad2 };
    const existingIdx = dynamicCats.findIndex(c => c.id === 'esports');
    if (existingIdx >= 0) dynamicCats.splice(existingIdx, 1);
    const sportsIdx = dynamicCats.findIndex(c => c.id === 'sports');
    dynamicCats.splice(sportsIdx >= 0 ? sportsIdx + 1 : dynamicCats.length, 0, esportsEntry);

    // Drop elections — gamma still surfaces the tab but the section
    // (along with its Macro / Fed Rates / Trump subroutes) is no
    // longer maintained on our side. The URLs still resolve but the
    // header tab + subcategory bar are hidden.
    const filtered = dynamicCats.filter(c => c.id !== 'elections');

    const result = [...staticPrefixCategories, ...filtered];
    // World Cup tab — pinned right after Trending. Top-level category that
    // fetches the Polymarket `fifa-world-cup` tag (NOT the ESPN sports view).
    const wcEntry = { id: 'world-cup', label: 'World Cup', icon: Trophy };
    const wcExisting = result.findIndex(c => c.id === 'world-cup');
    if (wcExisting >= 0) result.splice(wcExisting, 1);
    const trendingIdx = result.findIndex(c => c.id === 'trending');
    result.splice(trendingIdx >= 0 ? trendingIdx + 1 : 0, 0, wcEntry);

    return result;
  }, [taxonomy]); // eslint-disable-line react-hooks/exhaustive-deps

  // World Cup nav highlight: pulse a "live" dot on the World Cup tab only on
  // days that actually have a match (user-local day window via endDate). One
  // idle, edge-cacheable fetch per session — gated by a ref so it never repeats
  // and never blocks paint. Off-tournament it just returns no matches → no dot.
  const [wcGamesToday, setWcGamesToday] = useState(false);
  const wcTodayCheckedRef = useRef(false);
  useEffect(() => {
    if (wcTodayCheckedRef.current) return;
    wcTodayCheckedRef.current = true;
    const run = async () => {
      try {
        const res = await fetch(`/api/markets/events?tag_slug=fifa-world-cup&limit=50&active=true&closed=false&order=volume24hr&ascending=false`);
        const data = await res.json();
        const evs: any[] = Array.isArray(data) ? data : data.events || data.markets || [];
        const now = new Date();
        const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
        const endOfDay = startOfDay + 86_400_000;
        const hasToday = evs.some((e) => {
          const smt = (e.sportsMarketType || e.markets?.[0]?.sportsMarketType || "").toLowerCase();
          if (smt !== "moneyline") return false;
          if (e.live === true) return true;
          const t = e.endDate ? new Date(e.endDate).getTime() : 0;
          return t >= startOfDay && t < endOfDay;
        });
        setWcGamesToday(hasToday);
      } catch {
        /* non-fatal — no dot */
      }
    };
    const ric: (cb: () => void) => void =
      (window as any).requestIdleCallback || ((cb: () => void) => window.setTimeout(cb, 1200));
    ric(run);
  }, []);

  // Kalshi category icons — imported from ./helpers/categories.

  // Fallback categories (used while loading or if fetch fails)
  // Must match backend's new_standard_tabs to prevent layout jump when taxonomy loads
  const kalshiFallbackCategories = [
    { id: "trending", label: "Trending", icon: TrendingUp, kalshiCategory: "" },
    { id: "politics", label: "Politics", icon: Landmark, kalshiCategory: "Politics" },
    { id: "sports", label: "Sports", icon: Trophy, kalshiCategory: "Sports" },
    { id: "entertainment", label: "Culture", icon: Palette, kalshiCategory: "Entertainment" },
    { id: "crypto", label: "Crypto", icon: Bitcoin, kalshiCategory: "Crypto" },
    // Commodities is missing from Kalshi's own tabs.json.gz (the file we
    // scrape for new_standard_tabs) even though kalshi.com exposes
    // /category/commodities and /v2/search/tags_by_categories lists it.
    // Hardcode it here so users see it until Kalshi publishes an updated
    // tabs.json.gz. Positioned after Crypto to keep the grouping intuitive.
    { id: "commodities", label: "Commodities", icon: Wheat, kalshiCategory: "Commodities" },
    { id: "climate", label: "Climate", icon: Leaf, kalshiCategory: "Climate and Weather" },
    { id: "economics", label: "Economics", icon: LineChart, kalshiCategory: "Economics" },
    { id: "mentions", label: "Mentions", icon: MessageCircle, kalshiCategory: "Mentions" },
    { id: "companies", label: "Companies", icon: Cpu, kalshiCategory: "Companies" },
    { id: "financials", label: "Financials", icon: DollarSign, kalshiCategory: "Financials" },
    { id: "science", label: "Tech & Science", icon: Cpu, kalshiCategory: "Science and Technology" },
  ];

  // State for dynamically fetched Kalshi categories (taxonomy-style: includes
  // subcategories). Seed from the module/localStorage cache so a remount or
  // hard reload doesn't re-fetch.
  const [kalshiCategoriesData, setKalshiCategoriesData] = useState<KalshiCatEntry[] | null>(() => {
    if (kalshiCategoriesMemo) return kalshiCategoriesMemo;
    if (typeof window !== 'undefined') {
      try {
        const raw = window.localStorage.getItem(KALSHI_CATS_LS_KEY);
        if (raw) {
          const parsed = JSON.parse(raw) as Array<{ id: string; label: string; kalshiCategory: string; subcategories: string[] }>;
          if (Array.isArray(parsed) && parsed.length) {
            const seeded = parsed.map((c) => ({ ...c, icon: kalshiIconMap[c.label] || Globe2 }));
            kalshiCategoriesMemo = seeded;
            return seeded;
          }
        }
      } catch { /* ignore */ }
    }
    return null;
  });

  // Fetch Kalshi taxonomy (categories + subcategories) in a single call
  useEffect(() => {
    if (effectiveSourceFilter !== 'kalshi') return;
    if (kalshiCategoriesData) return; // Already fetched (or seeded from cache)

    const fetchKalshiTaxonomy = async () => {
      try {
        const res = await authFetch('/api/markets/kalshi/categories');
        if (res.ok) {
          const data = await res.json();
          if (data.categories && Array.isArray(data.categories)) {
            const mapped = data.categories.map((cat: { id: string; label: string; kalshiCategory: string; subcategories?: string[] }) => ({
              id: cat.id,
              label: cat.label,
              icon: kalshiIconMap[cat.label] || Globe2,
              kalshiCategory: cat.kalshiCategory,
              subcategories: cat.subcategories || [],
            }));
            setKalshiCategoriesData(mapped);
            kalshiCategoriesMemo = mapped;
            // Persist raw fields (icons aren't serializable; re-derived on read).
            try {
              window.localStorage.setItem(
                KALSHI_CATS_LS_KEY,
                JSON.stringify(mapped.map((c: KalshiCatEntry) => ({ id: c.id, label: c.label, kalshiCategory: c.kalshiCategory, subcategories: c.subcategories }))),
              );
            } catch { /* quota/full — non-fatal */ }
          }
        }
      } catch {
        // Use fallback on error
      }
    };

    fetchKalshiTaxonomy();
  }, [effectiveSourceFilter, kalshiCategoriesData]);

  // Resolve subcategories from taxonomy (no extra fetch needed)
  useEffect(() => {
    if (effectiveSourceFilter !== 'kalshi') return;
    const cats = kalshiCategoriesData || kalshiFallbackCategories;
    const kalshiCat = cats.find(c => c.id === category);

    if (!kalshiCat?.kalshiCategory) {
      setKalshiSubcategories([]);
      setActiveKalshiSubcategory(null);
      return;
    }

    const subs = ('subcategories' in kalshiCat && Array.isArray(kalshiCat.subcategories))
      ? kalshiCat.subcategories.map((name: string) => ({ name }))
      : [];
    setKalshiSubcategories(subs);
  }, [effectiveSourceFilter, category, kalshiCategoriesData]);

  // Reset Kalshi subcategory and adjust sort when category changes.
  // For /kalshi/markets/sports, hydrate sub/competition from URL query params
  // so deep links and back/forward navigation keep the sidebar selection.
  useEffect(() => {
    if (effectiveSourceFilter !== 'kalshi') {
      setActiveKalshiSubcategory(null);
      setActiveKalshiCompetition(null);
      return;
    }
    setKalshiSortBy(category === 'trending' ? 'all' : 'trending');
    if (category === 'sports' && typeof window !== 'undefined') {
      const sp = new URLSearchParams(window.location.search);
      const sub = sp.get('subcategory');
      const comp = sp.get('competition');
      setActiveKalshiSubcategory(sub || null);
      setActiveKalshiCompetition(comp || null);
    } else {
      setActiveKalshiSubcategory(null);
      setActiveKalshiCompetition(null);
    }
  }, [category, effectiveSourceFilter]);

  // Use dynamic categories or fallback
  const kalshiCategories = kalshiCategoriesData || kalshiFallbackCategories;

  // Predict.fun category icons — imported from ./helpers/categories.
  const predictFallbackCategories = [
    { id: "live", label: "Live", icon: ({ className }: any) => <span className="w-2 h-2 bg-red-500 rounded-[5px] animate-pulse inline-block" /> },
    { id: "trending", label: "Trending", icon: TrendingUp },
    { id: "new", label: "New", icon: Zap },
    { id: "sports", label: "Sports", icon: Trophy },
    { id: "politics", label: "Politics", icon: Landmark },
    { id: "crypto", label: "Crypto", icon: Bitcoin },
    { id: "esports", label: "Esports", icon: Gamepad2 },
    { id: "finance", label: "Finance", icon: DollarSign },
    { id: "economy", label: "Economy", icon: LineChart },
    { id: "culture", label: "Culture", icon: Palette },
  ];

  // hyperliquidCategories imported from ./helpers/categories.
  // Init from localStorage cache for instant render (no flash)
  const [predictCategoriesData, setPredictCategoriesData] = useState<Array<{ id: string; label: string; icon: any }> | null>(() => {
    if (typeof window === 'undefined') return null;
    try {
      const cached = localStorage.getItem('predict_categories_cache_v2');
      if (!cached) return null;
      const parsed = JSON.parse(cached) as Array<{ id: string; label: string }>;
      if (parsed.length > 5 && parsed.some(c => c.id === 'live')) {
        return parsed.map(c => ({ ...c, icon: c.id === 'live' ? (({ className }: any) => <span className="w-2 h-2 bg-red-500 rounded-[5px] animate-pulse inline-block" />) : predictIconMap[c.label] || Globe2 }));
      }
    } catch {}
    return null;
  });

  useEffect(() => {
    if (effectiveSourceFilter !== 'predict') return;
    if (predictCategoriesData && predictCategoriesData.length >= 8 && predictCategoriesData.some(c => c.id === 'live')) return;

    (async () => {
      try {
        const res = await authFetch('/api/markets/predict/categories');
        if (!res.ok) return;
        const data = await res.json();
        const cats = Array.isArray(data) ? data : data.categories || [];
        if (cats.length > 0) {
          // Solo el nav PRINCIPAL de Predict.fun, en orden fijo. Las categorías
          // del dropdown "More" de predict.fun NO se muestran en FrenFlow
          // (decisión 2026-06). Orden pedido: Live, Trending, New + estos 7.
          const MAIN_ORDER = ['Sports', 'Politics', 'Crypto', 'Esports', 'Finance', 'Economy', 'Culture'];
          const byName = new Map<string, any>(cats.map((c: any) => [c.name, c]));
          const mainCats = MAIN_ORDER.map((name) => byName.get(name)).filter(Boolean);

          const dynamic = [
            { id: "live", label: "Live", icon: ({ className }: any) => <span className="w-2 h-2 bg-red-500 rounded-[5px] animate-pulse inline-block" /> },
            { id: "trending", label: "Trending", icon: TrendingUp },
            { id: "new", label: "New", icon: Zap },
            ...mainCats.map((c: any) => ({
              id: (c.slug || c.name.toLowerCase().replace(/\s+/g, '-')),
              label: c.name,
              icon: predictIconMap[c.name] || Globe2,
            })),
          ];
          // Dedup by id
          const seen = new Set<string>();
          const deduped = dynamic.filter(c => { if (seen.has(c.id)) return false; seen.add(c.id); return true; });
          setPredictCategoriesData(deduped);
          try { localStorage.setItem('predict_categories_cache_v2', JSON.stringify(deduped.map(c => ({ id: c.id, label: c.label })))); } catch {}
        }
      } catch { /* use fallback */ }
    })();
  }, [effectiveSourceFilter, predictCategoriesData]);

  // Show nothing until real categories load (no fallback flash)
  const predictCategories = predictCategoriesData || (effectiveSourceFilter === 'predict' ? [] : predictFallbackCategories);

  // Sort options per source
  const polymarketSortOptions = [
    { value: 'volume24hr' as const, label: t("filterBar.sort.volume24hr") },
    { value: 'volume' as const, label: t("filterBar.sort.volume") },
    { value: 'liquidity' as const, label: t("filterBar.sort.liquidity") },
    { value: 'newest' as const, label: t("filterBar.sort.newest") },
    { value: 'endingSoon' as const, label: t("filterBar.sort.endingSoon") },
    { value: 'competitive' as const, label: t("filterBar.sort.competitive") },
  ];
  const predictSortOptions = [
    { value: 'volume24hr' as const, label: t("filterBar.sort.trending") },
    { value: 'volume' as const, label: t("filterBar.sort.volume") },
    { value: 'liquidity' as const, label: t("filterBar.sort.volume24hr") },
    { value: 'endingSoon' as const, label: t("filterBar.sort.endingSoon") },
  ];
  const activeSortOptions = effectiveSourceFilter === 'predict' || effectiveSourceFilter === 'hyperliquid'
    ? predictSortOptions
    : polymarketSortOptions;
  const sortLabelMap = Object.fromEntries(activeSortOptions.map(o => [o.value, o.label]));

  // Filter categories based on auth state, watchlist, and source
  // Use isLoggedIn (optimistic from useAuth) instead of raw Privy authenticated
  // to prevent category pills from flashing on page load
  const categories = useMemo(() => {
    // Use Kalshi categories when Kalshi is selected
    if (effectiveSourceFilter === 'kalshi') {
      if (isLoggedIn && hasBookmarksForSource('kalshi')) {
        const bookmarksCat = { id: "bookmarks", label: "Bookmarks", icon: Bookmark };
        return [bookmarksCat, ...kalshiCategories];
      }
      return kalshiCategories;
    }

    // Predict.fun categories — bookmarks after Live (first position)
    if (effectiveSourceFilter === 'predict') {
      if (isLoggedIn && hasBookmarksForSource('predict')) {
        const bookmarksCat = { id: "bookmarks", label: "Bookmarks", icon: Bookmark };
        // Insert after first category (Live/Trending)
        const liveIdx = predictCategories.findIndex(c => c.id === 'live');
        const insertAt = liveIdx >= 0 ? liveIdx + 1 : 0;
        return [...predictCategories.slice(0, insertAt), bookmarksCat, ...predictCategories.slice(insertAt)];
      }
      return predictCategories;
    }

    // Hyperliquid — only the meta-rails + 'recurring'. Polymarket categories
    // (Elon / Politics / Iran / etc.) don't apply to HL's outcome universe.
    if (effectiveSourceFilter === 'hyperliquid') {
      return hyperliquidCategories;
    }

    // Polymarket categories with auth-based filtering
    let filtered = polymarketCategories;
    // Hide "for-you" when confirmed not authenticated
    if (privyReady && !isLoggedIn) {
      filtered = filtered.filter(c => c.id !== "for-you");
    }
    // Hide "bookmarks" when not authenticated or no Polymarket bookmarks
    if (!isLoggedIn || !hasBookmarksForSource('polymarket')) {
      filtered = filtered.filter(c => c.id !== "bookmarks");
    }
    return filtered;
  }, [privyReady, isLoggedIn, hasBookmarksForSource, effectiveSourceFilter, kalshiCategories]);

  // Crypto subcategories - Two-level navigation like Polymarket
  // Level 1: Timeframes OR Coins (mutually exclusive primary selection)
  // Level 2: Dynamic based on Level 1 selection
  const cryptoSubcategories = {
    // Level 1 - Primary tabs
    timeframes: [
      { id: "all", label: "All", tag: "" },
      { id: "5m", label: "5 Min", tag: "5m" },
      { id: "15m", label: "15 Min", tag: "15m" },
      { id: "1h", label: "Hourly", tag: "1h" },
      { id: "4h", label: "4 Hour", tag: "4h" },
      { id: "daily", label: "Daily", tag: "daily" },
      { id: "weekly", label: "Weekly", tag: "weekly" },
      { id: "monthly", label: "Monthly", tag: "monthly" },
      { id: "yearly", label: "Yearly", tag: "yearly" },
      { id: "pre-market", label: "Pre-Market", tag: "pre-market" },
      { id: "etf", label: "ETF", tag: "etf" },
    ],
    coins: [
      { id: "btc", label: "BTC", tags: ["Bitcoin", "BTC"] },
      { id: "eth", label: "ETH", tags: ["Ethereum", "ETH"] },
      { id: "sol", label: "SOL", tags: ["Solana", "SOL", "sol"] },
      { id: "xrp", label: "XRP", tags: ["XRP", "Ripple"] },
      { id: "doge", label: "DOGE", tags: ["Dogecoin", "DOGE", "doge"] },
      { id: "mstr", label: "MSTR", tags: ["MicroStrategy", "Microstrategy", "MSTR", "microstrategy", "Saylor"] },
    ],
    // Level 2 - Secondary filters (dynamic based on primary selection)
    marketTypes: [
      { id: "all", label: "All" },
      { id: "above-below", label: "Above/Below", keywords: ["above", "below"] },
      { id: "up-down", label: "Up/Down", keywords: ["up or down", "up/down", "up ", " down"] },
    ],
    etfFilters: [
      { id: "all", label: "All" },
      { id: "ibit", label: "IBIT", keywords: ["IBIT", "iShares"] },
      { id: "fbtc", label: "FBTC", keywords: ["FBTC", "Fidelity"] },
      { id: "gbtc", label: "GBTC", keywords: ["GBTC", "Grayscale"] },
      { id: "ethe", label: "ETHE", keywords: ["ETHE"] },
      { id: "inflows", label: "Inflows", keywords: ["inflow", "inflows"] },
      { id: "outflows", label: "Outflows", keywords: ["outflow", "outflows"] },
    ],
  };

  // Crypto filter states - Two levels.
  // initialSubcategory from URL takes priority, then legacy
  // initialCryptoTimeFilter prop. Commodities shares this state with
  // crypto so a `/kalshi/markets/commodities/daily` reload keeps the
  // "Daily" filter instead of snapping back to the unfiltered URL.
  const [cryptoPrimaryFilter, setCryptoPrimaryFilter] = useState<string>(
    ((category === "crypto" || category === "commodities") && initialSubcategory)
      ? initialSubcategory
      : (initialCryptoTimeFilter || "all")
  );
  const [cryptoSecondaryFilter, setCryptoSecondaryFilter] = useState<string>("all");

  // Crypto sidebar collapsed state
  const [cryptoSidebarCollapsed, setCryptoSidebarCollapsed] = useState(false);
  // Finance sidebar collapsed state
  const [financeSidebarCollapsed, setFinanceSidebarCollapsed] = useState(false);
  // Mobile bottom sheet sidebar states
  const [mobileCryptoSidebarOpen, setMobileCryptoSidebarOpen] = useState(false);
  const [mobileFinanceSidebarOpen, setMobileFinanceSidebarOpen] = useState(false);
  const [mobileKalshiSportsSidebarOpen, setMobileKalshiSportsSidebarOpen] = useState(false);

  // Crypto counts for sidebar (fetched from backend for PM, derived for Kalshi)
  // Initialize empty to match SSR, then hydrate from localStorage in useEffect
  const [cryptoCountsRaw, setCryptoCounts] = useState<Record<string, number>>({});
  useEffect(() => {
    try {
      const saved = localStorage.getItem('cryptoCounts');
      if (saved) setCryptoCounts(JSON.parse(saved));
    } catch {}
  }, []);

  // Fetch crypto counts when crypto category is active.
  // PM-only: the comment at the state declaration above notes that for
  // Kalshi the counts get derived elsewhere from the sidebar response
  // (useCryptoSidebar at line below already gates on `effectiveSourceFilter
  // === 'polymarket'`). Without the source guard here, switching from
  // PM to Kalshi inside the crypto tab still fired this PM-only request
  // and contributed to the cross-source pollution Chrome DevTools flagged.
  useEffect(() => {
    if (category !== "crypto" || effectiveSourceFilter !== "polymarket") return;

    const fetchCryptoCounts = async () => {
      try {
        const res = await authFetch('/api/markets/crypto/counts');
        if (res.ok) {
          const data = await res.json();
          if (data.success && data.counts) {
            // Normalize keys to lowercase for case-insensitive lookup
            // (backend returns "MicroStrategy" but scraper labels "Microstrategy", etc.)
            const normalized: Record<string, number> = {};
            for (const [k, v] of Object.entries(data.counts)) {
              normalized[k.toLowerCase()] = typeof v === 'number' ? v : Number(v);
            }
            setCryptoCounts(normalized);
            localStorage.setItem('cryptoCounts', JSON.stringify(normalized));
          }
        }
      } catch {
        // Silent fail - counts are optional
      }
    };

    fetchCryptoCounts();
  }, [category, effectiveSourceFilter]);

  // Scraped crypto sidebar structure (auto-discovered from PM every 8h)
  type ScrapedCryptoSidebar = {
    sidebar: Array<{ slug: string; label: string; count: number; href: string; image: string | null; isToken: boolean; headerTabs: Array<{ slug: string; label: string }> }>;
    defaultTabs: Array<{ slug: string; label: string }>;
    tabsByCategory: Record<string, Array<{ slug: string; label: string }>>;
    keywordMapping: Record<string, string[]>;
    tokens: Array<{ id: string; slug: string; image: string; filter: string }>;
    meta: { total: number; categories: number };
  };
  const { data: scrapedCrypto = null } = useCryptoSidebar(category === 'crypto' && effectiveSourceFilter === 'polymarket') as { data: ScrapedCryptoSidebar | null };
  // Kalshi sidebar (frequencies + subcategories from /v1/series).
  // Prefetch as soon as the user lands in Kalshi (any tab) so the sidebar
  // for the current category is already cached when they land on it.
  // Map URL category → Kalshi API category label. Kept in sync with the
  // server-side KALSHI_CATEGORY_MAP in markets.ts; any new entry here
  // must also be added to the page.tsx sidebar fetch map.
  const kalshiSidebarCategoryLabel = useMemo(() => {
    // URL slug → label the backend's KALSHI_CATEGORY_MAP accepts. Previously
    // only crypto/commodities were mapped and every other category fell
    // back to 'Crypto', so /kalshi/markets/politics fetched the Crypto
    // sidebar (wrong frequencies/subcategories) until the user navigated.
    const map: Record<string, string> = {
      crypto: 'Crypto',
      commodities: 'Commodities',
      politics: 'Politics',
      sports: 'Sports',
      entertainment: 'Entertainment',
      economics: 'Economics',
      financials: 'Financials',
      companies: 'Companies',
      climate: 'Climate and Weather',
      science: 'Science and Technology',
      mentions: 'Mentions',
    };
    // Meta-categories don't map to a single Kalshi category — they don't
    // have a sidebar (trending = curated mix; for-you = personalised;
    // bookmarks = user's saves; breaking/new = sort-only views). The
    // previous fallback to 'Crypto' was firing
    // /api/markets/kalshi/sidebar/Crypto on every /kalshi/markets/trending
    // load, painting the wrong filter chips and burning a request.
    return map[category] || null;
  }, [category]);
  // Only seed React Query with the SSR payload when it actually matches
  // the current category — client-side nav between Kalshi categories
  // keeps the MarketsPageContent mounted and the initialKalshiSidebar
  // prop carries whichever category was first rendered. Reusing a
  // mismatched initialData was painting the Commodities sidebar on the
  // Crypto page (and vice-versa) until the background fetch resolved.
  const seedInitialKalshiSidebar = useMemo(() => {
    if (!initialKalshiSidebar || !kalshiSidebarCategoryLabel) return undefined;
    return initialKalshiSidebar.category === kalshiSidebarCategoryLabel
      ? initialKalshiSidebar
      : undefined;
  }, [initialKalshiSidebar, kalshiSidebarCategoryLabel]);
  const { data: kalshiSidebar = null } = useKalshiSidebar(
    kalshiSidebarCategoryLabel || '',
    effectiveSourceFilter === 'kalshi' && !!kalshiSidebarCategoryLabel,
    seedInitialKalshiSidebar,
  );
  const { data: kalshiSportsSidebar = null } = useKalshiSportsSidebar(category === 'sports' && effectiveSourceFilter === 'kalshi');
  const { data: kalshiLiveSet } = useKalshiLiveTickers(effectiveSourceFilter === 'kalshi' && (category === 'sports' || category === 'trending'));
  const [expandedKalshiSport, setExpandedKalshiSport] = useState<string | null>(null);

  // Counts: derived from Kalshi sidebar when source is kalshi, otherwise PM counts
  const cryptoCounts = useMemo(() => {
    if (kalshiSidebar && effectiveSourceFilter === 'kalshi') {
      const counts: Record<string, number> = { '': kalshiSidebar.total };
      for (const f of kalshiSidebar.frequencies) counts[f.value] = f.count;
      for (const s of kalshiSidebar.subcategories) {
        counts[s.label.toLowerCase()] = s.count;
        // Also index by nested scope label so cryptoSidebarCoins child rows
        // resolve their own count key (coin.tags[0] = child.label.toLowerCase()).
        for (const child of s.children || []) {
          counts[child.label.toLowerCase()] = child.count;
        }
      }
      return counts;
    }
    return cryptoCountsRaw;
  }, [kalshiSidebar, effectiveSourceFilter, cryptoCountsRaw]);

  // Legacy aliases for compatibility (will refactor later)
  const cryptoTimeFilter = cryptoPrimaryFilter;
  const setCryptoTimeFilter = setCryptoPrimaryFilter;
  const cryptoCoinFilter = null; // Deprecated - coins are now in primary filter
  const setCryptoCoinFilter = (_: string | null) => {}; // No-op for now

  // Computed sidebar items from scraper (with fallback to hardcoded)
  // Scraper slugs come from PM URLs (e.g., "5M", "1H") — normalize id to lowercase
  // for timeFilterMap compatibility, keep tag as-is for count lookups
  const cryptoSidebarTimeframes = useMemo(() => {
    // Kalshi: use frequencies from /v1/series API
    if (effectiveSourceFilter === 'kalshi') {
      if (!kalshiSidebar) return [{ id: 'all', label: 'All markets', tag: '' }];
      return [
        { id: 'all', label: 'All markets', tag: '' },
        ...kalshiSidebar.frequencies.map(f => ({ id: f.value, label: f.label, tag: f.value })),
      ];
    }
    if (!scrapedCrypto) return cryptoSubcategories.timeframes;
    // PM scraper slugs → our internal IDs (for timeFilterMap, TIMEFRAME_ICONS, URL routing)
    const slugToId: Record<string, string> = {
      '5M': '5m', '15M': '15m', 'hourly': '1h', '4hour': '4h',
      'daily': 'daily', 'weekly': 'weekly', 'monthly': 'monthly',
      'yearly': 'yearly', 'pre-market': 'pre-market', 'etf': 'etf',
    };
    // PM scraper slugs → count keys (lowercase, matches normalized cryptoCounts)
    const slugToCountKey: Record<string, string> = {
      '5M': '5m', '15M': '15m', 'hourly': '1h', '4hour': '4h',
      'daily': 'daily', 'weekly': 'weekly', 'monthly': 'monthly',
      'yearly': 'yearly', 'pre-market': 'pre-market', 'etf': 'etf',
    };
    return scrapedCrypto.sidebar
      .filter(item => !item.isToken)
      .map(item => {
        const slug = item.slug;
        const id = slug === 'all' ? 'all' : (slugToId[slug] || slug.toLowerCase());
        const tag = slug === 'all' ? '' : (slugToCountKey[slug] || slug);
        return { id, label: item.label, tag };
      });
  }, [scrapedCrypto, kalshiSidebar, effectiveSourceFilter]);

  const cryptoSidebarCoins = useMemo(() => {
    // Kalshi: use subcategories (BTC, ETH, SOL, etc.), optionally with
    // nested scope children (Commodities → Oil & Gas → WTI Crude etc.).
    // Flatten parent + children into a single list with a `depth` flag so
    // the render loop only has to check one property to decide indent.
    if (effectiveSourceFilter === 'kalshi') {
      if (!kalshiSidebar) return [];
      const rows: Array<{
        id: string;
        label: string;
        tags: string[];
        depth: number;
        parentTag?: string;
        scope?: string;
      }> = [];
      for (const s of kalshiSidebar.subcategories) {
        const parentId = s.label.toLowerCase().replace(/\s+/g, '-');
        rows.push({
          id: parentId,
          label: s.label,
          tags: [s.label.toLowerCase()],
          depth: 0,
        });
        for (const child of s.children || []) {
          rows.push({
            id: `${parentId}__${child.label.toLowerCase().replace(/\s+/g, '-')}`,
            label: child.label,
            tags: [child.label.toLowerCase()],
            depth: 1,
            parentTag: s.label,
            scope: child.label,
          });
        }
      }
      return rows;
    }
    if (!scrapedCrypto) return cryptoSubcategories.coins;
    return scrapedCrypto.sidebar
      .filter(item => item.isToken)
      .map(item => ({ id: item.slug, label: item.label, tags: [item.label.toLowerCase()] }));
  }, [scrapedCrypto, kalshiSidebar, effectiveSourceFilter]);

  // Computed token images from scraper (with fallback to CoinGecko hardcoded)
  const cryptoTokenImages = useMemo(() => {
    if (!scrapedCrypto) return COIN_ICON_URLS;
    const map: Record<string, string> = { ...COIN_ICON_URLS };
    for (const token of scrapedCrypto.tokens) {
      // Skip broken polymarket.com/icons URLs (return 404)
      if (token.image && !token.image.includes('polymarket.com/icons')) {
        map[token.filter] = token.image;
      }
    }
    return map;
  }, [scrapedCrypto]);

  // Computed secondary tabs from scraper (with fallback to hardcoded)
  // tabsByCategory keys come from scraper (may differ in case from our normalized IDs)
  const cryptoSecondaryTabs = useMemo(() => {
    if (!scrapedCrypto?.tabsByCategory) return null;
    // Try exact match first, then case-insensitive lookup
    const tabs = scrapedCrypto.tabsByCategory[cryptoPrimaryFilter]
      || Object.entries(scrapedCrypto.tabsByCategory).find(([k]) => k.toLowerCase() === cryptoPrimaryFilter.toLowerCase())?.[1];
    if (!tabs || tabs.length === 0) return null;
    return [{ slug: 'all', label: 'All' }, ...tabs];
  }, [scrapedCrypto, cryptoPrimaryFilter]);

  // ==========================================================================
  // FINANCE SUBCATEGORIES - Sidebar + Horizontal Tabs (like Polymarket)
  // ==========================================================================
  const financeSubcategories = {
    // Sidebar items (from finance-counts in __NEXT_DATA__)
    sidebar: [
      { id: "all", label: "All", slug: "" },
      { id: "daily", label: "Daily", slug: "daily" },
      { id: "weekly", label: "Weekly", slug: "weekly" },
      { id: "monthly", label: "Monthly", slug: "monthly" },
      { id: "stocks", label: "Stocks", slug: "stocks" },
      { id: "earnings", label: "Earnings", slug: "earnings" },
      { id: "indicies", label: "Indices", slug: "indicies" },
      { id: "commodities", label: "Commodities", slug: "commodities" },
      { id: "forex", label: "Forex", slug: "forex" },
      { id: "collectibles", label: "Collectibles", slug: "collectibles" },
      { id: "acquisitions", label: "Acquisitions", slug: "acquisitions" },
      { id: "earnings-calls", label: "Earnings Calls", slug: "earnings-calls" },
      { id: "ipo", label: "IPOs", slug: "ipo" },
      { id: "fed-rates", label: "Fed Rates", slug: "fed-rates" },
      { id: "prediction-markets", label: "Prediction Markets", slug: "prediction-markets" },
      { id: "treasuries", label: "Treasuries", slug: "treasuries" },
    ],
    // Horizontal tabs per sidebar category (hardcoded - PM rarely changes these)
    tabs: {
      // All, Daily, Weekly, Monthly share same tabs
      "all": [
        { id: "all", label: "All" },
        { id: "up-or-down", label: "Up/Down", keywords: ["up", "down", "↑", "↓"] },
        { id: "daily-close", label: "Daily Close", keywords: ["close"] },
        { id: "sp-500", label: "S&P 500", keywords: ["S&P", "SPX", "500"] },
        { id: "stocks", label: "Stocks", keywords: [] },
        { id: "indicies", label: "Indices", keywords: [] },
        { id: "gold", label: "Gold", keywords: ["gold", "XAU"] },
        { id: "silver", label: "Silver", keywords: ["silver", "XAG"] },
        { id: "tsla", label: "Tesla", keywords: ["Tesla", "TSLA"] },
        { id: "nvda", label: "NVIDIA", keywords: ["NVIDIA", "NVDA"] },
      ],
      "daily": "all", // Same as all
      "weekly": "all",
      "monthly": "all",
      "stocks": [
        { id: "all", label: "All" },
        { id: "up-or-down", label: "Up/Down", keywords: ["up", "down"] },
        { id: "daily-close", label: "Daily Close", keywords: ["close"] },
        { id: "nvda", label: "NVDA", keywords: ["NVIDIA", "NVDA"] },
        { id: "tsla", label: "TSLA", keywords: ["Tesla", "TSLA"] },
        { id: "googl", label: "GOOGL", keywords: ["Google", "GOOGL", "Alphabet"] },
        { id: "aapl", label: "AAPL", keywords: ["Apple", "AAPL"] },
        { id: "meta", label: "META", keywords: ["Meta", "META", "Facebook"] },
        { id: "amzn", label: "AMZN", keywords: ["Amazon", "AMZN"] },
        { id: "hood", label: "HOOD", keywords: ["Robinhood", "HOOD"] },
        { id: "nflx", label: "NFLX", keywords: ["Netflix", "NFLX"] },
        { id: "pltr", label: "PLTR", keywords: ["Palantir", "PLTR"] },
        { id: "open", label: "OPEN", keywords: ["Opendoor", "OPEN"] },
        { id: "coin", label: "COIN", keywords: ["Coinbase", "COIN"] },
      ],
      "indicies": [
        { id: "all", label: "All" },
        { id: "up-or-down", label: "Up/Down", keywords: ["up", "down"] },
        { id: "sp-500", label: "S&P 500", keywords: ["S&P", "SPX", "500"] },
        { id: "nasdaq", label: "Nasdaq 100", keywords: ["Nasdaq", "QQQ", "NDX"] },
        { id: "dow", label: "Dow Jones", keywords: ["Dow", "DJIA", "DJI"] },
        { id: "russell", label: "Russell 2000", keywords: ["Russell", "RUT", "IWM"] },
        { id: "nya", label: "NYA", keywords: ["NYSE", "NYA"] },
        { id: "ftse", label: "FTSE 100", keywords: ["FTSE", "UK100"] },
        { id: "hangseng", label: "Hang Seng", keywords: ["Hang Seng", "HSI"] },
        { id: "kospi", label: "KOSPI", keywords: ["KOSPI", "Korea"] },
      ],
      "commodities": [
        { id: "all", label: "All" },
        { id: "silver", label: "Silver", keywords: ["silver", "XAG"] },
        { id: "gold", label: "Gold", keywords: ["gold", "XAU"] },
        { id: "oil", label: "Oil", keywords: ["oil", "crude", "WTI", "brent"] },
        { id: "uranium", label: "Uranium", keywords: ["uranium"] },
      ],
      "forex": [
        { id: "all", label: "All" },
        { id: "up-or-down", label: "Up/Down", keywords: ["up", "down"] },
        { id: "eurusd", label: "EUR/USD", keywords: ["EUR/USD", "EURUSD", "euro"] },
        { id: "usdjpy", label: "USD/JPY", keywords: ["USD/JPY", "USDJPY", "yen"] },
        { id: "usdkrw", label: "USD/KRW", keywords: ["USD/KRW", "USDKRW", "won"] },
        { id: "gbpusd", label: "GBP/USD", keywords: ["GBP/USD", "GBPUSD", "pound"] },
        { id: "usdcad", label: "USD/CAD", keywords: ["USD/CAD", "USDCAD", "loonie"] },
      ],
      // These categories have no horizontal tabs
      "earnings": null,
      "collectibles": null,
      "acquisitions": null,
      "earnings-calls": null,
      "ipo": null,
      "fed-rates": null,
      "prediction-markets": null,
      "treasuries": null,
    } as Record<string, Array<{ id: string; label: string; keywords?: string[] }> | string | null>,
  };

  // Finance filter states — persist primary filter in localStorage
  // initialSubcategory from URL takes priority, then hydrate from localStorage
  const [financePrimaryFilter, setFinancePrimaryFilterState] = useState<string>(
    (category === "finance" && initialSubcategory) ? initialSubcategory : "all"
  );
  useEffect(() => {
    // URL subcategory takes priority over localStorage
    if (category === "finance" && initialSubcategory) return;
    try {
      const saved = localStorage.getItem('frenflow-finance-filter');
      if (saved) setFinancePrimaryFilterState(saved);
    } catch {}
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const setFinancePrimaryFilter = (filter: string) => {
    setFinancePrimaryFilterState(filter);
    try { localStorage.setItem('frenflow-finance-filter', filter); } catch {}
  };
  const [financeSecondaryFilter, setFinanceSecondaryFilter] = useState<string>("all");

  // Get current finance horizontal tabs based on sidebar selection
  const currentFinanceTabs = useMemo(() => {
    const tabsConfig = financeSubcategories.tabs[financePrimaryFilter];
    if (tabsConfig === null) return []; // No tabs for this category
    if (typeof tabsConfig === 'string') {
      // Reference to another category's tabs
      return financeSubcategories.tabs[tabsConfig] as Array<{ id: string; label: string; keywords?: string[] }>;
    }
    return tabsConfig || [];
  }, [financePrimaryFilter]);

  // Finance counts for sidebar (fetched from backend)
  // Initialize empty to match SSR, then hydrate from localStorage in useEffect
  const [financeCounts, setFinanceCounts] = useState<Record<string, number>>({});
  useEffect(() => {
    try {
      const saved = localStorage.getItem('financeCounts');
      if (saved) setFinanceCounts(JSON.parse(saved));
    } catch {}
  }, []);

  // Fetch finance counts when finance category is active
  useEffect(() => {
    if (category !== "finance") return;

    const fetchFinanceCounts = async () => {
      try {
        const res = await authFetch('/api/markets/finance/counts');
        if (res.ok) {
          const data = await res.json();
          if (data.success && data.counts) {
            setFinanceCounts(data.counts);
            localStorage.setItem('financeCounts', JSON.stringify(data.counts));
          }
        }
      } catch {
        // Silent fail - counts are optional
      }
    };

    fetchFinanceCounts();
  }, [category]);

  // Scraped finance sidebar structure (auto-discovered from PM every 8h)
  type ScrapedFinanceSidebar = {
    sidebar: Array<{ slug: string; label: string; count: number; href: string; icon: string | null; headerTabs: Array<{ slug: string; label: string }>; tagIds: string[] }>;
    defaultTabs: Array<{ slug: string; label: string }>;
    tabsByCategory: Record<string, Array<{ slug: string; label: string }>>;
    keywordMapping: Record<string, string[]>;
    assets: Array<{ id: string; slug: string; icon: string; filter: string }>;
    tagIdsByCategory: Record<string, string[]>;
  };
  const { data: scrapedFinance = null } = useFinanceSidebar(category === 'finance') as { data: ScrapedFinanceSidebar | null };

  // Computed finance sidebar: prefer scraper (dynamic), fallback to hardcoded
  const financeSidebarItems = useMemo((): Array<{ id: string; label: string; slug: string }> => {
    if (!scrapedFinance?.sidebar || scrapedFinance.sidebar.length === 0) {
      return financeSubcategories.sidebar;
    }
    // Map scraped sidebar to match hardcoded shape { id, label, slug }
    // Prepend "All" if scraper doesn't include it
    const items = scrapedFinance.sidebar.map(item => ({
      id: item.slug || 'all',
      label: item.label,
      slug: item.slug || '',
    }));
    if (!items.some(i => i.id === 'all')) {
      items.unshift({ id: 'all', label: 'All', slug: '' });
    }
    return items;
  }, [scrapedFinance]);

  // Computed finance tabs: prefer scraper, fallback to hardcoded
  // Respect hardcoded null (no pills) even if scraper returns tabs
  const financeSecondaryTabs = useMemo(() => {
    // If hardcoded config explicitly says null → no pills for this category
    const hardcoded = financeSubcategories.tabs[financePrimaryFilter];
    if (hardcoded === null) return null;

    if (!scrapedFinance?.tabsByCategory) return null;
    const tabs = scrapedFinance.tabsByCategory[financePrimaryFilter]
      || Object.entries(scrapedFinance.tabsByCategory).find(([k]) => k.toLowerCase() === financePrimaryFilter.toLowerCase())?.[1];
    if (!tabs || tabs.length === 0) return null;
    return [{ slug: 'all', label: 'All' }, ...tabs];
  }, [scrapedFinance, financePrimaryFilter]);

  const [advancedFilters, setAdvancedFiltersState] = useState<AdvancedFiltersState>(defaultFilters);

  // Restore the user's last-applied advanced filter set from localStorage
  // so it survives navigation between categories. We can't use URL params
  // here because we want the filter to follow the user across redirects
  // to /markets/trending (the canonical "all markets" view we redirect
  // to when filters get applied from a narrow category like /sports).
  useEffect(() => {
    try {
      const raw = localStorage.getItem("frenflow_active_advanced_filters");
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === "object") {
        setAdvancedFiltersState({ ...defaultFilters, ...parsed });
      }
    } catch {
      /* ignore corrupt JSON */
    }
  }, []);

  // Wrapped setter:
  //   1. Persists to localStorage so the filters survive navigation.
  //   2. When filters become active and the user is currently inside a
  //      narrow category (anything other than the broad ones), bounces
  //      them to /markets/trending so the filter actually filters the
  //      global market pool — otherwise a "Crypto" category include from
  //      /markets/sports would never match (the page only fetches
  //      sports markets to begin with).
  const setAdvancedFilters = useCallback(
    (next: AdvancedFiltersState) => {
      setAdvancedFiltersState(next);
      try {
        if (countActiveFilters(next) === 0) {
          localStorage.removeItem("frenflow_active_advanced_filters");
        } else {
          localStorage.setItem(
            "frenflow_active_advanced_filters",
            JSON.stringify(next)
          );
        }
      } catch {
        /* storage may be disabled */
      }

      // Broad categories already pull a wide market pool — leave the
      // user where they are. For everything else, redirect so filters
      // act globally.
      const broad = new Set([
        "trending",
        "for-you",
        "breaking",
        "bookmarks",
        "new",
      ]);
      if (countActiveFilters(next) > 0 && !broad.has(category || "")) {
        setCategory("trending");
        try {
          window.history.pushState(
            {},
            "",
            sourceUrl(`/markets/trending`, source)
          );
        } catch {
          /* history API may not be available in some shells */
        }
      }
    },
    [category, source]
  );
  const [breakingSubcategory, setBreakingSubcategory] = useState<string>(
    (category === "breaking" && initialSubcategory) ? initialSubcategory : "all"
  );

  // Trending subcategories now come from backend (scraped from Polymarket's __NEXT_DATA__)
  // Uses the same currentSubcategories system as other categories

  // New/Trending subcategories consolidated into the dynamic subcategory bar (same DOM position for all categories)

  // Breaking subcategories
  const breakingSubcategories = [
    { id: "all", label: t("breaking.subcat.all") },
    { id: "politics", label: t("breaking.subcat.politics") },
    { id: "world", label: t("breaking.subcat.world") },
    { id: "sports", label: t("breaking.subcat.sports") },
    { id: "crypto", label: t("breaking.subcat.crypto") },
    { id: "finance", label: t("breaking.subcat.finance") },
    { id: "tech", label: t("breaking.subcat.tech") },
    { id: "culture", label: t("breaking.subcat.culture") },
  ];

  // Elections subcategories (Polymarket dashboard style)
  // Each has specific tag_slug and order matching Polymarket's sidebar
  const electionsSubcategories = [
    { id: "elections", label: "Elections", tag: "world-elections", order: "startTime", ascending: true, Icon: Vote },
    { id: "macro", label: "Macro", tag: "macro-single", order: "volume", ascending: false, Icon: BarChart3 },
    { id: "fed-rates", label: "Fed Rates", tag: "fed-rates", order: "startTime", ascending: true, Icon: Landmark },
    { id: "trump", label: "Trump", tag: "trump-presidency", order: "volume", ascending: false, Icon: null as any, iconSrc: "https://images.frenflow.com/static/web/icons/trump.png" },
  ];

  // State for elections filter — initialize from URL if direct-access (e.g. /markets/macro)
  const [electionsFilter, setElectionsFilter] = useState<string>(
    isElectionsSubcategory(initialCategory || "") ? initialCategory! : "elections"
  );

  // Unified URL sync — ALL categories use /markets/{category}/{subcategory} pattern
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const basePath = sourceUrl(`/markets/${category}`, source);
    const currentPath = window.location.pathname;

    // Determine active subcategory slug based on category type
    let subSlug: string | null = null;

    if (category === "elon") {
      subSlug = elonSubcategory !== "all" ? elonSubcategory : null;
    } else if (category === "crypto" || category === "commodities") {
      // Commodities reuses the crypto sidebar state (cryptoPrimaryFilter)
      // since both share the same Kalshi-sidebar rendering pipeline.
      subSlug = cryptoPrimaryFilter !== "all" ? cryptoPrimaryFilter : null;
    } else if (category === "finance") {
      subSlug = financePrimaryFilter !== "all" ? financePrimaryFilter : null;
    } else if (category === "breaking") {
      subSlug = breakingSubcategory !== "all" ? breakingSubcategory : null;
    } else {
      // Tag-based categories (politics, geopolitics, trending, new, etc.)
      subSlug = activeSubcategory;
    }

    if (subSlug) {
      const targetPath = sourceUrl(`/markets/${category}/${subSlug}`, source);
      if (currentPath !== targetPath) {
        window.history.replaceState(null, '', targetPath);
      }
    } else {
      // No subcategory — go back to category URL
      if (currentPath !== basePath && currentPath.startsWith(basePath + '/')) {
        window.history.replaceState(null, '', basePath);
      }
    }
  }, [category, activeSubcategory, elonSubcategory, cryptoPrimaryFilter, financePrimaryFilter, breakingSubcategory, source]);

  // Helper to update source filter and URL together
  const updateSourceFilter = (newSource: "polymarket" | "kalshi" | "predict" | "hyperliquid") => {
    // Persist + navigate immediately (URL/storage are external-state side
    // effects, not React state — they should not be deferred).
    try { localStorage.setItem("frenflow_source", newSource); } catch { /* ignore */ }
    const currentPath = window.location.pathname;
    const stripped = currentPath.replace(/^\/(polymarket|kalshi|predictfun)\//, '/');
    const newPath = sourceUrl(stripped, newSource);
    router.push(newPath, { scroll: false });
    // Defer the heavy grid swap (Polymarket → Kalshi → Predict swaps the
    // entire markets dataset and remounts every card with a different
    // realtime-prices hook) so the source-picker click stays snappy.
    startTransition(() => {
      setSourceFilter(newSource);
    });
  };

  // Navigate to previous/next category or subcategory (for mobile swipe)
  // Smart behavior: if category has subcategories, swipe navigates within them first
  const navigateCategory = useCallback((direction: 'prev' | 'next') => {
    // Check if current category has subcategories and handle them first
    let handledBySubcategory = false;

    // Crypto - navigate primary filters (timeframes + coins)
    if (category === 'crypto') {
      // Combine timeframes and coins into one navigation list
      const allPrimaryFilters = [
        ...cryptoSidebarTimeframes.map(t => t.id),
        ...cryptoSidebarCoins.map(c => c.id),
      ];
      const currentIdx = allPrimaryFilters.findIndex(id => id === cryptoPrimaryFilter);
      if (currentIdx !== -1) {
        if (direction === 'prev' && currentIdx > 0) {
          setCryptoPrimaryFilter(allPrimaryFilters[currentIdx - 1]);
          setCryptoSecondaryFilter("all"); // Reset secondary
          handledBySubcategory = true;
        } else if (direction === 'next' && currentIdx < allPrimaryFilters.length - 1) {
          setCryptoPrimaryFilter(allPrimaryFilters[currentIdx + 1]);
          setCryptoSecondaryFilter("all"); // Reset secondary
          handledBySubcategory = true;
        }
      }
    }

    // Breaking - navigate subcategories
    if (category === 'breaking') {
      const currentIdx = breakingSubcategories.findIndex(s => s.id === breakingSubcategory);
      if (currentIdx !== -1) {
        if (direction === 'prev' && currentIdx > 0) {
          setBreakingSubcategory(breakingSubcategories[currentIdx - 1].id);
          handledBySubcategory = true;
        } else if (direction === 'next' && currentIdx < breakingSubcategories.length - 1) {
          setBreakingSubcategory(breakingSubcategories[currentIdx + 1].id);
          handledBySubcategory = true;
        }
      }
    }

    // Elections - navigate subcategories
    if (category === 'elections') {
      const currentIdx = electionsSubcategories.findIndex(s => s.id === electionsFilter);
      if (currentIdx !== -1) {
        if (direction === 'prev' && currentIdx > 0) {
          setElectionsFilter(electionsSubcategories[currentIdx - 1].id);
          handledBySubcategory = true;
        } else if (direction === 'next' && currentIdx < electionsSubcategories.length - 1) {
          setElectionsFilter(electionsSubcategories[currentIdx + 1].id);
          handledBySubcategory = true;
        }
      }
    }

    // Dynamic subcategories from taxonomy (for categories like politics, sports, tech, culture, etc.)
    if (!handledBySubcategory && currentSubcategories.length > 0) {
      const currentIdx = activeSubcategory
        ? currentSubcategories.findIndex(s => s.id === activeSubcategory)
        : -1;

      if (direction === 'next') {
        // If no subcategory selected, select first one
        if (currentIdx === -1) {
          setActiveSubcategory(currentSubcategories[0].id);
          handledBySubcategory = true;
        } else if (currentIdx < currentSubcategories.length - 1) {
          setActiveSubcategory(currentSubcategories[currentIdx + 1].id);
          handledBySubcategory = true;
        }
        // At last subcategory - let it fall through to main category navigation
      } else if (direction === 'prev') {
        if (currentIdx > 0) {
          setActiveSubcategory(currentSubcategories[currentIdx - 1].id);
          handledBySubcategory = true;
        } else if (currentIdx === 0) {
          // At first subcategory, go back to "all" (no filter)
          setActiveSubcategory(null);
          handledBySubcategory = true;
        }
        // No subcategory selected - let it fall through to main category navigation
      }
    }

    // If subcategory handled it, animate and return
    if (handledBySubcategory) {
      setSlideDirection(direction === 'prev' ? 'right' : 'left');
      setTimeout(() => setSlideDirection(null), 500);
      return;
    }

    // No subcategories or at edge - navigate main categories
    const currentIndex = categories.findIndex(c => c.id === category);
    if (currentIndex === -1) return;

    let newIndex: number;
    if (direction === 'prev') {
      newIndex = currentIndex <= 0 ? categories.length - 1 : currentIndex - 1;
    } else {
      newIndex = currentIndex >= categories.length - 1 ? 0 : currentIndex + 1;
    }

    // Set slide direction for animation (opposite of swipe direction for visual feedback)
    // Swiping right (prev) = content slides in from left
    // Swiping left (next) = content slides in from right
    setSlideDirection(direction === 'prev' ? 'right' : 'left');

    const newCategory = categories[newIndex];
    window.history.pushState({}, '', sourceUrl(`/markets/${newCategory.id}`, source));
    // Same rationale as the tab click handler above — defer the heavy
    // grid remount so the swipe/keyboard interaction stays snappy.
    startTransition(() => {
      setCategory(newCategory.id);
      // Reset subcategory filters when changing main category
      setCryptoPrimaryFilter("all");
      setCryptoSecondaryFilter("all");
      setElectionsFilter("elections");
      setBreakingSubcategory("all");
      setActiveSubcategory(null);
    });

    // Clear slide direction after animation completes
    setTimeout(() => setSlideDirection(null), 500);
  }, [categories, category, effectiveSourceFilter, source, setCategory, cryptoPrimaryFilter, breakingSubcategory, electionsFilter, currentSubcategories, activeSubcategory]);

  // ==========================================================================
  // CATEGORY CACHE HELPERS - For instant tab switching
  // Defined after filter state declarations to avoid hoisting issues
  // ==========================================================================

  // Helper to get cache key including filters.
  // Locale is part of every key so flipping EN ↔ ES invalidates the
  // cached payload — otherwise the client would serve the EN markets
  // it had in `markets-cache` instead of refetching with `?locale=es`.
  const getCacheKey = useCallback((cat: string) => {
    // Predict.fun has its own cache namespace
    if (sourceFilter === 'predict') {
      return `predict-${cat}-${sortBy}-${locale}`;
    }
    // Include Kalshi sort/reverse/subcategory in cache key so changing any invalidates cache
    // IMPORTANT: This must be checked BEFORE trending/new subcategory check to avoid
    // using a cache key that doesn't include kalshiSortBy
    if (sourceFilter === 'kalshi') {
      // Commodities reuses the crypto sidebar state (cryptoPrimaryFilter)
      // to drive frequency / parent-tag / nested-scope filters. Without
      // including it in the cache key here, clicking a sidebar row kept
      // the previous page's result set intact — the fetch effect re-ran
      // but hit a cache HIT on the old key and bailed before the network
      // request ever fired.
      const usesCryptoSidebar = cat === 'crypto' || cat === 'commodities';
      const cryptoKey = usesCryptoSidebar ? `-${cryptoPrimaryFilter || 'all'}` : '';
      return `${cat}-${kalshiSortBy}-${kalshiReverse}-${activeKalshiSubcategory || 'all'}-${activeKalshiCompetition || 'all'}-${sourceFilter}${cryptoKey}-${locale}`;
    }
    // New and Trending use activeSubcategory from backend (Polymarket only)
    if ((cat === 'trending' || cat === 'new') && activeSubcategory) {
      return `${cat}-${activeSubcategory}-${sourceFilter}-${locale}`;
    }
    if (cat === 'crypto') {
      return `${cat}-${cryptoPrimaryFilter}-${cryptoSecondaryFilter}-${sourceFilter}-${locale}`;
    }
    if (cat === 'finance') {
      return `${cat}-${financePrimaryFilter}-${financeSecondaryFilter}-${sourceFilter}-${locale}`;
    }
    // Include activeSubcategory and weather date so each combination has its own cache entry
    const subKey = activeSubcategory || 'all';
    const dateKey = activeWeatherDate || 'all';
    const tempKey = activeWeatherTempType || 'all';
    return `${cat}-${filter}-${sortBy}-${sourceFilter}-${subKey}-${dateKey}-${tempKey}-${locale}`;
  }, [activeSubcategory, activeWeatherDate, activeWeatherTempType, cryptoPrimaryFilter, cryptoSecondaryFilter, financePrimaryFilter, financeSecondaryFilter, filter, sortBy, sourceFilter, kalshiSortBy, kalshiReverse, activeKalshiSubcategory, activeKalshiCompetition, locale]);


  // Helper to prefetch next batch in background
  const prefetchNextBatch = useCallback(async (cat: string, currentOffset: number) => {
    const cacheKey = getCacheKey(cat);
    const prefetchKey = `${cacheKey}-${currentOffset}`;

    // Don't prefetch if already in progress
    if (isPrefetchInProgress(prefetchKey)) return;

    markPrefetchStart(prefetchKey);

    try {
      let url = '';
      const nextOffset = currentOffset;

      if (cat === 'trending') {
        if (activeSubcategory) {
          url = `/api/markets/trending/by-tag?tag=${activeSubcategory}&limit=20&offset=${nextOffset}&active=${filter === "active" ? "true" : filter === "all" ? "false" : "true"}&closed=${filter === "closed" ? "true" : "false"}`;
        } else {
          url = `/api/markets/trending?limit=20&offset=${nextOffset}&active=${filter === "active" ? "true" : "false"}&closed=${filter === "closed" ? "true" : "false"}`;
        }
      } else if (cat === 'new') {
        if (activeSubcategory) {
          // Use backend proxy for filtered new markets (caches Gamma API responses)
          url = `/api/markets/events?tag_slug=${activeSubcategory}&order=startDate&ascending=false&limit=20&offset=${nextOffset}&view=${viewMode}`;
        } else {
          url = `/api/markets/new?limit=20&offset=${nextOffset}`;
        }
      } else {
        // For other categories, skip prefetch (complex logic)
        return;
      }

      const response = await authFetch(url);
      if (response.ok) {
        const data = await response.json();
        const newEvents = Array.isArray(data.markets) ? data.markets : [];

        // Update cache with prefetched data (append to existing)
        const cached = getCachedCategory(cacheKey);
        if (cached && newEvents.length > 0) {
          setCategoryCache(cacheKey, {
            events: [...cached.events, ...newEvents],
            offset: nextOffset + newEvents.length,
            hasMore: data.hasMore,
            timestamp: cached.timestamp, // Keep original timestamp
          });
        }
      }
    } catch {
      // Silently fail prefetch
    } finally {
      markPrefetchEnd(prefetchKey);
    }
  }, [getCacheKey, activeSubcategory]);

  // Mobile swipe to change categories — DISABLED
  // Was causing accidental category changes when scrolling horizontally
  const handleTouchStart = useCallback((_e: React.TouchEvent) => {}, []);
  const handleTouchEnd = useCallback((_e: React.TouchEvent) => {}, []);

  useEffect(() => {
    setMounted(true);
  }, []);

  // Global search - fetches from all markets regardless of current category filter
  // Uses debounce to avoid API spam
  useEffect(() => {
    // Clear previous timeout
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }

    // If query is empty or too short, clear results and restore original data
    if (!searchQuery || searchQuery.length < 2) {
      setGlobalSearchResults([]);
      setSearchLoading(false);
      // Restore Kalshi/Predict markets from cache when search is cleared
      if ((effectiveSourceFilter === 'kalshi' || effectiveSourceFilter === 'predict') && kalshiSearchActiveRef.current) {
        kalshiSearchActiveRef.current = false;
        const cacheKey = getCacheKey(category);
        const cached = getCachedCategory(cacheKey);
        if (cached) {
          setAggregatedMarkets(cached.events as any[]);
        } else {
          fetchAggregatedMarkets(effectiveSourceFilter, effectiveSourceFilter === 'kalshi' ? kalshiSortBy : sortBy, false);
        }
      }
      return;
    }

    setSearchLoading(true);

    // Debounce the search (300ms delay)
    searchDebounceRef.current = setTimeout(async () => {
      try {
        // Kalshi source: search Kalshi markets and inject into aggregatedMarkets
        if (effectiveSourceFilter === 'kalshi') {
          const response = await authFetch(
            `/api/markets/kalshi/text-search?q=${encodeURIComponent(searchQuery)}&limit=20`
          );
          if (!response.ok) {
            setSearchLoading(false);
            return;
          }
          const data = await response.json();
          const kalshiResults = data.results || [];
          kalshiSearchActiveRef.current = true;
          // Replace aggregatedMarkets with search results
          setAggregatedMarkets(kalshiResults.map((m: any) => ({
            ...m,
            source: 'kalshi' as const,
          })));
          setSearchLoading(false);
          return;
        }

        // Predict source: search Predict.fun markets and inject into aggregatedMarkets
        if (effectiveSourceFilter === 'predict') {
          const response = await authFetch(
            `/api/markets/predict/search?q=${encodeURIComponent(searchQuery)}&limit=20`
          );
          if (!response.ok) {
            setSearchLoading(false);
            return;
          }
          const data = await response.json();
          const predictResults = data.markets || [];
          kalshiSearchActiveRef.current = true;
          // Replace aggregatedMarkets with search results
          setAggregatedMarkets(predictResults.map((m: any) => ({
            ...m,
            source: 'predict' as const,
            volume24h: m.volume24h ?? m.volume24hr ?? 0,
          })));
          setSearchLoading(false);
          return;
        }

        // Polymarket source: use polymarket search
        const response = await authFetch(
          `/api/polymarket/search?q=${encodeURIComponent(searchQuery)}&limit=30`
        );

        if (!response.ok) {
          setGlobalSearchResults([]);
          setSearchLoading(false);
          return;
        }

        const data = await response.json();
        const markets = data.results || [];

        // Transform flat markets into Event format
        // Each market becomes its own Event (since search is per-market)
        const transformedEvents: Event[] = markets.map((market: any) => ({
          id: market.id || market.slug,
          slug: market.slug,
          title: market.title || market.question || "",
          description: market.description || null,
          image: market.image || null,
          icon: market.icon || null,
          endDate: market.endDate || null,
          createdAt: market.createdAt,
          closed: market.closed || false,
          active: market.active !== false,
          volume: market.volume || 0,
          volume24hr: market.volume24hr || 0,
          liquidity: market.liquidity || 0,
          tags: market.tags || [],
          source: 'polymarket' as MarketSource,
          markets: [{
            id: market.id,
            question: market.title || market.question || "",
            slug: market.slug,
            conditionId: market.conditionId || "",
            outcomes: market.outcomes || ["Yes", "No"],
            outcomePrices: Array.isArray(market.outcomePrices)
              ? market.outcomePrices.map((p: any) => typeof p === 'string' ? parseFloat(p) : p)
              : [0.5, 0.5],
            clobTokenIds: market.clobTokenIds || [],
            acceptingOrders: true,
            active: market.active !== false,
            closed: market.closed || false,
            volume: market.volume || 0,
            liquidity: market.liquidity || 0,
          }],
        }));

        setGlobalSearchResults(transformedEvents);
      } catch (err) {
        setGlobalSearchResults([]);
      } finally {
        setSearchLoading(false);
      }
    }, 300);

    // Cleanup on unmount
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, [searchQuery]);

  // Sync URL source param with state (needed because useState only reads initial value once)
  // Falls back to localStorage saved source when no URL param present
  useEffect(() => {
    const urlSource = searchParams.get("source") || searchParams.get("_source");
    const normalizedSource = urlSource;
    if (normalizedSource && ["polymarket", "kalshi", "predict"].includes(normalizedSource)) {
      setSourceFilter(normalizedSource as "polymarket" | "kalshi" | "predict" | "hyperliquid");
    } else if (!urlSource) {
      const saved = localStorage.getItem("frenflow_source");
      if (saved && ["polymarket", "kalshi", "predict"].includes(saved)) {
        setSourceFilter(saved as "polymarket" | "kalshi" | "predict" | "hyperliquid");
      }
    }
  }, [searchParams]);

  const LIMIT = 20;

  // Force re-fetch when source changes — show loading instead of empty state
  const prevSourceRef = useRef(effectiveSourceFilter);
  const sourceChangeHandledRef = useRef(false);
  const recentPolymarketFetchRef = useRef<string | null>(null);
  const RECENT_POLYMARKET_FETCH_WINDOW_MS = 5000;

  const buildPolymarketFetchKey = useCallback(() => {
    return [
      category,
      sortBy,
      filter,
      effectiveSourceFilter,
      activeSubcategory || 'all',
      activeWeatherDate || 'all',
      activeWeatherTempType || 'all',
      cryptoPrimaryFilter || 'all',
      cryptoSecondaryFilter || 'all',
      financePrimaryFilter || 'all',
      financeSecondaryFilter || 'all',
      electionsFilter || 'all',
      breakingSubcategory || 'all',
      // Locale must be part of the recent-fetch key — without it the
      // 5 s "skip recent fetch" guard treats EN and ES as the same
      // request and silently blocks the refetch when the user switches
      // language. Result: market titles stay in the previous language
      // until something else triggers an effect re-run.
      locale,
    ].join('|');
  }, [
    activeSubcategory,
    activeWeatherDate,
    activeWeatherTempType,
    breakingSubcategory,
    category,
    cryptoPrimaryFilter,
    cryptoSecondaryFilter,
    effectiveSourceFilter,
    electionsFilter,
    filter,
    financePrimaryFilter,
    financeSecondaryFilter,
    sortBy,
    locale,
  ]);

  const markRecentPolymarketFetch = useCallback(() => {
    recentPolymarketFetchRef.current = `${buildPolymarketFetchKey()}::${Date.now()}`;
  }, [buildPolymarketFetchKey]);

  const shouldSkipRecentPolymarketFetch = useCallback(() => {
    if (!recentPolymarketFetchRef.current) return false;
    const separatorIndex = recentPolymarketFetchRef.current.lastIndexOf('::');
    if (separatorIndex === -1) return false;

    const recentKey = recentPolymarketFetchRef.current.slice(0, separatorIndex);
    const recentTime = recentPolymarketFetchRef.current.slice(separatorIndex + 2);

    if (recentKey !== buildPolymarketFetchKey()) return false;
    return Date.now() - parseInt(recentTime, 10) < RECENT_POLYMARKET_FETCH_WINDOW_MS;
  }, [buildPolymarketFetchKey, RECENT_POLYMARKET_FETCH_WINDOW_MS]);

  // Listen for immediate source change events from SourceSelector
  useEffect(() => {
    const handler = (e: globalThis.Event) => {
      const newSource = (e as unknown as CustomEvent).detail?.source as typeof effectiveSourceFilter;
      if (!newSource || newSource === prevSourceRef.current) return;
      prevSourceRef.current = newSource;
      sourceChangeHandledRef.current = true;
      setSourceFilter(newSource);
      if (newSource === 'polymarket') {
        // Clear stale PM events too — otherwise switching Kalshi→PM
        // flashes the previous PM session's events (often already
        // resolved by now) for ~300-2000ms until fetchEvents lands.
        setEvents([]);
        setAggregatedMarkets([]);
        setLoading(true);
        setLoadingAggregated(false);
        setHasFetchedAggregated(false);
        markRecentPolymarketFetch();
        fetchEvents(true);
      } else {
        setEvents([]);
        setLoadingAggregated(true);
        setHasFetchedAggregated(false);
        fetchAggregatedMarkets(newSource, newSource === 'kalshi' ? kalshiSortBy : sortBy);
      }
    };
    window.addEventListener('frenflow:source-change', handler as EventListener);
    return () => window.removeEventListener('frenflow:source-change', handler as EventListener);
  }, [kalshiSortBy, markRecentPolymarketFetch, sortBy]);

  // Fallback: handle source changes from URL navigation (not selector click)
  useEffect(() => {
    if (prevSourceRef.current !== effectiveSourceFilter) {
      // Skip if the custom event handler already handled this change
      if (sourceChangeHandledRef.current) {
        sourceChangeHandledRef.current = false;
        prevSourceRef.current = effectiveSourceFilter;
        return;
      }
      prevSourceRef.current = effectiveSourceFilter;

      if (effectiveSourceFilter === 'polymarket') {
        setEvents([]);
        setAggregatedMarkets([]);
        setLoading(true);
        setLoadingAggregated(false);
        setHasFetchedAggregated(false);
        markRecentPolymarketFetch();
        fetchEvents(true);
      } else {
        setEvents([]);
        setLoadingAggregated(true);
        setHasFetchedAggregated(false);
        fetchAggregatedMarkets(effectiveSourceFilter, effectiveSourceFilter === 'kalshi' ? kalshiSortBy : sortBy);
      }
    }
  }, [effectiveSourceFilter, kalshiSortBy, markRecentPolymarketFetch, sortBy]);

  // Main data fetching effect - uses searchParams directly to determine source
  // This avoids race conditions between URL sync and fetch
  // Track previous auth state to skip refetch when only auth changes
  const prevAuthRef = useRef({ privyReady, authenticated });
  useEffect(() => {
    // Use the same source as effectiveSourceFilter (from useCurrentSource hook)
    const effectiveSource = effectiveSourceFilter;

    // Handle "For You" category specially — wait for Privy to confirm auth
    if (category === 'for-you') {
      if (!privyReady) return; // Don't fetch until auth state is confirmed
      if (!authenticated) return; // Redirect effect will handle this

      // Stale-while-revalidate: show cached data instantly, refresh in background
      const cached = getCachedForYou();
      if (cached) {
        setForYouFeedItems(cached.feedItems);
        setForYouEvents(cached.events);
        setFrensActivity(cached.frensActivity);
        setForYouHasMore(cached.hasMore);
        setLoadingForYou(false);
        // Background refresh after 1s
        const refreshTimeout = setTimeout(() => {
          fetchForYouFeed(false, sortBy, true);
        }, 1000);
        prevAuthRef.current = { privyReady, authenticated };
        return () => clearTimeout(refreshTimeout);
      }

      fetchForYouFeed(false, sortBy);
      prevAuthRef.current = { privyReady, authenticated };
      return;
    }

    // Skip refetch when only auth state changed (only for-you needs auth)
    const authChanged = prevAuthRef.current.privyReady !== privyReady || prevAuthRef.current.authenticated !== authenticated;
    prevAuthRef.current = { privyReady, authenticated };
    if (authChanged && events.length > 0) return;

    // For trending/breaking/new, only fetch Polymarket events (these are PM-specific orderings)
    const isPolymarketOnlyCategory = ['trending', 'breaking', 'new'].includes(category);

    // ==========================================================================
    // CACHE CHECK - Use cached data for instant tab switching
    // If cache exists and is valid, use it immediately without loading state
    // Then fetch fresh data in background
    // ==========================================================================
    const cacheKey = getCacheKey(category);
    const cached = getCachedCategory(cacheKey);
    const cacheValid = isCacheValid(cacheKey);

    if (cached && cacheValid && effectiveSource !== 'kalshi' && effectiveSource !== 'predict') {
      // Instant display from cache - no loading spinner (Polymarket only)
      setEvents(cached.events);
      setOffset(cached.offset);
      setHasMore(cached.hasMore);
      setLoading(false);

      // Weather-science: always do background refresh to populate dateTabs/subcategories
      // Other categories: only refresh if cache is older than 2 min
      const cacheAge = Date.now() - cached.timestamp;
      if (category === 'weather-science' || cacheAge > 2 * 60 * 1000) {
        const refreshTimeout = setTimeout(() => {
          if (!shouldSkipRecentPolymarketFetch()) {
            fetchEvents(true, true); // skipLoading = true
          }
        }, category === 'weather-science' ? 100 : 1000);
        return () => clearTimeout(refreshTimeout);
      }

      return;
    }

    if (effectiveSource === 'kalshi') {
      const cacheKey = getCacheKey(category);
      const cached = getCachedCategory(cacheKey);
      const hasFreshCache = cached && isCacheValid(cacheKey);
      // SSR-skip guard for Kalshi. Two passes:
      //   1) First run: SSR provided markets → consume them, lock the
      //      key, no fetch.
      //   2) Subsequent runs: if the cacheKey hasn't changed (only
      //      tangential deps moved — locale, auth-ready, viewMode),
      //      keep skipping. As soon as the user changes category/sort,
      //      cacheKey moves and we drop into the normal fetch path.
      if (isSSRNonPolymarket && aggregatedMarkets.length > 0 && !ssrConsumedRef.current) {
        ssrConsumedRef.current = true;
        kalshiSSRKeyRef.current = cacheKey;
        return;
      }
      if (kalshiSSRKeyRef.current === cacheKey) return;
      // Show loading instead of clearing data — prevents "No markets" flash on navigation
      if (!hasFreshCache) {
        setLoadingAggregated(true);
      }
      fetchAggregatedMarkets(effectiveSource, kalshiSortBy, !!hasFreshCache);
    } else if (effectiveSource === 'predict') {
      // Predict: card prices stream over WebSocket (usePredictRealtimePrice
      // below). The list-level refetch only catches new/closed markets +
      // volume drift, which change slowly enough to allow a 5-min cadence.
      // Previously this re-fetched the full list every 30s "for fresh
      // prices" — with WS subs that's redundant traffic against backend.
      const PREDICT_LIST_REFRESH_MS = 5 * 60_000;
      if (isSSRNonPolymarket && aggregatedMarkets.length > 0 && !ssrConsumedRef.current) {
        ssrConsumedRef.current = true;
        const refreshTimeout = setTimeout(() => {
          fetchAggregatedMarkets(effectiveSource, sortBy, true);
        }, 2000);
        const predictPollInterval = setInterval(() => {
          fetchAggregatedMarkets(effectiveSource, sortBy, true);
        }, PREDICT_LIST_REFRESH_MS);
        return () => { clearTimeout(refreshTimeout); clearInterval(predictPollInterval); };
      }
      fetchAggregatedMarkets(effectiveSource, sortBy);
      const predictPollInterval = setInterval(() => {
        fetchAggregatedMarkets(effectiveSource, sortBy, true);
      }, PREDICT_LIST_REFRESH_MS);
      return () => clearInterval(predictPollInterval);
    } else if (effectiveSource === 'hyperliquid') {
      // Hyperliquid: same migration as Predict — useHyperliquidRealtimePrice
      // streams card prices over WS, so the list refetch only needs to
      // catch new/closed markets + volume drift (5-min cadence).
      const HL_LIST_REFRESH_MS = 5 * 60_000;
      if (isSSRNonPolymarket && aggregatedMarkets.length > 0 && !ssrConsumedRef.current) {
        ssrConsumedRef.current = true;
        const refreshTimeout = setTimeout(() => {
          fetchAggregatedMarkets(effectiveSource, sortBy, true);
        }, 2000);
        const hlPollInterval = setInterval(() => {
          fetchAggregatedMarkets(effectiveSource, sortBy, true);
        }, HL_LIST_REFRESH_MS);
        return () => { clearTimeout(refreshTimeout); clearInterval(hlPollInterval); };
      }
      fetchAggregatedMarkets(effectiveSource, sortBy);
      const hlPollInterval = setInterval(() => {
        fetchAggregatedMarkets(effectiveSource, sortBy, true);
      }, HL_LIST_REFRESH_MS);
      return () => clearInterval(hlPollInterval);
    } else {
      // Polymarket source OR polymarket-only categories (trending/breaking/new)
      // If we have SSR data on first mount, skip the immediate background refresh.
      // SSR data is fresh enough — React Query staleTime handles revalidation.
      if (hasSSRMarkets && !ssrConsumedRef.current) {
        ssrConsumedRef.current = true;
        return; // Trust SSR data, don't re-fetch immediately
      } else if (shouldSkipRecentPolymarketFetch()) {
        return;
      } else {
        // Only skip loading if we have a valid cache for THIS exact filter combination.
        // Without cache, visible events are from a DIFFERENT filter (e.g. Macro → Elections)
        // and showing them while loading new data causes a confusing flash.
        fetchEvents(true, !!(cached && cacheValid));
      }
    }
  }, [filter, sortBy, sourceFilter, effectiveSourceFilter, cryptoPrimaryFilter, cryptoSecondaryFilter, financePrimaryFilter, financeSecondaryFilter, category, searchParams, electionsFilter, breakingSubcategory, activeSubcategory, activeWeatherDate, activeWeatherTempType, kalshiSortBy, kalshiReverse, activeKalshiSubcategory, activeKalshiCompetition, privyReady, authenticated, shouldSkipRecentPolymarketFetch, locale, viewMode]); // eslint-disable-line react-hooks/exhaustive-deps

  // followingActivity is now fetched via useFollowingActivity hook (TanStack Query)

  // Prefetch a single category's events into the local cache. Used by
  // both the idle pre-warm (top-3 always-popular cats below) and by the
  // tab `onMouseEnter` handler to fetch on intent. No-op if the cache
  // is already valid or another prefetch is in flight for the same key.
  const prefetchCategoryEvents = useCallback(async (cat: string) => {
    if (effectiveSourceFilter !== 'polymarket') return;
    if (cat === category) return;
    // Use `getCacheKey(cat)` not `buildDefaultCacheKey(cat)` — the lookup in
    // fetchEvents reads from `getCacheKey(category)` which includes filter,
    // sortBy, activeSubcategory, weather filters, etc. If the user changed
    // sortBy or set a subcategory before hovering an adjacent tab, the
    // prefetch landed on a dead key and the cache hit never materialised.
    // Same pattern as the May 2026 cache-key drift bug.
    const cacheKey = getCacheKey(cat);
    if (isCacheValid(cacheKey)) return;
    if (isPrefetchInProgress(cacheKey)) return;
    markPrefetchStart(cacheKey);
    try {
      let url = '';
      if (cat === 'trending') url = `/api/markets/trending?limit=20&offset=0`;
      else if (cat === 'new') url = `/api/markets/new?limit=20&offset=0`;
      else if (cat === 'breaking') url = `/api/markets/breaking?limit=30&category=all`;
      else {
        const tagMap: Record<string, string> = {
          politics: 'politics',
          elections: 'world-elections',
          geopolitics: 'geopolitics',
          culture: 'pop-culture',
          world: 'world',
          economy: 'economy',
          tech: 'tech',
          'climate-science': 'climate-science',
          crypto: 'crypto',
          sports: 'sports',
          'world-cup': 'fifa-world-cup',
        };
        const tag = tagMap[cat] || cat;
        url = `/api/polymarket/markets?limit=20&offset=0&tag_slug=${tag}&active=true&closed=false&order=volume24hr&ascending=false`;
      }
      const res = await authFetch(url);
      if (res.ok) {
        const data = await res.json();
        const rawEvents = data.events || data.markets || [];
        const events = rawEvents.map(parseEventMarkets);
        setCategoryCache(cacheKey, {
          events,
          offset: events.length,
          hasMore: data.hasMore ?? events.length >= 20,
          timestamp: Date.now(),
        });
      }
    } catch {
      // Silent fail — prefetch is best-effort.
    } finally {
      markPrefetchEnd(cacheKey);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category, effectiveSourceFilter, buildDefaultCacheKey]);

  // Idle pre-warm of the top-3 always-popular categories (trending /
  // breaking / new) so a user's first lateral click is instant. The
  // remaining categories (politics, sports, crypto, culture, ...) now
  // pre-warm on tab `onMouseEnter` instead of all 6 going to network at
  // page load. Saves ~3 × 80 KB = ~240 KB of bandwidth on cold visits
  // for users who never click another category. Hover triggers ~200-
  // 500 ms before click, which is enough time to cover the typical
  // backend round-trip.
  //
  // Skip entirely if allCategories was provided (SSR already pre-loaded
  // all categories — see allCategoriesPopulated effect).
  useEffect(() => {
    if (allCategories) return;
    if (effectiveSourceFilter !== 'polymarket') return;

    const TOP_3 = ['trending', 'breaking', 'new'].filter(c => c !== category);
    if (TOP_3.length === 0) return;

    type IdleHandle = number;
    let idleHandle: IdleHandle | null = null;
    let timeoutHandle: ReturnType<typeof setTimeout> | null = null;
    const cancelled = { current: false };

    const run = async () => {
      await Promise.all(TOP_3.map(c => {
        if (cancelled.current) return Promise.resolve();
        return prefetchCategoryEvents(c);
      }));
    };

    if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
      idleHandle = (window as Window & { requestIdleCallback: (cb: IdleRequestCallback, opts?: IdleRequestOptions) => number }).requestIdleCallback(
        () => { void run(); },
        { timeout: 4000 },
      );
    } else {
      timeoutHandle = setTimeout(() => { void run(); }, 2000);
    }

    return () => {
      cancelled.current = true;
      if (idleHandle !== null && typeof window !== 'undefined' && 'cancelIdleCallback' in window) {
        (window as Window & { cancelIdleCallback: (h: number) => void }).cancelIdleCallback(idleHandle);
      }
      if (timeoutHandle !== null) clearTimeout(timeoutHandle);
    };
  }, [category, allCategories, effectiveSourceFilter, prefetchCategoryEvents]);

  // Prefetch adjacent Kalshi category tabs after 2s idle. Cold tab clicks
  // were taking 1-3s (network + Kalshi /v1/search/series + transform); with
  // the cache pre-populated, the click hits the SWR path in
  // fetchAggregatedMarkets and renders instantly.
  useEffect(() => {
    if (effectiveSourceFilter !== 'kalshi') return;
    if (!kalshiCategories || kalshiCategories.length === 0) return;

    const currentIdx = kalshiCategories.findIndex(c => c.id === category);
    if (currentIdx === -1) return;

    const adjacent: Array<{ id: string; kalshiCategory: string }> = [];
    if (currentIdx > 0) adjacent.push(kalshiCategories[currentIdx - 1]);
    if (currentIdx < kalshiCategories.length - 1) adjacent.push(kalshiCategories[currentIdx + 1]);
    if (adjacent.length === 0) return;

    // Defer the prefetch until the page is genuinely idle — the previous
    // 2 s timer landed in the middle of the first paint's settle, competing
    // with the search response render and the per-card price polls. Use
    // requestIdleCallback when available so the browser only spends cycles
    // on speculative work after the LCP frame has stabilised. Fall back to
    // a longer setTimeout for browsers without rIC (Safari < 17).
    const ric: number | undefined = (typeof window !== 'undefined' && (window as any).requestIdleCallback)
      ? (window as any).requestIdleCallback(scheduleAdjacent, { timeout: 4000 })
      : undefined;
    const fallbackTimeout = ric == null ? setTimeout(scheduleAdjacent, 4000) : null;

    function scheduleAdjacent() {
      void (async () => {
        for (const cat of adjacent) {
          const cacheKey = getCacheKey(cat.id);
          if (isCacheValid(cacheKey)) continue;

          // Per-category in-flight guard. Without this, every dep change
          // during the first paint (kalshiSortBy settling, getCacheKey
          // identity, etc.) re-fires the effect; the previous run's
          // setCategoryCache writes only AFTER the fetch resolves, so
          // multiple concurrent firings race and we hit
          // /api/markets/kalshi/search?category=Politics 5 times.
          if (kalshiPrefetchInFlightRef.current.has(cacheKey)) continue;
          kalshiPrefetchInFlightRef.current.add(cacheKey);

          const kalshiCategoryValue = cat.id === 'esports' ? 'Esports' : (cat.kalshiCategory || '');

          try {
            let url: string;
            if (!kalshiCategoryValue && (!kalshiSortBy || kalshiSortBy === 'all')) {
              url = '/api/markets/kalshi/homepage';
            } else {
              const params = new URLSearchParams({ limit: '50' });
              if (kalshiCategoryValue) params.set('category', kalshiCategoryValue);
              if (kalshiSortBy && kalshiSortBy !== 'all') params.set('orderBy', kalshiSortBy);
              if (kalshiReverse) params.set('reverse', 'true');
              url = `/api/markets/kalshi/search?${params.toString()}`;
            }

            const res = await fetchWithRetry(url, undefined, { dedupe: true });
            if (!res.ok) continue;
            const data = await res.json();
            const markets = url.includes('/homepage')
              ? [...(data.carousel || []), ...(data.topMarkets || [])]
              : (data.markets || []);
            if (!Array.isArray(markets) || markets.length === 0) continue;

            setCategoryCache(cacheKey, { events: markets, offset: 0, hasMore: false, timestamp: Date.now() });
          } catch {
            // Silent fail — prefetch is best-effort
          } finally {
            kalshiPrefetchInFlightRef.current.delete(cacheKey);
          }
        }
      })();
    }

    return () => {
      if (ric != null && (window as any).cancelIdleCallback) {
        (window as any).cancelIdleCallback(ric);
      }
      if (fallbackTimeout) clearTimeout(fallbackTimeout);
    };
  }, [effectiveSourceFilter, category, kalshiCategories, kalshiSortBy, kalshiReverse, getCacheKey]);

  // Crypto prices, Fear & Greed, Global data, and News are now handled by
  // TanStack Query hooks (useCryptoPrices, useFearGreed, useGlobalCrypto, useCryptoNews)
  // with automatic refetchInterval when category === 'crypto'.
  // Price changes are fetched once when live markets load (via allLiveCryptoMarkets watcher below).

  // Fetch live 5-min and 15-min markets when in crypto category
  useEffect(() => {
    if (category === "crypto") {
      fetchLive5MinMarkets();
      fetchLive15MinMarkets();
      // 2026-05-29 — visibility gate. These two manual intervals were the
      // only polls on this page NOT gated (the TanStack hooks use
      // refetchIntervalInBackground:false). A backgrounded crypto tab kept
      // hitting the live-markets endpoints forever. Skip while hidden;
      // refetch immediately on return so data isn't stale.
      const visible = () => typeof document === "undefined" || document.visibilityState === "visible";
      // 5M markets refresh every 10s (short-lived), 15M every 30s
      const fiveMinInterval = setInterval(() => { if (visible()) fetchLive5MinMarkets(); }, 10000);
      const fifteenMinInterval = setInterval(() => { if (visible()) fetchLive15MinMarkets(); }, 30000);
      const onVisible = () => {
        if (visible()) { fetchLive5MinMarkets(); fetchLive15MinMarkets(); }
      };
      document.addEventListener("visibilitychange", onVisible);
      return () => {
        clearInterval(fiveMinInterval);
        clearInterval(fifteenMinInterval);
        document.removeEventListener("visibilitychange", onVisible);
      };
    }
  }, [category]);

  // Combine 5-min (first) and 15-min markets with timeframe marker
  const allLiveCryptoMarkets = useMemo(() => [...live5MinMarkets, ...live15MinMarkets], [live5MinMarkets, live15MinMarkets]);
  const allLiveCryptoWithTimeframe = useMemo(() => [
    ...live5MinMarkets.map(e => ({ event: e, timeframe: "5M" as const })),
    ...live15MinMarkets.map(e => ({ event: e, timeframe: "15M" as const })),
  ], [live5MinMarkets, live15MinMarkets]);
  // Use ref to avoid stale closure in interval callbacks
  const allLiveCryptoMarketsRef = useRef(allLiveCryptoMarkets);
  allLiveCryptoMarketsRef.current = allLiveCryptoMarkets;

  // Extract UP token IDs for WebSocket subscription (carousel live prices)
  const liveCryptoTokenIds = useMemo(() => {
    const ids = new Set<string>();
    for (const event of allLiveCryptoMarkets) {
      const market = getLiveCryptoDisplayMarket(event);
      const upId = market?.clobTokenIds?.[0];
      if (upId && typeof upId === 'string' && upId.trim()) {
        ids.add(upId);
      }
    }
    return [...ids];
  }, [allLiveCryptoMarkets]);

  // WebSocket subscription for live crypto carousel prices
  const { prices: wsPriceMap } = usePriceStream({
    assetIds: liveCryptoTokenIds,
    enabled: category === "crypto" && liveCryptoTokenIds.length > 0,
  });

  // Merge WS prices into marketPrices (WS takes priority over HTTP polling)
  useEffect(() => {
    if (wsPriceMap.size === 0) return;
    const wsUpdates: Record<string, number> = {};
    for (const event of allLiveCryptoMarketsRef.current) {
      const market = getLiveCryptoDisplayMarket(event);
      const upId = market?.clobTokenIds?.[0];
      const downId = market?.clobTokenIds?.[1];
      if (!upId) continue;
      const wsData = wsPriceMap.get(upId);
      if (wsData) {
        const price = wsData.lastTradePrice ?? wsData.midPrice;
        wsUpdates[upId] = price;
        if (downId) wsUpdates[downId] = 1 - price;
      }
    }
    if (Object.keys(wsUpdates).length > 0) {
      setMarketPrices(prev => ({ ...prev, ...wsUpdates }));
    }
  }, [wsPriceMap]);

  useEffect(() => {
    if (allLiveCryptoMarkets.length > 0) {
      fetchEventPriceChanges(allLiveCryptoMarkets);
    }
  }, [allLiveCryptoMarkets]);

  // ─── Predict: Real-time prices via WebSocket ─────────────────────────
  // Subscribe to all visible Predict markets (any category, not just live)
  // so the cards always reflect live prices instead of waiting on the 5-min
  // list refetch. Replaces the prior 30s full re-fetch hack.
  const predictLiveMarketIds = useMemo(() => {
    if (effectiveSourceFilter !== 'predict') return [];
    return aggregatedMarkets
      .filter(m => m.source === 'predict')
      .flatMap(m => (m.markets || []).map((sm: any) => String(sm.id)).filter(Boolean));
  }, [effectiveSourceFilter, aggregatedMarkets]);

  const { prices: predictWsPrices } = usePredictRealtimePrice({
    marketIds: predictLiveMarketIds,
    enabled: predictLiveMarketIds.length > 0,
  });

  // Merge WS prices into aggregated markets (mutate outcomePrices for re-render)
  useEffect(() => {
    if (predictWsPrices.size === 0) return;
    let updated = false;
    setAggregatedMarkets(prev => {
      const next = prev.map(event => {
        if (event.source !== 'predict') return event;
        const markets = (event.markets || []).map((sm: any) => {
          const wsPrice = predictWsPrices.get(String(sm.id));
          if (wsPrice?.midPrice != null) {
            const yes = wsPrice.midPrice;
            const no = 1 - yes;
            if (sm.outcomePrices?.[0] !== yes.toFixed(2)) {
              updated = true;
              return { ...sm, outcomePrices: [yes.toFixed(2), no.toFixed(2)] };
            }
          }
          return sm;
        });
        return updated ? { ...event, markets } : event;
      });
      return updated ? next : prev;
    });
  }, [predictWsPrices]);

  // ─── Hyperliquid: Real-time prices via WebSocket ─────────────────────
  // Mirrors the Predict subscription. HL aggregated markets carry numeric
  // assetIds the backend HL price-stream publishes on `hl-market:<id>`.
  const hlLiveAssetIds = useMemo(() => {
    if (effectiveSourceFilter !== 'hyperliquid') return [];
    return aggregatedMarkets
      .filter(m => m.source === 'hyperliquid')
      .flatMap(m => (m.markets || []).map((sm: any) => Number(sm.assetId ?? sm.asset_id ?? sm.id)))
      .filter((n: number) => Number.isFinite(n) && n > 0);
  }, [effectiveSourceFilter, aggregatedMarkets]);

  const { prices: hlWsPrices } = useHyperliquidRealtimePrice({
    assetIds: hlLiveAssetIds,
    enabled: hlLiveAssetIds.length > 0,
  });

  useEffect(() => {
    if (hlWsPrices.size === 0) return;
    let updated = false;
    setAggregatedMarkets(prev => {
      const next = prev.map(event => {
        if (event.source !== 'hyperliquid') return event;
        const markets = (event.markets || []).map((sm: any) => {
          const aid = Number(sm.assetId ?? sm.asset_id ?? sm.id);
          if (!Number.isFinite(aid)) return sm;
          const tick = hlWsPrices.get(aid);
          if (tick?.mid != null) {
            const yes = tick.mid;
            const no = 1 - yes;
            if (sm.outcomePrices?.[0] !== yes.toFixed(2)) {
              updated = true;
              return { ...sm, outcomePrices: [yes.toFixed(2), no.toFixed(2)] };
            }
          }
          return sm;
        });
        return updated ? { ...event, markets } : event;
      });
      return updated ? next : prev;
    });
  }, [hlWsPrices]);

  // Fetch bookmarked markets when entering the bookmarks or for-you category
  // Also re-fetch when watchlist or source changes (context may load async after initial render)
  useEffect(() => {
    if ((category === "bookmarks" || category === "for-you") && authenticated && watchlist.length > 0) {
      fetchWatchingMarkets();
    }
  }, [category, authenticated, watchlist.length, effectiveSourceFilter]);

  // Fetch trending tags when entering the trending category
  // Optimistic update: when a bookmark is removed, animate out and remove from local state
  // This avoids a full refetch which causes loading flicker
  const prevWatchlistRef = useRef(watchlist);
  useEffect(() => {
    if (category !== "bookmarks") return;

    // Detect removed bookmark by comparing current vs previous watchlist
    const prevSlugs = new Set(prevWatchlistRef.current.map(w => w.market_slug));
    const currentSlugs = new Set(watchlist.map(w => w.market_slug));

    // Find removed slug
    for (const slug of prevSlugs) {
      if (!currentSlugs.has(slug)) {
        // Animate out the removed item
        setRemovingSlug(slug);
        // After animation, remove from local state
        setTimeout(() => {
          setWatchingEvents(prev => prev.filter(e => e.slug !== slug));
          setRemovingSlug(null);
        }, 300);
        break;
      }
    }

    prevWatchlistRef.current = watchlist;
  }, [watchlist, category]);

  // Redirect to trending if user is on bookmarks but not authenticated or no bookmarks for current source
  useEffect(() => {
    if (privyReady && category === "bookmarks") {
      if (!authenticated || !hasBookmarksForSource(effectiveSourceFilter)) {
        setCategory("trending");
        window.history.replaceState({}, '', sourceUrl("/markets/trending", source));
      }
    }
  }, [privyReady, authenticated, hasBookmarksForSource, effectiveSourceFilter, category, source]);

  // Get the featured market (highest volume) for passing title to API
  const featuredElonMarket = useMemo(() => {
    if (elonTweetMarkets.length === 0) return null;
    return [...elonTweetMarkets].sort((a, b) => (b.volume || 0) - (a.volume || 0))[0];
  }, [elonTweetMarkets]);

  // Fetch Elon tweet markets + tweet data when navigating to elon category
  useEffect(() => {
    if (category === "elon") {
      fetchElonTweetMarkets();
      fetchElonTweetData();
    }
  }, [category]);

  // Re-fetch tweet data with precise title once featured market is known
  useEffect(() => {
    if (category === "elon" && featuredElonMarket) {
      fetchElonTweetData(featuredElonMarket.title ?? undefined, featuredElonMarket.endDate ?? undefined);
      const interval = setInterval(() => fetchElonTweetData(featuredElonMarket.title ?? undefined, featuredElonMarket.endDate ?? undefined), 30000);
      return () => clearInterval(interval);
    }
  }, [category, featuredElonMarket?.title]);

  // Real-time price updates for tweet markets (every 3s like 15min crypto markets)
  useEffect(() => {
    if (category === "elon" && elonTweetMarkets.length > 0 && isPageVisible) {
      // Initial fetch already done, start interval for real-time updates
      const interval = setInterval(() => {
        fetchTweetMarketPrices(elonTweetMarkets);
      }, 3000);
      return () => clearInterval(interval);
    }
  }, [category, elonTweetMarkets, isPageVisible]);

  async function fetchElonTweetData(title?: string, endDate?: string) {
    try {
      setLoadingElonData(true);
      const params = new URLSearchParams();
      if (title) params.set("title", title);
      if (endDate) params.set("endDate", endDate);
      const url = params.toString()
        ? `/api/elontweets/summary?${params.toString()}`
        : "/api/elontweets/summary";
      const response = await authFetch(url);
      if (response.ok) {
        const data = await response.json();
        setElonTweetData(data);
      }
    } catch (error) {
    } finally {
      setLoadingElonData(false);
    }
  }

  // Fetch tweet counter markets specifically using the tweets-markets tag
  async function fetchElonTweetMarkets() {
    try {
      // Tweet markets are tagged as "tweets-markets" in Polymarket, not "elon-musk"
      const response = await authFetch("/api/polymarket/markets?tag_slug=tweets-markets&limit=50&active=true&closed=false&order=volume24hr&ascending=false");
      if (response.ok) {
        const data = await response.json();
        // Filter for Elon-specific tweet markets (contains "elon" in title or slug)
        const filteredMarkets = data.events.filter((e: Event) => {
          const title = (e.title || "").toLowerCase();
          const slug = (e.slug || "").toLowerCase();
          return title.includes("elon") || slug.includes("elon");
        });
        setElonTweetMarkets(filteredMarkets);
        // Fetch prices for these markets
        if (filteredMarkets.length > 0) {
          fetchTweetMarketPrices(filteredMarkets);
        }
      }
    } catch (error) {
    }
  }

  // Fetch prices for tweet market outcomes
  async function fetchTweetMarketPrices(markets: Event[]) {
    try {
      // Collect all token IDs from all markets (filter out empty strings)
      const tokenIds: string[] = [];
      markets.forEach(event => {
        (event.markets || []).forEach(market => {
          if (market.clobTokenIds && market.clobTokenIds.length > 0) {
            // Get the YES token (first one) - only if it's a non-empty string
            const tokenId = market.clobTokenIds[0];
            if (tokenId && tokenId.trim()) {
              tokenIds.push(tokenId);
            }
          }
        });
      });

      if (tokenIds.length === 0) return;

      // Batch tokenIds into chunks of 50 (backend limit)
      const BATCH_SIZE = 50;
      const batches: string[][] = [];
      for (let i = 0; i < tokenIds.length; i += BATCH_SIZE) {
        batches.push(tokenIds.slice(i, i + BATCH_SIZE));
      }

      // Fetch all batches and combine results
      const prices: Record<string, number> = {};
      // Fire all batches in parallel — they're independent POSTs; running them
      // in series added one round-trip per 50 tokens before prices appeared.
      await Promise.all(batches.map(async (batch) => {
        try {
          const response = await authFetch("/api/markets/prices/batch", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ tokenIds: batch }),
          });

          if (response.ok) {
            const data = await response.json();
            if (data.prices) {
              Object.entries(data.prices).forEach(([tokenId, priceData]) => {
                const p = priceData as { midPrice?: number | null };
                prices[tokenId] = p.midPrice ?? 0;
              });
            }
          }
        } catch {
          // Continue with other batches on error
        }
      }));
      setTweetMarketPrices(prices);
    } catch (error) {
    }
  }

  // Crypto polling: HTTP every 5s as a hedge for tokens without recent
  // trades (the WS feed only fires on trade events). WS via usePriceStream
  // is the primary source for all actively-traded tokens; HTTP just keeps
  // illiquid markets from drifting. Bumped 3s → 5s after WS coverage was
  // verified in production — saves ~40% of /api/markets/prices/batch hits
  // with imperceptible latency impact (5s vs 3s on a price that updates
  // by trade event in milliseconds).
  useEffect(() => {
    if (category !== "crypto" || allLiveCryptoMarkets.length === 0 || !isPageVisible) return;
    fetchMarketPrices();
    let tick = 0;
    const interval = setInterval(() => {
      fetchMarketPrices();
      tick++;
      if (tick % 2 === 0) {
        fetchEventPriceChanges(allLiveCryptoMarketsRef.current);
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [category, allLiveCryptoMarkets, isPageVisible]);

  async function fetchMarketPrices() {
    try {
      // Collect all tokenIds from live crypto markets (5-min + 15-min)
      const tokenIdPairs: { upTokenId: string; downTokenId: string }[] = [];
      allLiveCryptoMarkets.forEach(event => {
        (event.markets || []).forEach(market => {
          if (market.clobTokenIds && market.clobTokenIds.length >= 2) {
            const upTokenId = market.clobTokenIds[0];
            const downTokenId = market.clobTokenIds[1];
            // Only add if both tokenIds are non-empty strings
            if (upTokenId && upTokenId.trim() && downTokenId && downTokenId.trim()) {
              tokenIdPairs.push({ upTokenId, downTokenId });
            }
          }
        });
      });

      if (tokenIdPairs.length === 0) return;

      // Collect all UP tokenIds for batch request
      const tokenIds = tokenIdPairs.map(pair => pair.upTokenId);

      // Batch tokenIds into chunks of 50 (backend limit)
      const BATCH_SIZE = 50;
      const batches: string[][] = [];
      for (let i = 0; i < tokenIds.length; i += BATCH_SIZE) {
        batches.push(tokenIds.slice(i, i + BATCH_SIZE));
      }

      // Fetch all batches in parallel and combine results — independent POSTs,
      // so series-await just stacked round-trips before crypto prices showed.
      const allPriceData: Record<string, { midPrice?: number }> = {};
      await Promise.all(batches.map(async (batch) => {
        try {
          const response = await authFetch('/api/markets/prices/batch', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ tokenIds: batch })
          });

          if (response.ok) {
            const data = await response.json();
            if (data.prices) {
              Object.assign(allPriceData, data.prices);
            }
          }
        } catch {
          // Continue with other batches on error
        }
      }));

      const prices: Record<string, number> = {};

      // Process batch response and calculate DOWN prices
      tokenIdPairs.forEach(({ upTokenId, downTokenId }) => {
        const priceData = allPriceData[upTokenId];
        if (priceData?.midPrice !== undefined) {
          const upPrice = parseFloat(String(priceData.midPrice));
          if (!isNaN(upPrice)) {
            prices[upTokenId] = upPrice;
            prices[downTokenId] = 1 - upPrice; // DOWN = 1 - UP
          }
        }
      });

      setMarketPrices(prev => ({ ...prev, ...prices }));
    } catch {
      // Silently fail, will retry on next interval
    }
  }

  // fetchCryptoPrices, fetchFearGreed, fetchGlobalCrypto, fetchCryptoNews
  // are now handled by TanStack Query hooks (useCryptoPrices, useFearGreed, useGlobalCrypto, useCryptoNews)

  // Fetch price changes since event start from Polymarket (for 15-min markets)
  async function fetchEventPriceChanges(markets: Event[]) {
    if (markets.length === 0) return;

    try {
      // Build market data matching the same Polymarket API the detail page uses
      // Variants confirmed from PM DevTools: 5m→fiveminute, 15m→fifteen, 1h→hourly, 4h→4hourly.
      const variantMap: Record<string, string> = { '5m': 'fiveminute', '15m': 'fifteen', '1h': 'hourly', '4h': '4hourly' };
      const marketData: Array<{ symbol: string; eventStartTime: number; variant: string; endDate: string; key: string }> = [];

      markets.forEach(event => {
        if (!event.endDate) return;

        const questionText = (event.markets[0]?.question || event.title).toLowerCase();
        const slug = (event as any).slug || '';
        const detected = detectCryptoFromTitle(questionText);
        const symbol = detected.priceKey;
        if (!symbol) return;

        // Detect interval from slug first (most reliable), then question
        let interval = '15m';
        if (slug.includes('-5m-')) interval = '5m';
        else if (slug.includes('-1h-')) interval = '1h';
        else if (slug.includes('-4h-')) interval = '4h';
        else if (slug.includes('-15m-')) interval = '15m';
        else if (/\b5 min/i.test(questionText) && !/15 min/i.test(questionText)) interval = '5m';
        else if (/\b1 hour/i.test(questionText)) interval = '1h';
        else if (/\b4 hour/i.test(questionText)) interval = '4h';

        const variant = variantMap[interval] || 'fifteen';
        const endTime = new Date(event.endDate).getTime();
        // eventStartTime: interval duration before endDate
        const intervalMs = { '5m': 5, '15m': 15, '1h': 60, '4h': 240 }[interval]! * 60 * 1000;
        const eventStartTime = endTime - intervalMs;

        // Unique key per market (symbol alone would collide for ETH-5M vs ETH-15M)
        const key = `${symbol}:${eventStartTime}`;
        marketData.push({ symbol, eventStartTime, variant, endDate: event.endDate, key });
      });

      if (marketData.length === 0) return;

      const response = await authFetch("/api/crypto/price-changes", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ markets: marketData }),
      });

      if (response.ok) {
        const data = await response.json();
        setEventPriceChanges(prev => ({ ...prev, ...(data.prices || {}) }));
      }
    } catch (error) {
      // Silent fail
    }
  }


  async function fetchLive15MinMarkets() {
    try {
      setLoading15Min(true);
      const response = await authFetch(`/api/markets/crypto?category=15M&limit=20`);
      if (response.ok) {
        const data = await response.json();
        const now = Date.now();
        const liveMarkets = (data.events || []).filter((event: Event) => {
          if (!event.endDate || event.closed) return false;
          const endTime = new Date(event.endDate).getTime();
          return endTime > now;
        });
        // Only update if we got results — keeps previous data visible during gaps
        if (liveMarkets.length > 0) setLive15MinMarkets(liveMarkets);
      }
    } catch (error) {
    } finally {
      setLoading15Min(false);
      setLiveMarketsLoaded(true);
    }
  }

  async function fetchLive5MinMarkets() {
    try {
      const response = await authFetch(`/api/markets/crypto?category=5M&limit=20`);
      if (response.ok) {
        const data = await response.json();
        const now = Date.now();
        const liveMarkets = (data.events || []).filter((event: Event) => {
          if (!event.endDate || event.closed) return false;
          const endTime = new Date(event.endDate).getTime();
          return endTime > now;
        });
        // Only update if we got new markets — keeps resolving cards visible during gaps
        if (liveMarkets.length > 0) setLive5MinMarkets(liveMarkets);
      }
    } catch (error) {
    } finally {
      setLiveMarketsLoaded(true);
    }
  }

  async function fetchAggregatedMarkets(source?: string, orderBy?: string, skipLoading = false, categoryOverride?: string) {
    // Use passed source param to avoid stale closure issues
    const effectiveSource = source ?? sourceFilter;
    // categoryOverride evita el stale-closure: cuando el tab-click dispara el
    // fetch ANTES de que `setCategory` commitee, el closure `category` aún es
    // el viejo. El click pasa la categoría nueva explícita aquí.
    const cat = categoryOverride ?? category;
    const cacheKey = getCacheKey(cat);

    // Dedupe concurrent identical calls. Source-switch + URL-sync + the
    // main fetching effect can each fire this with the same args within
    // the same tick — without this guard we issued 3 parallel HTTP calls
    // to /api/markets/hyperliquid (verified in devtools).
    const inflightKey = `${effectiveSource}|${orderBy ?? ''}|${cat ?? ''}|${skipLoading ? '1' : '0'}`;
    const inflight = aggregatedInflightRef.current.get(inflightKey);
    if (inflight) return inflight;
    let resolveInflight: () => void = () => {};
    const inflightPromise = new Promise<void>((resolve) => { resolveInflight = resolve; });
    aggregatedInflightRef.current.set(inflightKey, inflightPromise);

    // Increment version — only the latest fetch will apply results
    const fetchVersion = ++aggregatedFetchVersionRef.current;
    // Tracks whether the fetch actually delivered data. The `finally` block
    // below uses this so a transient miss (rate limit, response not ok,
    // race-loser) doesn't flip `hasFetchedAggregated=true` against an
    // empty `aggregatedMarkets` and trip the "No Kalshi markets" empty
    // state during fast PM↔Kalshi source switching.
    let success = false;

    // Stale-while-revalidate: show cached data instantly, fetch in background
    const cached = getCachedCategory(cacheKey);
    if (cached && isCacheValid(cacheKey)) {
      setAggregatedMarkets(cached.events as any[]);
      // HL pagination state lives outside aggregatedMarkets — restore from
      // cache too. Without this, a cache hit on /hyperliquid keeps the
      // stale `hyperliquidHasMore=false` from React's initial state and
      // the "Load more" button vanishes even when more pages exist.
      if (effectiveSource === 'hyperliquid') {
        // Prefer cached.offset (set by loadMore and the initial fetch) over
        // events.length — these usually match, but if a downstream dedupe
        // ever removed an event from the array we'd query past the actual
        // upstream cursor and miss a page. Fall back to length for older
        // cache rows that pre-date the offset write.
        const cachedOffset = typeof (cached as any).offset === 'number' ? (cached as any).offset : (cached.events as any[]).length;
        setHyperliquidOffset(cachedOffset);
        setHyperliquidHasMore(cached.hasMore === true);
      }
      setLoadingAggregated(false);
      // Schedule ONE background refresh (not recursive — skipLoading prevents re-scheduling)
      if (!skipLoading) {
        setTimeout(() => fetchAggregatedMarkets(source, orderBy, true), 1000);
      }
      aggregatedInflightRef.current.delete(inflightKey);
      resolveInflight();
      return;
    }
    // If we have stale cache, show it but still fetch fresh data (no loading spinner)
    if (cached) {
      setAggregatedMarkets(cached.events as any[]);
      skipLoading = true;
    }

    try {
      if (!skipLoading) setLoadingAggregated(true);

      // For Kalshi, use different endpoints based on sort and category
      if (effectiveSource === 'kalshi') {
        // Look up the proper Kalshi category value (e.g., "Politics" not "politics")
        const kalshiCat = kalshiCategories.find(c => c.id === cat);
        // Esports isn't a real Kalshi top-level category — force the search path
        // and let the backend map it to category=Sports + esports tag filter.
        const kalshiCategoryValue = cat === 'esports'
          ? 'Esports'
          : (kalshiCat?.kalshiCategory || '');

        // Only use homepage when no specific category and default sort
        const useHomepageOrder = (!orderBy || orderBy === 'all') && !kalshiCategoryValue;
        let markets: any[] | null = null;
        if (useHomepageOrder) {
          const response = await fetchWithRetry('/api/markets/kalshi/homepage');
          if (response.ok) {
            const data = await response.json();
            markets = [...(data.carousel || []), ...(data.topMarkets || [])];
          }
        } else {
          // Use search endpoint for categories and/or non-default sort
          // limit=50 for fast initial load (matches DexTools); "Load more" fetches next page via cursor
          const params = new URLSearchParams({ limit: '50' });
          if (kalshiCategoryValue) params.set('category', kalshiCategoryValue);
          if (activeKalshiSubcategory) params.set('subcategory', activeKalshiSubcategory);
          if (activeKalshiCompetition) params.set('competition', activeKalshiCompetition);
          // Crypto/Commodities sidebar filter: primary can be a Kalshi
          // frequency, a parent subcategory (tag) or a nested scope row
          // (Oil & Gas → WTI Crude). Compound ids use the "parent__child"
          // shape set up in cryptoSidebarCoins. We don't gate on
          // kalshiSidebar so the filter applies on first fetch (sidebar
          // loads asynchronously and isn't always ready).
          if ((category === 'crypto' || category === 'commodities') && cryptoPrimaryFilter && cryptoPrimaryFilter !== 'all') {
            const KALSHI_FREQ_VALUES = new Set([
              'fifteen_min', 'hourly', 'daily', 'weekly', 'monthly', 'annual', 'one_off',
            ]);
            if (KALSHI_FREQ_VALUES.has(cryptoPrimaryFilter)) {
              params.set('frequency', cryptoPrimaryFilter);
            } else if (kalshiSidebar) {
              // Scope (nested child) — compound id "parent__child"
              if (cryptoPrimaryFilter.includes('__')) {
                const [parentId, childId] = cryptoPrimaryFilter.split('__');
                for (const s of kalshiSidebar.subcategories) {
                  if (s.label.toLowerCase().replace(/\s+/g, '-') !== parentId) continue;
                  const child = (s.children || []).find(
                    c => c.label.toLowerCase().replace(/\s+/g, '-') === childId,
                  );
                  if (child) {
                    params.set('scope', child.label);
                    // Setting the parent tag too narrows results — Kalshi
                    // also accepts scope alone but this mirrors how their
                    // sidebar clicks load pages, so cache keys line up.
                    params.set('subcategory', s.label);
                  }
                  break;
                }
              } else {
                const subMatch = kalshiSidebar.subcategories.find(
                  s => s.label.toLowerCase().replace(/\s+/g, '-') === cryptoPrimaryFilter
                );
                if (subMatch) params.set('subcategory', subMatch.label);
              }
            } else {
              // Fallback: assume id ≈ tag label (e.g. 'btc' → 'BTC')
              params.set('subcategory', cryptoPrimaryFilter.toUpperCase());
            }
          }
          if (orderBy && orderBy !== 'all') params.set('orderBy', orderBy);
          if (kalshiReverse) params.set('reverse', 'true');

          const response = await fetchWithRetry(`/api/markets/kalshi/search?${params.toString()}`, undefined, { dedupe: true });
          if (response.ok) {
            const data = await response.json();
            markets = data.markets || [];
            setKalshiNextCursor(data.nextCursor || null);
            setKalshiTotalCount(data.totalCount || 0);
          }
        }

        // Only apply results if this is still the latest fetch (prevents race conditions)
        if (fetchVersion !== aggregatedFetchVersionRef.current) return;

        // If fetch failed (markets === null), keep existing data instead of showing empty state
        if (markets === null) return;

        setAggregatedMarkets(markets);
        success = true;
        // Reset pagination when new data arrives (not before — avoids visual shrink)
        setKalshiDisplayLimit(KALSHI_PAGE_SIZE);
        if (!useHomepageOrder) {
          // nextCursor/totalCount already set above from response
        } else {
          setKalshiNextCursor(null);
          setKalshiTotalCount(0);
        }
        // Don't cache empty results — likely a transient Kalshi API failure
        if (markets.length > 0) {
          setCategoryCache(cacheKey, { events: markets, offset: 0, hasMore: false, timestamp: Date.now() });
        }
        return;
      }

      // Hyperliquid HIP-4 — dedicated endpoint, question-grouped catalog.
      // No category taxonomy yet (HL outcomeMeta doesn't tag questions);
      // when a non-meta category is selected we forward it as a search hint.
      if (effectiveSource === 'hyperliquid') {
        const params = new URLSearchParams({ limit: String(HYPERLIQUID_PAGE_SIZE), grouped: 'true' });
        // HL taxonomy: Trending (default order, no param), New (order by sync
        // time), Recurring (metadata.recurring filter), Crypto / Events (real
        // category column populated by the sync poller from
        // description.underlying). 'breaking' is a polymarket-only bucket and
        // a no-op here.
        const HL_META_CATEGORIES = new Set(['trending', 'breaking', 'new', 'recurring', 'crypto', 'events']);
        if (cat && HL_META_CATEGORIES.has(cat)) {
          if (cat !== 'trending' && cat !== 'breaking') params.set('category', cat);
        } else if (cat) {
          params.set('search', cat);
        }
        const response = await fetchWithRetry(`/api/markets/hyperliquid?${params.toString()}`);
        if (response.ok && fetchVersion === aggregatedFetchVersionRef.current) {
          const data = await response.json();
          const questions: any[] = Array.isArray(data?.questions) ? data.questions : [];
          const markets: AggregatedMarket[] = questions.map((q) => buildHlEventMarket(q));
          setAggregatedMarkets(markets);
          success = true;
          setHyperliquidOffset(markets.length);
          const hlHasMore = markets.length >= HYPERLIQUID_PAGE_SIZE;
          setHyperliquidHasMore(hlHasMore);
          if (markets.length > 0) {
            setCategoryCache(cacheKey, { events: markets, offset: markets.length, hasMore: hlHasMore, timestamp: Date.now() });
          }
        }
        return;
      }

      // Predict.fun — use dedicated endpoint with pagination
      if (effectiveSource === 'predict') {
        const params = new URLSearchParams({ limit: String(PREDICT_PAGE_SIZE) });
        if (cat && !['trending', 'breaking', 'new'].includes(cat)) {
          params.set('category', cat);
        }
        // Map meta-categories to sort order for Predict.fun
        // "new" → sort by newest, "trending/breaking" → sort by popular (default)
        const effectiveSort = cat === 'new' ? 'endingSoon'
          : cat === 'breaking' ? 'liquidity'
          : sortBy;
        if (effectiveSort) {
          params.set('sort', effectiveSort);
        }
        const response = await fetchWithRetry(`/api/markets/predict/list?${params.toString()}`);
        if (response.ok && fetchVersion === aggregatedFetchVersionRef.current) {
          const data = await response.json();
          const markets = (data.markets || []).map((m: any) => ({
            ...m,
            source: 'predict',
            // Normalize field name: backend returns volume24hr, AggregatedMarket uses volume24h
            volume24h: m.volume24h ?? m.volume24hr ?? 0,
          }));
          setAggregatedMarkets(markets);
          success = true;
          setPredictOffset(markets.length);
          setPredictTotal(data.total || markets.length);
          if (markets.length > 0) {
            setCategoryCache(cacheKey, { events: markets, offset: 0, hasMore: markets.length < (data.total || 0), timestamp: Date.now() });
          }
        }
        return;
      }

      // For other sources, use aggregated endpoint
      const params = new URLSearchParams({
        limit: '100',
        source: effectiveSource,
      });
      if (category && !['trending', 'breaking', 'new'].includes(category)) {
        params.set('category', category);
      }
      const response = await fetchWithRetry(`/api/markets/aggregated?${params.toString()}`);
      if (response.ok && fetchVersion === aggregatedFetchVersionRef.current) {
        const data = await response.json();
        const markets = data.markets || [];
        setAggregatedMarkets(markets);
        success = true;
        setCategoryCache(cacheKey, { events: markets, offset: 0, hasMore: false, timestamp: Date.now() });
      }
    } catch (error) {
    } finally {
      if (fetchVersion === aggregatedFetchVersionRef.current) {
        setLoadingAggregated(false);
        // Only mark "fetched" when the fetch actually delivered something.
        // A transient miss leaves hasFetchedAggregated as it was so the
        // skeleton stays up instead of falling through to the empty state.
        if (success) setHasFetchedAggregated(true);
      }
      aggregatedInflightRef.current.delete(inflightKey);
      resolveInflight();
    }
  }

  // Fetch more Kalshi markets using cursor pagination
  async function loadMoreKalshi() {
    if (!kalshiNextCursor || loadingMoreKalshi) return;
    setLoadingMoreKalshi(true);
    try {
      const kalshiCat = kalshiCategories.find(c => c.id === category);
      const kalshiCategoryValue = kalshiCat?.kalshiCategory || '';
      const orderBy = kalshiSortBy;

      const params = new URLSearchParams({ limit: '50' });
      if (kalshiCategoryValue) params.set('category', kalshiCategoryValue);
      if (activeKalshiSubcategory) params.set('subcategory', activeKalshiSubcategory);
      if (activeKalshiCompetition) params.set('competition', activeKalshiCompetition);
      // Crypto frequency/coin filter (mirrors fetchAggregatedMarkets)
      if (category === 'crypto' && cryptoPrimaryFilter && cryptoPrimaryFilter !== 'all') {
        const KALSHI_FREQ_VALUES = new Set([
          'fifteen_min', 'hourly', 'daily', 'weekly', 'monthly', 'annual', 'one_off',
        ]);
        if (KALSHI_FREQ_VALUES.has(cryptoPrimaryFilter)) {
          params.set('frequency', cryptoPrimaryFilter);
        } else if (kalshiSidebar) {
          const subMatch = kalshiSidebar.subcategories.find(
            s => s.label.toLowerCase().replace(/\s+/g, '-') === cryptoPrimaryFilter
          );
          if (subMatch) params.set('subcategory', subMatch.label);
        } else {
          params.set('subcategory', cryptoPrimaryFilter.toUpperCase());
        }
      }
      if (orderBy && orderBy !== 'all') params.set('orderBy', orderBy);
      if (kalshiReverse) params.set('reverse', 'true');
      params.set('cursor', kalshiNextCursor);

      const response = await authFetch(`/api/markets/kalshi/search?${params.toString()}`);
      if (response.ok) {
        const data = await response.json();
        const newMarkets = data.markets || [];
        if (newMarkets.length > 0) {
          setAggregatedMarkets(prev => [...prev, ...newMarkets]);
          setKalshiNextCursor(data.nextCursor || null);
          setKalshiTotalCount(data.totalCount || 0);
          // Bump display limit to show new results
          setKalshiDisplayLimit(prev => prev + KALSHI_PAGE_SIZE);
        } else {
          // No results after filtering — stop pagination to avoid infinite empty loads
          setKalshiNextCursor(null);
          setKalshiTotalCount(0);
        }
      }
    } catch (error) {
      // Silently fail — user can retry
    } finally {
      setLoadingMoreKalshi(false);
    }
  }

  // Fetch more Predict.fun markets using offset pagination
  async function loadMorePredict() {
    if (loadingMorePredict || predictOffset >= predictTotal) return;
    setLoadingMorePredict(true);
    try {
      const params = new URLSearchParams({ limit: String(PREDICT_PAGE_SIZE), offset: String(predictOffset) });
      if (category && !['trending', 'breaking', 'new'].includes(category)) {
        params.set('category', category);
      }
      if (sortBy) {
        params.set('sort', sortBy);
      }
      const response = await authFetch(`/api/markets/predict/list?${params.toString()}`);
      if (response.ok) {
        const data = await response.json();
        const newMarkets = (data.markets || []).map((m: any) => ({ ...m, source: 'predict' }));
        if (newMarkets.length > 0) {
          setAggregatedMarkets(prev => [...prev, ...newMarkets]);
          setPredictOffset(prev => prev + newMarkets.length);
          setPredictTotal(data.total || 0);
        }
      }
    } catch {
      // Silently fail
    } finally {
      setLoadingMorePredict(false);
    }
  }

  // Fetch more Hyperliquid questions using offset pagination. Backend
  // doesn't return a total count for the grouped questions endpoint, so
  // hasMore is inferred from whether the last response filled the page.
  async function loadMoreHyperliquid() {
    if (loadingMoreHyperliquid || !hyperliquidHasMore) return;
    setLoadingMoreHyperliquid(true);
    try {
      const params = new URLSearchParams({
        limit: String(HYPERLIQUID_PAGE_SIZE),
        offset: String(hyperliquidOffset),
        grouped: 'true',
      });
      const HL_META_CATEGORIES = new Set(['trending', 'breaking', 'new', 'recurring', 'crypto', 'events']);
      if (category && HL_META_CATEGORIES.has(category)) {
        if (category !== 'trending' && category !== 'breaking') params.set('category', category);
      } else if (category) {
        params.set('search', category);
      }
      const response = await authFetch(`/api/markets/hyperliquid?${params.toString()}`);
      if (response.ok) {
        const data = await response.json();
        const questions: any[] = Array.isArray(data?.questions) ? data.questions : [];
        const newMarkets: AggregatedMarket[] = questions.map((q) => buildHlEventMarket(q));
        const hasMoreNow = newMarkets.length >= HYPERLIQUID_PAGE_SIZE;
        if (newMarkets.length > 0) {
          setAggregatedMarkets(prev => {
            const merged = [...prev, ...newMarkets];
            // Persist the accumulated page set + hasMore to the category cache
            // so that revisiting the same HL category doesn't drop the user
            // back to the first page. The initial-fetch path writes the cache
            // (line ~3879) but loadMore previously did not, so any pagination
            // beyond page 1 was lost on next visit.
            const cacheKey = getCacheKey(category || 'trending');
            setCategoryCache(cacheKey, {
              events: merged,
              offset: merged.length,
              hasMore: hasMoreNow,
              timestamp: Date.now(),
            });
            return merged;
          });
          setHyperliquidOffset(prev => prev + newMarkets.length);
        }
        setHyperliquidHasMore(hasMoreNow);
      }
    } catch {
      // Silently fail — user can retry
    } finally {
      setLoadingMoreHyperliquid(false);
    }
  }

  // fetchFollowingActivity is now handled by useFollowingActivity hook (TanStack Query)

  // Fetch personalized "For You" feed (events + markets)
  async function fetchForYouFeed(refresh = false, sortOrder: "volume24hr" | "volume" | "liquidity" | "newest" | "endingSoon" | "competitive" = "volume24hr", skipLoading = false) {
    try {
      if (!skipLoading) setLoadingForYou(true);

      // Map sortOrder to Polymarket API parameters
      const polymarketOrder = sortOrder === "newest" ? "startDate"
        : sortOrder === "endingSoon" ? "endDate"
        : sortOrder === "competitive" ? "competitive"
        : sortOrder;
      const polymarketAscending = sortOrder === "endingSoon" ? "true" : "false";

      // When refreshing, only re-fetch AI recommendations (trending doesn't depend on them)
      // Pipe the active locale through so the backend can overlay
      // translated titles/questions/outcomes onto the cached EN payload.
      const localeQuery = locale && locale !== 'en' ? `&locale=${encodeURIComponent(locale)}` : '';
      const marketsPromise = fetch(`/api/recommendations/for-you?limit=20&offset=0&refresh=${refresh ? 'true' : 'false'}&sortBy=${sortOrder}${localeQuery}`);
      let processedEvents: Event[] = forYouEvents; // default to current state (for refresh case)

      // Fetch friends activity (fire-and-forget, non-blocking).
      // Has to be authFetch — `following=true` is a per-user filter on
      // the backend (req.userId required), and the /activity/stream
      // proxy is configured with `requireAuth: false` precisely so the
      // anonymous public feed still works, which means it ONLY injects
      // X-User-Id when the caller uses authFetch. A plain `fetch` here
      // sent the request without auth, the backend returned an empty
      // `events: []`, and the "Your Frens Are Trading" section never
      // rendered — the section was effectively dead for everyone.
      if (!refresh) {
        authFetch('/api/activity/stream?following=true&types=user_trade,copy_trade,tracker_trade&limit=20')
          .then(r => r.ok ? r.json() : null)
          .then(data => { if (data?.events?.length) setFrensActivity(data.events); })
          .catch(() => {});
      }

      if (!refresh) {
        // Initial load: fire both in parallel but show trending events as soon as ready
        const eventsPromise = fetch(`/api/polymarket/markets?limit=12&order=${polymarketOrder}&ascending=${polymarketAscending}&active=true&closed=false${localeQuery}`);

        // Show trending events as soon as they arrive (don't wait for recommendations)
        eventsPromise.then(async (eventsResponse) => {
          if (eventsResponse.ok) {
            const eventsData = await eventsResponse.json();
            processedEvents = (eventsData.events || []).map((e: any) => {
              const markets = Array.isArray(e.markets) ? e.markets : [];
              return {
                ...e,
                markets: markets.map((m: any) => ({
                  ...m,
                  outcomes: typeof m.outcomes === 'string' ? JSON.parse(m.outcomes) : (m.outcomes || ['Yes', 'No']),
                  outcomePrices: typeof m.outcomePrices === 'string' ? JSON.parse(m.outcomePrices) : (m.outcomePrices || ['0.5', '0.5']),
                  clobTokenIds: typeof m.clobTokenIds === 'string' ? JSON.parse(m.clobTokenIds) : (m.clobTokenIds || []),
                })),
                tags: Array.isArray(e.tags) ? e.tags : [],
              };
            });
            setForYouEvents(processedEvents);
          } else {
            setForYouEvents([]);
          }
        }).catch(() => setForYouEvents([]));

        // Wait for recommendations (the slower call)
        var marketsResponseFinal = await marketsPromise;
      } else {
        // Refresh: only re-fetch recommendations, keep trending as-is
        var marketsResponseFinal = await marketsPromise;
      }

      // Process recommended feed (mixed events + markets)
      if (marketsResponseFinal.ok) {
        const data = await marketsResponseFinal.json();
        const items: typeof forYouFeedItems = [];

        for (const m of (data.markets || [])) {
          if (m.type === 'event' && Array.isArray(m.eventMarkets) && m.eventMarkets.length > 0) {
            // Event item: build an Event object with nested markets for EventCard
            const eventObj: Event = {
              id: m.eventSlug || m.slug,
              slug: m.eventSlug || m.slug,
              title: m.title,
              description: m.description || null,
              image: m.image || null,
              icon: null,
              endDate: m.endDate,
              closed: false,
              active: true,
              volume: m.volume24h || 0,
              volume24hr: m.volume24h || 0,
              liquidity: m.liquidity || 0,
              markets: m.eventMarkets.map((em: any) => ({
                id: em.id || em.slug,
                question: em.question || em.title || '',
                slug: em.slug || '',
                conditionId: em.conditionId || '',
                outcomes: typeof em.outcomes === 'string' ? JSON.parse(em.outcomes) : (em.outcomes || ['Yes', 'No']),
                outcomePrices: typeof em.outcomePrices === 'string' ? JSON.parse(em.outcomePrices) : (Array.isArray(em.outcomePrices) ? em.outcomePrices : [0.5, 0.5]),
                clobTokenIds: typeof em.clobTokenIds === 'string' ? JSON.parse(em.clobTokenIds) : (em.clobTokenIds || []),
                groupItemTitle: em.groupItemTitle || '',
                acceptingOrders: em.acceptingOrders !== false,
                active: !em.closed,
                closed: em.closed === true,
                image: em.image ?? null,
                sportsMarketType: em.sportsMarketType ?? undefined,
              })),
              tags: Array.isArray(m.tags) ? m.tags : [],
              source: 'polymarket',
              teams: Array.isArray(m.teams) ? m.teams : undefined,
            };
            items.push({ type: 'event', event: eventObj, reason: m.reason || '', position: m.position || 0 });
          } else {
            // Market item: build an AggregatedMarket
            const market: AggregatedMarket = {
              id: m.slug,
              slug: m.slug,
              title: m.title,
              description: m.description,
              image: m.image,
              source: 'polymarket' as const,
              yesPrice: m.yesPrice || 0.5,
              noPrice: m.noPrice || 0.5,
              volume: m.volume24h || 0,
              volume24h: m.volume24h || 0,
              liquidity: m.liquidity || 0,
              status: 'active',
              endDate: m.endDate,
              category: m.category || 'Other',
              sourceData: {},
            };
            items.push({ type: 'market', market, reason: m.reason || '', position: m.position || 0 });
          }
        }

        // Split into main feed + replacement pool
        const mainItems = items.slice(0, 28);
        const extraItems = items.slice(28);
        setForYouFeedItems(mainItems);
        forYouReplacementPoolRef.current = extraItems;
        setForYouHasMore(data.hasMore || false);

        // Cache for instant tab switching on return
        setCachedForYou({
          feedItems: mainItems,
          events: processedEvents,
          frensActivity,
          hasMore: data.hasMore || false,
        });
      } else {
        setForYouFeedItems([]);
        forYouReplacementPoolRef.current = [];
      }
    } catch (error) {
      setForYouFeedItems([]);
      forYouReplacementPoolRef.current = [];
      setForYouEvents([]);
    } finally {
      setLoadingForYou(false);
    }
  }

  // Track how many items have been dismissed to use as offset for fetching replacements
  const forYouDismissedCountRef = useRef(0);

  // Handle "Not interested" — replace dismissed item with one from the pool or fetch new
  const handleForYouHide = useCallback((slug: string) => {
    forYouDismissedCountRef.current += 1;

    setForYouFeedItems(prev => {
      const index = prev.findIndex(item =>
        item.type === 'event' ? item.event.slug === slug : item.market.slug === slug
      );
      if (index === -1) return prev;

      const next = [...prev];
      if (forYouReplacementPoolRef.current.length > 0) {
        const replacement = forYouReplacementPoolRef.current.shift()!;
        next.splice(index, 1, replacement);
      } else {
        // Pool empty — remove item and fetch a replacement async
        next.splice(index, 1);
        fetchSingleReplacement(index);
      }
      return next;
    });
  }, []);

  // Fetch a single replacement from the API when the pool is empty
  async function fetchSingleReplacement(insertIndex: number) {
    try {
      const offset = 40 + forYouDismissedCountRef.current;
      const res = await authFetch(`/api/recommendations/for-you?limit=1&offset=${offset}&sortBy=volume24hr`);
      if (!res.ok) return;
      const data = await res.json();
      const markets = data.markets || [];
      if (markets.length === 0) return;

      const m = markets[0];
      let newItem: typeof forYouFeedItems[number];

      if (m.type === 'event' && Array.isArray(m.eventMarkets) && m.eventMarkets.length > 0) {
        const eventObj: Event = {
          id: m.eventSlug || m.slug,
          slug: m.eventSlug || m.slug,
          title: m.title,
          description: m.description || null,
          image: m.image || null,
          icon: null,
          endDate: m.endDate,
          closed: false,
          active: true,
          volume: m.volume24h || 0,
          volume24hr: m.volume24h || 0,
          liquidity: m.liquidity || 0,
          markets: m.eventMarkets.map((em: any) => ({
            id: em.id || em.slug,
            question: em.question || em.title || '',
            slug: em.slug || '',
            conditionId: em.conditionId || '',
            outcomes: typeof em.outcomes === 'string' ? JSON.parse(em.outcomes) : (em.outcomes || ['Yes', 'No']),
            outcomePrices: typeof em.outcomePrices === 'string' ? JSON.parse(em.outcomePrices) : (Array.isArray(em.outcomePrices) ? em.outcomePrices : [0.5, 0.5]),
            clobTokenIds: typeof em.clobTokenIds === 'string' ? JSON.parse(em.clobTokenIds) : (em.clobTokenIds || []),
            groupItemTitle: em.groupItemTitle || '',
            acceptingOrders: em.acceptingOrders !== false,
            active: !em.closed,
            closed: em.closed === true,
            image: em.image ?? null,
            sportsMarketType: em.sportsMarketType ?? undefined,
          })),
          tags: Array.isArray(m.tags) ? m.tags : [],
          source: 'polymarket',
          teams: Array.isArray(m.teams) ? m.teams : undefined,
        };
        newItem = { type: 'event', event: eventObj, reason: m.reason || '', position: m.position || 0 };
      } else {
        const market: AggregatedMarket = {
          id: m.slug,
          slug: m.slug,
          title: m.title,
          description: m.description,
          image: m.image,
          source: 'polymarket' as const,
          yesPrice: m.yesPrice || 0.5,
          noPrice: m.noPrice || 0.5,
          volume: m.volume24h || 0,
          volume24h: m.volume24h || 0,
          liquidity: m.liquidity || 0,
          status: 'active',
          endDate: m.endDate,
          category: m.category || 'Other',
          sourceData: {},
        };
        newItem = { type: 'market', market, reason: m.reason || '', position: m.position || 0 };
      }

      // Insert at the position where item was removed, or at the end
      setForYouFeedItems(prev => {
        const next = [...prev];
        const idx = Math.min(insertIndex, next.length);
        next.splice(idx, 0, newItem);
        return next;
      });
    } catch {
      // Silently fail — item was already removed
    }
  }

  // Fetch markets from watchlist (bookmarked markets) — single batch request
  async function fetchWatchingMarkets() {
    // Filter watchlist by current source
    const currentSource = effectiveSourceFilter;
    const sourceWatchlist = watchlist.filter(item => (item.source || 'polymarket') === currentSource);

    if (sourceWatchlist.length === 0) {
      setWatchingEvents([]);
      return;
    }

    try {
      setLoadingWatching(true);

      const slugs = sourceWatchlist.map(item => item.market_slug);

      // For Kalshi, fetch each market by ticker (fallback to event endpoint if 404)
      if (currentSource === 'kalshi') {
        const results = await Promise.all(slugs.map(async (slug) => {
          try {
            const ticker = slug.toUpperCase();
            // Try individual market first
            const res = await authFetch(`/api/markets/kalshi/${encodeURIComponent(ticker)}`);
            if (res.ok) {
              const data = await res.json();
              const m = data.market;
              if (m) {
                const event = aggregatedMarketToEvent({ ...m, source: 'kalshi' } as AggregatedMarket);
                event.slug = slug; // Preserve watchlist slug so isInWatchlist matches
                return event;
              }
            }
            // Fallback: slug might be an event ticker, not a market ticker
            const eventRes = await authFetch(`/api/markets/kalshi/event?ticker=${encodeURIComponent(ticker)}`);
            if (!eventRes.ok) return null;
            const eventData = await eventRes.json();
            // Response may be { event: {...} } or the event object directly
            const event = eventData.event || eventData;
            if (!event?.title) return null;
            // Build event from event data
            const markets = (event.markets || []).map((m: any) => ({
              id: `ks-${m.ticker}`,
              question: m.title,
              slug: m.ticker?.toLowerCase(),
              conditionId: '',
              outcomes: ['Yes', 'No'] as string[],
              outcomePrices: [m.yesPrice || 0, m.noPrice || 0],
              clobTokenIds: [] as string[],
              acceptingOrders: m.status === 'open',
              active: m.status === 'open',
              closed: m.status !== 'open',
            }));
            return {
              id: `ks-${event.eventTicker || event.ticker}`,
              slug: slug,
              title: event.title,
              description: event.subtitle || null,
              image: event.imageUrl || null,
              icon: null,
              endDate: event.closeTime,
              closed: event.status !== 'open',
              active: event.status === 'open',
              volume: event.volume || 0,
              markets: markets.length > 0 ? markets : [{ id: `ks-${ticker}`, question: event.title, slug, conditionId: '', outcomes: ['Yes', 'No'], outcomePrices: [event.yesPrice || 0, event.noPrice || 0], clobTokenIds: [], acceptingOrders: event.status === 'open', active: event.status === 'open', closed: event.status !== 'open' }],
              tags: [],
              source: 'kalshi' as MarketSource,
              sourceData: { kalshi: { eventTicker: event.ticker } },
            } as unknown as Event;
          } catch { return null; }
        }));
        setWatchingEvents(results.filter((e): e is Event => e !== null));
        return;
      }

      // For Predict, search by title or fetch by market ID
      if (currentSource === 'predict') {
        const results = await Promise.all(slugs.map(async (slug) => {
          try {
            // If slug is "predict-{id}", extract the title from watchlist for search
            const watchlistItem = sourceWatchlist.find(w => w.market_slug === slug);
            const searchTerm = watchlistItem?.market_title || slug;
            const res = await authFetch(`/api/markets/predict/search?q=${encodeURIComponent(searchTerm)}&limit=1`);
            if (!res.ok) return null;
            const data = await res.json();
            const m = (data.markets || [])[0];
            if (!m) return null;
            const event = aggregatedMarketToEvent({ ...m, source: 'predict', volume24h: m.volume24h ?? m.volume24hr ?? 0 } as AggregatedMarket);
            // Preserve original watchlist slug so isInWatchlist matches
            event.slug = slug;
            return event;
          } catch { return null; }
        }));
        setWatchingEvents(results.filter((e): e is Event => e !== null));
        return;
      }

      const response = await authFetch('/api/polymarket/events-batch', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ slugs }),
      });

      if (!response.ok) {
        setWatchingEvents([]);
        return;
      }

      const data = await response.json();
      const eventsMap: Record<string, any> = data.events || {};

      // Preserve watchlist order, filter out missing
      const validEvents = slugs
        .map(slug => eventsMap[slug])
        .filter((e): e is Event => e !== null && e !== undefined);

      // Sort by volume (highest first)
      validEvents.sort((a, b) => (b.volume || 0) - (a.volume || 0));

      setWatchingEvents(validEvents);
    } catch {
      setWatchingEvents([]);
    } finally {
      setLoadingWatching(false);
    }
  }

  // Parse JSON string fields in markets (outcomes, outcomePrices, clobTokenIds)
  // Gamma API returns these as JSON strings, but our components expect arrays
  // Defined at component scope so it can be reused by prefetch logic
  function parseEventMarkets(event: any) {
    if (!event.markets || !Array.isArray(event.markets)) return event;
    // Gamma API sub-markets don't include active/closed/acceptingOrders fields —
    // inherit from the parent event so EventCard doesn't treat them as closed
    const eventActive = event.active !== false;
    const eventClosed = event.closed === true;
    // /api/markets/events returns `volume24h`; the rest of the codebase
    // (and the list-view 24h Vol column) reads `volume24hr`. Normalise
    // here so swapping fetcher endpoints doesn't zero the column.
    const normalisedVolume24hr =
      typeof event.volume24hr === 'number'
        ? event.volume24hr
        : typeof event.volume24h === 'number'
          ? event.volume24h
          : parseFloat(event.volume24hr ?? event.volume24h ?? '0') || 0;
    return {
      ...event,
      volume24hr: normalisedVolume24hr,
      markets: event.markets.map((m: any) => ({
        ...m,
        active: m.active ?? eventActive,
        closed: m.closed ?? eventClosed,
        acceptingOrders: m.acceptingOrders ?? (eventActive && !eventClosed),
        outcomes: typeof m.outcomes === 'string' ? JSON.parse(m.outcomes) : (m.outcomes || ['Yes', 'No']),
        outcomePrices: typeof m.outcomePrices === 'string' ? JSON.parse(m.outcomePrices) : (m.outcomePrices || ['0.5', '0.5']),
        clobTokenIds: typeof m.clobTokenIds === 'string' ? JSON.parse(m.clobTokenIds) : (m.clobTokenIds || []),
      })),
    };
  }

  async function fetchEvents(reset = false, skipLoading = false) {
    // Guard against double fetches - skip if same params fetch started within 500ms.
    // CRITICAL: locale MUST be in this key. Same bug class as CLAUDE.md regla 9 —
    // without it, switching language within 500 ms of a recent fetch hits this
    // de-dupe guard with an identical key and silently blocks the new-locale
    // fetch. Symptom reported 2026-05-10: first EN→ES toggle on /markets/trending
    // sometimes left market titles in English until something else triggered
    // the effect (cat-switch, scroll, source toggle). Already fixed in
    // `buildPolymarketFetchKey` (the recent-fetch ref); this is the parallel
    // guard 200 lines lower in the same component that drifted.
    const now = Date.now();
    const fetchKey = [
      category,
      sortBy,
      filter,
      reset ? 'reset' : 'append',
      skipLoading ? 'background' : 'foreground',
      effectiveSourceFilter,
      activeSubcategory || 'all',
      activeWeatherDate || 'all',
      activeWeatherTempType || 'all',
      cryptoPrimaryFilter || 'all',
      cryptoSecondaryFilter || 'all',
      financePrimaryFilter || 'all',
      financeSecondaryFilter || 'all',
      electionsFilter || 'all',
      breakingSubcategory || 'all',
      locale,
    ].join('|');
    if (reset && fetchInProgressRef.current) {
      const [prevKey, prevTime] = fetchInProgressRef.current.split('|');
      if (prevKey === fetchKey && now - parseInt(prevTime, 10) < 500) {
        return; // Skip duplicate fetch within 500ms window
      }
    }
    if (reset) {
      fetchInProgressRef.current = `${fetchKey}|${now}`;
      markRecentPolymarketFetch();
    }

    // Increment version to invalidate any in-flight fetches
    const currentVersion = ++fetchVersionRef.current;

    // Cancel previous in-flight HTTP requests on a foreground reset (i.e.
    // the user clicked another category). Background refresh keeps the
    // existing controller so a silent refresh doesn't kill a primary fetch.
    if (reset && !skipLoading) {
      fetchAbortRef.current?.abort();
      fetchAbortRef.current = new AbortController();
    }
    const fetchSignal = fetchAbortRef.current?.signal;

    // Get cache key for this fetch
    const cacheKey = getCacheKey(category);

    // Background refresh = reset + skipLoading (show cache first, silently update)
    const isBackgroundRefresh = reset && skipLoading;

    // Helper to safely set events only if this fetch is still current
    // During background refresh, preserves order of existing events to prevent visible reordering
    const safeSetEvents = (updater: Event[] | ((prev: Event[]) => Event[])) => {
      if (currentVersion !== fetchVersionRef.current) return;

      if (isBackgroundRefresh) {
        setEvents(prev => {
          const incoming = typeof updater === 'function' ? updater(prev) : updater;
          // On error/empty during background refresh, keep showing cached data
          if (incoming.length === 0) return prev;
          if (prev.length === 0) return incoming;
          // Stable merge: update existing events in-place (preserving order),
          // append genuinely new events at the end, remove stale ones
          const newById = new Map(incoming.map((e: Event) => [e.id, e]));
          const updated = prev
            .map(e => newById.get(e.id) ?? e)
            .filter(e => newById.has(e.id));
          const existingIds = new Set(prev.map(e => e.id));
          const added = incoming.filter((e: Event) => !existingIds.has(e.id));
          return [...updated, ...added];
        });
      } else {
        setEvents(updater);
      }
    };

    try {
      if (reset) {
        // skipLoading: for background refresh, don't show loading state or clear events
        if (!skipLoading) {
          setLoading(true);
          setOffset(0);
          safeSetEvents([]); // Clear old results immediately to prevent stale data flash
        }
      } else {
        setLoadingMore(true);
      }

      const currentOffset = reset ? 0 : offset;

      // =========================================================================
      // BREAKING: Use Polymarket's biggest-movers endpoint (via backend cache)
      // Returns markets with highest 24h price change, including:
      // - history: Price points for sparkline charts
      // - currentPrice: Current market price
      // - livePriceChange: Real-time price change percentage
      // Backend fetches each category directly from Polymarket's API
      // =========================================================================
      // =========================================================================
      // DAILY: Recurring markets from Gamma API (recurrence=daily)
      // Sorted by volume24hr, paginated
      // =========================================================================
      if (category === "ending-soon") {
        try {
          const dailyParams = new URLSearchParams({
            limit: "32",
            offset: currentOffset.toString(),
          });
          const response = await fetchWithRetry(`/api/markets/daily?${dailyParams.toString()}`, { signal: fetchSignal });

          if (response.ok) {
            const data = await response.json();
            const rawEvents = Array.isArray(data.events) ? data.events : [];
            const dailyEvents = rawEvents.map(parseEventMarkets);

            if (reset) {
              safeSetEvents(dailyEvents);
            } else {
              safeSetEvents(prev => [...prev, ...dailyEvents]);
            }
            const newHasMore = data.hasMore ?? dailyEvents.length === 32;
            const newOffset = currentOffset + dailyEvents.length;
            setHasMore(newHasMore);
            setOffset(newOffset);

            if (reset) {
              setCategoryCache(cacheKey, {
                events: dailyEvents,
                offset: newOffset,
                hasMore: newHasMore,
                timestamp: Date.now(),
              });
            }
          } else if (reset) {
            safeSetEvents([]);
            setHasMore(false);
          }
        } catch {
          if (reset) {
            safeSetEvents([]);
            setHasMore(false);
          }
        }
        setLoading(false);
        setLoadingMore(false);
        if (reset) fetchInProgressRef.current = null;
        return;
      }

      if (category === "breaking") {
        try {
          const response = await fetchWithRetry(`/api/markets/breaking?limit=30&category=${breakingSubcategory}&locale=${locale}`, { signal: fetchSignal });
          if (!response.ok) {
            if (reset) {
              safeSetEvents([]);
              setHasMore(false);
            }
            setLoading(false);
            setLoadingMore(false);
            return;
          }
          const data = await response.json();
          const marketsData = (Array.isArray(data.markets) ? data.markets : [])
            .filter((m: any) => !m.closed && m.active !== false);

          // Transform markets to events format
          // Data from biggest-movers already has arrays parsed (not JSON strings)
          const eventsFromMarkets = marketsData.map((m: any) => {
            // Create the inner market object
            const innerMarket = {
              ...m,
              outcomes: m.outcomes || ['Yes', 'No'],
              outcomePrices: m.outcomePrices || [m.currentPrice?.toString() || '0.5', (1 - (m.currentPrice || 0.5)).toString()],
              clobTokenIds: m.clobTokenIds || [],
              title: m.question || m.title,
              active: true,  // Breaking markets are always active
              acceptingOrders: true,  // Always accepting orders
            };

            return {
              id: m.conditionId || m.slug || m.id,
              title: m.question || m.title,
              slug: m.parentEventSlug || m.events?.[0]?.slug || m.slug,
              marketSlug: m.slug,
              image: m.image,
              icon: m.icon || m.image,
              category: m.tags?.[0]?.label || m.category || 'Other',
              volume: typeof m.volume === 'number' ? m.volume : parseFloat(m.volume || '0'),
              volume24hr: typeof m.volume24hr === 'number' ? m.volume24hr : parseFloat(m.volume24hr || '0'),
              liquidity: typeof m.liquidity === 'number' ? m.liquidity : parseFloat(m.liquidity || '0'),
              endDate: m.endDate,
              active: m.active !== false,
              closed: m.closed === true,
              createdAt: m.createdAt,
              // Breaking-specific fields from biggest-movers
              oneDayPriceChange: m.oneDayPriceChange,
              currentPrice: m.currentPrice,
              livePriceChange: m.livePriceChange,
              history: m.history, // Price history for sparkline
              markets: [innerMarket],
              tags: m.tags || [],
            };
          });

          if (reset) {
            safeSetEvents(eventsFromMarkets);
          } else {
            safeSetEvents(prev => [...prev, ...eventsFromMarkets]);
          }
          setHasMore(false);
          setOffset(currentOffset + eventsFromMarkets.length);
        } catch {
          if (reset) {
            safeSetEvents([]);
            setHasMore(false);
          }
        }
        setLoading(false);
        setLoadingMore(false);
        if (reset) fetchInProgressRef.current = null;
        return;
      }

      // =========================================================================
      // NEW: Use cached backend first, then Gamma API for infinite scroll
      // Backend has ~100 events sorted by startDate
      // When cache is exhausted, fetch directly from Gamma API with pagination
      // =========================================================================
      if (category === "new") {
        try {
          // If a subcategory is selected, use backend proxy (caches Gamma API)
          if (activeSubcategory) {
            const eventsUrl = `/api/markets/events?tag_slug=${activeSubcategory}&order=startDate&ascending=false&limit=20&offset=${currentOffset}&view=${viewMode}`;
            const eventsResponse = await fetchWithRetry(eventsUrl, { signal: fetchSignal });

            if (eventsResponse.ok) {
              const data = await eventsResponse.json();
              const rawEvents = Array.isArray(data.markets) ? data.markets : [];
              const newEvents = rawEvents.map(parseEventMarkets);

              if (reset) {
                safeSetEvents(newEvents);
              } else {
                safeSetEvents(prev => [...prev, ...newEvents]);
              }

              const newHasMore = data.hasMore ?? rawEvents.length === 32;
              const newOffset = currentOffset + newEvents.length;
              setHasMore(newHasMore);
              setOffset(newOffset);

              // Save to cache
              if (reset) {
                setCategoryCache(cacheKey, {
                  events: newEvents,
                  offset: newOffset,
                  hasMore: newHasMore,
                  timestamp: Date.now(),
                });
              }
            } else {
              if (reset) {
                safeSetEvents([]);
                setHasMore(false);
              }
            }
            setLoading(false);
            setLoadingMore(false);
            if (reset) fetchInProgressRef.current = null;
            return;
          }

          // No tag filter - use backend cache (32 for grid alignment)
          const newParams = new URLSearchParams({
            limit: "32",
            offset: currentOffset.toString(),
            active: filter === "active" ? "true" : filter === "all" ? "false" : "true",
            closed: filter === "closed" ? "true" : "false",
            locale,
          });
          const backendResponse = await fetchWithRetry(`/api/markets/new?${newParams.toString()}`, { signal: fetchSignal });

          if (backendResponse.ok) {
            const data = await backendResponse.json();
            const rawEvents = Array.isArray(data.markets) ? data.markets : [];

            if (rawEvents.length > 0) {
              // Got data from backend cache
              const newEvents = rawEvents.map(parseEventMarkets);
              if (reset) {
                safeSetEvents(newEvents);
              } else {
                safeSetEvents(prev => [...prev, ...newEvents]);
              }
              // If backend has more OR we can still fetch from Gamma
              const newHasMore = data.hasMore ?? (currentOffset + rawEvents.length < 500);
              const newOffset = currentOffset + newEvents.length;
              setHasMore(newHasMore);
              setOffset(newOffset);

              // Save to cache and prefetch next batch
              if (reset) {
                setCategoryCache(cacheKey, {
                  events: newEvents,
                  offset: newOffset,
                  hasMore: newHasMore,
                  timestamp: Date.now(),
                });
                // Prefetch next batch in background
                if (newHasMore) {
                  setTimeout(() => prefetchNextBatch('new', newOffset), 2000);
                }
              }
            } else if (!data.hasMore && currentOffset > 0) {
              // Backend cache exhausted, fetch more via events proxy
              const eventsUrl = `/api/markets/events?order=startDate&ascending=false&limit=20&offset=${currentOffset}&view=${viewMode}`;
              const eventsResponse = await fetchWithRetry(eventsUrl);

              if (eventsResponse.ok) {
                const eventsData = await eventsResponse.json();
                const rawEvents = Array.isArray(eventsData.markets) ? eventsData.markets : [];
                const newEvents = rawEvents.map(parseEventMarkets);

                safeSetEvents(prev => [...prev, ...newEvents]);
                setHasMore(eventsData.hasMore ?? false);
                setOffset(currentOffset + newEvents.length);
              } else {
                setHasMore(false);
              }
            } else if (reset) {
              safeSetEvents([]);
              setHasMore(false);
            }
          } else if (reset) {
            safeSetEvents([]);
            setHasMore(false);
          }
        } catch {
          if (reset) {
            safeSetEvents([]);
            setHasMore(false);
          }
        }
        setLoading(false);
        setLoadingMore(false);
        if (reset) fetchInProgressRef.current = null;
        return;
      }

      // =========================================================================
      // TRENDING: Use cached backend first, then Gamma API for infinite scroll
      // Backend has featured events + volume-sorted cache (~500 events)
      // When a tag is selected, use the by-tag endpoint for server-side filtering
      // =========================================================================
      if (category === "trending") {
        try {
          let backendResponse;

          if (activeSubcategory) {
            // Use by-tag endpoint for server-side filtering
            const tagParams = new URLSearchParams({
              tag: activeSubcategory,
              limit: "32",
              offset: currentOffset.toString(),
              active: filter === "active" ? "true" : filter === "all" ? "false" : "true",
              closed: filter === "closed" ? "true" : "false",
              // Locale baked into the URL so browser/CDN cache keys
              // naturally differ EN ↔ ES; the proxy already injects
              // it from the cookie too, but having it on the URL
              // means flipping language doesn't get served a stale
              // EN response from the same cache slot.
              locale,
            });
            backendResponse = await fetchWithRetry(`/api/markets/trending/by-tag?${tagParams.toString()}`, { signal: fetchSignal });
          } else if (sortBy === "volume24hr") {
            // Default sort: use Gamma `/events/pagination` via our
            // /api/markets/events proxy. We previously read from the
            // pre-computed trending cache, but that path only enriches
            // a subset of events with `yesHistory` / 1h+24h price
            // change deltas (the sync worker's leader-token detection
            // skips multi-outcome events whose token isn't in the
            // priceHistoryMap). The list view's % change columns then
            // showed "–" for many big events. /events runs the full
            // sparkline enrichment per request, so every row gets the
            // 168 hourly history points the columns depend on.
            const gammaParams = new URLSearchParams({
              limit: "32",
              offset: currentOffset.toString(),
              order: "volume24hr",
              ascending: "false",
              active: filter === "active" ? "true" : filter === "all" ? "" : "true",
              archived: "false",
              closed: filter === "closed" ? "true" : "false",
              view: viewMode,
            });
            // Drop empty `active` so the proxy doesn't pass an empty
            // string to gamma.
            if (gammaParams.get("active") === "") gammaParams.delete("active");
            backendResponse = await fetchWithRetry(`/api/markets/events?${gammaParams.toString()}`, { signal: fetchSignal });
          } else {
            // Other sort options use Gamma API
            const gammaOrder = sortBy === "volume" ? "volume"
              : sortBy === "liquidity" ? "liquidity"
              : sortBy === "newest" ? "startDate"
              : sortBy === "endingSoon" ? "endDate"
              : sortBy === "competitive" ? "competitive"
              : "volume24hr";
            const ascending = sortBy === "endingSoon" ? "true" : "false";
            const gammaParams = new URLSearchParams({
              limit: "32",
              offset: currentOffset.toString(),
              order: gammaOrder,
              ascending,
              active: "true",
              archived: "false",
              closed: filter === "closed" ? "true" : "false",
              view: viewMode,
            });
            backendResponse = await fetchWithRetry(`/api/markets/events?${gammaParams.toString()}`, { signal: fetchSignal });
          }

          if (backendResponse.ok) {
            const data = await backendResponse.json();
            const rawEvents = Array.isArray(data.markets) ? data.markets : [];

            if (rawEvents.length > 0) {
              // Backend already filters sports/match events, just parse the data
              const trendingEvents = rawEvents.map(parseEventMarkets);

              if (reset) {
                safeSetEvents(trendingEvents);
              } else {
                safeSetEvents(prev => {
                  const existingIds = new Set(prev.map(e => e.id));
                  const unique = trendingEvents.filter((e: any) => !existingIds.has(e.id));
                  return [...prev, ...unique];
                });
              }

              const newHasMore = data.hasMore;
              const newOffset = currentOffset + rawEvents.length;
              setHasMore(newHasMore);
              setOffset(newOffset);

              // Save to cache and prefetch next batch
              if (reset) {
                setCategoryCache(cacheKey, {
                  events: trendingEvents,
                  offset: newOffset,
                  hasMore: newHasMore,
                  timestamp: Date.now(),
                });
                // Prefetch next batch in background
                if (newHasMore) {
                  setTimeout(() => prefetchNextBatch('trending', newOffset), 2000);
                }
              }
            } else if (reset) {
              safeSetEvents([]);
              setHasMore(false);
            }
          } else if (reset) {
            safeSetEvents([]);
            setHasMore(false);
          }
        } catch {
          if (reset) {
            safeSetEvents([]);
            setHasMore(false);
          }
        }
        setLoading(false);
        setLoadingMore(false);
        if (reset) fetchInProgressRef.current = null;
        return;
      }

      // =========================================================================
      // CRYPTO: Use Polymarket's internal crypto API for proper filtering
      // This API properly handles coin-specific and time-based filters
      // =========================================================================
      if (category === "crypto") {
        try {
          // Map filters to Polymarket crypto API parameters
          let apiCategory = "crypto";
          let timeRange: string | null = null;

          // Coin API mapping (only coins that have dedicated API categories)
          // LINK and BNB don't have dedicated categories, so we use "crypto" and filter locally
          const coinApiMap: Record<string, string> = {
            "btc": "bitcoin",
            "eth": "ethereum",
            "sol": "solana",
            "xrp": "xrp",
            "doge": "dogecoin",
            "mstr": "microstrategy",
          };

          // Check if primary filter is a coin (use scraped sidebar if available)
          const isCoinFilter = cryptoSidebarCoins.some(c => c.id === cryptoPrimaryFilter);
          // Check if secondary filter is a coin (for when primary is a timeframe)
          const isSecondaryCoin = cryptoSidebarCoins.some(c => c.id === cryptoSecondaryFilter);

          // Map time filters to API parameters
          const timeFilterMap: Record<string, { category: string; timeRange?: string }> = {
            "5m": { category: "5M" },
            "15m": { category: "15M" },
            "1h": { category: "1H" },
            "4h": { category: "4h" },
            "daily": { category: "today" },
            "weekly": { category: "crypto", timeRange: "weekly" },
            "monthly": { category: "crypto", timeRange: "monthly" },
            "yearly": { category: "crypto", timeRange: "yearly" },
            "pre-market": { category: "pre-market" },
            "etf": { category: "etf" },
          };

          if (isCoinFilter) {
            // Primary filter is a coin (sidebar)
            apiCategory = coinApiMap[cryptoPrimaryFilter] || cryptoPrimaryFilter;
          } else if (cryptoPrimaryFilter && cryptoPrimaryFilter !== "all") {
            // Primary is a timeframe — keep API params for this timeframe
            // Secondary coin pills filter client-side (PM does the same)
            const mapping = timeFilterMap[cryptoTimeFilter] || timeFilterMap[cryptoTimeFilter.toLowerCase()];
            if (mapping) {
              apiCategory = mapping.category;
              timeRange = mapping.timeRange || null;
            }
          }

          // Build the API URL
          const cryptoParams = new URLSearchParams({
            category: apiCategory,
            limit: "50",
            offset: currentOffset.toString(),
          });
          if (timeRange) {
            cryptoParams.set("timeRange", timeRange);
          }

          const response = await fetchWithRetry(`/api/markets/crypto?${cryptoParams.toString()}`);

          if (response.ok) {
            const data = await response.json();
            let rawEvents = Array.isArray(data.events) ? data.events : [];

            // For 4h filter, show only markets in the current 4-hour period AND today's date
            if (cryptoTimeFilter === "4h") {
              // Get current date and hour in ET (Eastern Time)
              const nowET = new Date(new Date().toLocaleString("en-US", { timeZone: "America/New_York" }));
              const hourET = nowET.getHours();
              const monthET = nowET.toLocaleString("en-US", { month: "long", timeZone: "America/New_York" });
              const dayET = nowET.getDate();

              // Determine current 4h period boundaries
              const periodStart = Math.floor(hourET / 4) * 4;
              const periodEnd = periodStart + 4;

              // Format period string to match title (e.g., "4:00AM-8:00AM" or "12:00PM-4:00PM")
              const formatHour = (h: number) => {
                const hour12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
                const ampm = h < 12 ? "AM" : "PM";
                return `${hour12}:00${ampm}`;
              };
              const periodStr = `${formatHour(periodStart)}-${formatHour(periodEnd === 24 ? 0 : periodEnd)}`;
              // Format date string (e.g., "February 7")
              const dateStr = `${monthET} ${dayET}`;

              rawEvents = rawEvents.filter((event: any) => {
                const title = (event.title || "").toUpperCase().replace(/\s+/g, '');
                // Match both the date AND period pattern in title
                const hasPeriod = title.includes(periodStr.toUpperCase().replace(/\s+/g, ''));
                const hasDate = title.includes(dateStr.toUpperCase().replace(/\s+/g, ''));
                return hasPeriod && hasDate;
              });
            }

            // Transform events to match expected format
            const newEvents = rawEvents.map((event: any) => ({
              ...event,
              markets: (event.markets || []).map((m: any) => ({
                ...m,
                outcomes: typeof m.outcomes === 'string' ? JSON.parse(m.outcomes) : (m.outcomes || ['Yes', 'No']),
                outcomePrices: typeof m.outcomePrices === 'string' ? JSON.parse(m.outcomePrices) : (m.outcomePrices || ['0.5', '0.5']),
                clobTokenIds: typeof m.clobTokenIds === 'string' ? JSON.parse(m.clobTokenIds) : (m.clobTokenIds || []),
              })),
            }));

            if (reset) {
              safeSetEvents(newEvents);
            } else {
              safeSetEvents(prev => [...prev, ...newEvents]);
            }
            const newHasMore = data.hasMore ?? newEvents.length === 50;
            const newOffset = currentOffset + newEvents.length;
            setHasMore(newHasMore);
            setOffset(newOffset);

            // Save to cache for instant tab switching
            if (reset) {
              setCategoryCache(cacheKey, {
                events: newEvents,
                offset: newOffset,
                hasMore: newHasMore,
                timestamp: Date.now(),
              });
            }
          } else if (reset) {
            safeSetEvents([]);
            setHasMore(false);
          }
        } catch {
          if (reset) {
            safeSetEvents([]);
            setHasMore(false);
          }
        }
        setLoading(false);
        setLoadingMore(false);
        if (reset) fetchInProgressRef.current = null;
        return;
      }

      // =========================================================================
      // SPORTS2: Rendered by Sports2View component (has its own data fetching)
      // Only for Polymarket — Kalshi has its own view, Predict uses normal grid
      // =========================================================================
      if (
        ((category === "sports" || category === "esports") && effectiveSourceFilter === "polymarket") ||
        // World Cup: WorldCupView self-fetches the full set (no pagination),
        // so the page must not also fetch/paginate this category.
        (category === "world-cup" && effectiveSourceFilter === "polymarket")
      ) {
        setLoading(false);
        setLoadingMore(false);
        if (reset) fetchInProgressRef.current = null;
        return;
      }

      // =========================================================================
      // FINANCE: Use Polymarket's internal Finance API for proper filtering
      // =========================================================================
      if (category === "finance") {
        try {
          // Get the filter from sidebar selection
          const sidebarItem = financeSidebarItems.find(s => s.id === financePrimaryFilter);
          const apiFilter = sidebarItem?.slug || '';

          // Build params — backend handles all filtering via gamma-api tag_slug
          const financeParams = new URLSearchParams({
            filter: apiFilter,
            limit: "32",
            offset: currentOffset.toString(),
          });

          // Pass secondary pill to backend for tag-based filtering (e.g. daily-close, up-or-down, nvda)
          if (financeSecondaryFilter && financeSecondaryFilter !== "all") {
            financeParams.set("pill", financeSecondaryFilter);
          }

          const response = await fetchWithRetry(`/api/markets/finance?${financeParams.toString()}`);

          if (response.ok) {
            const data = await response.json();
            const rawEvents = Array.isArray(data.events) ? data.events : [];

            const newEvents = rawEvents.map(parseEventMarkets);

            if (reset) {
              safeSetEvents(newEvents);
            } else {
              safeSetEvents((prev) => [...prev, ...newEvents]);
            }

            const newHasMore = data.hasMore ?? false;
            setHasMore(newHasMore);
            const newOffset = currentOffset + newEvents.length;
            setOffset(newOffset);

            // Save to cache for instant tab switching
            if (reset) {
              setCategoryCache(cacheKey, {
                events: newEvents,
                offset: newOffset,
                hasMore: newHasMore,
                timestamp: Date.now(),
              });
            }
          } else {
            if (reset) {
              safeSetEvents([]);
              setHasMore(false);
            }
          }
        } catch {
          if (reset) {
            safeSetEvents([]);
            setHasMore(false);
          }
        }
        setLoading(false);
        setLoadingMore(false);
        if (reset) fetchInProgressRef.current = null;
        return;
      }

      // =========================================================================
      // WEATHER-SCIENCE: Scrape from Polymarket's custom ["weather","markets"] query
      // Polymarket doesn't use standard tag_slug for this category
      // =========================================================================
      if (category === "weather-science" || category === "weather") {
        try {
          const weatherParams = new URLSearchParams({
            limit: "32",
            offset: currentOffset.toString(),
          });
          // Pass subcategory tag for filtering (e.g. "temperature", "earthquakes")
          if (activeSubcategory) {
            weatherParams.set("subcategory", activeSubcategory);
          }
          // Pass date filter for daily events (e.g. temperature by day)
          if (activeWeatherDate) {
            weatherParams.set("date", activeWeatherDate);
          }
          // High/Low chip inside Temperature subcategory
          if (activeSubcategory === "temperature" && activeWeatherTempType) {
            weatherParams.set("tempType", activeWeatherTempType);
          }

          const response = await fetchWithRetry(`/api/markets/weather?${weatherParams.toString()}`);

          if (response.ok) {
            const data = await response.json();
            const rawEvents = Array.isArray(data.events) ? data.events : [];
            const newEvents = rawEvents.map(parseEventMarkets);

            // Save subcategories from weather endpoint (derived from event tags)
            if (reset && Array.isArray(data.subcategories)) {
              const subs = data.subcategories.map((s: any) => ({
                id: s.slug,
                label: s.label?.charAt(0).toUpperCase() + s.label?.slice(1) || s.slug,
              }));
              setWeatherSubcategories(subs);
              try { sessionStorage.setItem('frenflow_weather_subcats', JSON.stringify(subs)); } catch {}
            }
            // Save date tabs (available when events have daily endDates)
            if (reset && Array.isArray(data.dateTabs) && data.dateTabs.length > 0) {
              setWeatherDateTabs(data.dateTabs);
              try { sessionStorage.setItem('frenflow_weather_dateTabs', JSON.stringify(data.dateTabs)); } catch {}
              // Default to tomorrow's date (like PM), or first available date with most events
              if (activeWeatherDate === null) {
                const tomorrow = new Date();
                tomorrow.setDate(tomorrow.getDate() + 1);
                const tomorrowStr = tomorrow.toISOString().split("T")[0];
                const defaultDate = data.dateTabs.find((dt: any) => dt.date === tomorrowStr)?.date
                  || data.dateTabs.reduce((a: any, b: any) => a.count > b.count ? a : b).date;
                setActiveWeatherDate(defaultDate);
              }
            }

            if (reset) {
              safeSetEvents(newEvents);
            } else {
              safeSetEvents((prev) => [...prev, ...newEvents]);
            }

            const newHasMore = data.hasMore ?? false;
            setHasMore(newHasMore);
            const newOffset = currentOffset + newEvents.length;
            setOffset(newOffset);

            if (reset) {
              setCategoryCache(cacheKey, {
                events: newEvents,
                offset: newOffset,
                hasMore: newHasMore,
                timestamp: Date.now(),
              });
            }
          } else {
            if (reset) {
              safeSetEvents([]);
              setHasMore(false);
            }
          }
        } catch {
          if (reset) {
            safeSetEvents([]);
            setHasMore(false);
          }
        }
        setLoading(false);
        setLoadingMore(false);
        if (reset) fetchInProgressRef.current = null;
        return;
      }

      // Build query params
      let order = sortBy === "volume" ? "volume" : sortBy === "volume24hr" ? "volume24hr" : "endDate";
      let ascending = "false";

      // Use 32 for grid alignment on most categories
      // Elections uses smaller limit (12) with Load More for better UX
      // All tag-based categories use 32 for grid alignment; elections uses 12 with Load More
      const effectiveLimit = category === "elections" ? 12 : 32;

      const params = new URLSearchParams({
        limit: effectiveLimit.toString(),
        offset: currentOffset.toString(),
        closed: filter === "closed" ? "true" : "false",
        active: filter === "active" ? "true" : filter === "all" ? "false" : "true",
        order,
        ascending,
      });

      // Map categories to Polymarket tag slugs (default when no subcategory selected)
      const categoryToTagSlug: Record<string, string> = {
        "crypto": "crypto",
        "economy": "economy",
        "politics": "politics",
        "elections": "world-elections",
        "macro": "macro-single",
        "fed-rates": "fed-rates",
        "trump": "trump-presidency",
        "geopolitics": "geopolitics",
        "culture": "pop-culture",
        "climate-science": "climate-science",
        "weather-science": "climate-science",
        "world": "world",
        "elon": "elon-musk",
        "finance": "finance",
        "tech": "tech",
        "world-cup": "fifa-world-cup",
      };

      // Get the active subcategory tag based on current category and filter
      const getActiveTagSlug = (): string => {
        // Dynamic subcategory from taxonomy takes priority
        if (activeSubcategory) {
          return activeSubcategory;
        }
        if (category === "crypto") {
          // Check if primary filter is a coin
          const isCoinFilter = cryptoSubcategories.coins.some(c => c.id === cryptoPrimaryFilter);
          if (isCoinFilter) {
            const coinTagMap: Record<string, string> = {
              "btc": "bitcoin",
              "eth": "ethereum",
              "sol": "solana",
              "xrp": "xrp",
              "doge": "dogecoin",
              "mstr": "microstrategy",
            };
            return coinTagMap[cryptoPrimaryFilter] || "crypto";
          }
          // Time filter - note: 4h/daily/weekly tags may not exist in Polymarket
          if (cryptoPrimaryFilter !== "all") {
            const timeSubcat = cryptoSubcategories.timeframes.find(t => t.id === cryptoPrimaryFilter);
            return timeSubcat?.tag || "crypto";
          }
          return "crypto";
        }
        // Elections uses subcategory tag (elections, macro, sports, fed-rates, trump)
        if (category === "elections") {
          const subcat = electionsSubcategories.find(s => s.id === electionsFilter);
          return subcat?.tag || "world-elections";
        }
        // Elon subcategory: tweets uses a different Polymarket tag
        if (category === "elon" && elonSubcategory === "tweets") {
          return "tweets-markets";
        }
        // Direct access to elections subcategories via /markets/macro, /markets/fed-rates, /markets/trump
        const directSubcat = electionsSubcategories.find(s => s.id === category);
        if (directSubcat) {
          return directSubcat.tag;
        }
        // Default: use mapped tag or slug directly (works for dynamic categories like "iran")
        return category ? (categoryToTagSlug[category] || category) : "";
      };

      const activeTagSlug = getActiveTagSlug();

      if (activeTagSlug) {
        params.set("tag_slug", activeTagSlug);
        // For crypto short-term markets, order by endDate
        if (category === "crypto" && ['15m', '1h', '4h'].includes(cryptoTimeFilter)) {
          params.set("order", "endDate");
          params.set("ascending", "true");
        }
        // For elections dashboard categories, use specific order from Polymarket
        const electionsDashboardCategories = ["elections", "macro", "fed-rates", "trump"];
        if (electionsDashboardCategories.includes(category || "")) {
          const subcat = electionsSubcategories.find(s => s.id === (category === "elections" ? electionsFilter : category));
          if (subcat && 'order' in subcat) {
            params.set("order", subcat.order as string);
            params.set("ascending", String(subcat.ascending));
          }
        }
      }

      // Source guard for the catch-all Polymarket path. Kalshi/Predict/HL
      // each have their own fetch effects that populate aggregatedMarkets;
      // when those are active, hitting /api/polymarket/markets here is
      // pure waste — the result is immediately overwritten by the
      // source-specific fetch. Chrome DevTools traced this as the
      // residual `/api/polymarket/markets?tag_slug=geopolitics` request
      // firing during a Polymarket → Kalshi switch.
      if (effectiveSourceFilter !== 'polymarket') {
        if (reset) fetchInProgressRef.current = null;
        setLoading(false);
        setLoadingMore(false);
        return;
      }

      const response = await fetchWithRetry(`/api/polymarket/markets?${params.toString()}`, { signal: fetchSignal });

      if (!response.ok) {
        throw new Error(t("common.fetchEventsError"));
      }

      const data: EventsResponse = await response.json();

      if (reset) {
        safeSetEvents(data.events);
      } else {
        safeSetEvents(prev => [...prev, ...data.events]);
      }

      const newHasMore = data.hasMore ?? data.events.length === effectiveLimit;
      const newOffset = currentOffset + data.events.length;
      setHasMore(newHasMore);
      setOffset(newOffset);

      // Save to cache for instant tab switching
      if (reset) {
        setCategoryCache(cacheKey, {
          events: data.events,
          offset: newOffset,
          hasMore: newHasMore,
          timestamp: Date.now(),
        });
      }
    } catch (err: any) {
      // Aborted fetches are expected when the user changes category mid-flight.
      // Don't surface them as user-facing errors and don't clear the version
      // guard either — the next fetchEvents call already owns the state.
      if (err?.name === 'AbortError' || err?.code === 20) {
        return;
      }
      setError(err.message || t("common.fetchEventsError"));
    } finally {
      setLoading(false);
      setLoadingMore(false);
      if (reset) fetchInProgressRef.current = null;
    }
  }

  // Determine if we're using global search results
  // When user searches with 2+ chars, we use results from the global search API
  // instead of filtering the category-specific events
  const isGlobalSearch = searchQuery.length >= 2;

  // Filter events by search query and category
  // If global search is active and we have results, use those (already filtered by API)
  // Otherwise, filter the local events
  const filteredEvents = (isGlobalSearch && globalSearchResults.length > 0 ? globalSearchResults : events).filter(event => {
    // For global search results, skip the search filter since API already filtered
    // For local events, apply the search filter
    // Use defensive check for title (breaking markets may have 'question' instead)
    const eventTitle = event.title || '';
    const matchesSearch = isGlobalSearch || eventTitle.toLowerCase().includes(searchQuery.toLowerCase());

    // When doing global search, skip category-specific filters
    // (results already come from all markets via the search API)
    if (!isGlobalSearch) {
      // Breaking / New / Trending / Ending Soon come pre-sorted and
      // pre-filtered by the backend for the category itself, so we
      // skip the per-category subcategory predicates below — but we
      // STILL need to apply the advanced filter overlay (categories
      // include/exclude, price range, days left, liquidity, volume,
      // etc.). When the user comes from /markets/sports → applies an
      // "Include Tech" filter → we redirect them here, so trending
      // must honour that overlay or the filter pill silently does
      // nothing. Bail early on search mismatch, otherwise fall
      // through to the advanced filter block at the bottom.
      const isBroadBackendCategory =
        category === "breaking" || category === "new" ||
        category === "trending" || category === "ending-soon";
      if (isBroadBackendCategory) {
        if (!matchesSearch) return false;
        // Skip the rest of the per-category narrowing — go straight
        // to the advanced filter section. We use a labelled flag so
        // the next `if (!isGlobalSearch)` blocks fall through.
      } else {

      // For "elon" category: Apply subcategory filter if elonMode is active
      if (category === "elon" && elonSubcategory !== "all") {
        const subcat = ELON_SUBCATEGORIES.find(s => s.id === elonSubcategory);
        if (subcat && subcat.pattern) {
          // Check event title AND all submarket questions
          const matchesTitle = subcat.pattern.test(eventTitle);
          const matchesSubmarket = event.markets?.some(
            (m: any) => subcat.pattern!.test(m.question || '') || subcat.pattern!.test(m.groupItemTitle || '')
          );
          if (!matchesTitle && !matchesSubmarket) {
            return false;
          }
        }
        // When using tweets-markets tag, also filter for Elon-related markets only
        if (elonSubcategory === "tweets") {
          const elonPattern = /elon|musk/i;
          const isElon = elonPattern.test(eventTitle) || event.markets?.some(
            (m: any) => elonPattern.test(m.question || '') || elonPattern.test(m.groupItemTitle || '')
          );
          if (!isElon) return false;
        }
      }
      } // end else (non-broad-category branch)
    }

    // Category filter
    // Note: Categories with tag_slug (economy, politics, sports, etc.) are already filtered by the API
    // So we only need local filtering for categories without a matching Polymarket tag
    // Skip this for global search since results come from all categories
    let matchesCategory = true;
    const categoriesWithTagSlug = ["crypto", "economy", "politics", "elections", "geopolitics", "culture", "climate-science", "weather-science", "world", "elon", "finance", "tech", "world-cup"];

    if (!isGlobalSearch && category && category !== "trending" && category !== "breaking" && category !== "new" && category !== "ending-soon" && !categoriesWithTagSlug.includes(category)) {
      // Map our category IDs to Polymarket categories (for categories without tag_slug)
      const categoryMap: Record<string, string[]> = {
        "finance": ["Finance"],
        "earnings": ["Business", "Earnings"],
        "tech": ["Technology", "Tech"],
        "world": ["World"],
      };

      const allowedCategories = categoryMap[category] || [category.charAt(0).toUpperCase() + category.slice(1)];

      // Keywords for categories that Polymarket doesn't categorize well
      const categoryKeywords: Record<string, string[]> = {
        "earnings": ["earnings", "revenue", "profit", "quarterly", "fiscal", "stock price", "ipo", "market cap"],
        "tech": ["technology", "tech", "ai", "software", "hardware", "startup"],
      };

      // Check if event has any market with matching category or if event tags match
      matchesCategory = (event.markets || []).some((market: any) =>
        allowedCategories.some(cat => market.category === cat)
      ) || event.tags?.some((tag: any) =>
        allowedCategories.some(cat => tag.label?.toLowerCase().includes(category))
      );

      // If still no match, check title/description for category keywords
      if (!matchesCategory && categoryKeywords[category]) {
        const titleLower = (event.title || '').toLowerCase();
        const descLower = (event.description || '').toLowerCase();
        matchesCategory = categoryKeywords[category].some(keyword =>
          titleLower.includes(keyword) || descLower.includes(keyword)
        );
      }
    }

    // Crypto subcategory filters (skip for global search)
    let matchesCryptoSubcategory = true;
    if (!isGlobalSearch && category === "crypto") {
      const eventTags = event.tags?.map((t: any) => typeof t === 'string' ? t : t.label) || [];
      const titleLower = (event.title || '').toLowerCase();
      const descLower = (event.description || '').toLowerCase();
      const combinedText = titleLower + ' ' + descLower;

      // Check if primary filter is a timeframe or a coin (use scraped sidebar for detection)
      const isPrimaryCoin = cryptoSidebarCoins.some(c => c.id === cryptoPrimaryFilter);
      const isPrimaryTimeframe = cryptoSidebarTimeframes.some(t => t.id === cryptoPrimaryFilter);

      // PRIMARY FILTER: Coin selected from sidebar (API already filters by category,
      // but verify client-side with word-boundary to avoid false positives like "sol" in "resolve")
      if (isPrimaryCoin && cryptoPrimaryFilter !== "all") {
        const coinFilter = cryptoSubcategories.coins.find(c => c.id === cryptoPrimaryFilter);
        if (coinFilter) {
          const matchByTag = eventTags.some((tag: string) =>
            coinFilter.tags.some(t => tag.toLowerCase() === t.toLowerCase())
          );
          const matchByText = coinFilter.tags.some(t => {
            const regex = new RegExp('\\b' + t.toLowerCase().replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '\\b');
            return regex.test(combinedText);
          });
          matchesCryptoSubcategory = matchByTag || matchByText;
        }
      }
      // PRIMARY FILTER: Timeframe selected (15M, 1H, 4H, Daily, Weekly, etc.)
      else if (isPrimaryTimeframe && cryptoPrimaryFilter !== "all") {
        // Timeframes with timeRange param (weekly, monthly, yearly) are already
        // filtered server-side by the API — no client-side tag check needed.
        // PM returns events that may not have the exact timeframe tag (e.g. "Bitcoin hit in 2026"
        // has tag "Yearly" but appears in monthly results). Trust the API.
        const timeRangeFilters = ["weekly", "monthly", "yearly"];
        if (timeRangeFilters.includes(cryptoPrimaryFilter)) {
          matchesCryptoSubcategory = true; // API already filtered
        } else {
          // For direct-category timeframes (5m, 15m, 1h, 4h, daily, pre-market, etf),
          // verify tag client-side as extra safety
          const timeSubcat = cryptoSubcategories.timeframes.find(t => t.id === cryptoPrimaryFilter);
          if (timeSubcat && timeSubcat.tag) {
            const hasTimeTag = eventTags.some((tag: string) =>
              tag?.toLowerCase() === timeSubcat.tag.toLowerCase()
            );
            matchesCryptoSubcategory = hasTimeTag;
          }
        }
      }

      // SECONDARY FILTER (above/below, up/down, coins for timeframes, ETF specifics)
      if (matchesCryptoSubcategory && cryptoSecondaryFilter !== "all") {
        const isSecondaryCoinFilter = cryptoSidebarCoins.some(c => c.id === cryptoSecondaryFilter);
        if (isSecondaryCoinFilter) {
          // Secondary is a coin pill within a timeframe (e.g. Monthly + BTC)
          // Filter client-side by coin keywords/tags (PM does the same)
          const coinDef = cryptoSubcategories.coins.find(c => c.id === cryptoSecondaryFilter);
          const scrapedCoin = cryptoSidebarCoins.find(c => c.id === cryptoSecondaryFilter);
          const coinTags = coinDef?.tags || scrapedCoin?.tags || [cryptoSecondaryFilter];
          const scrapedCoinKw = scrapedCrypto?.keywordMapping?.[cryptoSecondaryFilter];
          const allCoinKeywords = [...coinTags, ...(scrapedCoinKw || [])];
          // 1. Prefer exact tag match (most reliable, no false positives)
          const matchesByExactTag = eventTags.some((tag: string) =>
            allCoinKeywords.some(kw => tag.toLowerCase() === kw.toLowerCase())
          );
          // 2. Word-boundary match in text (avoids "sol" matching "resolve")
          const matchesByText = allCoinKeywords.some(kw => {
            const kwLower = kw.toLowerCase();
            const regex = new RegExp('\\b' + kwLower.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '\\b');
            return regex.test(combinedText);
          });
          matchesCryptoSubcategory = matchesByExactTag || matchesByText;
        } else {
          // Map pill slugs → PM event tags (primary method, most reliable)
          const slugToTag: Record<string, string[]> = {
            "updown": ["Up or Down"],
            "above": ["Multi Strikes"],  // PM tags "above/below" markets as "Multi Strikes"
            "price-range": ["Neg Risk"],  // PM tags price bracket markets as "Neg Risk"
            "hit-price": ["Hit Price"],
            "above-below": ["Multi Strikes"],
            "up-down": ["Up or Down"],
          };

          // 1. Try tag-based filtering first (most reliable)
          const expectedTags = slugToTag[cryptoSecondaryFilter];
          const matchesByTag = expectedTags && eventTags.some((tag: string) =>
            expectedTags.some(et => tag.toLowerCase() === et.toLowerCase())
          );

          // 2. Try scraped keyword mapping (with alias bridge)
          const keywordAliases: Record<string, string> = {
            "above-below": "above", "up-down": "updown",
          };
          const scrapedKeywords = scrapedCrypto?.keywordMapping?.[cryptoSecondaryFilter]
            || scrapedCrypto?.keywordMapping?.[keywordAliases[cryptoSecondaryFilter] || ''];
          const matchesByKeyword = scrapedKeywords && scrapedKeywords.length > 0
            && scrapedKeywords.some(kw =>
              combinedText.includes(kw.toLowerCase())
              || eventTags.some((tag: string) => tag.toLowerCase() === kw.toLowerCase())
            );

          // 3. Fallback to hardcoded keywords
          let matchesByHardcoded = false;
          if (!matchesByTag && !matchesByKeyword) {
            const marketType = cryptoSubcategories.marketTypes.find(m => m.id === cryptoSecondaryFilter);
            if (marketType && 'keywords' in marketType) {
              const keywords = (marketType as { id: string; label: string; keywords: string[] }).keywords;
              matchesByHardcoded = keywords.some(kw => combinedText.includes(kw.toLowerCase()));
            }
            const etfFilter = cryptoSubcategories.etfFilters.find(e => e.id === cryptoSecondaryFilter);
            if (etfFilter && 'keywords' in etfFilter) {
              const keywords = (etfFilter as { id: string; label: string; keywords: string[] }).keywords;
              matchesByHardcoded = keywords.some(kw => combinedText.includes(kw.toLowerCase()));
            }
          }

          matchesCryptoSubcategory = !!(matchesByTag || matchesByKeyword || matchesByHardcoded);
        }
      }
    }

    // Advanced filters
    let matchesAdvancedFilters = true;

    // Days left filter
    if (advancedFilters.daysLeft.min !== null || advancedFilters.daysLeft.max !== null) {
      const endDate = event.endDate ? new Date(event.endDate) : null;
      if (endDate) {
        const daysLeft = Math.ceil((endDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
        if (advancedFilters.daysLeft.min !== null && daysLeft < advancedFilters.daysLeft.min) {
          matchesAdvancedFilters = false;
        }
        if (advancedFilters.daysLeft.max !== null && daysLeft > advancedFilters.daysLeft.max) {
          matchesAdvancedFilters = false;
        }
      } else {
        matchesAdvancedFilters = false; // No end date, can't filter
      }
    }

    // Price range filter (check if any market has a price in range)
    if (matchesAdvancedFilters && (advancedFilters.priceRange.min !== null || advancedFilters.priceRange.max !== null)) {
      const hasMarketInPriceRange = (event.markets || []).some((market: any) => {
        const prices = (market.outcomePrices || []).map((p: any) =>
          typeof p === 'string' ? parseFloat(p) * 100 : (p || 0) * 100
        );
        const yesPrice = prices[0] || 50;
        const priceInRange =
          (advancedFilters.priceRange.min === null || yesPrice >= advancedFilters.priceRange.min) &&
          (advancedFilters.priceRange.max === null || yesPrice <= advancedFilters.priceRange.max);
        return priceInRange;
      });
      if (!hasMarketInPriceRange) matchesAdvancedFilters = false;
    }

    // Liquidity filter
    if (matchesAdvancedFilters && advancedFilters.liquidityMin !== null) {
      if (event.liquidity < advancedFilters.liquidityMin) {
        matchesAdvancedFilters = false;
      }
    }

    // 24h Volume filter
    if (matchesAdvancedFilters && advancedFilters.volume24hMin !== null) {
      if ((event.volume24hr || 0) < advancedFilters.volume24hMin) {
        matchesAdvancedFilters = false;
      }
    }

    // Spread filter (check if any market has spread below max).
    // Polymarket events expose bestBid/bestAsk per market but not a flat
    // `spread` field, so we derive it on the fly. Skip a market when
    // either side is missing rather than defaulting to 100% (which made
    // the entire filter exclude everything).
    if (matchesAdvancedFilters && advancedFilters.maxSpread !== null) {
      const hasLowSpread = (event.markets || []).some((market: any) => {
        let spread: number | null = null;
        if (typeof market.spread === 'number' && market.spread > 0) {
          spread = market.spread;
        } else {
          const bid = Number(market.bestBid);
          const ask = Number(market.bestAsk);
          if (Number.isFinite(bid) && Number.isFinite(ask) && ask > bid) {
            spread = (ask - bid) * 100; // % points (e.g. 0.012 → 1.2%)
          }
        }
        return spread !== null && spread <= advancedFilters.maxSpread!;
      });
      if (!hasLowSpread) matchesAdvancedFilters = false;
    }

    // Rewards only filter — `holdingRewardsEnabled` is the truthy field
    // we actually receive; `rewardsMinSize` is rarely populated and was
    // letting `null > 0` short-circuit. Treat it as a numeric only when
    // it's a positive number.
    if (matchesAdvancedFilters && advancedFilters.rewardsOnly) {
      const hasRewards = (event.markets || []).some((market: any) => {
        const rewardsSize = Number(market.rewardsMinSize);
        return market.holdingRewardsEnabled === true ||
          (Number.isFinite(rewardsSize) && rewardsSize > 0);
      });
      if (!hasRewards) matchesAdvancedFilters = false;
    }

    // Category include/exclude filter from advanced filters
    if (matchesAdvancedFilters && (advancedFilters.includedCategories.length > 0 || advancedFilters.excludedCategories.length > 0)) {
      // Get event categories from markets and tags
      const eventCategories: string[] = [];

      // Get categories from markets
      (event.markets || []).forEach((market: any) => {
        if (market.category) {
          eventCategories.push(market.category.toLowerCase());
        }
      });

      // Get categories from tags
      event.tags?.forEach((tag: any) => {
        const tagLabel = typeof tag === 'string' ? tag : tag.label;
        if (tagLabel) {
          eventCategories.push(tagLabel.toLowerCase());
        }
      });

      // Map our category IDs to possible Polymarket tag/category names.
      //
      // Aliases live in two flavours:
      //  - `aliases` matched as a substring (covers stems like "elect"
      //    matching both "Elections" and "Electoral").
      //  - `exact` matched whole-string only — used to disambiguate sports
      //    where a single tag like "Football" otherwise would match both
      //    NFL and Soccer filters at once.
      type Alias = { aliases: string[]; exact?: string[] };
      const categoryAliases: Record<string, Alias> = {
        "crypto":      { aliases: ["crypto", "cryptocurrency", "bitcoin", "ethereum"] },
        "politics":    { aliases: ["politic"] },
        "sports":      { aliases: ["sport"] },
        "finance":     { aliases: ["finance", "financial"] },
        "tech":        { aliases: ["tech", "technology"] },
        "culture":     { aliases: ["culture", "pop culture", "entertainment", "music", "movies", "tv"] },
        "world":       { aliases: ["world"] },
        "economy":     { aliases: ["economy", "economic", "macro"] },
        "elections":   { aliases: ["election", "vote"] },
        "geopolitics": { aliases: ["geopolitic"] },
        "earnings":    { aliases: ["earnings", "earnings season"] },
        "weather":     { aliases: ["weather"] },
        "mentions":    { aliases: ["mention"] },
        "nba":         { aliases: ["nba", "basketball"] },
        "nfl":         { aliases: ["nfl", "american football"], exact: ["football"] }, // collide-safe
        "soccer":      { aliases: ["soccer", "fútbol"], exact: ["football"] },          // collide-safe
        "mlb":         { aliases: ["mlb", "baseball"] },
      };

      const eventMatchesCategory = (categoryId: string): boolean => {
        const cfg = categoryAliases[categoryId] || { aliases: [categoryId] };
        return eventCategories.some(ec => {
          // Substring match for the regular aliases.
          if (cfg.aliases.some(a => ec.includes(a))) return true;
          // Exact-match fall-back so collide-safe sports tags only count
          // when the tag really is "football", not "footballer" etc.
          if (cfg.exact && cfg.exact.includes(ec)) return true;
          return false;
        });
      };

      // If included categories are set, event must match at least one (OR) or all (AND)
      if (advancedFilters.includedCategories.length > 0) {
        if (advancedFilters.categoryMode === "and") {
          // Must match ALL included categories
          const matchesAll = advancedFilters.includedCategories.every(cat => eventMatchesCategory(cat));
          if (!matchesAll) matchesAdvancedFilters = false;
        } else {
          // Must match at least ONE included category (OR mode)
          const matchesAny = advancedFilters.includedCategories.some(cat => eventMatchesCategory(cat));
          if (!matchesAny) matchesAdvancedFilters = false;
        }
      }

      // If excluded categories are set, event must NOT match any of them
      if (matchesAdvancedFilters && advancedFilters.excludedCategories.length > 0) {
        const matchesExcluded = advancedFilters.excludedCategories.some(cat => eventMatchesCategory(cat));
        if (matchesExcluded) matchesAdvancedFilters = false;
      }
    }

    return matchesSearch && matchesCategory && matchesCryptoSubcategory && matchesAdvancedFilters;
  }).sort((a, b) => {
    // IMPORTANT: "breaking" comes pre-sorted from backend (by price change), preserve order!
    // "trending" and "new" are also pre-sorted from backend but allow user-initiated re-sorts
    // via advanced filters (line ~4753) or list view column headers (line ~4780)
    if (category === "breaking") {
      return 0;
    }

    // For crypto short-term markets, always sort by endDate (soonest first) to show LIVE markets first
    if (category === "crypto" && ['15m', '1h', '4h'].includes(cryptoTimeFilter)) {
      const dateA = a.endDate ? new Date(a.endDate).getTime() : Infinity;
      const dateB = b.endDate ? new Date(b.endDate).getTime() : Infinity;
      return dateA - dateB;
    }

    // Apply advanced filters sort if set
    if (advancedFilters.sortBy) {
      let comparison = 0;
      switch (advancedFilters.sortBy) {
        case "volume24hr":
          comparison = (a.volume24hr || 0) - (b.volume24hr || 0);
          break;
        case "volume":
          comparison = (a.volume || 0) - (b.volume || 0);
          break;
        case "liquidity":
          comparison = (a.liquidity || 0) - (b.liquidity || 0);
          break;
        case "endDate":
          const dateA = a.endDate ? new Date(a.endDate).getTime() : Infinity;
          const dateB = b.endDate ? new Date(b.endDate).getTime() : Infinity;
          comparison = dateA - dateB;
          break;
        case "price":
          // Sort by the first market's YES price
          const priceA = a.markets?.[0]?.outcomePrices?.[0] || 0;
          const priceB = b.markets?.[0]?.outcomePrices?.[0] || 0;
          comparison = priceA - priceB;
          break;
      }
      return advancedFilters.sortOrder === "asc" ? comparison : -comparison;
    }

    // Apply table sort in list view if no advanced sort
    if (viewMode !== "list") return 0;

    let comparison = 0;
    // Per-event change values: null sorts last regardless of order so
    // markets without data don't pollute the top of the leaderboard.
    const changeCmp = (key: '1h' | '24h' | '7d') => {
      const av = getEventPriceChange(a, key);
      const bv = getEventPriceChange(b, key);
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      return av - bv;
    };
    switch (tableSortBy) {
      case "market":
        comparison = a.title.localeCompare(b.title);
        break;
      case "volume24hr":
        comparison = (a.volume24hr || 0) - (b.volume24hr || 0);
        break;
      case "volume":
        comparison = (a.volume || 0) - (b.volume || 0);
        break;
      case "liquidity":
        comparison = (a.liquidity || 0) - (b.liquidity || 0);
        break;
      case "expires":
        const dateA = a.endDate ? new Date(a.endDate).getTime() : 0;
        const dateB = b.endDate ? new Date(b.endDate).getTime() : 0;
        comparison = dateA - dateB;
        break;
      case "change1h":
        comparison = changeCmp('1h');
        break;
      case "change24h":
        comparison = changeCmp('24h');
        break;
      case "change7d":
        comparison = changeCmp('7d');
        break;
    }
    return tableSortOrder === "asc" ? comparison : -comparison;
  });

  // Collect all markets from visible events for real-time price streaming
  // Filter out markets without valid clobTokenIds to avoid 400 errors on prices/batch
  const allMarkets = useMemo(() => {
    const markets: any[] = [];
    filteredEvents.forEach(event => {
      (event.markets || []).forEach((market: any) => {
        // Only include markets with valid clobTokenIds (non-empty array with non-empty string)
        if (market.clobTokenIds?.length > 0 && market.clobTokenIds[0]?.trim()) {
          markets.push(market);
        }
      });
    });
    return markets;
  }, [filteredEvents]);

  // Extract all token IDs for batch polling via ListPriceProvider
  // This replaces per-card WebSocket subscriptions with a single batch poll every 5s
  const allTokenIds = useMemo(() => {
    const ids: string[] = [];
    for (const market of allMarkets) {
      if (market.clobTokenIds) {
        for (const id of market.clobTokenIds) {
          if (id && typeof id === 'string' && id.trim()) ids.push(id);
        }
      }
    }
    return ids;
  }, [allMarkets]);

  // Keep useRealtimeMarketPrice for internal MarketsPageContent usage
  // (table view sort, quick-trade panel, etc.) — EventCards use ListPriceContext instead
  const { getMarketPrice: getRealtimePrice, getBuyPrices, lastUpdate } = useRealtimeMarketPrice({
    markets: allMarkets,
    enabled: effectiveSourceFilter === 'polymarket',
  });
  void lastUpdate;

  // Rewards lookup for the table list view. One condition per event (the
  // primary/lead market); batches into the CLOB-direct proxy (`sponsored=true`)
  // so each row can render the gift icon + tooltip. Capped at 50 per chunk by
  // the hook to stay under CLOB's batch limit.
  const listRewardsConditionIds = useMemo(() => {
    if (effectiveSourceFilter !== 'polymarket') return [];
    const ids = new Set<string>();
    for (const ev of filteredEvents) {
      const primary = ((ev.markets && ev.markets[0] && (ev.markets[0] as any).conditionId)
        || (ev as any).sourceData?.polymarket?.conditionId) as string | undefined;
      if (primary && /^0x[a-fA-F0-9]{64}$/.test(primary)) ids.add(primary);
    }
    return Array.from(ids).slice(0, 50);
  }, [filteredEvents, effectiveSourceFilter]);
  const { data: listRewardsMap } = useMarketsRewardsParams(
    listRewardsConditionIds,
    effectiveSourceFilter === 'polymarket' && listRewardsConditionIds.length > 0,
  );

  // Loading skeleton component (used inline, not as early return).
  // Matches EventCard's structure — header (image + title) at p-3.5, a
  // trading block that flex-grows to fill (2 outcome rows for binary /
  // 3 for multi-outcome), and a thin footer row. Height is tuned to the
  // real average (~230px) so replacing the skeleton with content doesn't
  // jump the layout when data arrives.
  // MarketCardSkeleton + LoadingSkeleton — see ./components/LoadingSkeletons.tsx

  // Show loading skeleton only in content area (keep filters visible)
  // Exclude for-you, bookmarks, and breaking categories (they have their own loading states)
  // For Kalshi/Predict, only check loadingAggregated (not main loading state which is for Polymarket)
  const isAggregatedSource = effectiveSourceFilter === 'kalshi' || effectiveSourceFilter === 'predict' || effectiveSourceFilter === 'hyperliquid';
  const showContentLoading = (isAggregatedSource ? (loadingAggregated || !hasFetchedAggregated) : (loading || loadingAggregated)) && category !== 'for-you' && category !== 'bookmarks' && category !== 'breaking' && !['macro', 'fed-rates', 'trump'].includes(category || '');

  if (error) {
    return (
      <div className="min-h-screen">
        <div className="w-full">
          <div className="text-center py-12">
            <p className="text-red-500 dark:text-red-400 mb-4 font-satoshi">{error}</p>
            <button
              onClick={() => fetchEvents(true)}
              className="px-4 py-2 bg-gray-900 dark:bg-white text-white dark:text-black rounded-[5px] font-inter-tight font-bold transition-colors hover:bg-gray-800 dark:hover:bg-gray-100"
            >
              {t("common.tryAgain")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <ListPriceProvider tokenIds={effectiveSourceFilter === 'polymarket' ? allTokenIds : []} source={effectiveSourceFilter}>
    <div className="min-h-screen">
      {/* Category Filter Tabs - NEON LIME REDESIGN - FIXED */}
      {/*
        Category bar — `padding-left` matches the sidebar width so cats
        don't render under the sidebar. Same pattern as the desktop
        header at AppLayoutWithSidebar.tsx:1320: while pre-hydration
        the inline CSS in layout.tsx targets `[data-cat-bar]:not([data-hydrated])`
        with the html root's `data-sidebar-collapsed` attribute (already
        set by the head inline script from localStorage). Once React
        hydrates we swap to Tailwind classes that read the live
        `isCollapsed` state. Without the gate, SSR shipped pl-20
        unconditionally and any user with the sidebar expanded saw a
        176 px shift on hydration — measured at 0.081 CLS, the
        single largest contributor to /markets idle CLS.
      */}
      <div
        data-cat-bar
        {...(sidebarHydrated ? { 'data-hydrated': '' } : {})}
        className={`fixed top-12 left-0 right-0 z-30 bg-white/95 dark:bg-[#1c1c1e]/95 border-b border-white/20 dark:border-[#1A1A1A] lg:backdrop-blur-2xl lg:backdrop-saturate-[1.8] lg:bg-gray-50/70 lg:dark:bg-[#0f0f12]/40 lg:border-gray-200/20 lg:dark:border-white/[0.04] transition-[top] duration-300 mobile-cat-bar lg:top-14 lg:z-[90] ${sidebarHydrated ? (isCollapsed ? 'lg:pl-20' : 'lg:pl-64') : ''}`}
      >
        <div className="w-full px-4 lg:px-6 py-2 lg:py-2">
          <HorizontalScroll
            ref={categoriesScrollRef}
            className="cursor-grab active:cursor-grabbing select-none"
            fadeFrom="bg-white dark:bg-[#1c1c1e] lg:bg-gray-50 lg:dark:bg-[#0f0f12]"
          >
            <div
              className="flex gap-1.5 lg:gap-2 pb-0.5"
              role="tablist"
              aria-label="Market categories"
              onDragStart={(e) => e.preventDefault()}
            >
              {categories.map((cat) => {
                const IconComponent = cat.icon;
                return (
                  <Link
                    key={cat.id}
                    href={sourceUrl(`/markets/${cat.id}`, source)}
                    role="tab"
                    aria-selected={category === cat.id}
                    data-category-id={cat.id}
                    prefetch={false}
                    draggable={false}
                    // Hover-triggered prefetch for non-top-3 categories.
                    // Top-3 (trending / breaking / new) are pre-warmed at
                    // idle (effect above); the rest fetch on intent. Hover
                    // → click is typically 200-500 ms — enough cover for
                    // most backend round-trips so the click still hits
                    // cache. No-op on mobile (no hover); those users get
                    // the cold path with skeleton, same as before.
                    onMouseEnter={() => { void prefetchCategoryEvents(cat.id); }}
                    onFocus={() => { void prefetchCategoryEvents(cat.id); }}
                    onClick={(e) => {
                      // SPA navigation: pushState keeps component mounted (no RSC re-render)
                      e.preventDefault();
                      if (cat.id === category) return;
                      window.history.pushState({}, '', sourceUrl(`/markets/${cat.id}`, source));
                      // Non-PM sources (kalshi/predict/hyperliquid) renderizan desde
                      // aggregatedMarkets vía fetchAggregatedMarkets, NO desde el estado
                      // PM `events`/cache de abajo. El main fetch effect no re-dispara
                      // de forma fiable en estos switches client-side (timing de la
                      // transición), así que la grid quedaba stale hasta un refresh
                      // (bug repro 2026-06-11 en Predict). Disparamos el fetch explícito
                      // con la categoría NUEVA (categoryOverride evita el closure viejo
                      // de `category`, que aún no commiteó).
                      if (effectiveSourceFilter !== 'polymarket') {
                        setCategory(cat.id);
                        fetchAggregatedMarkets(
                          effectiveSourceFilter,
                          effectiveSourceFilter === 'kalshi' ? kalshiSortBy : sortBy,
                          false,
                          cat.id,
                        );
                        return;
                      }
                      // Cache-aware swap. The May-10 fix clear-then-transition
                      // pattern was getting eaten by React 18: setEvents([])
                      // outside startTransition is urgent, but with a transition
                      // pending in the same handler React keeps the previous
                      // render visible until the new one is ready — so users
                      // saw the previous category's cards lingering before
                      // the new ones swapped in (no skeleton in between).
                      // buildDefaultCacheKey matches what the all-categories +
                      // SSR populators write to, and subcategory filters reset
                      // to "all" on every category click, so the default key
                      // is the right thing to read here.
                      const newKey = buildDefaultCacheKey(cat.id);
                      const cached = getCachedCategory(newKey);
                      const hit = !!cached && isCacheValid(newKey);
                      // 2026-05-29 — reverted the "skeleton-clear + transition
                      // on hit" experiment: a DevTools re-measure on prod showed
                      // it did NOT reduce the freeze (INP ~730ms, processing
                      // still ~690ms) because the flushSync skeleton-clear itself
                      // forces a full synchronous re-render of this monolithic
                      // component — and it added a skeleton flash on hits. The
                      // real bottleneck is that monolithic synchronous render;
                      // the proper fix is to decompose/memoize/virtualize the
                      // grid so a category setState doesn't re-render the whole
                      // tree (tracked separately). Restored the instant-swap.
                      if (hit && cached) {
                        // Hit: swap events + category atomically in a single
                        // urgent commit. flushSync forces the new render before
                        // the handler returns so React can't fall back to the
                        // previous frame.
                        flushSync(() => {
                          setEvents(cached.events);
                          setOffset(cached.offset);
                          setHasMore(cached.hasMore);
                          setLoading(false);
                          setCategory(cat.id);
                          setCryptoPrimaryFilter("all");
                          setCryptoSecondaryFilter("all");
                          setElectionsFilter("elections");
                        });
                      } else {
                        // Miss (cold category): force the skeleton in before the
                        // transition so the old cards don't linger.
                        flushSync(() => {
                          setEvents([]);
                          setLoading(true);
                        });
                        startTransition(() => {
                          setCategory(cat.id);
                          setCryptoPrimaryFilter("all");
                          setCryptoSecondaryFilter("all");
                          setElectionsFilter("elections");
                        });
                      }
                    }}
                    className={`flex items-center gap-1.5 lg:gap-2 px-3 lg:px-4 py-[7px] lg:py-2 rounded-[5px] text-[11px] lg:text-xs font-semibold whitespace-nowrap transition-colors duration-200 ${
                      category === cat.id
                        ? "bg-gray-900 dark:bg-white text-white dark:text-gray-900"
                        : "bg-black/[0.04] dark:bg-white/[0.06] text-gray-600 dark:text-gray-400 hover:bg-black/[0.12] dark:hover:bg-white/[0.14]"
                    }`}
                  >
                    <IconComponent className={`w-3.5 h-3.5 lg:w-3.5 lg:h-3.5 ${cat.id === 'world-cup' && category !== cat.id ? 'text-amber-500' : ''}`} />
                    <span>{tCategory(cat.id) || cat.label}</span>
                  </Link>
                );
              })}
            </div>
          </HorizontalScroll>
        </div>

        {/* Elections Subcategories - Hidden: the Elections section is no
            longer maintained. /markets/elections still resolves as a
            dynamic category for back-compat, but the subcategory bar
            (Macro / Fed Rates / Trump) is no longer rendered. */}
        {false && category === "elections" && (
          <div className="mobile-subcats w-full px-4 lg:px-6 py-1.5 lg:py-2 border-t border-black/[0.04] dark:border-white/[0.04]">
            <HorizontalScroll fadeFrom="bg-white dark:bg-[#1c1c1e] lg:bg-gray-50 lg:dark:bg-[#0f0f12]">
              <div className="flex gap-1.5 lg:gap-2" role="tablist" aria-label="Elections subcategories">
                {electionsSubcategories.map((sub) => {
                  const isActive = electionsFilter === sub.id;
                  return (
                    <button
                      key={sub.id}
                      type="button"
                      role="tab"
                      aria-selected={isActive}
                      onClick={() => {
                        setElectionsFilter(sub.id);
                        window.history.pushState({}, '', sourceUrl(sub.id === "elections" ? "/markets/elections" : `/markets/${sub.id}`, source));
                      }}
                      className={`flex items-center gap-1 px-3 lg:px-4 py-[6px] lg:py-2 rounded-[5px] text-[11px] lg:text-xs font-semibold whitespace-nowrap transition-colors ${
                        isActive
                          ? "bg-gray-900 dark:bg-white text-white dark:text-black"
                          : "bg-black/[0.04] dark:bg-white/[0.06] text-gray-600 dark:text-gray-400 hover:bg-black/[0.12] dark:hover:bg-white/[0.14]"
                      }`}
                    >
                      {sub.iconSrc ? (
                        <CdnImage src={sub.iconSrc} alt="" width={13} height={13} className={`w-[13px] h-[13px] rounded-sm object-cover ${
                          isActive
                            ? "brightness-0 invert dark:invert-0"
                            : "brightness-0 opacity-55 dark:invert"
                        }`} />
                      ) : sub.Icon ? (
                        <sub.Icon size={13} />
                      ) : null}
                      {tCategory(sub.id) || sub.label}
                    </button>
                  );
                })}
              </div>
            </HorizontalScroll>
          </div>
        )}

        {/* Polymarket Subcategories — mobile only, inside fixed bar */}
        {effectiveSourceFilter === 'polymarket' && currentSubcategories.length > 0 && category !== 'crypto' && category !== 'finance' && (
          <div className="mobile-subcats lg:hidden w-full px-4 py-1.5 border-t border-black/[0.04] dark:border-white/[0.04]">
            <HorizontalScroll
              ref={subcategoriesScrollRef}
              className="flex items-center gap-1.5 cursor-grab active:cursor-grabbing select-none"
              fadeFrom="bg-white dark:bg-[#1c1c1e]"
              role="tablist"
              ariaLabel="Polymarket subcategories"
            >
              <button
                type="button"
                role="tab"
                aria-selected={activeSubcategory === null}
                onClick={() => changeSubcategory(null, 'right')}
                className={`px-2.5 py-1 rounded-[5px] text-[11px] font-semibold transition-colors whitespace-nowrap flex-shrink-0 ${
                  activeSubcategory === null
                    ? "bg-gray-900 dark:bg-white text-white dark:text-black"
                    : "bg-black/[0.04] dark:bg-white/[0.06] text-gray-600 dark:text-gray-400 hover:bg-black/[0.12] dark:hover:bg-white/[0.14]"
                }`}
              >
                {t("common.all")}
              </button>
              {currentSubcategories.map((sub) => (
                <button
                  key={sub.id}
                  type="button"
                  role="tab"
                  aria-selected={activeSubcategory === sub.id}
                  onClick={() => changeSubcategory(sub.id === activeSubcategory ? null : sub.id)}
                  className={`px-2.5 py-1 rounded-[5px] text-[11px] font-semibold transition-colors whitespace-nowrap flex-shrink-0 ${
                    activeSubcategory === sub.id
                      ? "bg-gray-900 dark:bg-white text-white dark:text-black"
                      : "bg-black/[0.04] dark:bg-white/[0.06] text-gray-600 dark:text-gray-400 hover:bg-black/[0.12] dark:hover:bg-white/[0.14]"
                  }`}
                >
                  {tCategory(sub.id) || sub.label}
                </button>
              ))}
            </HorizontalScroll>
          </div>
        )}


        {/* Kalshi Subcategories — mobile only, inside fixed bar */}
        {effectiveSourceFilter === 'kalshi' && kalshiSubcategories.length > 0 && category !== 'trending' && category !== 'crypto' && category !== 'sports' && category !== 'commodities' && (
          <div className="mobile-subcats lg:hidden w-full px-4 py-1.5 border-t border-black/[0.04] dark:border-white/[0.04]">
            <HorizontalScroll
              ref={kalshiSubcatsScrollRef}
              className="flex items-center gap-1.5 cursor-grab active:cursor-grabbing select-none"
              fadeFrom="bg-white dark:bg-[#1c1c1e]"
              role="tablist"
              ariaLabel="Kalshi subcategories"
            >
              <button
                type="button"
                role="tab"
                aria-selected={activeKalshiSubcategory === null}
                onClick={() => startTransition(() => setActiveKalshiSubcategory(null))}
                className={`px-2.5 py-1 rounded-[5px] text-[11px] font-semibold transition-colors whitespace-nowrap flex-shrink-0 ${
                  activeKalshiSubcategory === null
                    ? "bg-gray-900 dark:bg-white text-white dark:text-black"
                    : "bg-black/[0.04] dark:bg-white/[0.06] text-gray-600 dark:text-gray-400 hover:bg-black/[0.12] dark:hover:bg-white/[0.14]"
                }`}
              >
                {t("kalshiSort.all")}
              </button>
              {kalshiSubcategories.map((sub) => (
                <button
                  key={sub.name}
                  type="button"
                  role="tab"
                  aria-selected={activeKalshiSubcategory === sub.name}
                  onClick={() => startTransition(() => setActiveKalshiSubcategory(activeKalshiSubcategory === sub.name ? null : sub.name))}
                  className={`px-2.5 py-1 rounded-[5px] text-[11px] font-semibold transition-colors whitespace-nowrap flex-shrink-0 ${
                    activeKalshiSubcategory === sub.name
                      ? "bg-gray-900 dark:bg-white text-white dark:text-black"
                      : "bg-black/[0.04] dark:bg-white/[0.06] text-gray-600 dark:text-gray-400 hover:bg-black/[0.12] dark:hover:bg-white/[0.14]"
                  }`}
                >
                  {tKalshiSubcat(sub.name)}
                </button>
              ))}
            </HorizontalScroll>
          </div>
        )}

        {/* Elon Subcategories — mobile only, inside fixed bar */}
        {category === "elon" && (
          <div className="mobile-subcats lg:hidden w-full px-4 py-1.5 border-t border-black/[0.04] dark:border-white/[0.04]">
            <HorizontalScroll
              className="flex items-center gap-1.5"
              fadeFrom="bg-white dark:bg-[#1c1c1e]"
              role="tablist"
              ariaLabel="Elon subcategories"
            >
              {ELON_SUBCATEGORIES.map((sub) => (
                <button
                  key={sub.id}
                  type="button"
                  role="tab"
                  aria-selected={elonSubcategory === sub.id}
                  onClick={() => startTransition(() => setElonSubcategory(sub.id))}
                  className={`px-2.5 py-1 rounded-[5px] text-[11px] font-semibold transition-colors whitespace-nowrap flex-shrink-0 ${
                    elonSubcategory === sub.id
                      ? "bg-gray-900 dark:bg-white text-white dark:text-black"
                      : "bg-black/[0.04] dark:bg-white/[0.06] text-gray-600 dark:text-gray-400 hover:bg-black/[0.12] dark:hover:bg-white/[0.14]"
                  }`}
                >
                  {t(`elon.subcat.${sub.id}` as any)}
                </button>
              ))}
            </HorizontalScroll>
          </div>
        )}

        {/* Crypto Primary Filters — mobile only, inside fixed bar */}
        {category === "crypto" && effectiveSourceFilter === 'polymarket' && (
          <div className="mobile-subcats lg:hidden w-full px-4 py-1.5 border-t border-black/[0.04] dark:border-white/[0.04]">
            <HorizontalScroll
              className="flex items-center gap-1.5"
              fadeFrom="bg-white dark:bg-[#1c1c1e]"
            >
              {cryptoSidebarTimeframes.slice(0, 6).map((tf) => (
                <button
                  key={tf.id}
                  onClick={() => { startTransition(() => { setCryptoPrimaryFilter(tf.id); setCryptoSecondaryFilter("all"); }); }}
                  className={`px-2.5 py-1 rounded-[5px] text-[11px] font-semibold transition-colors whitespace-nowrap flex-shrink-0 ${
                    cryptoPrimaryFilter === tf.id
                      ? "bg-gray-900 dark:bg-white text-white dark:text-black"
                      : "bg-black/[0.04] dark:bg-white/[0.06] text-gray-600 dark:text-gray-400 hover:bg-black/[0.12] dark:hover:bg-white/[0.14]"
                  }`}
                >
                  {tf.label}
                </button>
              ))}
              {cryptoSidebarCoins.slice(0, 6).map((coin) => (
                <button
                  key={coin.id}
                  onClick={() => { startTransition(() => { setCryptoPrimaryFilter(coin.id); setCryptoSecondaryFilter("all"); }); }}
                  className={`px-2.5 py-1 rounded-[5px] text-[11px] font-semibold transition-colors whitespace-nowrap flex-shrink-0 ${
                    cryptoPrimaryFilter === coin.id
                      ? "bg-gray-900 dark:bg-white text-white dark:text-black"
                      : "bg-black/[0.04] dark:bg-white/[0.06] text-gray-600 dark:text-gray-400 hover:bg-black/[0.12] dark:hover:bg-white/[0.14]"
                  }`}
                >
                  {coin.label}
                </button>
              ))}
            </HorizontalScroll>
          </div>
        )}
      </div>

      {/* Main Content with padding to account for fixed category bar and subcategory filters */}
      <div
        className={`w-full ${
          // Mobile: extra padding when subcategory rows are visible in fixed bar
          ["elections", "macro", "fed-rates", "trump", "elon"].includes(category || "")
            || (category === 'crypto' && effectiveSourceFilter === 'polymarket')
            || (effectiveSourceFilter === 'polymarket' && currentSubcategories.length > 0 && category !== 'crypto' && category !== 'finance')
            || (effectiveSourceFilter === 'kalshi' && kalshiSubcategories.length > 0 && category !== 'trending' && category !== 'crypto' && category !== 'sports')
            ? 'pt-28' // ~7rem — two rows in fixed bar
            : 'pt-[5.5rem]' // single row
        } ${
          // Desktop: elections subcats show in fixed bar (need more room)
          ["elections", "macro", "fed-rates", "trump"].includes(category || "")
            ? 'lg:pt-[5.25rem]'
            : 'lg:pt-[2.75rem]'
        }`}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        {/* Filters and Search - Compact on mobile - Hide on dashboard pages */}
        {(!["elections", "macro", "fed-rates", "trump", "mentions"].includes(category || "") || effectiveSourceFilter === "kalshi") && !((category === "sports" || category === "esports") && effectiveSourceFilter === "polymarket") && !(category === "world-cup" && effectiveSourceFilter === "polymarket") && (
        <div className="mb-3 lg:mb-4">
          {/* Mobile: Compact layout */}
          <div className="flex flex-col gap-2 lg:hidden">
            {/* Row 1: Sort + Filters Toggle + View Mode (no search on mobile — header has search icon) */}
            {/* Hidden for for-you, bookmarks, breaking — they have their own compact headers with controls */}
            {category !== "for-you" && category !== "bookmarks" && category !== "breaking" && (
            <div className="flex items-center gap-2">
              {/* Sort - Hidden in Breaking, Weather */}
              {category !== "breaking" && category !== "weather-science" && category !== "weather" && (
              effectiveSourceFilter === 'kalshi' && category !== 'trending' && category !== 'crypto' && category !== 'sports' ? (
                /* Kalshi category sort — dropdown */
                <div className="relative flex-shrink-0" ref={kalshiSortRef}>
                  <button
                    onClick={() => setKalshiSortDropdownOpen(!kalshiSortDropdownOpen)}
                    className="flex items-center gap-1 px-3 py-1.5 bg-black/[0.04] dark:bg-white/[0.06] rounded-[5px] text-[11px] font-semibold text-gray-600 dark:text-gray-400 hover:bg-black/[0.12] dark:hover:bg-white/[0.14] transition-colors"
                  >
                    {{
                    trending: t("filterBar.sort.trending"),
                    'price-delta': t("filterBar.sort.volatile"),
                    newest: t("filterBar.sort.new"),
                    closing: t("filterBar.sort.closingSoon"),
                    'event-volume': t("filterBar.sort.eventVolume"),
                    'price-balance': t("filterBar.sort.priceBalance"),
                    all: t("filterBar.sort.relevance"),
                  }[kalshiSortBy] || t("filterBar.sort")}
                    <ChevronDown className="w-3 h-3" />
                  </button>
                  {kalshiSortDropdownOpen && (
                    <div className="absolute top-full left-0 mt-1 w-44 bg-white dark:bg-[#18181D] border border-gray-200 dark:border-[#1A1A1A] rounded-[5px] shadow-lg z-50 py-1 overflow-hidden">
                      {([
                        { value: 'trending', label: 'Trending' },
                        { value: 'price-delta', label: 'Volatile' },
                        { value: 'newest', label: 'New' },
                        { value: 'closing', label: 'Closing Soon' },
                        { value: 'event-volume', label: 'Volume' },
                        { value: 'price-balance', label: '50-50' },
                      ] as const).map(opt => (
                        <button
                          key={opt.value}
                          onClick={() => { setKalshiSortBy(opt.value); setKalshiSortDropdownOpen(false); }}
                          className={`w-full text-left px-3 py-2 text-xs font-inter-tight transition-colors ${
                            kalshiSortBy === opt.value
                              ? 'bg-gray-100 dark:bg-[#0f0f12] text-gray-900 dark:text-white font-bold'
                              : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-[#0f0f12]/50'
                          }`}
                        >
                          {opt.label}
                        </button>
                      ))}
                      <div className="border-t border-gray-200 dark:border-[#1A1A1A] mx-2 my-1" />
                      <button
                        onClick={() => { setKalshiReverse(!kalshiReverse); setKalshiSortDropdownOpen(false); }}
                        className="w-full flex items-center justify-between px-3 py-2 text-xs font-inter-tight text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-[#0f0f12]/50 transition-colors"
                      >
                        <span>{t("filterBar.reverseSort")}</span>
                        <div className={`w-7 h-4 rounded-[5px] transition-colors flex items-center ${kalshiReverse ? 'bg-[#CCFF00] justify-end' : 'bg-gray-300 dark:bg-gray-700 justify-start'}`}>
                          <div className="w-3 h-3 rounded-[5px] bg-white shadow-sm mx-0.5" />
                        </div>
                      </button>
                    </div>
                  )}
                </div>
              ) : (
              <div className="flex gap-0.5 bg-black/[0.03] dark:bg-white/[0.04] rounded-[5px] p-0.5 flex-shrink-0">
                {effectiveSourceFilter === 'kalshi' && category === 'trending' ? (
                  <>
                    <button
                      onClick={() => setKalshiSortBy("all")}
                      className={`px-2 py-1 rounded-[5px] text-[11px] font-semibold transition-colors ${
                        kalshiSortBy === "all" ? "bg-gray-900 dark:bg-white text-white dark:text-black" : "text-gray-500"
                      }`}
                    >
                      {t("kalshiSort.all")}
                    </button>
                    <button
                      onClick={() => setKalshiSortBy("trending")}
                      className={`px-2 py-1 rounded-[5px] text-[11px] font-semibold transition-colors flex items-center gap-0.5 ${
                        kalshiSortBy === "trending" ? "bg-gray-900 dark:bg-white text-white dark:text-black" : "text-gray-500"
                      }`}
                    >
                      <FiTrendingUp className="w-2.5 h-2.5" />
                      {t("kalshiSort.hot")}
                    </button>
                    <button
                      onClick={() => setKalshiSortBy("price-delta")}
                      className={`px-2 py-1 rounded-[5px] text-[11px] font-semibold transition-colors ${
                        kalshiSortBy === "price-delta" ? "bg-gray-900 dark:bg-white text-white dark:text-black" : "text-gray-500"
                      }`}
                    >
                      {t("kalshiSort.movers")}
                    </button>
                    <button
                      onClick={() => setKalshiSortBy("newest")}
                      className={`px-2 py-1 rounded-[5px] text-[11px] font-semibold transition-colors flex items-center gap-0.5 ${
                        kalshiSortBy === "newest" ? "bg-gray-900 dark:bg-white text-white dark:text-black" : "text-gray-500"
                      }`}
                    >
                      <FiClock className="w-2.5 h-2.5" />
                      {t("kalshiSort.new")}
                    </button>
                    <button
                      onClick={() => setKalshiSortBy("event-volume")}
                      className={`px-2 py-1 rounded-[5px] text-[11px] font-semibold transition-colors ${
                        kalshiSortBy === "event-volume" ? "bg-gray-900 dark:bg-white text-white dark:text-black" : "text-gray-500"
                      }`}
                    >
                      {t("kalshiSort.volAbbr")}
                    </button>
                  </>
                ) : (
                  <div className="relative" ref={sortDropdownRef}>
                    <button
                      onClick={() => setSortDropdownOpen(!sortDropdownOpen)}
                      className="flex items-center gap-1 px-2 py-1 rounded-[5px] text-[11px] font-semibold bg-gray-900 dark:bg-white text-white dark:text-black transition-colors"
                    >
                      {sortLabelMap[sortBy] || sortBy}
                      <ChevronDown className={`w-2.5 h-2.5 transition-transform ${sortDropdownOpen ? 'rotate-180' : ''}`} />
                    </button>
                    {sortDropdownOpen && (
                      <div className="absolute top-full left-0 mt-1 w-40 bg-white dark:bg-[#18181D] border border-gray-200 dark:border-[#1A1A1A] rounded-[5px] shadow-lg z-50 py-1 overflow-hidden">
                        {activeSortOptions.map(opt => (
                          <button
                            key={opt.value}
                            onClick={() => { setSortBy(opt.value); setSortDropdownOpen(false); }}
                            className={`w-full text-left px-3 py-2 text-xs transition-colors ${
                              sortBy === opt.value
                                ? 'bg-gray-100 dark:bg-[#0f0f12] text-gray-900 dark:text-white font-bold'
                                : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-[#0f0f12]/50'
                            }`}
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
              )
              )}

              {/* Weather chips row - mobile (High/Low + date tabs in a single line) */}
              <WeatherChipsRow
                category={category}
                activeSubcategory={activeSubcategory}
                weatherDateTabs={weatherDateTabs}
                activeWeatherDate={activeWeatherDate}
                setActiveWeatherDate={setActiveWeatherDate}
                activeWeatherTempType={activeWeatherTempType}
                setActiveWeatherTempType={setActiveWeatherTempType}
                size="sm"
              />

              <div className="flex-1" />

              {/* Filters Toggle Button — hidden for Kalshi, Predict, Ending Soon */}
              {category !== "trending" && category !== "breaking" && category !== "new" && category !== "ending-soon" && category !== "weather-science" && category !== "weather" && effectiveSourceFilter !== 'kalshi' && effectiveSourceFilter !== 'predict' && (
                <button
                  onClick={() => setMobileFiltersExpanded(!mobileFiltersExpanded)}
                  className={`w-7 h-7 rounded-[5px] transition-colors flex-shrink-0 flex items-center justify-center ${
                    mobileFiltersExpanded
                      ? "bg-gray-900 dark:bg-white text-white dark:text-black"
                      : "bg-black/[0.04] dark:bg-white/[0.06] text-gray-500"
                  }`}
                >
                  <SlidersHorizontalIcon size={14} isAnimated={true} />
                </button>
              )}

              {/* View Mode - Grid/List */}
              {viewModeLoaded && (
                <ViewModeToggle viewMode={viewMode} onChange={setViewMode} />
              )}

            </div>
            )}


            {/* Row 2: Status + Sort + Advanced Filters in horizontal scroll - Collapsible */}
            <div className={`overflow-hidden transition-colors ${
              mobileFiltersExpanded ? "max-h-20 opacity-100" : "max-h-0 opacity-0"
            }`}>
              <div className="flex items-center gap-2 overflow-x-auto scrollbar-hide pb-1">
              {/* Advanced Filters — available on every category. The button
                  itself decides whether to redirect to /markets/trending
                  on Apply (so filters can act globally even when launched
                  from a narrow category). */}
              <AdvancedFilters
                filters={advancedFilters}
                onChange={setAdvancedFilters}
                activeFilterCount={countActiveFilters(advancedFilters)}
              />

              {/* Status Filter - Compact - Hidden in For You, Trending, Breaking, New, Ending Soon, Elon, Weather, Kalshi */}
              {category !== "for-you" && category !== "trending" && category !== "breaking" && category !== "new" && category !== "ending-soon" && category !== "elon" && category !== "weather-science" && category !== "weather" && effectiveSourceFilter !== 'kalshi' && effectiveSourceFilter !== 'predict' && (
                <StatusFilterButtons filter={filter} setFilter={setFilter} variant="mobile" />
              )}

              </div>
            </div>

          </div>

          {/* Desktop: Original layout */}
          <div className="hidden lg:flex items-center gap-3 justify-between">
            {/* Left: Subcategories (scrollable) */}
            <div className="flex-1 min-w-0 overflow-hidden">
              {/* Polymarket Subcategories */}
              {effectiveSourceFilter === 'polymarket' && currentSubcategories.length > 0 && category !== 'crypto' && category !== 'finance' && (
                <HorizontalScroll
                  ref={subcategoriesScrollRefDesktopMain}
                  className="flex items-center gap-1.5 cursor-grab active:cursor-grabbing select-none"
                  fadeFrom="bg-gray-50 dark:bg-[#0f0f12]"
                >
                  <button
                    onClick={() => changeSubcategory(null, 'right')}
                    className={`px-3 py-1.5 rounded-[5px] text-xs font-semibold transition-colors whitespace-nowrap flex-shrink-0 ${
                      activeSubcategory === null
                        ? "bg-gray-900 dark:bg-white text-white dark:text-black"
                        : "bg-black/[0.04] dark:bg-white/[0.06] text-gray-600 dark:text-gray-400 hover:bg-black/[0.12] dark:hover:bg-white/[0.14]"
                    }`}
                  >
                    {t("common.all")}
                  </button>
                  {currentSubcategories.map((sub) => (
                    <button
                      key={sub.id}
                      onClick={() => changeSubcategory(sub.id === activeSubcategory ? null : sub.id)}
                      className={`px-3 py-1.5 rounded-[5px] text-xs font-semibold transition-colors whitespace-nowrap flex-shrink-0 ${
                        activeSubcategory === sub.id
                          ? "bg-gray-900 dark:bg-white text-white dark:text-black"
                          : "bg-black/[0.04] dark:bg-white/[0.06] text-gray-600 dark:text-gray-400 hover:bg-black/[0.12] dark:hover:bg-white/[0.14]"
                      }`}
                    >
                      {tCategory(sub.id) || sub.label}
                    </button>
                  ))}
                </HorizontalScroll>
              )}

              {/* Kalshi Subcategories */}
              {effectiveSourceFilter === 'kalshi' && kalshiSubcategories.length > 0 && category !== 'trending' && category !== 'crypto' && category !== 'sports' && category !== 'commodities' && (
                <HorizontalScroll
                  ref={kalshiSubcatsScrollRefDesktopMain}
                  className="flex items-center gap-1.5 cursor-grab active:cursor-grabbing select-none"
                  fadeFrom="bg-gray-50 dark:bg-[#0f0f12]"
                >
                  <button
                    onClick={() => startTransition(() => setActiveKalshiSubcategory(null))}
                    className={`px-3 py-1.5 rounded-[5px] text-xs font-semibold transition-colors whitespace-nowrap flex-shrink-0 ${
                      activeKalshiSubcategory === null
                        ? "bg-gray-900 dark:bg-white text-white dark:text-black"
                        : "bg-black/[0.04] dark:bg-white/[0.06] text-gray-600 dark:text-gray-400 hover:bg-black/[0.12] dark:hover:bg-white/[0.14]"
                    }`}
                  >
                    {t("kalshiSort.all")}
                  </button>
                  {kalshiSubcategories.map((sub) => (
                    <button
                      key={sub.name}
                      onClick={() => startTransition(() => setActiveKalshiSubcategory(activeKalshiSubcategory === sub.name ? null : sub.name))}
                      className={`px-3 py-1.5 rounded-[5px] text-xs font-semibold transition-colors whitespace-nowrap flex-shrink-0 ${
                        activeKalshiSubcategory === sub.name
                          ? "bg-gray-900 dark:bg-white text-white dark:text-black"
                          : "bg-black/[0.04] dark:bg-white/[0.06] text-gray-600 dark:text-gray-400 hover:bg-black/[0.12] dark:hover:bg-white/[0.14]"
                      }`}
                    >
                      {tKalshiSubcat(sub.name)}
                    </button>
                  ))}
                </HorizontalScroll>
              )}

              {/* Weather chips row - desktop (High/Low + date tabs in a single line) */}
              <WeatherChipsRow
                category={category}
                activeSubcategory={activeSubcategory}
                weatherDateTabs={weatherDateTabs}
                activeWeatherDate={activeWeatherDate}
                setActiveWeatherDate={setActiveWeatherDate}
                activeWeatherTempType={activeWeatherTempType}
                setActiveWeatherTempType={setActiveWeatherTempType}
                size="md"
              />
            </div>

            {/* Right: Controls (flex-shrink-0) */}
            <div className="flex items-center gap-3 flex-shrink-0">
            {/* Status Filter - Hidden in For You, Trending, Breaking, New, Ending Soon, Elon, Mentions, Weather, Crypto, Kalshi */}
            {category !== "for-you" && category !== "trending" && category !== "breaking" && category !== "new" && category !== "ending-soon" && category !== "elon" && category !== "mentions" && category !== "weather-science" && category !== "weather" && category !== "crypto" && effectiveSourceFilter !== 'kalshi' && effectiveSourceFilter !== 'predict' && (
              <StatusFilterButtons filter={filter} setFilter={setFilter} variant="desktop" />
            )}

            {/* Sort By - Hidden in Breaking, Mentions (PM only), Weather, and Kalshi sports */}
            {category !== "breaking" && (category !== "mentions" || effectiveSourceFilter === "kalshi") && category !== "weather-science" && category !== "weather" && !(category === "sports" && effectiveSourceFilter === "kalshi") && (
            effectiveSourceFilter === 'kalshi' && category !== 'trending' && category !== 'crypto' && category !== 'sports' ? (
              /* Kalshi category sort — dropdown (desktop) */
              <div className="relative" ref={kalshiSortRef}>
                <button
                  onClick={() => setKalshiSortDropdownOpen(!kalshiSortDropdownOpen)}
                  className="flex items-center gap-2 px-4 py-2 bg-black/[0.03] dark:bg-white/[0.04] rounded-[5px] text-xs font-semibold text-gray-600 dark:text-gray-400 hover:bg-black/[0.06] dark:hover:bg-white/[0.08] transition-colors"
                >
                  <span className="text-gray-500">{t("filterBar.sort")}:</span>
                  {{
                    trending: t("filterBar.sort.trending"),
                    'price-delta': t("filterBar.sort.volatile"),
                    newest: t("filterBar.sort.new"),
                    closing: t("filterBar.sort.closingSoon"),
                    'event-volume': t("filterBar.sort.eventVolume"),
                    'price-balance': t("filterBar.sort.priceBalance"),
                    all: t("filterBar.sort.relevance"),
                  }[kalshiSortBy] || t("filterBar.sort")}
                  {kalshiReverse && <span className="text-[9px] text-[#CCFF00]">REV</span>}
                  <ChevronDown className={`w-3.5 h-3.5 transition-transform ${kalshiSortDropdownOpen ? 'rotate-180' : ''}`} />
                </button>
                {kalshiSortDropdownOpen && (
                  <div className="absolute top-full right-0 mt-1 w-48 bg-white dark:bg-[#18181D] border border-gray-200 dark:border-[#1A1A1A] rounded-[5px] shadow-lg z-50 py-1 overflow-hidden">
                    {([
                      { value: 'trending', label: t("filterBar.sort.trending") },
                      { value: 'price-delta', label: t("filterBar.sort.volatile") },
                      { value: 'newest', label: t("filterBar.sort.new") },
                      { value: 'closing', label: t("filterBar.sort.closingSoon") },
                      { value: 'event-volume', label: t("filterBar.sort.eventVolume") },
                      { value: 'price-balance', label: t("filterBar.sort.priceBalance") },
                    ] as const).map(opt => (
                      <button
                        key={opt.value}
                        onClick={() => { setKalshiSortBy(opt.value); setKalshiSortDropdownOpen(false); }}
                        className={`w-full text-left px-4 py-2 text-xs font-inter-tight transition-colors ${
                          kalshiSortBy === opt.value
                            ? 'bg-gray-100 dark:bg-[#0f0f12] text-gray-900 dark:text-white font-bold'
                            : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-[#0f0f12]/50'
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                    <div className="border-t border-gray-200 dark:border-[#1A1A1A] mx-3 my-1" />
                    <button
                      onClick={() => { setKalshiReverse(!kalshiReverse); setKalshiSortDropdownOpen(false); }}
                      className="w-full flex items-center justify-between px-4 py-2 text-xs font-inter-tight text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-[#0f0f12]/50 transition-colors"
                    >
                      <span>{t("filterBar.reverseSort")}</span>
                      <div className={`w-8 h-4.5 rounded-[5px] transition-colors flex items-center ${kalshiReverse ? 'bg-[#CCFF00] justify-end' : 'bg-gray-300 dark:bg-gray-700 justify-start'}`}>
                        <div className="w-3.5 h-3.5 rounded-[5px] bg-white shadow-sm mx-0.5" />
                      </div>
                    </button>
                  </div>
                )}
              </div>
            ) : (
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                {t("filterBar.sort")}:
              </span>
              <div className="flex gap-1 bg-black/[0.03] dark:bg-white/[0.04] rounded-[5px] p-0.5">
                {effectiveSourceFilter === 'kalshi' && category === 'trending' ? (
                  <>
                    <button
                      onClick={() => setKalshiSortBy("all")}
                      className={`group relative px-4 py-1.5 rounded-[5px] text-xs font-semibold transition-colors flex items-center gap-1.5 ${
                        kalshiSortBy === "all"
                          ? "bg-gray-900 dark:bg-white text-white dark:text-black"
                          : "text-gray-600 dark:text-gray-400 hover:bg-black/[0.06] dark:hover:bg-white/[0.08]"
                      }`}
                    >
                      {t("kalshiSort.all")}
                    </button>
                    <button
                      onClick={() => setKalshiSortBy("trending")}
                      className={`group relative px-4 py-1.5 rounded-[5px] text-xs font-semibold transition-colors flex items-center gap-1.5 ${
                        kalshiSortBy === "trending"
                          ? "bg-gray-900 dark:bg-white text-white dark:text-black"
                          : "text-gray-600 dark:text-gray-400 hover:bg-black/[0.06] dark:hover:bg-white/[0.08]"
                      }`}
                    >
                      <FiTrendingUp className="w-3 h-3" />
                      {t("kalshiSort.hot")}
                    </button>
                    <button
                      onClick={() => setKalshiSortBy("price-delta")}
                      className={`group relative px-4 py-1.5 rounded-[5px] text-xs font-semibold transition-colors flex items-center gap-1.5 ${
                        kalshiSortBy === "price-delta"
                          ? "bg-gray-900 dark:bg-white text-white dark:text-black"
                          : "text-gray-600 dark:text-gray-400 hover:bg-black/[0.06] dark:hover:bg-white/[0.08]"
                      }`}
                    >
                      <FiTrendingUp className="w-3 h-3" />
                      {t("kalshiSort.movers")}
                    </button>
                    <button
                      onClick={() => setKalshiSortBy("newest")}
                      className={`group relative px-4 py-1.5 rounded-[5px] text-xs font-semibold transition-colors flex items-center gap-1.5 ${
                        kalshiSortBy === "newest"
                          ? "bg-gray-900 dark:bg-white text-white dark:text-black"
                          : "text-gray-600 dark:text-gray-400 hover:bg-black/[0.06] dark:hover:bg-white/[0.08]"
                      }`}
                    >
                      <FiClock className="w-3 h-3" />
                      {t("kalshiSort.new")}
                    </button>
                    <button
                      onClick={() => setKalshiSortBy("event-volume")}
                      className={`group relative px-4 py-1.5 rounded-[5px] text-xs font-semibold transition-colors flex items-center gap-1.5 ${
                        kalshiSortBy === "event-volume"
                          ? "bg-gray-900 dark:bg-white text-white dark:text-black"
                          : "text-gray-600 dark:text-gray-400 hover:bg-black/[0.06] dark:hover:bg-white/[0.08]"
                      }`}
                    >
                      <FiTrendingUp className="w-3 h-3" />
                      {t("kalshiSort.volume")}
                    </button>
                  </>
                ) : (
                  <div className="relative" ref={sortDropdownRef}>
                    <button
                      onClick={() => setSortDropdownOpen(!sortDropdownOpen)}
                      className="flex items-center gap-1.5 px-4 py-1.5 rounded-[5px] text-xs font-semibold bg-gray-900 dark:bg-white text-white dark:text-black transition-colors"
                    >
                      {sortLabelMap[sortBy] || sortBy}
                      <ChevronDown className={`w-3 h-3 transition-transform ${sortDropdownOpen ? 'rotate-180' : ''}`} />
                    </button>
                    {sortDropdownOpen && (
                      <div className="absolute top-full right-0 mt-1 w-44 bg-white dark:bg-[#18181D] border border-gray-200 dark:border-[#1A1A1A] rounded-[5px] shadow-lg z-50 py-1 overflow-hidden">
                        {activeSortOptions.map(opt => (
                          <button
                            key={opt.value}
                            onClick={() => { setSortBy(opt.value); setSortDropdownOpen(false); }}
                            className={`w-full text-left px-4 py-2 text-xs transition-colors ${
                              sortBy === opt.value
                                ? 'bg-gray-100 dark:bg-[#0f0f12] text-gray-900 dark:text-white font-bold'
                                : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-[#0f0f12]/50'
                            }`}
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
            )
            )}

            {/* View Mode Toggle - Hidden until loaded to prevent flash, not shown for Sports */}
            {viewModeLoaded && category !== "sports" && category !== "esports" && (
              <ViewModeToggle viewMode={viewMode} onChange={setViewMode} />
            )}

            {/* Advanced Filters — always visible. Apply from a narrow
                category redirects the user to /markets/trending so the
                filters operate over the global market pool. */}
            <AdvancedFilters
              filters={advancedFilters}
              onChange={setAdvancedFilters}
              activeFilterCount={countActiveFilters(advancedFilters)}
            />
            </div>

          </div>
        </div>
        )}

        {/* Extra spacing when search bar is hidden */}
        {["elections", "macro", "fed-rates", "trump"].includes(category || "") && (
          <div className="h-4 lg:h-6" />
        )}

        {/* FOR YOU - Header + Trending Carousel (always visible in for-you) */}
        {category === "for-you" && (
          <div className="mb-8">
            {/* Desktop: original gradient banner */}
            <div className="hidden lg:block bg-gray-50 dark:bg-[#18181D] rounded-[5px] py-3 px-6 mb-6 border border-gray-200 dark:border-[#1A1A1A]">
              <div className="flex items-center gap-2">
                <SparklesIcon size={20} className="text-gray-900 dark:text-white" />
                <h2 className="text-2xl font-monument font-bold text-gray-900 dark:text-white">{t("forYou.title")}</h2>
                <button
                  onClick={() => setShowForYouInfo(true)}
                  className="group/info p-1.5 rounded-[5px] text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                  aria-label={t("forYou.howItWorks")}
                >
                  <FiInfo className="w-4 h-4" />
                </button>
                <span className="text-gray-600 dark:text-gray-400 font-satoshi text-sm">
                  — {t("forYou.subtitle")}
                </span>
                <div className="flex-1" />
              </div>
            </div>

            {/* For You Info Modal */}
            {showForYouInfo && (
              <div className="fixed inset-0 z-[350] flex items-center justify-center p-4" onClick={() => setShowForYouInfo(false)}>
                <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
                <div
                  className="relative bg-white dark:bg-[#18181D] rounded-[5px] border border-gray-200 dark:border-[#1A1A1A] p-6 max-w-md w-full shadow-xl"
                  onClick={(e) => e.stopPropagation()}
                >
                  <button
                    onClick={() => setShowForYouInfo(false)}
                    className="absolute top-4 right-4 p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
                  >
                    <FiX className="w-5 h-5" />
                  </button>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-[5px] bg-gray-100 dark:bg-[#0f0f12] flex items-center justify-center">
                      <SparklesIcon size={20} className="text-gray-500 dark:text-gray-400" />
                    </div>
                    <h3 className="font-monument text-lg font-bold text-gray-900 dark:text-white">How For You works</h3>
                  </div>
                  <div className="space-y-3 font-satoshi text-sm text-gray-600 dark:text-gray-300">
                    <p>
                      FrenFlow&apos;s AI learns from your activity to surface markets where you have the most edge.
                    </p>
                    <p>
                      It analyzes the categories you trade, the topics you engage with, and the types of markets where you perform best — then finds similar opportunities you might have missed.
                    </p>
                    <p>
                      The more you trade and interact, the smarter your feed gets. Recommendations improve over time as the algorithm understands your style.
                    </p>
                    <p className="text-xs text-gray-400 dark:text-gray-500 pt-1">
                      Use the &quot;Not interested&quot; button to help us refine your suggestions even faster.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Same race-gap fix as bookmarks: keep the skeleton up while
                the watchlist context is still resolving so we don't flash
                "No personalized recommendations yet" before the fetch
                even has a chance to start. */}
            {watchlistLoading || loadingForYou ? (
              <>
                {/* Desktop: skeleton grid */}
                <div className="hidden lg:grid grid-cols-3 xl:grid-cols-4 gap-3 py-4">
                  {[...Array(8)].map((_, i) => <SimpleCardSkeleton key={i} />)}
                </div>
                {/* Mobile: Revolut skeleton cards */}
                <div className="lg:hidden space-y-3 px-4">
                  {[1,2,3,4].map(i => (
                    <div key={i} className="rounded-[5px] bg-white dark:bg-white/[0.04] border border-gray-200/80 dark:border-[#1A1A1A] p-4 animate-pulse">
                      <div className="flex gap-3 items-center">
                        <div className="w-12 h-12 rounded-[5px] bg-gray-200 dark:bg-white/[0.06] flex-shrink-0" />
                        <div className="flex-1 space-y-2">
                          <div className="h-4 w-3/4 bg-gray-200 dark:bg-white/[0.06] rounded" />
                          <div className="h-3 w-1/2 bg-gray-200 dark:bg-white/[0.06] rounded" />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2 mt-3">
                        <div className="h-10 bg-gray-200 dark:bg-white/[0.06] rounded-[5px]" />
                        <div className="h-10 bg-gray-200 dark:bg-white/[0.06] rounded-[5px]" />
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : forYouEvents.length === 0 && forYouFeedItems.length === 0 ? (
              <div className="text-center py-16">
                <SparklesIcon size={48} className="text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-monument font-bold text-gray-700 dark:text-gray-300 mb-2">
                  No personalized recommendations yet
                </h3>
                <p className="text-gray-500 dark:text-gray-400 font-satoshi max-w-md mx-auto">
                  Start trading and exploring markets to get personalized recommendations tailored just for you.
                </p>
              </div>
            ) : (
              <>
                {/* Trending Events Carousel — always visible regardless of viewMode */}
                {forYouEvents.length > 0 && (
                  <TrendingEventsCarousel
                    events={forYouEvents}
                    getEventSocialActivity={getEventSocialActivity}
                    sortBy={sortBy}
                    onSortChange={setSortBy}
                    onInfoClick={openForYouInfo}
                    teamLogos={teamLogos}
                  />
                )}

                {/* Bookmarks Mini Cards — compact grid */}
                <BookmarksMiniGrid
                  loadingWatching={loadingWatching}
                  watchingEvents={watchingEvents}
                  watchlistCount={watchlist.length}
                  newsSidebarOpen={newsSidebarOpen}
                  source={source}
                  miniCardExplosion={miniCardExplosion}
                  setMiniCardExplosion={setMiniCardExplosion}
                  bookmarkHoveredSlug={bookmarkHoveredSlug}
                  setBookmarkHoveredSlug={setBookmarkHoveredSlug}
                  toggleWatchlist={toggleWatchlist}
                  getRealtimePrice={getRealtimePrice}
                />

                {/* In list view: show Frens first, then Recommended header + table */}
                {/* In grid view: show Recommended header + grid first, then Frens */}

                {/* Recommended For You header — in grid view, show here (before grid).
                    skipLoading=true on refresh keeps the existing cards visible
                    while we refetch; the spinning icon signals work in flight. */}
                {viewMode === "grid" && forYouFeedItems.length > 0 && (
                  <RecommendedForYouHeader
                    refreshing={refreshingForYou}
                    onRefresh={async () => {
                      setRefreshingForYou(true);
                      toast(t("forYou.refreshing"), { duration: 2000 });
                      await fetchForYouFeed(true, sortBy, true);
                      setRefreshingForYou(false);
                      toast.success(t("forYou.refreshed"), { duration: 2000 });
                    }}
                  />
                )}

                {/* Recommended For You grid — only in grid view (list view renders below) */}
                {viewMode === "grid" && forYouFeedItems.length > 0 && (
                  <>
                    <div className={`grid grid-cols-1 ${!newsSidebarOpen ? 'sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4' : 'sm:grid-cols-2 xl:grid-cols-2 2xl:grid-cols-3'} gap-4`}>
                      {forYouFeedItems.map((item, index) => {
                        if (item.type === 'event') {
                          return (
                            <EventCard
                              key={`event-${item.event.slug}`}
                              {...eventToEventCardProps(item.event)}
                              openInterest={getEventOI(item.event)}
                              volumeHistory={getEventVolumeHistory(item.event)}
                              reason={item.reason}
                              socialActivity={getEventSocialActivity(item.event)}
                              showBackgroundImage
                              teamLogos={teamLogos}
                              trackingSource="for_you"
                              priorityImage={index < 2}
                              onCardClick={(s) => { trackRecommendationClick(s); setNavigationSource('for_you'); }}
                              onHide={handleForYouHide}
                            />
                          );
                        }
                        const event = aggregatedMarketToEvent(item.market);
                        return (
                          <MarketCardPoly
                            key={`market-${item.market.slug}`}
                            {...event}
                            reason={item.reason}
                            socialActivity={socialActivity[item.market.slug]}
                            trackingSource="for_you"
                            onCardClick={(s) => { trackRecommendationClick(s); setNavigationSource('for_you'); }}
                            onHide={handleForYouHide}
                          />
                        );
                      })}
                    </div>
                  </>
                )}
              </>
            )}

            {/* Your Frens Are Trading — activity cards with diversity dedup */}
            <FrensTradingGrid activity={frensActivity} newsSidebarOpen={newsSidebarOpen} source={source} />

                {/* Recommended For You header — in list view, show here (after Frens, before table) */}
                {viewMode === "list" && forYouFeedItems.length > 0 && (
                  <RecommendedForYouHeader
                    refreshing={refreshingForYou}
                    topMargin
                    onRefresh={async () => {
                      setRefreshingForYou(true);
                      toast(t("forYou.refreshing"), { duration: 2000 });
                      await fetchForYouFeed(true, sortBy, true);
                      setRefreshingForYou(false);
                      toast.success(t("forYou.refreshed"), { duration: 2000 });
                    }}
                  />
                )}

          </div>
        )}

        {/* WATCHING - Bookmarks Header (shown in both grid and list modes) */}
        {category === "bookmarks" && (
          <div className="mb-3 lg:mb-6">
            {/* Watching Header - Compact single line on mobile, gradient banner on desktop */}
            <div className="lg:bg-gray-50 dark:lg:bg-[#18181D] lg:rounded-[5px] lg:py-3 lg:px-6 lg:border lg:border-gray-200 dark:lg:border-[#1A1A1A]">
              <div className="flex items-center gap-2">
                <Bookmark className="w-4 h-4 text-[#7C3AED] dark:text-[#CCFF00] fill-current lg:hidden" />
                <Bookmark className="w-5 h-5 text-[#7C3AED] dark:text-[#CCFF00] fill-current hidden lg:block" />
                <h2 className="text-sm lg:text-2xl font-monument font-bold text-gray-900 dark:text-white">Bookmarks</h2>
                <span className="hidden lg:inline text-gray-600 dark:text-gray-400 font-satoshi text-sm">
                  — Your bookmarked markets
                </span>
                <button
                  onClick={async () => {
                    toast("Refreshing bookmarks...", { duration: 2000 });
                    await fetchWatchingMarkets();
                    toast.success("Bookmarks updated", { duration: 2000 });
                  }}
                  disabled={loadingWatching}
                  className="p-1.5 rounded-[5px] text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-black/5 dark:hover:bg-white/5 transition-colors disabled:opacity-50"
                  aria-label="Refresh bookmarks"
                >
                  <RefreshCw className={`w-4 h-4 ${loadingWatching ? 'animate-spin' : ''}`} />
                </button>

                <div className="flex-1" />

                {/* Sort dropdown - Mobile */}
                <div className="relative lg:hidden flex-shrink-0" ref={sortDropdownRef}>
                  <button
                    onClick={() => setSortDropdownOpen(!sortDropdownOpen)}
                    className="flex items-center gap-1 px-2.5 py-1 rounded-[5px] text-[11px] font-semibold bg-gray-900 dark:bg-white text-white dark:text-black transition-colors"
                  >
                    {sortLabelMap[sortBy] || sortBy}
                    <ChevronDown className={`w-2.5 h-2.5 transition-transform ${sortDropdownOpen ? 'rotate-180' : ''}`} />
                  </button>
                  {sortDropdownOpen && (
                    <div className="absolute top-full right-0 mt-1 w-40 bg-white dark:bg-[#18181D] border border-gray-200 dark:border-[#1A1A1A] rounded-[5px] shadow-lg z-50 py-1 overflow-hidden">
                      {([
                        { value: 'volume24hr' as const, label: t("filterBar.sort.volume24hr") },
                        { value: 'volume' as const, label: t("filterBar.sort.volume") },
                        { value: 'liquidity' as const, label: t("filterBar.sort.liquidity") },
                        { value: 'newest' as const, label: t("filterBar.sort.newest") },
                        { value: 'endingSoon' as const, label: t("filterBar.sort.endingSoon") },
                        { value: 'competitive' as const, label: t("filterBar.sort.competitive") },
                      ]).map(opt => (
                        <button
                          key={opt.value}
                          onClick={() => { setSortBy(opt.value); setSortDropdownOpen(false); }}
                          className={`w-full text-left px-3 py-2 text-xs transition-colors ${
                            sortBy === opt.value
                              ? 'bg-gray-100 dark:bg-[#0f0f12] text-gray-900 dark:text-white font-bold'
                              : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-[#0f0f12]/50'
                          }`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* View Mode - Mobile */}
                {viewModeLoaded && (
                  <ViewModeToggle viewMode={viewMode} onChange={setViewMode} className="lg:hidden" />
                )}
              </div>
            </div>
          </div>
        )}

        {/* WATCHING - Bookmarked Markets Grid (List view uses unified table below) */}
        {category === "bookmarks" && viewMode === "grid" && (
          <div className="mb-8">
            {/* Show skeleton while the watchlist context is still loading too —
                otherwise we flash the "No bookmarked markets yet" empty state
                between the click and the context resolving, then flip to a
                second skeleton once the fetch starts. Three states in one
                cat-switch felt like real lag even when the API was fast. */}
            {watchlistLoading || loadingWatching ? (
              <div className={`grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-3 py-4`}>
                {[...Array(4)].map((_, i) => <SimpleCardSkeleton key={i} />)}
              </div>
            ) : watchingEvents.length === 0 ? (
              <div className="text-center py-16">
                <Bookmark className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-monument font-bold text-gray-700 dark:text-gray-300 mb-2">
                  No bookmarked markets yet
                </h3>
                <p className="text-gray-500 dark:text-gray-400 font-satoshi max-w-md mx-auto">
                  Click the bookmark icon on any market to save it here.
                </p>
              </div>
            ) : filteredWatchingEvents.length === 0 ? (
              <div className="text-center py-16">
                <Bookmark className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-monument font-bold text-gray-700 dark:text-gray-300 mb-2">
                  No {filter === "closed" ? "closed" : "active"} bookmarked markets
                </h3>
                <p className="text-gray-500 dark:text-gray-400 font-satoshi max-w-md mx-auto">
                  {filter === "closed"
                    ? "None of your bookmarks have resolved yet."
                    : "All your bookmarks have already resolved — switch to Closed to see them."}
                </p>
              </div>
            ) : (
              <div className={`grid ${viewMode === "grid" ? `grid-cols-1 ${!newsSidebarOpen ? 'sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4' : 'sm:grid-cols-2 xl:grid-cols-2 2xl:grid-cols-3'}` : "grid-cols-1"} gap-4`}>
                {filteredWatchingEvents.map((event, index) => (
                  <div
                    key={event.id}
                    className={`transition-colors ${
                      removingSlug === event.slug
                        ? "opacity-0 scale-95 -translate-y-2"
                        : "opacity-100 scale-100 translate-y-0"
                    }`}
                  >
                    <EventCard
                      {...(event as any)}
                      openInterest={getEventOI(event)}
                      volumeHistory={getEventVolumeHistory(event)}
                      image={event.image || event.icon || null}
                      source={event.source || 'polymarket'}
                      socialActivity={getEventSocialActivity(event as any)}
                      showBackgroundImage={viewMode === "grid"}
                      teamLogos={teamLogos}
                      trackingSource="feed"
                      priorityImage={index < 2}
                      explodeOnBookmarkRemove
                    />
                  </div>
                ))}
              </div>
            )}

            {/* Refresh Button */}
            {watchingEvents.length > 0 && (
              <div className="text-center mt-6">
                <button
                  onClick={() => fetchWatchingMarkets()}
                  className="px-5 py-2 bg-transparent text-gray-700 dark:text-white border border-gray-300 dark:border-gray-600 rounded-[5px] hover:bg-gray-100 dark:hover:bg-[#18181D] hover:border-gray-400 dark:hover:border-gray-400 active:scale-95 transition-colors font-satoshi font-medium text-sm"
                >
                  Refresh bookmarks
                </button>
              </div>
            )}
          </div>
        )}

        {/* BREAKING NEWS - Special Layout */}
        {category === "breaking" && effectiveSourceFilter === "polymarket" && (
          <div className="mb-8">
            {/* Breaking Header - Compact single line on mobile, full banner on desktop */}
            {/* Mobile: compact row */}
            <div className="flex lg:hidden items-center gap-2 mb-3">
              <h2 className="text-sm font-monument font-bold text-gray-900 dark:text-white">{t("breaking.short")}</h2>
              <span className="text-[10px] font-satoshi text-gray-400 dark:text-gray-500">
                {new Date().toLocaleDateString(locale === 'es' ? 'es-ES' : 'en-US', { month: 'short', day: 'numeric' })}
              </span>

              <div className="flex-1" />

              {/* View Mode - Mobile */}
              {viewModeLoaded && (
                <ViewModeToggle viewMode={viewMode} onChange={setViewMode} />
              )}
            </div>
            {/* Desktop: breaking news banner — Revolut/fintech style.
                Flat neutral card, single red accent for "live" urgency,
                cover image tucked at 60% opacity so the text stays the
                hero. Zero weird gradients, matches the rest of the 5px
                card system. */}
            <div className="hidden lg:flex relative overflow-hidden items-center gap-4 rounded-[5px] bg-white dark:bg-[#18181D] border border-gray-200 dark:border-[#1A1A1A] px-5 py-4 mb-4">
              <div className="flex flex-col gap-1.5 flex-1 min-w-0 relative z-10">
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center gap-1.5 h-5 px-2 rounded-[5px] bg-red-500/10 text-red-500 text-[10px] font-bold uppercase tracking-[0.14em]">
                    <span className="relative flex h-1.5 w-1.5">
                      <span className="absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-70 animate-ping" />
                      <span className="relative inline-flex h-1.5 w-1.5 rounded-[5px] bg-red-500" />
                    </span>
                    {t("breaking.live")}
                  </span>
                  <span className="text-[11px] text-gray-500 dark:text-gray-400 tabular-nums">
                    {new Date().toLocaleDateString(locale === 'es' ? 'es-ES' : 'en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </span>
                </div>
                <h2 className="text-xl font-bold text-gray-900 dark:text-white tracking-tight leading-tight">
                  {t("breaking.title")}
                </h2>
                <p className="text-[13px] text-gray-500 dark:text-gray-400 max-w-md leading-snug">
                  {t("breaking.subtitle")}
                </p>
              </div>
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src="/uploads/covers/breaking.png"
                alt=""
                aria-hidden
                className="absolute right-2 top-1/2 -translate-y-1/2 h-[140%] w-auto object-contain object-right pointer-events-none select-none opacity-60 dark:opacity-45"
              />
            </div>

            {/* Breaking Subcategory Filters */}
            <div className="flex items-center gap-2 mb-4 overflow-x-auto pb-1 scrollbar-hide">
              {breakingSubcategories.map((sub) => (
                <button
                  key={sub.id}
                  onClick={() => startTransition(() => setBreakingSubcategory(sub.id))}
                  className={`px-3 py-1.5 rounded-[5px] text-xs font-semibold whitespace-nowrap transition-colors ${
                    breakingSubcategory === sub.id
                      ? "bg-gray-900 dark:bg-white text-white dark:text-black"
                      : "bg-black/[0.04] dark:bg-white/[0.06] text-gray-600 dark:text-gray-400 hover:bg-black/[0.12] dark:hover:bg-white/[0.14]"
                  }`}
                >
                  {sub.label}
                </button>
              ))}
            </div>

            {/* Breaking Markets */}
            {loading ? (
              <div className={`grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-3 py-4`}>
                {[...Array(8)].map((_, i) => <SimpleCardSkeleton key={i} />)}
              </div>
            ) : (() => {
              // Breaking events come pre-filtered by category from Polymarket's biggest-movers API
              // No frontend filtering needed - backend fetches each category separately
              const breakingEvents = filteredEvents.slice(0, 30);

              if (breakingEvents.length === 0) {
                return (
                  <div className="text-center py-12">
                    <p className="text-gray-500 dark:text-gray-400 font-satoshi">{t("breaking.empty")}</p>
                  </div>
                );
              }

              if (viewMode === "grid") {
                return (
                  <div className={`grid grid-cols-1 ${!newsSidebarOpen ? 'sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4' : 'sm:grid-cols-2 xl:grid-cols-2 2xl:grid-cols-3'} gap-4`}>
                    {breakingEvents.map((event, index) => (
                      <EventCard
                        key={event.id}
                        {...(event as any)}
                        openInterest={getEventOI(event)}
                        volumeHistory={getEventVolumeHistory(event)}
                        image={event.image || event.icon || null}
                        source={event.source || 'polymarket'}
                        socialActivity={getEventSocialActivity(event)}
                        showBackgroundImage
                        teamLogos={teamLogos}
                        trackingSource="trending"
                        priorityImage={index < 2}
                      />
                    ))}
                  </div>
                );
              }

              return (
                <div className="space-y-2">
                  {breakingEvents.map((event, index) => {
                    const primaryMarket = event.markets?.[0];
                    const prices = primaryMarket?.outcomePrices || [];
                    const priceValue = prices[0];
                    const yesPrice = typeof priceValue === 'number' ? priceValue : parseFloat(String(priceValue || "0.5"));
                    // clobTokenIds can arrive as JSON string '["abc","xyz"]'
                    // (backend persists it stringified) or as a real array.
                    // Indexing the string version returned `'['` as tokenId,
                    // which the proxy then encoded into `/api/polymarket/%5B/prices-history`
                    // — see Railway logs spamming that 500 path. Normalise here.
                    const rawTokens = primaryMarket?.clobTokenIds as unknown;
                    const tokenId: string | undefined = Array.isArray(rawTokens)
                      ? rawTokens[0]
                      : typeof rawTokens === 'string' && rawTokens.startsWith('[')
                        ? (() => { try { const a = JSON.parse(rawTokens); return Array.isArray(a) ? a[0] : undefined; } catch { return undefined; } })()
                        : undefined;
                    const eventImage = event.image || event.icon || undefined;

                    return (
                      <BreakingMarketItem
                        key={event.id}
                        rank={index + 1}
                        slug={event.slug}
                        // Pass the primary sub-market's slug so the YES/NO
                        // shortcuts deep-link with `?market=<sub>` and the
                        // event page lands with the right outcome highlighted
                        // (multi-outcome events otherwise default to the
                        // highest-volume sub-market).
                        marketSlug={primaryMarket?.slug || undefined}
                        title={event.title}
                        image={eventImage}
                        tokenId={tokenId}
                        currentPrice={yesPrice}
                        livePriceChange={event.livePriceChange}
                        history={event.history}
                        category={(event as { category?: string; eventCategory?: string })?.category || (event as { eventCategory?: string }).eventCategory}
                        volume24hr={Number((event as { volume24hr?: number | string }).volume24hr ?? 0) || Number((event as { volume?: number | string }).volume ?? 0)}
                        endDate={(event as { endDate?: string | null }).endDate}
                        // Live sports data
                        isLiveSport={event._isLiveSport}
                        score={event.score}
                        period={event.period}
                        elapsed={event.elapsed}
                      />
                    );
                  })}
                </div>
              );
            })()}
          </div>
        )}

        {/* Trending/New subcategories now consolidated into the dynamic bar above (lines ~4950/5326) */}
        {/* This eliminates the DOM position jump when switching between categories */}

        {/* Finance: Subcategory tabs + Market Overview toggle — single row */}
        {category === "finance" && effectiveSourceFilter === 'polymarket' && (
          <div className="flex items-center gap-3 mb-4">
            {/* Scrollable subcategory tabs */}
            {(() => {
              const tabs = financeSecondaryTabs
                ? financeSecondaryTabs.map(t => ({ id: t.slug, label: t.label }))
                : currentFinanceTabs;
              const hasTabs = tabs && tabs.length > 0;
              if (!hasTabs) return <div className="flex-1 min-w-0" />;
              return (
                <HorizontalScroll
                  wrapperClassName="flex-1 min-w-0"
                  fadeFrom="bg-gray-50 dark:bg-[#0f0f12]"
                >
                  <div className="flex items-center gap-1.5 pb-1">
                    {tabs.map((tab) => {
                      const isActive = financeSecondaryFilter === tab.id;
                      return (
                        <button
                          key={tab.id}
                          onClick={() => setFinanceSecondaryFilter(tab.id)}
                          className={`px-3 py-1.5 rounded-[5px] text-xs font-semibold transition-colors whitespace-nowrap flex-shrink-0 ${
                            isActive
                              ? "bg-gray-900 dark:bg-white text-white dark:text-black"
                              : "bg-black/[0.04] dark:bg-white/[0.06] text-gray-600 dark:text-gray-400 hover:bg-black/[0.12] dark:hover:bg-white/[0.14]"
                          }`}
                        >
                          {tCategory(tab.id) || tab.label}
                        </button>
                      );
                    })}
                  </div>
                </HorizontalScroll>
              );
            })()}
            {/* Market Overview toggle — desktop only (≥1400px), far right */}
            <button
              onClick={() => {
                const newValue = !financeOverviewExpanded;
                setFinanceOverviewExpanded(newValue);
                localStorage.setItem('financeOverviewExpanded', JSON.stringify(newValue));
              }}
              className="flex-shrink-0 hidden min-[1400px]:flex items-center gap-2"
              title={t("crypto.toggleOverview")}
            >
              <span className="text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wide whitespace-nowrap">{t("crypto.marketOverview")}</span>
              <div className={`relative w-9 h-5 rounded-[5px] transition-colors duration-200 ${
                financeOverviewExpanded
                  ? "bg-gray-900 dark:bg-white"
                  : "bg-gray-300 dark:bg-gray-600"
              }`}>
                <div className={`absolute top-[2px] w-4 h-4 rounded-[5px] bg-white dark:bg-gray-900 transition-transform duration-200 ${
                  financeOverviewExpanded ? "translate-x-[18px]" : "translate-x-[2px]"
                }`} />
              </div>
            </button>
          </div>
        )}

        {/* Elon Hero Section - Show only on elon category with elonMode */}
        {category === "elon" && (
          <ElonHeroSection
            tweetData={elonTweetData}
            tweetMarkets={elonTweetMarkets}
            tweetMarketPrices={tweetMarketPrices}
            elonSubcategory={elonSubcategory}
            setElonSubcategory={setElonSubcategory}
            onTradeClick={openTradingModal}
            getBuyPrices={getBuyPrices}
          />
        )}

        {/* Climate Dashboard - toggle + dashboard */}
        {category === 'climate-science' && activeSubcategory && ["earthquakes", "hurricanes", "weather", "natural-disasters"].includes(activeSubcategory) && (
          <DashboardToggleStrip
            expanded={climateOverviewExpanded}
            onToggle={() => {
              const v = !climateOverviewExpanded;
              setClimateOverviewExpanded(v);
              localStorage.setItem('climateOverviewExpanded', JSON.stringify(v));
            }}
            label={t("crypto.marketOverview")}
          >
            <ClimateDashboard activeSubcategory={activeSubcategory} />
          </DashboardToggleStrip>
        )}

        {/* Geopolitics Dashboard - toggle + dashboard */}
        {category === 'geopolitics' && (
          <DashboardToggleStrip
            expanded={geopoliticsOverviewExpanded}
            onToggle={() => {
              const v = !geopoliticsOverviewExpanded;
              setGeopoliticsOverviewExpanded(v);
              localStorage.setItem('geopoliticsOverviewExpanded', JSON.stringify(v));
            }}
            label={t("crypto.marketOverview")}
          >
            <GeopoliticsDashboard />
          </DashboardToggleStrip>
        )}

        {/* Mobile Bottom Sheet — Finance sidebar */}
        {category === "finance" && effectiveSourceFilter === 'polymarket' && (
          <MobileFinanceFiltersSheet
            open={mobileFinanceSidebarOpen}
            onClose={closeMobileFinanceSheet}
            items={financeSidebarItems}
            activeFilterId={financePrimaryFilter}
            counts={financeCounts}
            onSelectFilter={onSelectFinanceFilter}
            resolveLabel={resolveFinanceLabel}
          />
        )}

        {/* Mobile Bottom Sheet — Crypto / Commodities sidebar.
            Matches the desktop render gate so every category with a
            sidebar also has a mobile equivalent. Previously scoped to
            Polymarket crypto only, which meant Kalshi crypto on mobile
            opened a trigger but had no sheet to open. */}
        {((category === "crypto" && (effectiveSourceFilter === 'polymarket' || effectiveSourceFilter === 'kalshi'))
          || (category === "commodities" && effectiveSourceFilter === 'kalshi')) && (
          <MobileCryptoFiltersSheet
            open={mobileCryptoSidebarOpen}
            onClose={closeMobileCryptoSheet}
            variant={category === "commodities" ? "commodities" : "crypto"}
            headerLabel={t(category === "commodities" ? "crypto.commoditiesFiltersHeader" : "crypto.filtersHeader")}
            timeframes={cryptoSidebarTimeframes}
            coins={cryptoSidebarCoins}
            activeFilterId={cryptoPrimaryFilter}
            counts={cryptoCounts}
            tokenImages={cryptoTokenImages}
            timeframeIcons={TIMEFRAME_ICONS}
            onSelectFilter={onSelectCryptoFilter}
            resolveTimeframeLabel={resolveCryptoTimeframeLabel}
          />
        )}

        {/* Kalshi exchange status banner — only renders when exchange/trading is paused */}
        {effectiveSourceFilter === 'kalshi' && (
          <div className="mb-3">
            <KalshiExchangeBanner />
          </div>
        )}

        {/* Crypto/Finance desktop: sidebar widget + content side by side */}
        {/* Only show sidebar layout for Polymarket - Kalshi doesn't have these subcategories */}
        <div className={((category === "finance" && effectiveSourceFilter === 'polymarket') || (category === "crypto" && (effectiveSourceFilter === 'polymarket' || effectiveSourceFilter === 'kalshi')) || (category === "sports" && effectiveSourceFilter === 'kalshi') || (category === "commodities" && effectiveSourceFilter === 'kalshi')) ? "min-[1400px]:flex min-[1400px]:gap-6" : ""}>
          {/* Finance Sidebar - Desktop */}
          {category === "finance" && effectiveSourceFilter === 'polymarket' && (() => {
            // Calculate max count for progress bar percentages
            const allFinanceCounts = financeSidebarItems.map(item => financeCounts[item.slug || 'finance'] ?? 0);
            const maxFinanceCount = Math.max(...allFinanceCounts, 1);
            // Colors for asset-type items (slice 4+)
            const financeColors: Record<string, string> = {
              stocks: '#22C55E', earnings: '#F59E0B', indicies: '#3B82F6',
              commodities: '#A78BFA', forex: '#14B8A6', collectibles: '#EC4899',
              acquisitions: '#F97316', 'earnings-calls': '#8B5CF6', ipo: '#EF4444',
              'fed-rates': '#6366F1', 'prediction-markets': '#FBBF24', treasuries: '#0EA5E9',
            };
            const collapsed = financeSidebarCollapsed;
            return (
            <aside className={`hidden min-[1400px]:block flex-shrink-0 transition-all duration-200 ${collapsed ? 'w-16' : 'w-44'}`}>
              <div className="sticky top-32 space-y-0.5 pt-1 max-h-[calc(100vh-9rem)] overflow-y-auto scrollbar-hide">
                {/* Toggle button */}
                <button
                  onClick={() => setFinanceSidebarCollapsed(!collapsed)}
                  className={`w-full flex items-center px-2 pb-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors ${collapsed ? 'justify-center' : 'justify-end'}`}
                >
                  {collapsed ? <AnimatedChevronRight size={16} className="text-white" /> : <AnimatedChevronLeft size={16} className="text-white" />}
                </button>
                {/* Time-based filters with progress bars */}
                {financeSidebarItems.slice(0, 4).map((item) => {
                  const isActive = financePrimaryFilter === item.id;
                  const IconComponent = FINANCE_ICONS[item.id];
                  const countKey = item.slug || 'finance';
                  const count = financeCounts[countKey];
                  // Hide if count is explicitly 0 (but always show "All" and items before counts load)
                  if (item.id !== "all" && count === 0) return null;
                  const pct = count !== undefined ? (count / maxFinanceCount) * 100 : 0;
                  return (
                    <button
                      key={item.id}
                      onClick={() => {
                        startTransition(() => {
                          setFinancePrimaryFilter(item.id);
                          setFinanceSecondaryFilter("all");
                        });
                      }}
                      title={collapsed ? item.label : undefined}
                      className={`w-full relative overflow-hidden rounded-[5px] py-1.5 px-2.5 transition-colors ${
                        isActive
                          ? "bg-gray-100 dark:bg-[#1a1a1a]"
                          : "hover:bg-gray-50 dark:hover:bg-[#1a1a1a]/50"
                      }`}
                    >
                      {!collapsed && <div className="absolute inset-y-0 left-0 rounded-[5px] transition-all duration-300" style={{ width: `${pct}%`, backgroundColor: '#555', opacity: isActive ? 0.25 : 0.08 }} />}
                      {collapsed ? (
                        <div className="relative flex items-center justify-center">
                          {IconComponent && <IconComponent size={14} className={isActive ? "text-gray-900 dark:text-white" : "text-gray-400 dark:text-gray-500"} />}
                        </div>
                      ) : (
                        <div className="relative flex items-center justify-between text-xs font-inter-tight font-bold gap-2">
                          <div className="flex items-center gap-2 min-w-0 flex-1">
                            {IconComponent && <IconComponent size={14} className={`flex-shrink-0 ${isActive ? "text-gray-900 dark:text-white" : "text-gray-400 dark:text-gray-500"}`} />}
                            <span className={`truncate ${isActive ? "text-gray-900 dark:text-white" : "text-gray-500 dark:text-gray-400"}`} title={tCategory(item.id) || item.label}>{tCategory(item.id) || item.label}</span>
                          </div>
                          {count !== undefined && (
                            <span className="text-[10px] text-gray-400 dark:text-gray-500 flex-shrink-0">{count}</span>
                          )}
                        </div>
                      )}
                    </button>
                  );
                })}
                <div className="h-px bg-gray-200 dark:bg-gray-700 my-2" />
                {/* Asset type filters with colored progress bars */}
                {financeSidebarItems.slice(4).map((item) => {
                  const isActive = financePrimaryFilter === item.id;
                  const IconComponent = FINANCE_ICONS[item.id];
                  const count = financeCounts[item.slug];
                  // Hide if count is explicitly 0 (still show before counts load)
                  if (count === 0) return null;
                  const pct = count !== undefined ? (count / maxFinanceCount) * 100 : 0;
                  const barColor = financeColors[item.id] || '#555';
                  return (
                    <button
                      key={item.id}
                      onClick={() => {
                        startTransition(() => {
                          setFinancePrimaryFilter(item.id);
                          setFinanceSecondaryFilter("all");
                        });
                      }}
                      title={collapsed ? item.label : undefined}
                      className={`w-full relative overflow-hidden rounded-[5px] py-1.5 px-2.5 transition-colors ${
                        isActive
                          ? "bg-gray-100 dark:bg-[#1a1a1a]"
                          : "hover:bg-gray-50 dark:hover:bg-[#1a1a1a]/50"
                      }`}
                    >
                      {!collapsed && <div className="absolute inset-y-0 left-0 rounded-[5px] transition-all duration-300" style={{ width: `${pct}%`, backgroundColor: barColor, opacity: isActive ? 0.3 : 0.1 }} />}
                      {collapsed ? (
                        <div className="relative flex items-center justify-center">
                          {IconComponent && <IconComponent size={14} className={isActive ? "text-gray-900 dark:text-white" : "text-gray-400 dark:text-gray-500"} />}
                        </div>
                      ) : (
                        <div className="relative flex items-center justify-between text-xs font-inter-tight font-bold gap-2">
                          <div className="flex items-center gap-2 min-w-0 flex-1">
                            {IconComponent && <IconComponent size={14} className={`flex-shrink-0 ${isActive ? "text-gray-900 dark:text-white" : "text-gray-400 dark:text-gray-500"}`} />}
                            <span className={`truncate ${isActive ? "text-gray-900 dark:text-white" : "text-gray-500 dark:text-gray-400"}`} title={tCategory(item.id) || item.label}>{tCategory(item.id) || item.label}</span>
                          </div>
                          {count !== undefined && (
                            <span className="text-[10px] text-gray-400 dark:text-gray-500 flex-shrink-0">{count}</span>
                          )}
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </aside>
            );
          })()}
          {((category === "crypto" && (effectiveSourceFilter === 'polymarket' || effectiveSourceFilter === 'kalshi'))
            || (category === "commodities" && effectiveSourceFilter === 'kalshi')) && (() => {
            const collapsed = cryptoSidebarCollapsed;
            // Calculate max count for progress bar percentages
            const allCounts = [
              ...cryptoSidebarTimeframes.map(tf => cryptoCounts[tf.tag || 'crypto'] ?? 0),
              ...cryptoSidebarCoins.map(coin => cryptoCounts[(coin.tags?.[0] || coin.id).toLowerCase()] ?? 0),
            ];
            const maxCount = Math.max(...allCounts, 1);
            return (
            <aside className={`hidden min-[1400px]:block flex-shrink-0 transition-all duration-200 ${collapsed ? 'w-16' : 'w-44'}`}>
              {/* `max-h-[calc(100vh-9rem)] overflow-y-auto` so the rail
                  scrolls internally on shorter viewports — without it,
                  the sticky container clipped the bottom coins on
                  laptops/13" displays. */}
              <div className="sticky top-32 space-y-0.5 pt-2 max-h-[calc(100vh-9rem)] overflow-y-auto scrollbar-hide">
                {/* Toggle button */}
                <button
                  onClick={() => setCryptoSidebarCollapsed(!collapsed)}
                  className={`w-full flex items-center px-2 pb-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors ${collapsed ? 'justify-center' : 'justify-end'}`}
                >
                  {collapsed ? <AnimatedChevronRight size={16} className="text-white" /> : <AnimatedChevronLeft size={16} className="text-white" />}
                </button>
                {/* Timeframes */}
                {cryptoSidebarTimeframes.map((tf) => {
                  const isActive = cryptoPrimaryFilter === tf.id;
                  const IconComponent = TIMEFRAME_ICONS[tf.id];
                  const countKey = tf.tag || 'crypto';
                  const count = cryptoCounts[countKey];
                  const pct = count !== undefined ? (count / maxCount) * 100 : 0;
                  return (
                    <button
                      key={tf.id}
                      onClick={() => {
                        // Defer the heavy grid re-render — pointerdown stays
                        // sub-200 ms, the new filter result paints when React
                        // gets to it. Match the category-tab click pattern.
                        startTransition(() => {
                          setCryptoPrimaryFilter(tf.id);
                          setCryptoSecondaryFilter("all");
                        });
                      }}
                      title={collapsed ? (() => { const k = `crypto.tf.${tf.id}` as any; const v = t(k); return v && v !== k ? v : tf.label; })() : undefined}
                      className={`w-full relative overflow-hidden rounded-[5px] py-1.5 px-2.5 transition-colors ${
                        isActive
                          ? "bg-gray-100 dark:bg-[#1a1a1a]"
                          : "hover:bg-gray-50 dark:hover:bg-[#1a1a1a]/50"
                      }`}
                    >
                      {!collapsed && <div className="absolute inset-y-0 left-0 rounded-[5px] transition-all duration-300" style={{ width: `${pct}%`, backgroundColor: '#555', opacity: isActive ? 0.25 : 0.08 }} />}
                      {collapsed ? (
                        <div className="relative flex items-center justify-center gap-1.5">
                          {IconComponent && <IconComponent size={12} className={isActive ? "text-gray-900 dark:text-white" : "text-gray-400 dark:text-gray-500"} />}
                          <span className={`text-[10px] font-inter-tight font-bold ${isActive ? "text-gray-900 dark:text-white" : "text-gray-500 dark:text-gray-400"}`}>
                            {{ all: 'All', '5m': '5m', '15m': '15m', '1h': '1H', '4h': '4H', daily: '1D', weekly: '1W', monthly: '1M', yearly: '1Y', 'pre-market': 'Pre', etf: 'ETF' }[tf.id] || tf.label.slice(0, 3)}
                          </span>
                        </div>
                      ) : (
                        <div className="relative flex items-center justify-between text-xs font-inter-tight font-bold gap-2">
                          <div className="flex items-center gap-2 min-w-0 flex-1">
                            {IconComponent && <IconComponent size={14} className={`flex-shrink-0 ${isActive ? "text-gray-900 dark:text-white" : "text-gray-400 dark:text-gray-500"}`} />}
                            <span className={`truncate ${isActive ? "text-gray-900 dark:text-white" : "text-gray-500 dark:text-gray-400"}`}>{(() => { const k = `crypto.tf.${tf.id}` as any; const v = t(k); return v && v !== k ? v : tf.label; })()}</span>
                          </div>
                          {count !== undefined && (
                            <span className="text-[10px] text-gray-400 dark:text-gray-500 flex-shrink-0">{count}</span>
                          )}
                        </div>
                      )}
                    </button>
                  );
                })}
                <div className="h-px bg-gray-200 dark:bg-gray-700 my-2" />
                {/* Coins */}
                {cryptoSidebarCoins.map((coin) => {
                  const isActive = cryptoPrimaryFilter === coin.id;
                  const iconUrl = cryptoTokenImages[coin.id];
                  const countKey = (coin.tags?.[0] || coin.id).toLowerCase();
                  const count = countKey ? cryptoCounts[countKey] : undefined;
                  const pct = count !== undefined ? (count / maxCount) * 100 : 0;
                  const coinColors: Record<string, string> = {
                    btc: '#F7931A', eth: '#627EEB', sol: '#8B5CF6',
                    xrp: '#028CFF', doge: '#C3A634', mstr: '#FF6B35',
                  };
                  const barColor = coinColors[coin.id] || '#555';
                  // Nested scope rows (commodities: Oil & Gas → WTI Crude …)
                  // render indented so the hierarchy reads visually like
                  // Kalshi's own sidebar.
                  const depth = (coin as { depth?: number }).depth ?? 0;
                  const isChild = depth >= 1;
                  return (
                    <button
                      key={coin.id}
                      onClick={() => {
                        startTransition(() => {
                          setCryptoPrimaryFilter(coin.id);
                          setCryptoSecondaryFilter("all");
                        });
                      }}
                      title={collapsed ? coin.label : undefined}
                      className={`w-full relative overflow-hidden rounded-[5px] py-1.5 ${isChild && !collapsed ? 'pl-7 pr-2.5' : 'px-2.5'} transition-colors ${
                        isActive
                          ? "bg-gray-100 dark:bg-[#1a1a1a]"
                          : "hover:bg-gray-50 dark:hover:bg-[#1a1a1a]/50"
                      }`}
                    >
                      {!collapsed && <div className="absolute inset-y-0 left-0 rounded-[5px] transition-all duration-300" style={{ width: `${pct}%`, backgroundColor: barColor, opacity: isActive ? 0.3 : (isChild ? 0.05 : 0.1) }} />}
                      {(() => {
                        // Lucide fallbacks for non-coin sidebar items. For
                        // non-crypto Kalshi categories (commodities, etc.)
                        // the Bitcoin fallback was confusing next to rows
                        // like "Agriculture" / "Metals" — use the category
                        // icon instead.
                        const FallbackIcon = coin.id === 'pre-market' ? Sunrise
                          : coin.id === 'others' ? MoreHorizontal
                          : category === 'commodities' ? Wheat
                          : Bitcoin;
                        return collapsed ? (
                        <div className="relative flex items-center justify-center gap-1.5">
                          {iconUrl ? (
                            <CdnImage src={iconUrl} alt={coin.label} width={14} height={14} className="w-3.5 h-3.5 rounded-[5px] flex-shrink-0" />
                          ) : (
                            <FallbackIcon size={12} />
                          )}
                          <span className={`text-[10px] font-inter-tight font-bold ${isActive ? "text-gray-900 dark:text-white" : "text-gray-500 dark:text-gray-400"}`}>
                            {{ bitcoin: 'BTC', ethereum: 'ETH', solana: 'SOL', xrp: 'XRP', dogecoin: 'DOGE', microstrategy: 'MSTR' }[coin.id] || coin.id.toUpperCase().slice(0, 4)}
                          </span>
                        </div>
                      ) : (
                        <div className="relative flex items-center justify-between text-xs font-inter-tight font-bold gap-2">
                          <div className="flex items-center gap-2 min-w-0 flex-1">
                            {iconUrl ? (
                              <CdnImage src={iconUrl} alt={coin.label} width={16} height={16} className="w-4 h-4 rounded-[5px] flex-shrink-0" />
                            ) : (
                              <FallbackIcon size={14} />
                            )}
                            <span className={`truncate ${isActive ? "text-gray-900 dark:text-white" : "text-gray-500 dark:text-gray-400"}`}>{coin.label}</span>
                          </div>
                          {count !== undefined && (
                            <span className="text-[10px] text-gray-400 dark:text-gray-500 flex-shrink-0">{count}</span>
                          )}
                        </div>
                      );
                      })()}
                    </button>
                  );
                })}
                </div>
            </aside>
            );
          })()}
          {/* Kalshi Sports sidebar — sport → competitions (expandable) */}
          {category === "sports" && effectiveSourceFilter === 'kalshi' && kalshiSportsSidebar && (() => {
            const maxCount = Math.max(...kalshiSportsSidebar.sports.map(s => s.count), 1);
            return (
            <aside className="hidden min-[1400px]:block flex-shrink-0 w-56">
              <div className="sticky top-32 space-y-0.5 pt-2 max-h-[calc(100dvh-9rem)] overflow-y-auto scrollbar-hide">
                {/* All sports */}
                <button
                  onClick={() => {
                    setActiveKalshiSubcategory(null);
                    setActiveKalshiCompetition(null);
                    window.history.pushState({}, '', sourceUrl('/markets/sports', source));
                  }}
                  className={`w-full relative overflow-hidden rounded-[5px] py-1.5 px-2.5 transition-colors ${
                    !activeKalshiSubcategory
                      ? "bg-gray-100 dark:bg-[#1a1a1a]"
                      : "hover:bg-gray-50 dark:hover:bg-[#1a1a1a]/50"
                  }`}
                >
                  <div className="relative flex items-center justify-between text-xs font-inter-tight font-bold">
                    <span className={!activeKalshiSubcategory ? "text-gray-900 dark:text-white" : "text-gray-500 dark:text-gray-400"}>All sports</span>
                    <span className="text-[10px] text-gray-400 dark:text-gray-500">{kalshiSportsSidebar.total}</span>
                  </div>
                </button>
                <div className="h-px bg-gray-200 dark:bg-gray-800 my-1" />
                {/* Sports list */}
                {kalshiSportsSidebar.sports.map((s) => {
                  const isActive = activeKalshiSubcategory === s.sport;
                  const isExpanded = expandedKalshiSport === s.sport;
                  const pct = (s.count / maxCount) * 100;
                  return (
                    <div key={s.sport}>
                      <button
                        onClick={() => {
                          const next = isActive ? null : s.sport;
                          setActiveKalshiSubcategory(next);
                          setActiveKalshiCompetition(null);
                          setExpandedKalshiSport(isExpanded ? null : s.sport);
                          const url = next
                            ? sourceUrl(`/markets/sports?subcategory=${encodeURIComponent(next)}`, source)
                            : sourceUrl('/markets/sports', source);
                          window.history.pushState({}, '', url);
                        }}
                        className={`w-full relative overflow-hidden rounded-[5px] py-1.5 px-2.5 transition-colors ${
                          isActive
                            ? "bg-gray-100 dark:bg-[#1a1a1a]"
                            : "hover:bg-gray-50 dark:hover:bg-[#1a1a1a]/50"
                        }`}
                      >
                        <div className="absolute inset-y-0 left-0 rounded-[5px] transition-all duration-300" style={{ width: `${pct}%`, backgroundColor: '#555', opacity: isActive ? 0.25 : 0.08 }} />
                        <div className="relative flex items-center justify-between text-xs font-inter-tight font-bold">
                          <div className="flex items-center gap-1.5 min-w-0">
                            {s.competitions.length > 0 && (
                              <ChevronDown
                                size={11}
                                className={`text-gray-400 dark:text-gray-500 transition-transform flex-shrink-0 ${isExpanded ? '' : '-rotate-90'}`}
                              />
                            )}
                            <span className={`truncate ${isActive ? "text-gray-900 dark:text-white" : "text-gray-500 dark:text-gray-400"}`}>{s.sport}</span>
                          </div>
                          <span className="text-[10px] text-gray-400 dark:text-gray-500 ml-1 flex-shrink-0">{s.count}</span>
                        </div>
                      </button>
                      {isExpanded && s.competitions.length > 0 && (
                        <div className="ml-3 mt-0.5 mb-1 space-y-0.5 border-l border-gray-200 dark:border-gray-800 pl-2">
                          {s.competitions.slice(0, 15).map((c) => {
                            const compActive = activeKalshiCompetition === c.name && activeKalshiSubcategory === s.sport;
                            return (
                            <button
                              key={c.name}
                              onClick={() => {
                                if (compActive) {
                                  setActiveKalshiCompetition(null);
                                  window.history.pushState({}, '', sourceUrl(`/markets/sports?subcategory=${encodeURIComponent(s.sport)}`, source));
                                } else {
                                  setActiveKalshiSubcategory(s.sport);
                                  setActiveKalshiCompetition(c.name);
                                  window.history.pushState({}, '', sourceUrl(`/markets/sports?subcategory=${encodeURIComponent(s.sport)}&competition=${encodeURIComponent(c.name)}`, source));
                                }
                              }}
                              className={`w-full text-left py-1 px-2 rounded text-[11px] transition-colors flex items-center justify-between ${
                                compActive
                                  ? "bg-gray-100 dark:bg-[#1a1a1a] text-gray-900 dark:text-white font-semibold"
                                  : "text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-[#1a1a1a]/50"
                              }`}
                            >
                              <span className="truncate">{c.name}</span>
                              {c.count > 0 && (
                                <span className="text-[10px] text-gray-400 dark:text-gray-500 ml-1">{c.count}</span>
                              )}
                            </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </aside>
            );
          })()}
          <div className={(((category === "crypto" || category === "finance") && effectiveSourceFilter === 'polymarket') || (category === "crypto" && effectiveSourceFilter === 'kalshi') || (category === "sports" && effectiveSourceFilter === 'kalshi')) ? "flex-1 min-w-0" : ""}>

        {/* Finance: mobile row — menu button (left) + Market Overview toggle (right) */}
        {category === "finance" && effectiveSourceFilter === 'polymarket' && (
          <div className="flex items-center mb-3 min-[1400px]:hidden">
            <button
              onClick={() => setMobileFinanceSidebarOpen(true)}
              className="p-2 rounded-[5px] bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
            >
              <MenuIcon size={18} className="text-white" />
            </button>
            <div className="flex-1" />
            <button
              onClick={() => {
                const newValue = !financeOverviewExpanded;
                setFinanceOverviewExpanded(newValue);
                localStorage.setItem('financeOverviewExpanded', JSON.stringify(newValue));
              }}
              className="flex items-center gap-2"
              title={t("crypto.toggleOverview")}
            >
              <span className="text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wide whitespace-nowrap">{t("crypto.marketOverview")}</span>
              <div className={`relative w-9 h-5 rounded-[5px] transition-colors duration-200 ${
                financeOverviewExpanded
                  ? "bg-gray-900 dark:bg-white"
                  : "bg-gray-300 dark:bg-gray-600"
              }`}>
                <div className={`absolute top-[2px] w-4 h-4 rounded-[5px] bg-white dark:bg-gray-900 transition-transform duration-200 ${
                  financeOverviewExpanded ? "translate-x-[18px]" : "translate-x-[2px]"
                }`} />
              </div>
            </button>
          </div>
        )}

        {/* Live Crypto Markets - Scrolling ticker marquee (top of content) */}
        {category === "crypto" && effectiveSourceFilter !== 'kalshi' && effectiveSourceFilter !== 'predict' && (
          <CryptoLiveTicker
            liveMarketsLoaded={liveMarketsLoaded}
            allLiveCryptoMarkets={allLiveCryptoMarkets}
            liveCryptoCardItems={allLiveCryptoWithTimeframe}
            cryptoPrices={cryptoPrices}
            marketPrices={marketPrices}
            eventPriceChanges={eventPriceChanges}
            onRefresh5Min={fetchLive5MinMarkets}
            onRefresh15Min={fetchLive15MinMarkets}
          />
        )}

        {/* Finance Overview - Indices, Earnings, VIX, Sectors, Commodities */}
        {category === "finance" && effectiveSourceFilter === 'polymarket' && (
          <div className={financeOverviewExpanded ? "mb-6" : ""}>
            <FinanceOverview expanded={financeOverviewExpanded} />
          </div>
        )}


        {/* Crypto / Commodities: mobile row — menu button (left) + Market Overview toggle (right) */}
        {((category === "crypto" && (effectiveSourceFilter === 'polymarket' || effectiveSourceFilter === 'kalshi'))
          || (category === "commodities" && effectiveSourceFilter === 'kalshi')) && (
          <div className="flex items-center mb-3 min-[1400px]:hidden">
            <button
              onClick={() => setMobileCryptoSidebarOpen(true)}
              className="p-2 rounded-[5px] bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
            >
              <MenuIcon size={18} className="text-white" />
            </button>
            <div className="flex-1" />
            {/* Market Overview toggle is crypto-specific (fear/greed, BTC
                dominance, global market cap). Commodities has no analogous
                dashboard so hide it there. */}
            {category === "crypto" && (
            <button
              onClick={() => {
                const newValue = !cryptoOverviewExpanded;
                setCryptoOverviewExpanded(newValue);
                localStorage.setItem('cryptoOverviewExpanded', JSON.stringify(newValue));
              }}
              className="flex items-center gap-2"
              title={t("crypto.toggleOverview")}
            >
              <span className="text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wide whitespace-nowrap">{t("crypto.marketOverview")}</span>
              <div className={`relative w-9 h-5 rounded-[5px] transition-colors duration-200 ${
                cryptoOverviewExpanded
                  ? "bg-gray-900 dark:bg-white"
                  : "bg-gray-300 dark:bg-gray-600"
              }`}>
                <div className={`absolute top-[2px] w-4 h-4 rounded-[5px] bg-white dark:bg-gray-900 transition-transform duration-200 ${
                  cryptoOverviewExpanded ? "translate-x-[18px]" : "translate-x-[2px]"
                }`} />
              </div>
            </button>
            )}
          </div>
        )}

        {/* Sports (Kalshi): mobile menu button to open sport sidebar bottom sheet */}
        {category === "sports" && effectiveSourceFilter === 'kalshi' && (
          <div className="flex items-center mb-3 min-[1400px]:hidden">
            <button
              onClick={() => setMobileKalshiSportsSidebarOpen(true)}
              className="p-2 rounded-[5px] bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors flex items-center gap-2"
            >
              <MenuIcon size={18} className="text-white" />
              <span className="text-xs font-bold text-gray-600 dark:text-gray-300">All sports</span>
            </button>
          </div>
        )}

        {/* Sports (Kalshi): mobile bottom sheet — sport list with expandable competitions */}
        {category === "sports" && effectiveSourceFilter === 'kalshi' && kalshiSportsSidebar && (
          <MobileBottomSheet open={mobileKalshiSportsSidebarOpen} onClose={() => setMobileKalshiSportsSidebarOpen(false)}>
            <div className="space-y-1 px-4 py-3">
              <button
                onClick={() => {
                  setActiveKalshiSubcategory(null);
                  setMobileKalshiSportsSidebarOpen(false);
                  window.history.pushState({}, '', sourceUrl('/markets/sports', source));
                }}
                className={`w-full flex items-center justify-between rounded-[5px] px-3 py-2 text-sm font-bold ${
                  !activeKalshiSubcategory
                    ? "bg-gray-100 dark:bg-[#1a1a1a] text-gray-900 dark:text-white"
                    : "text-gray-500 dark:text-gray-400"
                }`}
              >
                <span>All sports</span>
                <span className="text-[11px] text-gray-400">{kalshiSportsSidebar.total}</span>
              </button>
              <div className="h-px bg-gray-200 dark:bg-gray-800 my-1" />
              {kalshiSportsSidebar.sports.map((s) => {
                const isActive = activeKalshiSubcategory === s.sport;
                return (
                  <button
                    key={s.sport}
                    onClick={() => {
                      setActiveKalshiSubcategory(s.sport);
                      setMobileKalshiSportsSidebarOpen(false);
                      window.history.pushState({}, '', sourceUrl(`/markets/sports?subcategory=${encodeURIComponent(s.sport)}`, source));
                    }}
                    className={`w-full flex items-center justify-between rounded-[5px] px-3 py-2 text-sm font-bold ${
                      isActive
                        ? "bg-gray-100 dark:bg-[#1a1a1a] text-gray-900 dark:text-white"
                        : "text-gray-500 dark:text-gray-400"
                    }`}
                  >
                    <span>{s.sport}</span>
                    <span className="text-[11px] text-gray-400">{s.count}</span>
                  </button>
                );
              })}
            </div>
          </MobileBottomSheet>
        )}

        {/* Market Overview - Global stats + Fear & Greed */}
        {category === "crypto" && effectiveSourceFilter !== 'kalshi' && effectiveSourceFilter !== 'predict' && (
          <div className={cryptoOverviewExpanded ? "mb-6" : ""}>
            <div className={`rounded-[5px] bg-[#f5f5f5] dark:bg-[#0f0f12] border border-gray-200/50 dark:border-gray-800 overflow-hidden transition-all duration-500 ease-in-out ${
              cryptoOverviewExpanded ? 'max-h-[600px] opacity-100' : 'max-h-0 opacity-0 border-transparent'
            }`}>
                <div className="flex flex-col md:flex-row md:flex-wrap items-stretch gap-3 p-3 pb-1">
              {/* Fear & Greed Index - Style like alternative.me with Historical Values */}
              <div className="w-full flex flex-col gap-4 p-4 bg-white/50 dark:bg-[#0f0f12] rounded-[5px] border border-gray-200/30 dark:border-gray-800/50">
                {/* Top row: Gauge + Historical + Over Time (desktop: row, mobile: column) */}
                <div className="flex flex-col md:flex-row md:items-stretch gap-4 md:gap-6">
                {/* Left: Gauge */}
                <div className="flex flex-col w-full md:w-auto" style={{ minWidth: '240px' }}>
                  {/* Header */}
                  <div className="flex items-center gap-2 mb-2">
                    <ChartAreaIcon size={16} className="text-gray-600 dark:text-gray-400" />
                    <span className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">{t("crypto.fearGreedIndex")}</span>
                  </div>
                  {/* Now + Classification */}
                  <div className="mb-2">
                    <span className="text-sm text-gray-600 dark:text-gray-400">{t("crypto.nowColon")}</span>
                    <span className={`text-sm font-semibold ${
                      !fearGreed ? 'text-gray-500' :
                      fearGreed.value <= 25 ? 'text-[#ea3943]' :
                      fearGreed.value <= 45 ? 'text-[#ea8c00]' :
                      fearGreed.value <= 55 ? 'text-[#f3d42f]' :
                      fearGreed.value <= 75 ? 'text-[#93d900]' :
                      'text-[#16c784]'
                    }`}>
                      {fearGreed?.classification
                        ? (() => {
                            const cls = fearGreed.classification.toLowerCase().replace(/\s+/g, '');
                            const map: Record<string, string> = {
                              extremefear: 'crypto.fg.extremeFear',
                              fear: 'crypto.fg.fear',
                              neutral: 'crypto.fg.neutral',
                              greed: 'crypto.fg.greed',
                              extremegreed: 'crypto.fg.extremeGreed',
                            };
                            const k = map[cls];
                            return k ? t(k as any) : fearGreed.classification;
                          })()
                        : t("common.loading")}
                    </span>
                  </div>
                  {/* Gauge with value circle on left */}
                  <div className="relative flex items-end justify-center md:justify-start">
                    {/* Value circle on left - animated */}
                    <div className={`w-14 h-14 rounded-[5px] flex items-center justify-center text-white text-xl font-bold flex-shrink-0 transition-colors duration-300 ${
                      !fearGreed ? 'bg-gray-500' :
                      animatedFearGreed.value <= 25 ? 'bg-[#ea3943]' :
                      animatedFearGreed.value <= 45 ? 'bg-[#ea8c00]' :
                      animatedFearGreed.value <= 55 ? 'bg-[#f3d42f]' :
                      animatedFearGreed.value <= 75 ? 'bg-[#93d900]' :
                      'bg-[#16c784]'
                    }`}>
                      {fearGreed ? animatedFearGreed.value : '--'}
                    </div>
                    {/* Gauge */}
                    <svg width="220" height="120" viewBox="0 0 200 115" className="flex-shrink-0 ml-2">
                      <defs>
                        <linearGradient id="gaugeGradMarket" x1="0%" y1="0%" x2="100%" y2="0%">
                          <stop offset="0%" stopColor="#ea3943" />
                          <stop offset="20%" stopColor="#ea8c00" />
                          <stop offset="45%" stopColor="#f3d42f" />
                          <stop offset="70%" stopColor="#93d900" />
                          <stop offset="100%" stopColor="#16c784" />
                        </linearGradient>
                      </defs>
                      {/* Colored arc */}
                      <path d="M 15 105 A 85 85 0 0 1 185 105" fill="none" stroke="url(#gaugeGradMarket)" strokeWidth="18" strokeLinecap="round" />
                      {/* Needle - Triangle pointer, animated */}
                      {fearGreed && (
                        <g
                          transform={`rotate(${(animatedFearGreed.value / 100) * 180 - 90}, 100, 105)`}
                          style={{ transition: 'transform 0.05s linear' }}
                        >
                          <polygon points="100,20 93,105 107,105" fill="#bbb" />
                          <circle cx="100" cy="105" r="8" fill="#bbb" />
                        </g>
                      )}
                    </svg>
                  </div>
                </div>
                {/* Separator - Desktop only */}
                <div className="hidden md:block w-px h-full bg-gray-300/30 dark:bg-gray-600/30 self-stretch" />
                {/* Right: Historical Values - Desktop only */}
                <div className="hidden md:flex flex-col min-w-[140px] self-stretch">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">{t("crypto.historical")}</span>
                  </div>
                  <div className="flex-1 flex flex-col justify-center">
                  <div className="divide-y divide-gray-200 dark:divide-gray-700 text-sm">
                    {/* Now */}
                    <div className="flex justify-between items-center py-1.5 gap-4">
                      <span className="text-gray-600 dark:text-gray-400">{t("crypto.now")}</span>
                      <span className={`font-semibold transition-colors duration-300 ${
                        !fearGreed ? 'text-gray-500' :
                        animatedFearGreed.value <= 25 ? 'text-[#ea3943]' :
                        animatedFearGreed.value <= 45 ? 'text-[#ea8c00]' :
                        animatedFearGreed.value <= 55 ? 'text-[#f3d42f]' :
                        animatedFearGreed.value <= 75 ? 'text-[#93d900]' :
                        'text-[#16c784]'
                      }`}>{fearGreed ? animatedFearGreed.value : '--'}</span>
                    </div>
                    {/* Yesterday */}
                    <div className="flex justify-between items-center py-1.5 gap-4">
                      <span className="text-gray-600 dark:text-gray-400">{t("crypto.yesterday")}</span>
                      <span className={`font-semibold transition-colors duration-300 ${
                        !fearGreed?.yesterday ? 'text-gray-500' :
                        animatedFearGreed.yesterday <= 25 ? 'text-[#ea3943]' :
                        animatedFearGreed.yesterday <= 45 ? 'text-[#ea8c00]' :
                        animatedFearGreed.yesterday <= 55 ? 'text-[#f3d42f]' :
                        animatedFearGreed.yesterday <= 75 ? 'text-[#93d900]' :
                        'text-[#16c784]'
                      }`}>{fearGreed?.yesterday ? animatedFearGreed.yesterday : '--'}</span>
                    </div>
                    {/* Last Week */}
                    <div className="flex justify-between items-center py-1.5 gap-4">
                      <span className="text-gray-600 dark:text-gray-400">{t("crypto.lastWeek")}</span>
                      <span className={`font-semibold transition-colors duration-300 ${
                        !fearGreed?.lastWeek ? 'text-gray-500' :
                        animatedFearGreed.lastWeek <= 25 ? 'text-[#ea3943]' :
                        animatedFearGreed.lastWeek <= 45 ? 'text-[#ea8c00]' :
                        animatedFearGreed.lastWeek <= 55 ? 'text-[#f3d42f]' :
                        animatedFearGreed.lastWeek <= 75 ? 'text-[#93d900]' :
                        'text-[#16c784]'
                      }`}>{fearGreed?.lastWeek ? animatedFearGreed.lastWeek : '--'}</span>
                    </div>
                    {/* Last Month */}
                    <div className="flex justify-between items-center py-1.5 gap-4">
                      <span className="text-gray-600 dark:text-gray-400">{t("crypto.lastMonth")}</span>
                      <span className={`font-semibold transition-colors duration-300 ${
                        !fearGreed?.lastMonth ? 'text-gray-500' :
                        animatedFearGreed.lastMonth <= 25 ? 'text-[#ea3943]' :
                        animatedFearGreed.lastMonth <= 45 ? 'text-[#ea8c00]' :
                        animatedFearGreed.lastMonth <= 55 ? 'text-[#f3d42f]' :
                        animatedFearGreed.lastMonth <= 75 ? 'text-[#93d900]' :
                        'text-[#16c784]'
                      }`}>{fearGreed?.lastMonth ? animatedFearGreed.lastMonth : '--'}</span>
                    </div>
                  </div>
                  </div>
                </div>
                {/* Separator - Desktop only */}
                {fearGreed?.historyChart && fearGreed.historyChart.length > 0 && (
                  <div className="hidden md:block w-px h-full bg-gray-300/30 dark:bg-gray-600/30 self-stretch" />
                )}
                {/* Fear & Greed Over Time Chart - Desktop inline */}
                {fearGreed?.historyChart && fearGreed.historyChart.length > 0 && (
                  <div className="hidden md:flex flex-col flex-1 min-w-[280px]">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">{t("crypto.overTime")}</span>
                      <div className="flex items-center gap-1">
                        {(['7d', '30d', '90d', '180d', '1y'] as const).map((tf) => (
                          <button
                            key={tf}
                            onClick={(e) => { e.stopPropagation(); setFearGreedTimeframe(tf); }}
                            className={`px-2 py-0.5 text-[10px] font-medium rounded transition-colors ${
                              fearGreedTimeframe === tf
                                ? 'bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white'
                                : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
                            }`}
                          >
                            {tf}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="flex-1">
                      <FearGreedChart
                        data={
                          fearGreedTimeframe === '7d' ? (fearGreed.historyChart7d || []) :
                          fearGreedTimeframe === '90d' ? (fearGreed.historyChart90d || []) :
                          fearGreedTimeframe === '180d' ? (fearGreed.historyChart180d || []) :
                          fearGreedTimeframe === '1y' ? (fearGreed.historyChart365d || []) :
                          fearGreed.historyChart
                        }
                        height={180}
                      />
                    </div>
                  </div>
                )}
                </div>
                {/* Historical Values - Mobile below gauge */}
                <div className="flex md:hidden flex-col w-full pt-3 border-t border-gray-200 dark:border-gray-700">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">{t("crypto.historical")}</span>
                  </div>
                  <div className="grid grid-cols-4 gap-2 text-sm">
                    <div className="flex flex-col items-center">
                      <span className="text-gray-500 text-xs">{t("crypto.now")}</span>
                      <span className={`font-semibold transition-colors duration-300 ${
                        !fearGreed ? 'text-gray-500' :
                        animatedFearGreed.value <= 25 ? 'text-[#ea3943]' :
                        animatedFearGreed.value <= 45 ? 'text-[#ea8c00]' :
                        animatedFearGreed.value <= 55 ? 'text-[#f3d42f]' :
                        animatedFearGreed.value <= 75 ? 'text-[#93d900]' :
                        'text-[#16c784]'
                      }`}>{fearGreed ? animatedFearGreed.value : '--'}</span>
                    </div>
                    <div className="flex flex-col items-center">
                      <span className="text-gray-500 text-xs">{t("crypto.yesterday")}</span>
                      <span className={`font-semibold transition-colors duration-300 ${
                        !fearGreed?.yesterday ? 'text-gray-500' :
                        animatedFearGreed.yesterday <= 25 ? 'text-[#ea3943]' :
                        animatedFearGreed.yesterday <= 45 ? 'text-[#ea8c00]' :
                        animatedFearGreed.yesterday <= 55 ? 'text-[#f3d42f]' :
                        animatedFearGreed.yesterday <= 75 ? 'text-[#93d900]' :
                        'text-[#16c784]'
                      }`}>{fearGreed?.yesterday ? animatedFearGreed.yesterday : '--'}</span>
                    </div>
                    <div className="flex flex-col items-center">
                      <span className="text-gray-500 text-xs">{t("crypto.weekShort")}</span>
                      <span className={`font-semibold transition-colors duration-300 ${
                        !fearGreed?.lastWeek ? 'text-gray-500' :
                        animatedFearGreed.lastWeek <= 25 ? 'text-[#ea3943]' :
                        animatedFearGreed.lastWeek <= 45 ? 'text-[#ea8c00]' :
                        animatedFearGreed.lastWeek <= 55 ? 'text-[#f3d42f]' :
                        animatedFearGreed.lastWeek <= 75 ? 'text-[#93d900]' :
                        'text-[#16c784]'
                      }`}>{fearGreed?.lastWeek ? animatedFearGreed.lastWeek : '--'}</span>
                    </div>
                    <div className="flex flex-col items-center">
                      <span className="text-gray-500 text-xs">{t("crypto.monthShort")}</span>
                      <span className={`font-semibold transition-colors duration-300 ${
                        !fearGreed?.lastMonth ? 'text-gray-500' :
                        animatedFearGreed.lastMonth <= 25 ? 'text-[#ea3943]' :
                        animatedFearGreed.lastMonth <= 45 ? 'text-[#ea8c00]' :
                        animatedFearGreed.lastMonth <= 55 ? 'text-[#f3d42f]' :
                        animatedFearGreed.lastMonth <= 75 ? 'text-[#93d900]' :
                        'text-[#16c784]'
                      }`}>{fearGreed?.lastMonth ? animatedFearGreed.lastMonth : '--'}</span>
                    </div>
                  </div>
                </div>
                {/* Fear & Greed Over Time Chart - Mobile below */}
                {fearGreed?.historyChart && fearGreed.historyChart.length > 0 && (
                  <div className="flex md:hidden flex-col w-full pt-3 border-t border-gray-200 dark:border-gray-700">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">{t("crypto.overTime")}</span>
                      <div className="flex items-center gap-1">
                        {(['7d', '30d', '90d', '180d', '1y'] as const).map((tf) => (
                          <button
                            key={tf}
                            onClick={(e) => { e.stopPropagation(); setFearGreedTimeframe(tf); }}
                            className={`px-1.5 py-0.5 text-[9px] font-medium rounded transition-colors ${
                              fearGreedTimeframe === tf
                                ? 'bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white'
                                : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
                            }`}
                          >
                            {tf}
                          </button>
                        ))}
                      </div>
                    </div>
                    <FearGreedChart
                      data={
                        fearGreedTimeframe === '7d' ? (fearGreed.historyChart7d || []) :
                        fearGreedTimeframe === '90d' ? (fearGreed.historyChart90d || []) :
                        fearGreedTimeframe === '180d' ? (fearGreed.historyChart180d || []) :
                        fearGreedTimeframe === '1y' ? (fearGreed.historyChart365d || []) :
                        fearGreed.historyChart
                      }
                      height={180}
                      compact
                    />
                  </div>
                )}
              </div>

                </div>
                {/* DEXTools footer */}
                <a
                  href="https://www.dextools.io/app/top-coins"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1.5 px-4 py-1 pb-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                >
                  <span className="text-[10px] font-medium">{t("crypto.exploreTop100")}</span>
                  {/* width/height explicit — sin esto el img generaba 0.22
                      de CLS al pintar (medido en producción 2026-05-10).
                      El logo es 240×60 nativo; mantenemos el aspect ratio
                      con `h-5 w-auto` pero aportamos `width`/`height` para
                      que el navegador reserve el slot antes de cargarlo. */}
                  <img src="/logos/dextools-logo.png" alt="DEXTools" width={80} height={20} className="h-5 w-auto opacity-60" />
                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" /></svg>
                </a>
            </div>
          </div>
        )}

        {/* Crypto Subcategories — after carousel, before market grid */}
        {category === "crypto" && effectiveSourceFilter === 'polymarket' && (
          <div className="hidden lg:flex items-center gap-3 mb-4">
            <HorizontalScroll
              wrapperClassName="flex-1 min-w-0"
              className="flex items-center gap-1.5 pb-1"
              fadeFrom="bg-gray-50 dark:bg-[#0f0f12]"
            >
              {(() => {
                if (cryptoSecondaryTabs && cryptoSecondaryTabs.length > 1) {
                  return (
                    <div className="flex items-center gap-1.5">
                      {cryptoSecondaryTabs.map((tab) => (
                        <button
                          key={tab.slug}
                          onClick={() => setCryptoSecondaryFilter(tab.slug)}
                          className={`px-3 py-1.5 rounded-[5px] text-xs font-semibold transition-colors whitespace-nowrap flex-shrink-0 ${
                            cryptoSecondaryFilter === tab.slug
                              ? "bg-gray-900 dark:bg-white text-white dark:text-black"
                              : "bg-black/[0.04] dark:bg-white/[0.06] text-gray-600 dark:text-gray-400 hover:bg-black/[0.12] dark:hover:bg-white/[0.14]"
                          }`}
                        >
                          {tCategory(tab.slug) || tab.label}
                        </button>
                      ))}
                    </div>
                  );
                }
                if (scrapedCrypto?.tabsByCategory) return null;
                const isTimeframe = cryptoSubcategories.timeframes.some(t => t.id === cryptoPrimaryFilter);
                const isCoin = cryptoSubcategories.coins.some(c => c.id === cryptoPrimaryFilter);
                const isETF = cryptoPrimaryFilter === "etf";
                const isPreMarket = cryptoPrimaryFilter === "pre-market";
                const noPills = ['5m', '15m', '1h', '4h', 'daily', 'yearly'].includes(cryptoPrimaryFilter);
                if (((isTimeframe && !isPreMarket && !isETF && !noPills) || isCoin)) {
                  return (
                    <div className="flex items-center gap-1.5">
                      {cryptoSubcategories.marketTypes.map((type) => (
                        <button key={type.id} onClick={() => setCryptoSecondaryFilter(type.id)}
                          className={`px-3 py-1.5 rounded-[5px] text-xs font-semibold transition-colors whitespace-nowrap flex-shrink-0 ${
                            cryptoSecondaryFilter === type.id
                              ? "bg-gray-900 dark:bg-white text-white dark:text-black"
                              : "bg-black/[0.04] dark:bg-white/[0.06] text-gray-600 dark:text-gray-400 hover:bg-black/[0.12] dark:hover:bg-white/[0.14]"
                          }`}>{(() => { const k = `crypto.mt.${type.id}` as any; const v = t(k); return v && v !== k ? v : type.label; })()}</button>
                      ))}
                      {!isCoin && (
                        <>
                          <div className="w-px h-5 bg-gray-300 dark:bg-gray-700 flex-shrink-0 mx-1" />
                          {cryptoSidebarCoins.map((coin) => (
                            <button key={coin.id} onClick={() => setCryptoSecondaryFilter(coin.id)}
                              className={`px-3 py-1.5 rounded-[5px] text-xs font-semibold transition-colors whitespace-nowrap flex-shrink-0 ${
                                cryptoSecondaryFilter === coin.id
                                  ? "bg-gray-900 dark:bg-white text-white dark:text-black"
                                  : "bg-black/[0.04] dark:bg-white/[0.06] text-gray-600 dark:text-gray-400 hover:bg-black/[0.12] dark:hover:bg-white/[0.14]"
                              }`}>{coin.label}</button>
                          ))}
                        </>
                      )}
                    </div>
                  );
                }
                if (isETF) {
                  return (
                    <div className="flex items-center gap-1.5">
                      {cryptoSubcategories.etfFilters.map((filter) => (
                        <button key={filter.id} onClick={() => setCryptoSecondaryFilter(filter.id)}
                          className={`px-3 py-1.5 rounded-[5px] text-xs font-semibold transition-colors whitespace-nowrap flex-shrink-0 ${
                            cryptoSecondaryFilter === filter.id
                              ? "bg-gray-900 dark:bg-white text-white dark:text-black"
                              : "bg-black/[0.04] dark:bg-white/[0.06] text-gray-600 dark:text-gray-400 hover:bg-black/[0.12] dark:hover:bg-white/[0.14]"
                          }`}>{(() => { const k = `crypto.etf.${filter.id}` as any; const v = t(k); return v && v !== k ? v : filter.label; })()}</button>
                      ))}
                    </div>
                  );
                }
                return null;
              })()}
            </HorizontalScroll>
            {/* Market Overview toggle — desktop only */}
            <button
              onClick={() => {
                const newValue = !cryptoOverviewExpanded;
                setCryptoOverviewExpanded(newValue);
                localStorage.setItem('cryptoOverviewExpanded', JSON.stringify(newValue));
              }}
              className="flex-shrink-0 hidden min-[1400px]:flex items-center gap-2"
              title={t("crypto.toggleOverview")}
            >
              <span className="text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wide whitespace-nowrap">{t("crypto.marketOverview")}</span>
              <div className={`relative w-9 h-5 rounded-[5px] transition-colors duration-200 ${
                cryptoOverviewExpanded ? "bg-gray-900 dark:bg-white" : "bg-gray-300 dark:bg-gray-600"
              }`}>
                <div className={`absolute top-[2px] w-4 h-4 rounded-[5px] bg-white dark:bg-gray-900 transition-transform duration-200 ${
                  cryptoOverviewExpanded ? "translate-x-[18px]" : "translate-x-[2px]"
                }`} />
              </div>
            </button>
          </div>
        )}

        {/* Source-aware grid for kalshi/predict/hyperliquid (polymarket falls
            through to the unified EventsDisplay below). Skipped for the
            breaking/for-you/bookmarks/dashboard categories that own their
            own layouts. */}
        {category !== 'breaking' && category !== 'for-you' && category !== 'bookmarks' && !['macro', 'fed-rates', 'trump', 'elections'].includes(category || '') && (() => {
          // Show Predict.fun markets when predict filter is selected (grid view only - list view handled below)
          if (effectiveSourceFilter === 'predict' && viewMode === 'grid') {
            // Skip if loading or still transitioning from another source
            if (loadingAggregated || !hasFetchedAggregated) {
              return null;
            }

            const predictMarkets = aggregatedMarkets.filter(m => m.source === 'predict');

            if (predictMarkets.length === 0) {
              return (
                <SourceEmptyState
                  source="predict"
                  onSwitchToPolymarket={() => updateSourceFilter("polymarket")}
                />
              );
            }

            return (
              <>
                <div className={`grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3 mb-4 ${isCollapsed ? '2xl:grid-cols-4 3xl:grid-cols-5' : '2xl:grid-cols-4'}`}>
                  {predictMarkets.map((market, index) => (
                    <EventCard key={market.id} {...aggregatedMarketToEventCardProps(market)} showBackgroundImage teamLogos={teamLogos} trackingSource="feed" hideLiveBadge={category === 'live'} priorityImage={index < 2} />
                  ))}
                </div>
                {predictOffset < predictTotal && (
                  <div className="flex justify-center mb-8">
                    <button
                      onClick={loadMorePredict}
                      disabled={loadingMorePredict}
                      className="px-5 py-2 bg-black/[0.04] dark:bg-white/[0.06] text-gray-700 dark:text-gray-300 rounded-[5px] hover:bg-black/[0.12] dark:hover:bg-white/[0.14] active:scale-[0.97] disabled:opacity-50 transition-colors font-satoshi font-semibold text-sm"
                    >
                      {loadingMorePredict ? t("common.loading") : t("common.loadMore")}
                    </button>
                  </div>
                )}
              </>
            );
          }

          // Show Hyperliquid markets when hyperliquid filter is selected (grid view).
          // Mirrors the Predict.fun branch — paginated with a Load more button.
          if (effectiveSourceFilter === 'hyperliquid' && viewMode === 'grid') {
            if (loadingAggregated || !hasFetchedAggregated) {
              return null;
            }
            const hyperliquidMarkets = aggregatedMarkets.filter(m => m.source === 'hyperliquid');
            if (hyperliquidMarkets.length === 0) {
              return null;
            }
            return (
              <>
                <div className={`grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3 mb-4 ${isCollapsed ? '2xl:grid-cols-4 3xl:grid-cols-5' : '2xl:grid-cols-4'}`}>
                  {hyperliquidMarkets.map((market, index) => (
                    <EventCard key={market.id} {...aggregatedMarketToEventCardProps(market)} showBackgroundImage teamLogos={teamLogos} trackingSource="feed" hideLiveBadge={category === 'live'} priorityImage={index < 2} />
                  ))}
                </div>
                {hyperliquidHasMore && hyperliquidMarkets.length >= HYPERLIQUID_PAGE_SIZE && (
                  <div className="flex justify-center mb-8">
                    <button
                      onClick={loadMoreHyperliquid}
                      disabled={loadingMoreHyperliquid}
                      className="px-5 py-2 bg-black/[0.04] dark:bg-white/[0.06] text-gray-700 dark:text-gray-300 rounded-[5px] hover:bg-black/[0.12] dark:hover:bg-white/[0.14] active:scale-[0.97] disabled:opacity-50 transition-colors font-satoshi font-semibold text-sm"
                    >
                      {loadingMoreHyperliquid ? t("common.loading") : t("common.loadMore")}
                    </button>
                  </div>
                )}
              </>
            );
          }

          // Show Kalshi markets when kalshi filter is selected (grid view only - list view handled below)
          if (effectiveSourceFilter === 'kalshi' && viewMode === 'grid') {
            // Skip if loading (LoadingSkeleton handles this)
            if (loadingAggregated) {
              return null;
            }

            const allKalshiMarkets = aggregatedMarkets.filter(m => m.source === 'kalshi');
            // Subcategory filtering is now handled server-side via ?subcategory= param
            const kalshiMarkets = allKalshiMarkets;

            if (allKalshiMarkets.length === 0 && hasFetchedAggregated && !loadingAggregated && !searchLoading) {
              return (
                <SourceEmptyState
                  source="kalshi"
                  searchQuery={searchQuery}
                  searchActive={kalshiSearchActiveRef.current}
                  onSwitchToPolymarket={() => updateSourceFilter("polymarket")}
                />
              );
            }

            const visibleMarkets = kalshiMarkets.slice(0, kalshiDisplayLimit);
            const hasMoreLocal = kalshiMarkets.length > kalshiDisplayLimit;
            const hasMoreFromApi = !!kalshiNextCursor;
            const hasMoreKalshi = hasMoreLocal || hasMoreFromApi;

            // Sports: render compact matchup cards (2 per row) instead of generic EventCard
            const isSportsKalshi = category === 'sports';
            return (
              <>
                <div className={
                  isSportsKalshi
                    ? 'grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8'
                    : `grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3 mb-8 ${isCollapsed ? '2xl:grid-cols-4 3xl:grid-cols-5' : '2xl:grid-cols-4'}`
                }>
                  {visibleMarkets.map((market, index) => {
                    const kalshiSrc = (market.sourceData as any)?.kalshi || {};
                    const evTicker: string | undefined = kalshiSrc.eventTicker;
                    if (isSportsKalshi && evTicker) {
                      return (
                        <KalshiSportsMatchCard
                          key={market.id}
                          eventTicker={evTicker}
                          slug={market.slug}
                          title={(market as any).title || (market as any).question || ''}
                          competition={kalshiSrc.competition || (market as any).competition}
                          sportTag={kalshiSrc.sportTag}
                          seriesImage={(market as any).image || null}
                          volume={market.volume}
                          marketsCount={(market as any).marketsCount}
                          markets={(market.markets as any[]) || []}
                          startDate={kalshiSrc.startDate}
                          endDate={(market as any).endDate}
                          teams={(market as any).teams || (market as any).sourceData?.polymarket?.teams}
                        />
                      );
                    }
                    const isLive = !!(evTicker && kalshiLiveSet?.has(evTicker.toUpperCase()));
                    return (
                      <EventCard key={market.id} {...aggregatedMarketToEventCardProps(market)} isLiveSport={isLive} showBackgroundImage teamLogos={teamLogos} trackingSource="feed" priorityImage={index < 2} />
                    );
                  })}
                </div>
                {hasMoreKalshi && (
                  <div className="text-center pt-4 pb-12">
                    <button
                      onClick={() => {
                        if (hasMoreLocal) {
                          setKalshiDisplayLimit(prev => prev + KALSHI_PAGE_SIZE);
                        } else {
                          loadMoreKalshi();
                        }
                      }}
                      disabled={loadingMoreKalshi}
                      className="px-5 py-2 bg-black/[0.04] dark:bg-white/[0.06] text-gray-700 dark:text-gray-300 rounded-[5px] hover:bg-black/[0.12] dark:hover:bg-white/[0.14] active:scale-[0.97] disabled:opacity-50 transition-colors font-satoshi font-semibold text-sm"
                    >
                      {loadingMoreKalshi ? t("common.loading") : t("common.loadMore")}
                    </button>
                  </div>
                )}
              </>
            );
          }

          return null;
        })()}

        {/* Events Display - Unified view for all sources */}
        {/* Skip when Breaking category is active (has its own layout) */}
        {/* For You uses this section when viewMode === 'list' */}
        {/* NOTE: Grid view for kalshi/predict is rendered in the blocks above */}
        {/* Dashboard categories - Polymarket only, with Market Overview toggle */}
        {category === 'macro' && effectiveSourceFilter !== 'kalshi' && (
          <DashboardOverviewToggle label={t("crypto.marketOverview")} expanded={macroOverviewExpanded} onToggle={toggleMacroOverview}>
            <MacroDashboard />
          </DashboardOverviewToggle>
        )}
        {category === 'fed-rates' && effectiveSourceFilter !== 'kalshi' && (
          <DashboardOverviewToggle label={t("crypto.marketOverview")} expanded={fedRatesOverviewExpanded} onToggle={toggleFedRatesOverview}>
            <FedRatesDashboard />
          </DashboardOverviewToggle>
        )}
        {category === 'trump' && effectiveSourceFilter !== 'kalshi' && (
          <DashboardOverviewToggle label={t("crypto.marketOverview")} expanded={trumpOverviewExpanded} onToggle={toggleTrumpOverview}>
            <TrumpDashboard />
          </DashboardOverviewToggle>
        )}
        {category === 'sports' && effectiveSourceFilter === 'polymarket' && <Sports2View mode="sports" />}
        {category === 'esports' && effectiveSourceFilter === 'polymarket' && <Sports2View mode="esports" />}
        {category === 'world-cup' && effectiveSourceFilter === 'polymarket' && <WorldCupView isCollapsed={isCollapsed} />}

        {showContentLoading ? (
          <LoadingSkeleton category={category} isCollapsed={isCollapsed} />
        ) : (category !== 'breaking' && category !== 'macro' && category !== 'fed-rates' && category !== 'trump' && category !== 'sports' && category !== 'esports' && category !== 'world-cup' && (category !== 'for-you' || viewMode === 'list') && (category !== 'bookmarks' || viewMode === 'list')) && (effectiveSourceFilter === 'polymarket' || category === 'elections' || (category === 'mentions' && effectiveSourceFilter !== 'kalshi') || (['kalshi', 'predict', 'hyperliquid'].includes(effectiveSourceFilter) && viewMode === 'list') || (category === 'for-you' && viewMode === 'list') || (category === 'bookmarks' && viewMode === 'list')) && (() => {

          // Build eventsToRender based on selected source filter
          let eventsToRender: Event[] = [];

          // Special case: For You in list mode uses forYouEvents + forYouFeedItems
          if (category === 'for-you' && viewMode === 'list') {
            const seenIds = new Set<string>();
            // Convert forYouEvents to Event format (trending carousel items)
            for (const e of forYouEvents) {
              seenIds.add(e.id);
              eventsToRender.push({ ...e, source: (e.source || 'polymarket') as MarketSource });
            }
            // Add forYouFeedItems (recommended) — skip duplicates
            for (const item of forYouFeedItems) {
              if (item.type === 'event') {
                if (!seenIds.has(item.event.id)) {
                  seenIds.add(item.event.id);
                  eventsToRender.push({ ...item.event, source: 'polymarket' as MarketSource });
                }
              } else {
                const converted = aggregatedMarketToEvent(item.market);
                if (!seenIds.has(converted.id)) {
                  seenIds.add(converted.id);
                  eventsToRender.push(converted);
                }
              }
            }
          } else if (category === 'bookmarks' && viewMode === 'list') {
            // Bookmarks in list mode uses watchingEvents (status-filtered)
            eventsToRender = filteredWatchingEvents.map(e => ({
              ...e,
              source: (e.source || 'polymarket') as MarketSource,
            }));
          } else if (category === 'elections') {
            // Elections only uses Polymarket events (no Kalshi/Predict.fun elections)
            eventsToRender = filteredEvents.map(e => ({ ...e, source: 'polymarket' as MarketSource }));
          } else if (effectiveSourceFilter === 'polymarket') {
            // Polymarket only
            eventsToRender = filteredEvents.map(e => ({ ...e, source: 'polymarket' as MarketSource }));
          } else if (effectiveSourceFilter === 'kalshi') {
            // Kalshi only — subcategory filtering handled server-side
            const allK = aggregatedMarkets.filter(m => m.source === 'kalshi');
            eventsToRender = allK
              .slice(0, kalshiDisplayLimit)
              .map(aggregatedMarketToEvent);
          } else if (effectiveSourceFilter === 'predict') {
            // Predict.fun only
            eventsToRender = aggregatedMarkets
              .filter(m => m.source === 'predict')
              .map(aggregatedMarketToEvent);
          } else if (effectiveSourceFilter === 'hyperliquid') {
            // Hyperliquid HIP-4 — same shape as Predict (one event per question
            // already grouped at SSR / fetchAggregatedMarkets), filter by source
            // tag to be safe against stale aggregated entries from a previous
            // source switch.
            eventsToRender = aggregatedMarkets
              .filter(m => m.source === 'hyperliquid')
              .map(aggregatedMarketToEvent);
          }

          // Sort events - certain categories have special sorting
          // IMPORTANT: "breaking" comes pre-sorted from backend, don't reorder!
          // Trending and New can be re-sorted by user with the Sort buttons
          const liveMarketIds = new Set(allLiveCryptoMarkets.map(m => m.id));

          // In list view, apply table sort for categories that don't go through filteredEvents
          // (for-you and bookmarks build eventsToRender from their own data sources)
          if (viewMode === "list" && (category === "for-you" || category === "bookmarks")) {
            eventsToRender.sort((a, b) => {
              let comparison = 0;
              const changeCmp = (key: '1h' | '24h' | '7d') => {
                const av = getEventPriceChange(a, key);
                const bv = getEventPriceChange(b, key);
                if (av == null && bv == null) return 0;
                if (av == null) return 1;
                if (bv == null) return -1;
                return av - bv;
              };
              switch (tableSortBy) {
                case "market":
                  comparison = (a.title || "").localeCompare(b.title || "");
                  break;
                case "volume24hr":
                  comparison = (a.volume24hr || 0) - (b.volume24hr || 0);
                  break;
                case "volume":
                  comparison = (a.volume || 0) - (b.volume || 0);
                  break;
                case "liquidity":
                  comparison = ((a as any).liquidity || 0) - ((b as any).liquidity || 0);
                  break;
                case "expires":
                  const dateA = a.endDate ? new Date(a.endDate).getTime() : 0;
                  const dateB = b.endDate ? new Date(b.endDate).getTime() : 0;
                  comparison = dateA - dateB;
                  break;
                case "change1h":
                  comparison = changeCmp('1h');
                  break;
                case "change24h":
                  comparison = changeCmp('24h');
                  break;
                case "change7d":
                  comparison = changeCmp('7d');
                  break;
              }
              return tableSortOrder === "asc" ? comparison : -comparison;
            });
          }

          // Skip sorting for categories that come pre-sorted from backend
          // "trending" preserves Polymarket's trending algorithm order
          // "new" preserves creation-date order from backend
          // Elections dashboard categories come sorted from API, don't reorder
          // In list view, filteredEvents already has the table sort applied — don't override it
          const presortedCategories = ["breaking", "elections", "macro", "fed-rates", "trump"];
          if (!presortedCategories.includes(category || "") && viewMode !== "list") {
            eventsToRender.sort((a, b) => {
              // In crypto category, prioritize live markets
              if (category === "crypto") {
                const aIsLive = liveMarketIds.has(a.id);
                const bIsLive = liveMarketIds.has(b.id);

                // If one is live and the other isn't, live goes first
                if (aIsLive && !bIsLive) return -1;
                if (bIsLive && !aIsLive) return 1;

                // If both are live, sort by end date (soonest first)
                if (aIsLive && bIsLive) {
                  const aEnd = a.endDate ? new Date(a.endDate).getTime() : Infinity;
                  const bEnd = b.endDate ? new Date(b.endDate).getTime() : Infinity;
                  return aEnd - bEnd;
                }
              }

              // Sort by user-selected criteria
              if (sortBy === "volume") {
                return (b.volume || 0) - (a.volume || 0);
              }
              if (sortBy === "newest") {
                const aCreated = a.createdAt ? new Date(a.createdAt).getTime() : 0;
                const bCreated = b.createdAt ? new Date(b.createdAt).getTime() : 0;
                return bCreated - aCreated;
              }
              if (sortBy === "endingSoon") {
                const aEnd = a.endDate ? new Date(a.endDate).getTime() : Infinity;
                const bEnd = b.endDate ? new Date(b.endDate).getTime() : Infinity;
                return aEnd - bEnd;
              }
              if (sortBy === "liquidity") {
                return (b.liquidity || 0) - (a.liquidity || 0);
              }
              // competitive: server-side sort, fallback to volume24hr locally
              return (b.volume24hr || 0) - (a.volume24hr || 0);
            });
          }

          // Skip empty state check for mentions - MentionsView handles its own data
          // Watching and For You have their own empty states
          if (eventsToRender.length === 0 && category !== 'mentions' && category !== 'bookmarks' && category !== 'for-you') {
            // Skip if still loading (LoadingSkeleton handles this)
            if (loading) {
              return null;
            }
            // Show skeleton instead of "No markets" while a tag-based category
            // is in flight. Empty state here is almost always a race condition
            // (tab/pill click is wrapped in startTransition since May 2026 — the
            // category state commits immediately while the fetch + loading
            // setState lag a tick, so the empty branch fires for ~50-150 ms with
            // events=[] before `loading` flips to true).
            //
            // The list below covers every category in CATEGORY_TAG_MAP +
            // VALID_CATEGORIES from categories-config.ts plus the static tabs.
            // Anything that matches the Polymarket dynamic-slug pattern
            // (`/^[a-z][a-z0-9-]+$/`) also gets the skeleton — Polymarket adds
            // categories on the fly (e.g. /trump-presidency, /world-elections),
            // and we used to flash "No markets yet" on those for the first
            // ~200 ms of any cold-fetch.
            const alwaysHasData = [
              'trending', 'new', 'breaking',
              'crypto', 'politics', 'sports', 'esports', 'economy', 'finance',
              'tech', 'culture', 'climate-science', 'weather-science',
              'world', 'geopolitics', 'elections', 'weather', 'iran',
              'macro', 'fed-rates', 'trump', 'ending-soon', 'elon',
            ];
            const isDynamicSlug = /^[a-z][a-z0-9-]+$/.test(category || '');
            if (alwaysHasData.includes(category || '') || isDynamicSlug) {
              return (
                <div className={`grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3 ${isCollapsed ? '2xl:grid-cols-4 3xl:grid-cols-5' : '2xl:grid-cols-4'}`}>
                  {[...Array(8)].map((_, i) => <SimpleCardSkeleton key={i} />)}
                </div>
              );
            }
            // For elon subcategory filters — client-side filter may return 0 results
            if (category === 'elon' && elonSubcategory !== 'all') {
              return (
                <div className="flex flex-col items-center justify-center py-20 px-4">
                  <p className="text-sm text-gray-500 dark:text-gray-400">No markets match this filter right now.</p>
                </div>
              );
            }
            // Empty state when no markets found (other categories)
            return (
              <div className="flex flex-col items-center justify-center py-20 px-4">
                <div className="w-20 h-20 rounded-[5px] bg-gradient-to-br from-gray-100 to-gray-50 dark:from-[#18181D] dark:to-[#0f0f12] flex items-center justify-center mb-6">
                  <Globe2 className="w-10 h-10 text-gray-400" />
                </div>
                <h3 className="text-xl font-monument font-bold text-gray-900 dark:text-white mb-2">
                  No markets yet
                </h3>
                <p className="text-gray-500 dark:text-gray-400 font-satoshi text-center max-w-md mb-6">
                  There are no active markets in this category right now. Check back soon or explore other categories!
                </p>
                <button
                  onClick={() => {
                    window.history.pushState({}, '', sourceUrl('/markets/trending', source));
                    setCategory("trending");
                  }}
                  className="px-6 py-3 bg-gray-900 dark:bg-white text-white dark:text-black rounded-[5px] font-inter-tight font-bold hover:bg-gray-800 dark:hover:bg-gray-100 transition-colors"
                >
                  Browse Trending Markets
                </button>
              </div>
            );
          }

          // Get contextual reason based on category (only for top items)
          const getCategoryReason = (index: number): string | undefined => {
            // Only show reason for top 6 items in each category to avoid clutter
            if (index >= 6) return undefined;

            switch (category) {
              case "trending":
                if (index < 3) return "Trending now";
                return undefined;
              case "new":
                if (index < 3) return "Newly created market";
                return undefined;
              case "elon":
                if (index < 3) return "Elon Musk related";
                return undefined;
              case "elections":
                if (index < 3) return "Global election";
                return undefined;
              default:
                return undefined;
            }
          };

          return (
            <>
              {/* Global search indicator */}
              {isGlobalSearch && globalSearchResults.length > 0 && (
                <GlobalSearchBanner
                  resultCount={globalSearchResults.length}
                  query={searchQuery}
                  onClear={() => setSearchQuery("")}
                />
              )}
              {/* MACRO DASHBOARD MODE */}
              {category === "elections" && electionsFilter === "macro" ? (
                <div>
                  <DashboardOverviewToggle label={t("crypto.marketOverview")} expanded={macroOverviewExpanded} onToggle={toggleMacroOverview}>
                    <MacroDashboard />
                  </DashboardOverviewToggle>
                </div>
              ) : /* FED RATES DASHBOARD MODE */
              category === "elections" && electionsFilter === "fed-rates" ? (
                <div>
                  <DashboardOverviewToggle label={t("crypto.marketOverview")} expanded={fedRatesOverviewExpanded} onToggle={toggleFedRatesOverview}>
                    <FedRatesDashboard />
                  </DashboardOverviewToggle>
                </div>
              ) : /* TRUMP DASHBOARD MODE */
              category === "elections" && electionsFilter === "trump" ? (
                <div>
                  <DashboardOverviewToggle label={t("crypto.marketOverview")} expanded={trumpOverviewExpanded} onToggle={toggleTrumpOverview}>
                    <TrumpDashboard />
                  </DashboardOverviewToggle>
                </div>
              ) : /* ELECTIONS MODE - Polymarket-style cards when "Elections" subcategory is selected */
              /* API returns already sorted by startTime ascending */
              category === "elections" && electionsFilter === "elections" ? (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mb-8">
                  {eventsToRender.map((event) => (
                    <ElectionCard
                      key={event.id}
                      event={event}
                    />
                  ))}
                </div>
              ) : /* SPORTS2 MODE - Polymarket sports live replica */
              category === "sports" && effectiveSourceFilter === "polymarket" ? (
                <Sports2View mode="sports" />
              ) : /* ESPORTS MODE - Polymarket esports live replica */
              category === "esports" && effectiveSourceFilter === "polymarket" ? (
                <Sports2View mode="esports" />
              ) : /* MENTIONS MODE - Live events for predicting words/phrases (Polymarket only) */
              category === "mentions" && effectiveSourceFilter !== "kalshi" ? (
                <MentionsView
                  onTradeClick={(event, market) => {
                    // Use the first market or the clicked market
                    const targetMarket = market || event.markets?.[0];
                    if (targetMarket) {
                      const yesPrice = targetMarket.outcomePrices?.[0] ?? 0.5;
                      openTradingModal(
                        targetMarket.slug || event.slug,
                        "YES",
                        yesPrice,
                        targetMarket.groupItemTitle || targetMarket.question || event.title,
                        event.image,
                        targetMarket.outcomes?.[0],
                        targetMarket.outcomes,
                        "polymarket",
                        undefined,
                        targetMarket.clobTokenIds || [],
                        event.slug,
                      );
                    }
                  }}
                />
              ) : viewMode === "grid" ? (
                /* GRID VIEW - POLYMARKET STYLE - 5 cols when sidebar collapsed */
                <div className={`grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3 mb-8 ${isCollapsed ? '2xl:grid-cols-4 3xl:grid-cols-5' : '2xl:grid-cols-4'}`}>
                  {eventsToRender.map((event, index) => {
                    // Map current tab to tracking source
                    const tabSource: InteractionSource = category === 'trending' ? 'trending' : 'feed';
                    return (
                      <EventCard
                        key={event.id}
                        {...eventToEventCardProps(event)}
                        openInterest={getEventOI(event)}
                        volumeHistory={getEventVolumeHistory(event)}
                        reason={getCategoryReason(index)}
                        socialActivity={getEventSocialActivity(event)}
                        showBackgroundImage
                        teamLogos={teamLogos}
                        trackingSource={tabSource}
                        // First 2 cards eager-load their image so the
                        // browser has an LCP candidate ready immediately
                        // (chrome-devtools showed 1.7s load-delay before
                        // this — the LCP element was a card image being
                        // lazy-deferred).
                        priorityImage={index < 2}
                        onCardClick={(s) => { trackRecommendation(s, 'view_detail', tabSource); setNavigationSource(tabSource); }}
                      />
                    );
                  })}
                </div>
              ) : (
              /* LIST VIEW - FRENFLOW STYLE */
              <>
              {/* MOBILE LIST VIEW - Cards with full titles (HIDDEN — mobile now uses the table below) */}
              <div className="hidden space-y-2 mb-8">
                {eventsToRender.map((event, eventIndex) => {
                  const mainMarket = event.markets[0];
                  if (!mainMarket) return null;

                  // Only predict.fun is truly external. Kalshi has internal pages.
                  const isExternalSource = event.source === 'predict';
                  const sourceConfig = {
                    polymarket: { bg: 'bg-[#2E5CFF]', logo: '/logos/polymarket/icon-white.png', name: 'Polymarket', logoStyle: undefined as React.CSSProperties | undefined },
                    kalshi: { bg: 'bg-[#09C285]', logo: '', letter: 'K', name: 'Kalshi', logoStyle: undefined as React.CSSProperties | undefined },
                    predict: { bg: 'bg-[#6366F1]', logo: '', letter: 'P', letterColor: 'text-black', name: 'Predict.fun', logoStyle: undefined as React.CSSProperties | undefined },
                    hyperliquid: { bg: 'bg-[#0f1a1f]', logo: '/logos/hyperliquid/symbol-mint.svg', name: 'Hyperliquid', logoStyle: undefined as React.CSSProperties | undefined },
                  };
                  const eventSourceConfig = (sourceConfig as any)[event.source || 'polymarket'] || sourceConfig.polymarket;
                  const isBinary = (event.markets || []).length === 1 && mainMarket?.outcomes?.length === 2;

                  const [yesPrice, noPrice] = getRealtimePrice(mainMarket);
                  // Get real orderbook prices for buy buttons
                  const { buyYes, buyNo } = getBuyPrices(mainMarket);

                  const endDate = event.endDate ? new Date(event.endDate) : null;
                  const daysLeft = endDate ? Math.ceil((endDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24)) : null;

                  const getExternalUrl = () => {
                    if (event.source === 'kalshi' && event.sourceData?.kalshi) {
                      const kt = event.sourceData.kalshi.eventTicker.toLowerCase();
                      const ks = kt.replace(/-\d[a-z0-9]*$/i, '');
                      const src = (event.description || event.title || ks);
                      const kslug = src.toLowerCase().replace(/['']/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 80);
                      return `https://kalshi.com/markets/${ks}/${kslug}/${kt}`;
                    }
                    if (event.source === 'predict' && event.sourceData?.predict) {
                      return `https://predict.fun/markets/${event.sourceData.predict.marketId}`;
                    }
                    return null;
                  };

                  // Collect following users for this event
                  let eventFollowingUsers: any[] = [];
                  if (followingActivity[event.slug]) {
                    followingActivity[event.slug].forEach((user: any) => {
                      if (!eventFollowingUsers.some(u => u.userId === user.userId)) {
                        eventFollowingUsers.push(user);
                      }
                    });
                  }

                  return (
                    <div
                      key={event.id}
                      className="bg-white dark:bg-[#18181D] rounded-[5px] border border-gray-200 dark:border-[#1A1A1A] p-3.5 active:bg-gray-50/70 dark:active:bg-white/[0.02] transition-colors cursor-pointer"
                      onClick={() => {
                        const listSource: InteractionSource = category === 'trending' ? 'trending' : category === 'for-you' ? 'for_you' : 'feed';
                        trackRecommendation(mainMarket.slug || event.slug, 'view_detail', listSource);
                        setNavigationSource(listSource);
                        if (isExternalSource) {
                          const url = getExternalUrl();
                          if (url) window.open(url, '_blank');
                        } else if (event.source === 'kalshi') {
                          router.push(sourceUrl(`/event/kalshi/${event.sourceData?.kalshi?.eventTicker || event.slug}`, "kalshi"));
                        } else {
                          router.push(sourceUrl(`/event/${event.slug}`, source));
                        }
                      }}
                    >
                      {/* Header: Image + Title */}
                      <div className="flex items-start gap-3">
                        {/* Image with source badge */}
                        <div className="relative w-12 h-12 flex-shrink-0 overflow-visible">
                          <div className="w-full h-full rounded-[5px] overflow-hidden bg-gray-100 dark:bg-[#0f0f12] border border-gray-200 dark:border-[#1A1A1A]">
                            {event.image ? (
                              <CdnImage src={event.image} alt={event.title} width={48} height={48} className="w-full h-full object-cover" />
                            ) : (
                              <div className={`w-full h-full flex items-center justify-center ${eventSourceConfig.bg}`}>
                                {eventSourceConfig.logo ? (
                                  <img src={eventSourceConfig.logo} alt={eventSourceConfig.name} className="w-6 h-6 object-contain" />
                                ) : (
                                  <span className={`text-lg font-black leading-none ${(eventSourceConfig as any).letterColor || 'text-white'}`}>{(eventSourceConfig as any).letter || eventSourceConfig.name[0]}</span>
                                )}
                              </div>
                            )}
                          </div>
                          <div className={`absolute -bottom-1 -right-1 z-10 w-[15px] h-[15px] rounded flex items-center justify-center ring-2 ring-white dark:ring-[#0f0f12] shadow-sm ${eventSourceConfig.bg}`}>
                            {eventSourceConfig.logo ? (
                              <img src={eventSourceConfig.logo} alt={eventSourceConfig.name} className="w-[15px] h-[15px] object-contain" />
                            ) : (
                              <span className={`text-[8px] font-black leading-none ${(eventSourceConfig as any).letterColor || 'text-white'}`}>{(eventSourceConfig as any).letter || eventSourceConfig.name[0]}</span>
                            )}
                          </div>
                        </div>
                        {/* Title - NO line clamp, full visibility */}
                        <div className="flex-1 min-w-0">
                          <h3 className="font-satoshi font-semibold text-gray-900 dark:text-white text-sm leading-tight">
                            {event.title}
                          </h3>
                          <div className="flex items-center gap-2 mt-1">
                            {daysLeft !== null && daysLeft > 0 && (
                              <span className="text-xs text-gray-500 dark:text-gray-400">
                                {t("table.daysLeft", { n: daysLeft })}
                              </span>
                            )}
                            {daysLeft === 0 && (
                              <span className="text-xs text-orange-500 font-medium">{t("table.today")}</span>
                            )}
                            {(() => {
                              const totalMarkets = (event as any).marketsCount
                                ?? (event.markets || []).length;
                              if (isBinary || totalMarkets <= 1) return null;
                              return (
                                <span className="text-xs text-gray-900 dark:text-white font-medium">
                                  {t("table.markets", { n: totalMarkets })}
                                </span>
                              );
                            })()}
                          </div>
                        </div>
                      </div>

                      {/* Following users */}
                      {eventFollowingUsers.length > 0 && (
                        <div className="mt-2 flex items-center">
                          <div className="inline-flex items-center gap-1.5 px-2 py-1 bg-[#CCFF00]/10 border border-[#CCFF00]/30 rounded-[5px]">
                            <div className="flex -space-x-1">
                              {eventFollowingUsers.slice(0, 3).map((user, idx) => (
                                <div
                                  key={user.userId}
                                  className="w-4 h-4 rounded-[5px] border border-[#CCFF00]/70 overflow-hidden bg-[#18181D]"
                                  style={{ zIndex: 3 - idx }}
                                >
                                  {user.image ? (
                                    <CdnImage src={user.image} alt={user.username || user.name} width={16} height={16} className="w-full h-full object-cover" />
                                  ) : (
                                    <div className="w-full h-full bg-gradient-to-br from-[#CCFF00] to-[#00FF9D] flex items-center justify-center text-[6px] font-bold text-[#0A0A0A]">
                                      {(user.username || user.name || '?')[0].toUpperCase()}
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                            <span className="text-[10px] font-medium text-[#CCFF00]">
                              {eventFollowingUsers.length === 1 ? eventFollowingUsers[0].username || 'fren' : `${eventFollowingUsers.length} frens`}
                            </span>
                          </div>
                        </div>
                      )}

                      {/* Yes/No Buttons - Use orderbook prices (buyYes/buyNo) */}
                      {isBinary && (
                        <div className="flex items-center gap-2 mt-3">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              openTradingModal(mainMarket.slug, "YES", buyYes, event.title, event.image, mainMarket.outcomes?.[0], mainMarket.outcomes, event.source as any, event.sourceData, mainMarket.clobTokenIds || [], event.slug);
                            }}
                            className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-[#359a5e]/15 hover:bg-[#359a5e]/25 rounded-[3px] transition-colors"
                          >
                            <span className="text-xs text-[#3db468]/80 font-medium">{mainMarket.outcomes?.[0] || t("trading.yes")}</span>
                            <span className="text-sm font-semibold text-[#3db468]">
                              {(buyYes * 100).toFixed(1)}¢
                            </span>
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              openTradingModal(mainMarket.slug, "NO", buyNo, event.title, event.image, mainMarket.outcomes?.[1], mainMarket.outcomes, event.source as any, event.sourceData, mainMarket.clobTokenIds || [], event.slug);
                            }}
                            className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-[#cb3131]/15 hover:bg-[#cb3131]/25 rounded-[3px] transition-colors"
                          >
                            <span className="text-xs text-[#cb3131]/80 font-medium">{mainMarket.outcomes?.[1] || t("trading.no")}</span>
                            <span className="text-sm font-semibold text-[#cb3131]">
                              {(buyNo * 100).toFixed(1)}¢
                            </span>
                          </button>
                        </div>
                      )}

                      {/* Multi-market expand hint */}
                      {(() => {
                        const totalMarkets = (event as any).marketsCount
                          ?? (event.markets || []).length;
                        if (isBinary || totalMarkets <= 1) return null;
                        return (
                          <div className="mt-3 text-center">
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              Tap to see all {totalMarkets} markets →
                            </span>
                          </div>
                        );
                      })()}

                      {/* Volume info */}
                      <div className="flex items-center justify-between mt-3 pt-2 border-t border-gray-100/50 dark:border-white/[0.04]">
                        <span className="text-[10px] text-gray-500 dark:text-gray-400">24h Vol</span>
                        <span className="text-xs font-medium text-gray-600 dark:text-gray-400">
                          ${event.volume24hr && event.volume24hr >= 1000000
                            ? `${(event.volume24hr / 1000000).toFixed(1)}M`
                            : event.volume24hr && event.volume24hr >= 1000
                            ? `${(event.volume24hr / 1000).toFixed(1)}K`
                            : event.volume24hr?.toFixed(0) || '0'}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* LIST VIEW - Table for all screen sizes */}
              <div className="bg-white dark:bg-[#18181D] rounded-[5px] border border-gray-200 dark:border-[#1A1A1A] overflow-hidden mb-8">
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[1100px] table-fixed">
                    <colgroup>
                      {/* OI removed across the board (was a dedicated PM
                          column AND the label of the "Liquidity / OI"
                          column for non-PM). PM keeps the Liquidity column.
                          Non-PM (Kalshi/Predict) drops it entirely — they
                          had no liquidity data, the column was OI-only.
                          Widths redistributed so the colgroup sums to 100%
                          for both layouts. */}
                      <col style={{width: effectiveSourceFilter === 'polymarket' ? '24%' : '40%'}} />  {/* Market */}
                      <col style={{width: effectiveSourceFilter === 'polymarket' ? '11%' : '18%'}} />  {/* Price */}
                      {/* Per-timeframe % change columns — Polymarket
                          only. Gamma exposes 1h+24h directly; 7d is
                          derived from yesHistory sparkline points. */}
                      {effectiveSourceFilter === 'polymarket' && <col style={{width: '6%'}} />}  {/* 1H % */}
                      {effectiveSourceFilter === 'polymarket' && <col style={{width: '6%'}} />}  {/* 24H % */}
                      {effectiveSourceFilter === 'polymarket' && <col style={{width: '6%'}} />}  {/* 7D % */}
                      <col style={{width: effectiveSourceFilter === 'polymarket' ? '8%' : '14%'}} />   {/* Chart */}
                      {effectiveSourceFilter === 'polymarket' && !HIDE_PREDEXON && <col style={{width: '8%'}} />} {/* Smart $ */}
                      {effectiveSourceFilter === 'polymarket' && !HIDE_PREDEXON && <col style={{width: '6%'}} />}  {/* ROI */}
                      <col style={{width: effectiveSourceFilter === 'polymarket' ? '8%' : '18%'}} />   {/* 24h Vol */}
                      {effectiveSourceFilter === 'polymarket' && <col style={{width: '8%'}} />}  {/* Liquidity (PM only) */}
                      <col style={{width: effectiveSourceFilter === 'polymarket' ? '6%' : '7%'}} />    {/* Total */}
                      <col style={{width: '3%'}} />   {/* Bookmark */}
                    </colgroup>
                    <MarketsListTableHeader
                      tableSortBy={tableSortBy}
                      setTableSortBy={setTableSortBy}
                      tableSortOrder={tableSortOrder}
                      setTableSortOrder={setTableSortOrder}
                      effectiveSourceFilter={effectiveSourceFilter}
                    />
                    <tbody className="divide-y divide-gray-100 dark:divide-white/[0.04]">
                      {eventsToRender.map((event, eventIndex) => {
                        const mainMarket = event.markets[0];
                        if (!mainMarket) return null;

                        // Only predict.fun is truly external. Kalshi has internal pages.
                        const isExternalSource = event.source === 'predict';
                        const sourceConfig = {
                          polymarket: { bg: 'bg-[#2E5CFF]', logo: '/logos/polymarket/icon-white.png', name: 'Polymarket', logoStyle: undefined as React.CSSProperties | undefined },
                          kalshi: { bg: 'bg-[#09C285]', logo: '/logos/kalshi/k-icon.svg', name: 'Kalshi', logoStyle: undefined as React.CSSProperties | undefined },
                          predict: { bg: 'bg-[#6366F1]', logo: '', letter: 'P', letterColor: 'text-black', name: 'Predict.fun', logoStyle: undefined as React.CSSProperties | undefined },
                          hyperliquid: { bg: 'bg-[#0f1a1f]', logo: '/logos/hyperliquid/symbol-mint.svg', name: 'Hyperliquid', logoStyle: undefined as React.CSSProperties | undefined },
                        };
                        const eventSourceConfig = (sourceConfig as any)[event.source || 'polymarket'] || sourceConfig.polymarket;

                        // Check if this is a binary event (single market with Yes/No)
                        const isBinary = (event.markets || []).length === 1 && mainMarket?.outcomes?.length === 2;
                        const isExpanded = expandedEvents.has(event.id);

                        // Collect following users for this event
                        let eventFollowingUsers: any[] = [];
                        if (followingActivity[event.slug]) {
                          followingActivity[event.slug].forEach((user: any) => {
                            if (!eventFollowingUsers.some(u => u.userId === user.userId)) {
                              eventFollowingUsers.push(user);
                            }
                          });
                        }
                        (event.markets || []).forEach(market => {
                          if (followingActivity[market.slug]) {
                            followingActivity[market.slug].forEach((user: any) => {
                              if (!eventFollowingUsers.some(u => u.userId === user.userId)) {
                                eventFollowingUsers.push(user);
                              }
                            });
                          }
                        });

                        // Use realtime prices from WebSocket
                        const [yesPrice, noPrice] = getRealtimePrice(mainMarket);
                        // Get real orderbook prices for buy buttons
                        const { buyYes, buyNo } = getBuyPrices(mainMarket);

                        // For multi-market events, find the market with highest YES price (the favorite)
                        // This will be used for the parent event's sparkline
                        let sparklinePrice = yesPrice;
                        if (!isBinary && (event.markets || []).length > 1) {
                          const favoriteMarket = (event.markets || []).reduce((best, current) => {
                            const [currentYes] = getRealtimePrice(current);
                            const [bestYes] = getRealtimePrice(best);
                            return currentYes > bestYes ? current : best;
                          }, event.markets[0]);
                          const [favoriteYes] = getRealtimePrice(favoriteMarket);
                          sparklinePrice = favoriteYes;
                        }

                        // Prefer real price history fetched by the sync worker:
                        //   - /trending   → event.yesHistory (array of numbers)
                        //   - /breaking   → event.history (array of { t, p } objects)
                        //   - fallback to mainMarket.history (biggest-movers shape)
                        const rawHistory: any = (event as any).yesHistory
                          || (event as any).history
                          || (mainMarket as any)?.history
                          || null;
                        let yesSparklineData: number[] = [];
                        if (Array.isArray(rawHistory) && rawHistory.length >= 2) {
                          yesSparklineData = rawHistory
                            .map((p: any) => {
                              const v = typeof p === 'number' ? p : (p?.p ?? p?.price ?? p?.value ?? p?.y);
                              const num = Number(v);
                              if (!Number.isFinite(num)) return null;
                              // Normalise to 0..1 range — some sources return %s
                              return num > 1 ? num / 100 : num;
                            })
                            .filter((v: number | null): v is number => v !== null && v >= 0 && v <= 1);
                          if (yesSparklineData.length < 2) yesSparklineData = [];
                        }
                        if (yesSparklineData.length === 0) {
                          // Deterministic noise fallback — unique visual per event.
                          yesSparklineData = Array.from({ length: 20 }, (_, i) => {
                            const noise = seededNoise(event.id, i) * 0.08;
                            const trend = (i / 20) * (sparklinePrice - 0.5) * 0.3;
                            return Math.max(0.05, Math.min(0.95, 0.5 + trend + noise));
                          });
                          yesSparklineData.push(sparklinePrice);
                        } else if (yesSparklineData[yesSparklineData.length - 1] !== sparklinePrice) {
                          // Keep the last point in sync with the realtime price.
                          yesSparklineData.push(sparklinePrice);
                        }

                        const noSparklineData = yesSparklineData.map(v => 1 - v);

                        // Format end date
                        const endDate = event.endDate ? new Date(event.endDate) : null;
                        const daysLeft = endDate ? Math.ceil((endDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24)) : null;
                        const formatDate = (date: Date) => {
                          return date.toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                            hour: 'numeric',
                            minute: '2-digit',
                          });
                        };

                        const toggleExpand = (e: React.MouseEvent) => {
                          e.stopPropagation();
                          setExpandedEvents(prev => {
                            const newSet = new Set(prev);
                            if (newSet.has(event.id)) {
                              newSet.delete(event.id);
                            } else {
                              newSet.add(event.id);
                            }
                            return newSet;
                          });
                        };

                        // Get external URL for non-Polymarket sources
                        const getExternalUrl = () => {
                          if (event.source === 'kalshi' && event.sourceData?.kalshi) {
                            const kt = event.sourceData.kalshi.eventTicker.toLowerCase();
                            const ks = kt.replace(/-\d[a-z0-9]*$/i, '');
                            const src = (event.description || event.title || ks);
                            const kslug = src.toLowerCase().replace(/['']/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 80);
                            return `https://kalshi.com/markets/${ks}/${kslug}/${kt}`;
                          }
                          if (event.source === 'predict' && event.sourceData?.predict) {
                            return `https://predict.fun/markets/${event.sourceData.predict.marketId}`;
                          }
                          return null;
                        };

                        return (
                          <React.Fragment key={event.id}>
                            {/* Parent Event Row */}
                            <tr
                              className="hover:bg-gray-50 dark:hover:bg-white/[0.04] transition-all cursor-pointer group border-l-2 border-transparent hover:border-gray-300 dark:hover:border-white/20"
                              onClick={() => {
                                const listSource: InteractionSource = category === 'trending' ? 'trending' : category === 'for-you' ? 'for_you' : 'feed';
                                trackRecommendation(mainMarket.slug || event.slug, 'view_detail', listSource);
                                setNavigationSource(listSource);
                                if (isExternalSource) {
                                  const url = getExternalUrl();
                                  if (url) window.open(url, '_blank');
                                } else if (event.source === 'kalshi') {
                                  router.push(sourceUrl(`/event/kalshi/${event.sourceData?.kalshi?.eventTicker || event.slug}`, "kalshi"));
                                } else {
                                  router.push(sourceUrl(`/event/${event.slug}`, source));
                                }
                              }}
                            >
                              {/* Market Info */}
                              <td className="px-3 py-2.5">
                                <div className="flex items-center gap-2.5">
                                  {/* Image with source badge */}
                                  <div className="relative w-9 h-9 flex-shrink-0 overflow-visible">
                                    <div className="w-full h-full rounded-[5px] overflow-hidden bg-gray-100 dark:bg-[#0f0f12] border border-gray-200 dark:border-[#1A1A1A] group-hover:border-gray-300 dark:group-hover:border-white/[0.1] transition-colors">
                                      {event.image ? (
                                        <CdnImage
                                          src={event.image}
                                          alt={event.title}
                                          width={36}
                                          height={36}
                                          className="w-full h-full object-cover"
                                        />
                                      ) : (
                                        <div className={`w-full h-full flex items-center justify-center ${eventSourceConfig.bg}`}>
                                          {eventSourceConfig.logo ? (
                                            <img src={eventSourceConfig.logo} alt={eventSourceConfig.name} className="w-5 h-5 object-contain" />
                                          ) : (
                                            <span className={`text-base font-black leading-none ${(eventSourceConfig as any).letterColor || 'text-white'}`}>{(eventSourceConfig as any).letter || eventSourceConfig.name[0]}</span>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                    {/* Source badge overlay */}
                                    <div className={`absolute -bottom-1 -right-1 z-10 w-[15px] h-[15px] rounded flex items-center justify-center ring-2 ring-white dark:ring-[#0f0f12] shadow-sm ${eventSourceConfig.bg}`}>
                                      {eventSourceConfig.logo ? (
                                        <img src={eventSourceConfig.logo} alt={eventSourceConfig.name} className="w-[15px] h-[15px] object-contain" />
                                      ) : (
                                        <span className={`text-[8px] font-black leading-none ${(eventSourceConfig as any).letterColor || 'text-white'}`}>{(eventSourceConfig as any).letter || eventSourceConfig.name[0]}</span>
                                      )}
                                    </div>
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-1.5">
                                      <div className="font-medium text-gray-900 dark:text-white text-sm line-clamp-1 transition-colors">
                                        {event.title}
                                      </div>
                                      {/* HOT badge — competitive markets (45-55% on the leader) */}
                                      {(() => {
                                        const p = Number(yesPrice) || 0;
                                        const isHot = p >= 0.45 && p <= 0.55;
                                        if (!isHot) return null;
                                        return (
                                          <span
                                            className="flex-shrink-0 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider bg-orange-500/10 text-orange-500 border border-orange-500/30"
                                            title={t("table.hotTooltip")}
                                          >
                                            {t("table.hot")}
                                          </span>
                                        );
                                      })()}
                                      {/* LIVE badge — ending within 6h */}
                                      {(() => {
                                        if (!event.endDate) return null;
                                        const ms = new Date(event.endDate).getTime() - Date.now();
                                        if (ms <= 0 || ms > 6 * 3600000) return null;
                                        return (
                                          <span className="flex-shrink-0 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider bg-red-500/10 text-red-500 border border-red-500/30">
                                            <span className="relative flex h-1.5 w-1.5"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75" /><span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-red-500" /></span>
                                            {t("table.live")}
                                          </span>
                                        );
                                      })()}
                                      {/* REWARDS badge — liquidity rewards active */}
                                      {(event as any).rewardsRatePerDay > 0 && (
                                        <span className="flex-shrink-0 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider bg-purple-500/10 text-purple-500 border border-purple-500/30" title={t("table.rewardsTooltip", { rate: ((event as any).rewardsRatePerDay || 0).toFixed(0) })}>
                                          {t("table.rewards")}
                                        </span>
                                      )}
                                    </div>
                                    <div className="flex flex-row items-center gap-2 mt-0.5 whitespace-nowrap">
                                      <div className="flex items-center gap-2">
                                        <span className="text-xs text-gray-500 dark:text-gray-400">
                                          {daysLeft !== null && daysLeft > 0 ? `${daysLeft}d` : daysLeft === 0 ? t("table.today") : ''}
                                        </span>
                                        {(() => {
                                          // Prefer `marketsCount` (the true count — `markets[]`
                                          // can arrive truncated in slim list responses).
                                          const totalMarkets = (event as any).marketsCount
                                            ?? (event.markets || []).length;
                                          if (isBinary || totalMarkets <= 1) return null;
                                          return (
                                            <span className="text-xs text-gray-900 dark:text-white font-medium">
                                              {t("table.markets", { n: totalMarkets })}
                                            </span>
                                          );
                                        })()}
                                        {/* Liquidity rewards — only when this
                                            Polymarket event has an active pool
                                            on its lead market. Tooltip mirrors
                                            PM's popover minus the sponsor CTA. */}
                                        {(() => {
                                          if (effectiveSourceFilter !== 'polymarket') return null;
                                          const cid = (((event.markets && event.markets[0] && (event.markets[0] as any).conditionId)
                                            || (event as any).sourceData?.polymarket?.conditionId || '') as string).toLowerCase();
                                          const rewards = cid ? listRewardsMap?.[cid] : null;
                                          if (!rewards || rewards.ratePerDay <= 0) return null;
                                          const tooltipContent = (
                                            <div className="min-w-[220px] text-left p-3 space-y-2">
                                              <p className="text-[11px] leading-snug text-gray-300">
                                                {t("tooltip.lpRewardsBlurb")}
                                              </p>
                                              <div className="pt-2 border-t border-white/10 space-y-1.5">
                                                <div className="flex justify-between items-center">
                                                  <span className="text-[11px] text-gray-400">{t("tooltip.lpRewards")}</span>
                                                  <span className="text-[11px] font-semibold text-white">
                                                    ${Math.round(rewards.ratePerDay)}<span className="text-gray-400 font-normal">/day</span>
                                                  </span>
                                                </div>
                                                {rewards.maxSpread > 0 && (
                                                  <div className="flex justify-between items-center">
                                                    <span className="text-[11px] text-gray-400">{t("tooltip.lpMaxSpread")}</span>
                                                    <span className="text-[11px] font-semibold text-white">±{Math.round(rewards.maxSpread)}¢</span>
                                                  </div>
                                                )}
                                                {rewards.minSize > 0 && (
                                                  <div className="flex justify-between items-center">
                                                    <span className="text-[11px] text-gray-400">{t("tooltip.lpMinShares")}</span>
                                                    <span className="text-[11px] font-semibold text-white">{rewards.minSize}</span>
                                                  </div>
                                                )}
                                              </div>
                                            </div>
                                          );
                                          return (
                                            <Tooltip content={tooltipContent} maxWidth={260}>
                                              <span
                                                className="inline-flex items-center text-gray-500 hover:text-gray-300 transition-colors"
                                                aria-label={t("tooltip.liquidityRewards")}
                                                onClick={(e) => { e.stopPropagation(); e.preventDefault(); }}
                                              >
                                                <Gift size={12} />
                                              </span>
                                            </Tooltip>
                                          );
                                        })()}
                                      </div>
                                      {/* Following users talking about this market */}
                                      {eventFollowingUsers.length > 0 && (
                                        <div className="inline-flex items-center gap-1 md:gap-1.5 px-1.5 md:px-2 py-0.5 bg-[#CCFF00]/10 border border-[#CCFF00]/30 rounded-[5px] w-fit">
                                          {/* Avatars with thin border */}
                                          <div className="flex -space-x-1">
                                            {eventFollowingUsers.slice(0, 3).map((user, idx) => (
                                              <div
                                                key={user.userId}
                                                className="w-3.5 h-3.5 md:w-4 md:h-4 rounded-[5px] border border-[#CCFF00]/70 overflow-hidden bg-[#18181D]"
                                                style={{ zIndex: 3 - idx }}
                                                title={user.username || user.name}
                                              >
                                                {user.image ? (
                                                  <img
                                                    src={user.image}
                                                    alt={user.username || user.name}
                                                    className="w-full h-full object-cover"
                                                  />
                                                ) : (
                                                  <div className="w-full h-full bg-gradient-to-br from-[#CCFF00] to-[#00FF9D] flex items-center justify-center text-[5px] md:text-[6px] font-bold text-[#0A0A0A]">
                                                    {(user.username || user.name || '?')[0].toUpperCase()}
                                                  </div>
                                                )}
                                              </div>
                                            ))}
                                          </div>
                                          {/* Text */}
                                          <span className="text-[9px] md:text-[10px] font-medium text-[#CCFF00]">
                                            {eventFollowingUsers.length === 1
                                              ? `${eventFollowingUsers[0].username || 'fren'}`
                                              : `${eventFollowingUsers.length} frens`}
                                          </span>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </td>

                              {/* Price — primary decision column */}
                              <td className="px-3 py-2.5">
                                {isBinary ? (
                                  <div className="flex items-center justify-center gap-1.5">
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        openTradingModal(
                                          mainMarket.slug,
                                          "YES",
                                          buyYes,
                                          event.title,
                                          event.image,
                                          mainMarket.outcomes?.[0],
                                          mainMarket.outcomes,
                                          event.source as any,
                                          event.sourceData,
                                          mainMarket.clobTokenIds || [],
                                          event.slug,
                                        );
                                      }}
                                      className="flex items-center justify-center gap-1 min-w-[62px] px-2 py-1.5 whitespace-nowrap bg-[#359a5e]/15 hover:bg-[#359a5e]/25 rounded-[3px] transition-colors group/btn"
                                    >
                                      <span className="text-xs font-semibold text-[#3db468]">
                                        {(buyYes * 100).toFixed(1)}¢
                                      </span>
                                      <span className="text-[9px] text-[#3db468]/80 font-medium">{mainMarket.outcomes?.[0] || t("trading.yes")}</span>
                                    </button>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        openTradingModal(
                                          mainMarket.slug,
                                          "NO",
                                          buyNo,
                                          event.title,
                                          event.image,
                                          mainMarket.outcomes?.[1],
                                          mainMarket.outcomes,
                                          event.source as any,
                                          event.sourceData,
                                          mainMarket.clobTokenIds || [],
                                          event.slug,
                                        );
                                      }}
                                      className="flex items-center justify-center gap-1 min-w-[62px] px-2 py-1.5 whitespace-nowrap bg-[#cb3131]/15 hover:bg-[#cb3131]/25 rounded-[3px] transition-colors group/btn"
                                    >
                                      <span className="text-xs font-semibold text-[#cb3131]">
                                        {(buyNo * 100).toFixed(1)}¢
                                      </span>
                                      <span className="text-[9px] text-[#cb3131]/80 font-medium">{mainMarket.outcomes?.[1] || t("trading.no")}</span>
                                    </button>
                                  </div>
                                ) : (
                                  <div className="flex items-center justify-center">
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        toggleExpand(e);
                                      }}
                                      className="flex items-center gap-1 px-2.5 py-1.5 bg-gray-50 dark:bg-white/[0.04] hover:bg-gray-100 dark:hover:bg-white/[0.06] border border-gray-200 dark:border-[#1A1A1A] hover:border-gray-300 dark:hover:border-white/[0.1] rounded-[5px] transition-colors text-gray-500 dark:text-gray-400 text-[10px] font-medium"
                                    >
                                      {isExpanded ? t("table.collapse") : t("table.expand")}
                                      {isExpanded ? <ChevronUp className="w-2.5 h-2.5" /> : <ChevronDown className="w-2.5 h-2.5" />}
                                    </button>
                                  </div>
                                )}
                              </td>

                              {/* Per-timeframe % change cells (1h / 24h / 7d) — PM only */}
                              {effectiveSourceFilter === 'polymarket' && (
                                <MarketsListChangeCells event={event} getEventPriceChange={getEventPriceChange} />
                              )}

                              {/* Chart — mini sparkline */}
                              <td className="px-3 py-2.5">
                                <div className="flex justify-center">
                                  <SparklineFilledGradient
                                    data={yesSparklineData}
                                    width={72}
                                    height={24}
                                  />
                                </div>
                              </td>

                              {/* Smart Money + Smart ROI cells — PM only */}
                              {effectiveSourceFilter === 'polymarket' && (
                                <MarketsListSmartCells
                                  row={getSmartRow(((mainMarket as any)?.conditionId || (event as any).conditionId || '').toLowerCase())}
                                  outcomeLabel={(mainMarket as any)?.groupItemTitle || (mainMarket as any)?.question || event.title}
                                />
                              )}

                              {/* 24h Volume + Liquidity (PM only) + Total Volume */}
                              <MarketsListVolumeCells
                                volume={event.volume || 0}
                                volume24hr={event.volume24hr || 0}
                                liquidity={Number((event as any).liquidity || 0)}
                                effectiveSourceFilter={effectiveSourceFilter}
                              />

                              {/* Bookmark */}
                              <td className="px-1 py-2.5 text-center">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleWatchlist(event.slug, event.title, mainMarket?.conditionId, event.source || 'polymarket');
                                  }}
                                  className={`p-1.5 rounded-[5px] transition-colors mx-auto ${
                                    isInWatchlist(event.slug)
                                      ? "text-[#CCFF00]"
                                      : "text-gray-400 dark:text-gray-600 hover:text-gray-600 dark:hover:text-gray-300"
                                  }`}
                                  title={isInWatchlist(event.slug) ? t("tooltip.removeFromWatchlist") : t("tooltip.addToWatchlist")}
                                >
                                  <Bookmark className={`w-4 h-4 ${isInWatchlist(event.slug) ? 'fill-current' : ''}`} />
                                </button>
                              </td>

                            </tr>

                            {/* Nested Market Rows (when expanded).
                                Cap at first 5 markets to keep the expanded
                                row compact — the full set lives on the
                                event page. The slim API projection often
                                already trims event.markets to 5, so the
                                count we display in the chip
                                (`marketsCount`) and the count we render
                                here can diverge — this branch handles
                                both cases consistently.

                                Filter + sort applied so we don't show:
                                  - resolved markets (BTC "by Sep 30 2025"
                                    at 0% — past dates that already paid out)
                                  - closed/inactive markets
                                  - PM placeholder slots that haven't been
                                    assigned (e.g. FIFA "Team AM", DEM 2028
                                    "Person P/S" — these sit at exactly 50/50
                                    because nothing's ever traded them, and
                                    their groupItemTitle matches PM's TBD
                                    naming convention).
                                Then sort the remaining by yes price descending
                                so the strongest contenders bubble to the top
                                of the expanded view (was raw gamma order). */}
                            {isExpanded && !isBinary && (() => {
                              const parsePrices = (m: any): number[] => {
                                const raw = m.outcomePrices;
                                if (Array.isArray(raw)) return raw.map(Number);
                                if (typeof raw === 'string') {
                                  try { const a = JSON.parse(raw); return Array.isArray(a) ? a.map(Number) : []; }
                                  catch { return []; }
                                }
                                return [];
                              };
                              const isPlaceholderName = (title: string | undefined | null): boolean => {
                                if (!title) return false;
                                // PM uses these for TBD slots: "Team A", "Team AM",
                                // "Person P", "Driver 23", "Candidate X" etc.
                                return /^(team|person|player|candidate|driver|athlete|contestant|option|slot)\s+[a-z0-9]{1,3}$/i.test(title.trim());
                              };
                              return (event.markets || [])
                                .filter((m) => {
                                  if (m.closed) return false;
                                  if (m.active === false) return false;
                                  const prices = parsePrices(m);
                                  const yes = prices[0];
                                  // Default 50/50 + placeholder-pattern name = TBD slot.
                                  // We require BOTH signals to avoid hiding legit coin-flips.
                                  if (yes === 0.5 && isPlaceholderName(m.groupItemTitle || m.question)) {
                                    return false;
                                  }
                                  return true;
                                })
                                .sort((a, b) => {
                                  const aYes = parsePrices(a)[0] ?? 0;
                                  const bYes = parsePrices(b)[0] ?? 0;
                                  return bYes - aYes;
                                })
                                .slice(0, 5);
                            })().map((market) => {
                              // Use realtime prices from WebSocket
                              const [marketYesPrice, marketNoPrice] = getRealtimePrice(market);
                              // gamma serves outcomes/outcomePrices as a JSON-encoded
                              // string ('["Yes","No"]'); the expand-list path fans out
                              // raw event.markets so we have to coerce here too. Crashed
                              // with `outcomes.slice(...).map is not a function` on click.
                              const marketOutcomes: string[] = (() => {
                                if (Array.isArray(market.outcomes)) return market.outcomes;
                                if (typeof market.outcomes === 'string') {
                                  try { const a = JSON.parse(market.outcomes); return Array.isArray(a) ? a : []; }
                                  catch { return []; }
                                }
                                return [];
                              })();
                              const marketOutcomePrices: number[] = (() => {
                                const raw = (market as any).outcomePrices;
                                if (Array.isArray(raw)) return raw.map(Number);
                                if (typeof raw === 'string') {
                                  try { const a = JSON.parse(raw); return Array.isArray(a) ? a.map(Number) : []; }
                                  catch { return []; }
                                }
                                return [];
                              })();
                              const marketIsBinary = marketOutcomes.length === 2;

                              // Use real sparkline from sync worker, fallback to seed-based noise
                              const realHistory = (market as any).yesHistory;
                              const marketYesSparkline = Array.isArray(realHistory) && realHistory.length >= 2
                                ? [...realHistory, marketYesPrice]
                                : (() => {
                                    const fallback = Array.from({ length: 20 }, (_, i) => {
                                      const noise = seededNoise(market.id, i) * 0.08;
                                      const trend = (i / 20) * (marketYesPrice - 0.5) * 0.3;
                                      return Math.max(0.05, Math.min(0.95, 0.5 + trend + noise));
                                    });
                                    fallback.push(marketYesPrice);
                                    return fallback;
                                  })();
                              const marketNoSparkline = marketYesSparkline.map(v => 1 - v);

                              return (
                                <tr
                                  key={market.id}
                                  className="bg-gray-50/30 dark:bg-white/[0.01] hover:bg-gray-50/70 dark:hover:bg-white/[0.02] transition-colors cursor-pointer group border-l-2 border-gray-200 dark:border-[#1A1A1A]"
                                  onClick={() => {
                                    const listSource: InteractionSource = category === 'trending' ? 'trending' : category === 'for-you' ? 'for_you' : 'feed';
                                    trackRecommendation(market.slug, 'view_detail', listSource);
                                    setNavigationSource(listSource);
                                    router.push(sourceUrl(`/event/${event.slug}?market=${market.id}`, event.source === 'kalshi' ? 'kalshi' : source));
                                  }}
                                >
                                  {/* Nested Market Info - Show market question with Yes % */}
                                  <td className="px-4 py-3 pl-8">
                                    {(() => {
                                      // Use realtime price from WebSocket (already calculated above)
                                      const yesPercentage = (marketYesPrice * 100).toFixed(0);

                                      // Use short groupItemTitle if available, otherwise clean up question
                                      let displayName = market.groupItemTitle || market.question;
                                      if (!market.groupItemTitle) {
                                        // Remove "Will " prefix and "?" suffix for cleaner display
                                        displayName = displayName.replace(/^Will\s+/i, '').replace(/\?$/, '');
                                        // Truncate if too long
                                        if (displayName.length > 50) {
                                          displayName = displayName.substring(0, 47) + '...';
                                        }
                                      }

                                      return (
                                        <div className="flex items-center gap-2.5">
                                          {(market as any).image ? (
                                            <div className="w-6 h-6 rounded overflow-hidden bg-gray-100 dark:bg-[#0f0f12] flex-shrink-0">
                                              <CdnImage src={(market as any).image} alt={displayName} width={24} height={24} className="w-full h-full object-cover" />
                                            </div>
                                          ) : (
                                            <div className="w-1.5 h-1.5 rounded-[5px] bg-gray-400 dark:bg-gray-500 flex-shrink-0" />
                                          )}
                                          <span className="font-satoshi text-sm text-gray-700 dark:text-gray-400 line-clamp-1 group-hover:text-gray-700 dark:group-hover:text-gray-300 transition-colors">
                                            {displayName}
                                          </span>
                                          <span className={`text-sm font-bold flex-shrink-0 ${marketYesPrice >= 0.5 ? 'text-[#00FF9D]' : 'text-gray-500'}`}>
                                            {yesPercentage}%
                                          </span>
                                        </div>
                                      );
                                    })()}
                                  </td>

                                  {/* Price — nested market outcomes */}
                                  <td className="px-4 py-3">
                                    {marketIsBinary ? (
                                      <div className="flex items-center justify-center gap-2">
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            openTradingModal(
                                              market.slug,
                                              "YES",
                                              marketYesPrice,
                                              market.question,
                                              event.image,
                                              marketOutcomes?.[0],
                                              marketOutcomes,
                                              event.source as any,
                                              event.sourceData,
                                              market.clobTokenIds || [],
                                              event.slug,
                                            );
                                          }}
                                          className="flex items-center justify-center gap-1 w-[64px] py-1.5 bg-[#359a5e]/15 hover:bg-[#359a5e]/25 rounded-[3px] transition-colors group/btn"
                                        >
                                          <span className="text-[9px] text-[#3db468]/80 font-medium">{marketOutcomes?.[0] || t("trading.yes")}</span>
                                          <span className="text-xs font-semibold text-[#3db468]">
                                            {(marketYesPrice * 100).toFixed(1)}¢
                                          </span>
                                        </button>
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            openTradingModal(
                                              market.slug,
                                              "NO",
                                              marketNoPrice,
                                              market.question,
                                              event.image,
                                              marketOutcomes?.[1],
                                              marketOutcomes,
                                              event.source as any,
                                              event.sourceData,
                                              market.clobTokenIds || [],
                                              event.slug,
                                            );
                                          }}
                                          className="flex items-center justify-center gap-1 w-[64px] py-1.5 bg-[#cb3131]/15 hover:bg-[#cb3131]/25 rounded-[3px] transition-colors group/btn"
                                        >
                                          <span className="text-[9px] text-[#cb3131]/80 font-medium">{marketOutcomes?.[1] || t("trading.no")}</span>
                                          <span className="text-xs font-semibold text-[#cb3131]">
                                            {(marketNoPrice * 100).toFixed(1)}¢
                                          </span>
                                        </button>
                                      </div>
                                    ) : (
                                      /* Multi-outcome market - show top outcomes */
                                      <div className="flex items-center justify-center gap-1 flex-wrap">
                                        {marketOutcomes.slice(0, 3).map((outcome, i) => {
                                          const price = marketOutcomePrices?.[i] || 0;
                                          return (
                                            <button
                                              key={outcome}
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                // Outcome chip lands on the event view with this market + outcome
                                                // pre-selected: /event/<eventSlug>?market=<marketSlug>&outcome=<label>.
                                                // Card-level clicks go to the bare /event/<slug>.
                                                router.push(marketPageUrl({
                                                  marketSlug: market.slug,
                                                  eventSlug: event.slug,
                                                  outcome,
                                                  source: event.source === 'kalshi' ? 'kalshi' : source,
                                                  eventCategory: (event as any).category ?? null,
                                                }));
                                              }}
                                              className="flex items-center gap-1 px-2 py-1 bg-gray-50 dark:bg-white/[0.04] hover:bg-gray-100 dark:hover:bg-white/[0.06] border border-gray-200 dark:border-[#1A1A1A] rounded-[5px] transition-colors"
                                            >
                                              <span className="text-[9px] text-gray-500 font-medium truncate max-w-[60px]">{outcome}</span>
                                              <span className="text-xs font-semibold text-gray-900 dark:text-white">
                                                {(price * 100).toFixed(1)}¢
                                              </span>
                                            </button>
                                          );
                                        })}
                                        {marketOutcomes.length > 3 && (
                                          <span className="text-[9px] text-gray-400">+{marketOutcomes.length - 3}</span>
                                        )}
                                      </div>
                                    )}
                                  </td>

                                  {/* % change cells per nested sub-market — reuses
                                      MarketsListChangeCells by wrapping the
                                      sub-market as a single-market pseudo-event so
                                      the colspans line up with the parent row. */}
                                  {effectiveSourceFilter === 'polymarket' && (
                                    <MarketsListChangeCells
                                      event={{ markets: [market] }}
                                      getEventPriceChange={getEventPriceChange}
                                      variant="nested"
                                    />
                                  )}

                                  {/* Chart — per nested market */}
                                  <td className="px-4 py-3">
                                    <div className="flex justify-center">
                                      <SparklineFilledGradient
                                        data={marketYesSparkline}
                                        width={72}
                                        height={24}
                                      />
                                    </div>
                                  </td>

                                  {/* Smart cells + Volume cells + Bookmark spacer (nested row tail) */}
                                  <MarketsListNestedTailCells
                                    market={market as any}
                                    smartRow={getSmartRow(((market as any)?.conditionId || '').toLowerCase())}
                                    effectiveSourceFilter={effectiveSourceFilter}
                                  />

                                </tr>
                              );
                            })}

                            {/* "View all N markets" row — shown when the
                                event has more sub-markets than what we
                                rendered above. Clicking takes the user
                                to the event page where they can browse
                                the full list with the proper layout
                                (orderbook, holders, etc.). */}
                            {isExpanded && !isBinary && (() => {
                              const renderedCount = Math.min(5, (event.markets || []).length);
                              const totalCount = (event as any).marketsCount ?? (event.markets || []).length;
                              if (totalCount <= renderedCount) return null;
                              return (
                                <tr
                                  className="bg-gray-50/60 dark:bg-white/[0.02] hover:bg-gray-50 dark:hover:bg-white/[0.04] transition-colors cursor-pointer border-l-2 border-gray-200 dark:border-[#1A1A1A]"
                                  onClick={() => {
                                    const listSource: InteractionSource = category === 'trending' ? 'trending' : category === 'for-you' ? 'for_you' : 'feed';
                                    setNavigationSource(listSource);
                                    router.push(sourceUrl(`/event/${event.slug}`, event.source === 'kalshi' ? 'kalshi' : source));
                                  }}
                                >
                                  <td colSpan={12} className="px-4 py-2.5 pl-8">
                                    <div className="flex items-center justify-between text-[12px] font-inter-tight">
                                      <span className="text-gray-500 dark:text-gray-400">
                                        Showing {renderedCount} of {totalCount} markets
                                      </span>
                                      <span className="inline-flex items-center gap-1 font-semibold text-gray-700 dark:text-gray-200 group-hover:text-gray-900 dark:group-hover:text-white">
                                        View all {totalCount} markets
                                        <ArrowRight className="w-3 h-3" />
                                      </span>
                                    </div>
                                  </td>
                                </tr>
                              );
                            })()}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
              </>
            )}

          </>
        );
        })()}


        {/* Load More Button - Outside the IIFE so it shows for all categories including trending */}
        {/* Hide for dashboard categories that have their own layouts */}
        {(() => {
          const hiddenCategories = ['breaking', 'for-you', 'bookmarks', 'mentions', 'sports', 'esports', 'world-cup', 'fed-rates', 'macro', 'trump'];
          if (searchQuery || hiddenCategories.includes(category || '')) return null;
          // Kalshi/Predict/Hyperliquid — grid view has its own load more, skip here.
          // Without 'hyperliquid' here, the Polymarket-pagination fallback below
          // (line ~10012, `if (hasMore)`) rendered a SECOND "Load more" button
          // on the HL grid (the global `hasMore` state defaults to true and is
          // never reset when the user is on a non-PM source). Clicking it
          // called `fetchEvents(false)` — i.e. asked the Polymarket pagination
          // path for the next page while viewing HL — which both polluted the
          // grid with PM markets and made the button itself a UX lie.
          if (['kalshi', 'predict', 'hyperliquid'].includes(effectiveSourceFilter) && viewMode === 'grid') return null;

          // Kalshi/Predict load more (list view) — uses client-side + cursor pagination.
          // Hyperliquid list view falls back to the grid component's own pagination too
          // (HL doesn't have a distinct list-view list yet), so skip the PM-pagination
          // fallback for HL regardless of view mode.
          if (effectiveSourceFilter === 'hyperliquid') return null;
          if (effectiveSourceFilter === 'kalshi' && viewMode === 'list') {
            const allKalshi = aggregatedMarkets.filter(m => m.source === 'kalshi');
            const hasMoreLocal = allKalshi.length > kalshiDisplayLimit;
            const hasMoreFromApi = !!kalshiNextCursor;
            if (hasMoreLocal || hasMoreFromApi) {
              return (
                <div className="text-center pt-4 pb-12">
                  <button
                    onClick={() => {
                      if (hasMoreLocal) {
                        setKalshiDisplayLimit(prev => prev + KALSHI_PAGE_SIZE);
                      } else {
                        loadMoreKalshi();
                      }
                    }}
                    disabled={loadingMoreKalshi}
                    className="px-5 py-2 bg-black/[0.04] dark:bg-white/[0.06] text-gray-700 dark:text-gray-300 rounded-[5px] hover:bg-black/[0.12] dark:hover:bg-white/[0.14] active:scale-[0.97] disabled:opacity-50 transition-colors font-satoshi font-semibold text-sm"
                  >
                    {loadingMoreKalshi ? t("common.loading") : t("common.loadMore")}
                  </button>
                </div>
              );
            }
            return null;
          }
          // Predict list-view: branch on `predictOffset < predictTotal`, the
          // exact gate the grid view uses (line ~8450). The previous block
          // shared the Kalshi cursor/limit state which is `null`/`0` while
          // on /predict — making the button render with no condition that
          // could ever flip true, and even if it did it would call
          // loadMoreKalshi against a different source. Same family of
          // "global state reused across sources" as the HL load-more bug.
          if (effectiveSourceFilter === 'predict' && viewMode === 'list') {
            if (predictOffset < predictTotal) {
              return (
                <div className="text-center pt-4 pb-12">
                  <button
                    onClick={loadMorePredict}
                    disabled={loadingMorePredict}
                    className="px-5 py-2 bg-black/[0.04] dark:bg-white/[0.06] text-gray-700 dark:text-gray-300 rounded-[5px] hover:bg-black/[0.12] dark:hover:bg-white/[0.14] active:scale-[0.97] disabled:opacity-50 transition-colors font-satoshi font-semibold text-sm"
                  >
                    {loadingMorePredict ? t("common.loading") : t("common.loadMore")}
                  </button>
                </div>
              );
            }
            return null;
          }

          // Polymarket load more — uses server-side pagination
          if (hasMore) {
            return (
              <div className="text-center pt-4 pb-12">
                <button
                  onClick={() => fetchEvents(false)}
                  disabled={loadingMore}
                  className="px-5 py-2 bg-black/[0.04] dark:bg-white/[0.06] text-gray-700 dark:text-gray-300 rounded-[5px] hover:bg-black/[0.12] dark:hover:bg-white/[0.14] active:scale-[0.97] disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-satoshi font-semibold text-sm"
                >
                  {loadingMore ? t("common.loading") : t("common.loadMore")}
                </button>
              </div>
            );
          }
          return null;
        })()}
          </div>{/* /flex-1 content area */}
        </div>{/* /crypto flex wrapper */}
      </div>

      {/* Crypto News Sidebar (Desktop only, Crypto category) */}
      {category === "crypto" && (
        <CryptoNewsSidebar
          cryptoNews={cryptoNews}
          open={newsSidebarOpen}
          onToggle={toggleNewsSidebar}
          onClose={closeNewsSidebar}
          labels={newsSidebarLabels}
        />
      )}

    </div>
    </ListPriceProvider>
  );
}
